import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo, Component } from 'react'
import { supabase } from './supabase'
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  ComposedChart, Line, Legend,
} from 'recharts'
import { usePlaidLink } from 'react-plaid-link'

// Inject global keyframes once
if (!document.getElementById("grove-keyframes")) {
  const s = document.createElement("style")
  s.id = "grove-keyframes"
  s.textContent = `@keyframes spin { to { transform: rotate(360deg) } }`
  document.head.appendChild(s)
}

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001"

// ── Global toast helper — fire from anywhere, no prop drilling ──
function fireToast(message, type = "error") {
  window.dispatchEvent(new CustomEvent("grove:toast", { detail: { message, type } }))
}

// ── Auto-report errors to server (best-effort, silent) ──
async function autoReportError(message, stack) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return // not logged in, skip
    // Deduplicate — don't send same error more than once per session
    const key = `grove_err_${btoa(message || "").slice(0, 20)}`
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, "1")
    fetch(`${API_BASE}/api/report-issue`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${session.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        isAuto: true, severity: "auto",
        errorMessage: message, errorStack: stack,
        page: window.location.href,
        userAgent: navigator.userAgent,
      }),
    }).catch(() => {})
  } catch {}
}

// ── ErrorBoundary — catches React render errors ──
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { crashed: false, error: null } }
  static getDerivedStateFromError(error) { return { crashed: true, error } }
  componentDidCatch(error, info) { autoReportError(error?.message, (error?.stack || "") + "\n\nComponent stack:\n" + info?.componentStack) }
  render() {
    if (!this.state.crashed) return this.props.children
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "sans-serif", padding: 24, background: "#f9fafb" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🌿</div>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Something went wrong</h1>
        <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, textAlign: "center", maxWidth: 360 }}>
          Grove hit an unexpected error. It's been automatically reported. Try refreshing — if the problem persists, go to Settings → Report an Issue.
        </p>
        <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", borderRadius: 8, background: "#2F855A", color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Refresh page
        </button>
      </div>
    )
  }
}

function ToastContainer() {
  const [toasts, setToasts] = useState([])

  useEffect(() => {
    function handle(e) {
      const id = Date.now() + Math.random()
      setToasts(prev => [...prev.slice(-4), { id, ...e.detail }])
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
    }
    window.addEventListener("grove:toast", handle)
    return () => window.removeEventListener("grove:toast", handle)
  }, [])

  if (!toasts.length) return null

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }}>
      {toasts.map(toast => (
        <div key={toast.id} style={{
          padding: "12px 16px",
          borderRadius: 10,
          background: toast.type === "error" ? "#EF4444" : toast.type === "success" ? "#059669" : "#F59E0B",
          color: "#fff",
          fontSize: 13,
          fontWeight: 500,
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          maxWidth: 320,
          lineHeight: 1.4,
          animation: "grove-toast-in 0.2s ease",
        }}>
          {toast.type === "error" && "⚠ "}{toast.message}
        </div>
      ))}
      <style>{`@keyframes grove-toast-in { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }`}</style>
    </div>
  )
}

async function apiPost(path, body, session) {
  const token = session?.access_token
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
  return res
}

const CATEGORY_MAP = {
  // Needs
  Groceries:    "needs",
  Utilities:    "needs",
  Healthcare:   "needs",
  Transport:    "needs",
  Insurance:    "needs",
  Rent:         "needs",
  "Food and Drink": "needs",

  // Wants
  Dining:          "wants",
  Entertainment:   "wants",
  Shopping:        "wants",
  Subscriptions:   "wants",
  Travel:          "wants",
  Recreation:      "wants",
  Miscellaneous:   "wants",

  // Savings
  "Cash Savings":   "savings",
  "Emergency Fund": "savings",
  "Investments":    "savings",
  "Retirement":     "savings",
  "Savings Goal":   "savings",

  // Income (Plaid sends these as negative amounts natively)
  Paycheck:       "income",
  Dividends:      "income",
  Interest:       "income",
  "Other Income": "income",
  Transfer:    "transfer",
}
const MERCHANT_PATTERNS = [
  { keywords: ["netflix","spotify","hulu","disney+","hbo","peacock","paramount","apple tv","youtube premium","amazon prime","sling","fubo"], category: "Subscriptions",   group: "wants"  },
  { keywords: ["tesla supercharger","supercharger","shell","exxon","chevron","bp ","sunoco","mobil","gulf","citgo","marathon","speedway","wawa","blink charging","chargepoint","evgo"], category: "Gas & Charging", group: "needs"  },
  { keywords: ["uber","lyft","taxi","yellow cab","metro","transit","bus pass","parking","e-zpass","ezpass","sunpass","toll"], category: "Transport",     group: "needs"  },
  { keywords: ["whole foods","trader joe","publix","kroger","safeway","aldi","wegmans","sprouts","costco","sam's club","bj's wholesale","market","grocery","supermarket"], category: "Groceries",    group: "needs"  },
  { keywords: ["starbucks","dunkin","coffee","cafe","la colombe","dutch bros","peet's"], category: "Coffee",       group: "wants"  },
  { keywords: ["mcdonald","burger king","wendy's","chipotle","chick-fil","taco bell","subway","domino","pizza hut","papa john","doordash","grubhub","uber eats","seamless","instacart meals"], category: "Dining",       group: "wants"  },
  { keywords: ["amazon","walmart","target","ebay","etsy","best buy","apple store","wayfair","ikea","home depot","lowe's","costco.com"], category: "Shopping",     group: "wants"  },
  { keywords: ["gym","planet fitness","equinox","orangetheory","solidcore","crossfit","peloton","classpass","crunch"], category: "Fitness",      group: "wants"  },
  { keywords: ["cvs","walgreens","rite aid","pharmacy","duane reade"], category: "Medications",  group: "needs"  },
  { keywords: ["dr ","dentist","dental","vision","optom","hospital","urgent care","clinic","medical","health"], category: "Healthcare",   group: "needs"  },
  { keywords: ["geico","progressive","state farm","allstate","nationwide","liberty mutual","usaa","insurance"], category: "Insurance",    group: "needs"  },
  { keywords: ["at&t","verizon","t-mobile","sprint","comcast","xfinity","spectrum","cox","internet","wireless"], category: "Phone/Internet",group:"needs"  },
  { keywords: ["robinhood","acorns","sofi securities","fidelity","schwab","vanguard","etrade","webull","coinbase","crypto"], category: "Investments",  group: "savings"},
  { keywords: ["paypal","venmo","zelle","cash app","wire transfer","bank transfer"], category: "Transfer",     group: "transfer"},
  { keywords: ["amex epayment","epayment","credit card payment","card payment"], category: "Credit Card Payment", group: "transfer"},
  { keywords: ["netflix","hulu","disney","paramount","peacock","max","apple tv+"], category: "Subscriptions",  group: "wants"  },
  { keywords: ["airline","delta","united","american airlines","southwest","jetblue","spirit","frontier","airbnb","vrbo","hotel","marriott","hilton","hyatt"], category: "Travel",       group: "wants"  },
  { keywords: ["steam","playstation","xbox","nintendo","gaming"], category: "Gaming",       group: "wants"  },
  { keywords: ["electric","gas & elec","pge","con ed","duke energy","fpl","utility"], category: "Utilities",    group: "needs"  },
]

function getPatternSuggestion(merchantName) {
  if (!merchantName) return null
  const lower = merchantName.toLowerCase()
  for (const rule of MERCHANT_PATTERNS) {
    if (rule.keywords.some(k => lower.includes(k))) {
      const cat = CATEGORIES.find(c => c.label === rule.category)
      return cat ? { category: rule.category, group: rule.group, emoji: cat.emoji, source: "pattern" } : null
    }
  }
  return null
}

const CATEGORIES = [
  // Income — Employment
  { label: "Paycheck",       group: "income",  subgroup: "Employment",  emoji: "💼" },
  { label: "Bonus",          group: "income",  subgroup: "Employment",  emoji: "🎁" },
  { label: "Freelance",      group: "income",  subgroup: "Employment",  emoji: "💻" },
  // Income — Returns
  { label: "Dividends",      group: "income",  subgroup: "Returns",     emoji: "📈" },
  { label: "Interest",       group: "income",  subgroup: "Returns",     emoji: "🏦" },
  { label: "Refund",         group: "income",  subgroup: "Returns",     emoji: "↩️" },
  { label: "Other Income",   group: "income",  subgroup: "Returns",     emoji: "💰" },
  // Needs — Housing
  { label: "Rent/Mortgage",    group: "needs", subgroup: "Housing",        emoji: "🏠" },
  { label: "Utilities",        group: "needs", subgroup: "Housing",        emoji: "⚡" },
  { label: "Home Maintenance", group: "needs", subgroup: "Housing",        emoji: "🔧" },
  // Needs — Transportation
  { label: "Car Payment",      group: "needs", subgroup: "Transportation", emoji: "🚘" },
  { label: "Car Maintenance",  group: "needs", subgroup: "Transportation", emoji: "🔩" },
  { label: "Gas & Charging",   group: "needs", subgroup: "Transportation", emoji: "⛽" },
  { label: "Transport",        group: "needs", subgroup: "Transportation", emoji: "🚗" },
  { label: "Tolls & Parking",  group: "needs", subgroup: "Transportation", emoji: "🅿️" },
  // Needs — Food
  { label: "Groceries",        group: "needs", subgroup: "Food",           emoji: "🛒" },
  // Needs — Health
  { label: "Healthcare",       group: "needs", subgroup: "Health",         emoji: "🏥" },
  { label: "Insurance",        group: "needs", subgroup: "Health",         emoji: "🛡️" },
  // Needs — Communication
  { label: "Phone/Internet",   group: "needs", subgroup: "Communication",  emoji: "📱" },
  // Needs — Family & Education
  { label: "Education",        group: "needs", subgroup: "Family",         emoji: "🎓" },
  { label: "Childcare",        group: "needs", subgroup: "Family",         emoji: "👶" },
  { label: "Pet Care",         group: "needs", subgroup: "Family",         emoji: "🐾" },
  // Needs — Debt
  { label: "Student Loans",    group: "needs", subgroup: "Debt",           emoji: "🎓" },
  { label: "ATM",              group: "needs", subgroup: "Debt",           emoji: "🏧" },
  // Wants — Food & Drink
  { label: "Dining",           group: "wants", subgroup: "Food & Drink",   emoji: "🍽️" },
  // Wants — Shopping & Style
  { label: "Shopping",         group: "wants", subgroup: "Shopping",       emoji: "🛍️" },
  { label: "Personal Care",    group: "wants", subgroup: "Shopping",       emoji: "💅" },
  // Wants — Entertainment
  { label: "Entertainment",    group: "wants", subgroup: "Entertainment",  emoji: "🎬" },
  { label: "Subscriptions",    group: "wants", subgroup: "Entertainment",  emoji: "📺" },
  { label: "Events",           group: "wants", subgroup: "Entertainment",  emoji: "🎟️" },
  // Wants — Fitness & Travel
  { label: "Fitness",          group: "wants", subgroup: "Fitness & Travel", emoji: "💪" },
  { label: "Travel",           group: "wants", subgroup: "Fitness & Travel", emoji: "✈️" },
  // Wants — Giving
  { label: "Gifts",            group: "wants", subgroup: "Giving",         emoji: "🎀" },
  { label: "Charity",          group: "wants", subgroup: "Giving",         emoji: "💝" },
  // Wants — Other
  { label: "Miscellaneous",    group: "wants", subgroup: "Other",          emoji: "🎲" },
  { label: "Other",            group: "wants", subgroup: "Other",          emoji: "•" },
  // Savings
  { label: "Cash Savings",     group: "savings", subgroup: "Savings",      emoji: "💰" },
  { label: "Emergency Fund",   group: "savings", subgroup: "Savings",      emoji: "🛟" },
  { label: "Investments",      group: "savings", subgroup: "Savings",      emoji: "📊" },
  { label: "Retirement",       group: "savings", subgroup: "Savings",      emoji: "🏖️" },
  { label: "Savings Goal",     group: "savings", subgroup: "Savings",      emoji: "🎯" },
  // Other
  { label: "Transfer",             group: "transfer",   subgroup: "Transfer",   emoji: "⇄" },
  { label: "Credit Card Payment",  group: "transfer",   subgroup: "Transfer",   emoji: "💳" },
  { label: "Investment",           group: "investment", subgroup: "Investment", emoji: "📊" },
]

const GROUP_STYLE = {
  income:     { label: "Income",     color: "#2F855A", bg: "rgba(47,133,90,0.10)"   },
  needs:      { label: "Needs",      color: "#3B82F6", bg: "rgba(59,130,246,0.10)"  },
  wants:      { label: "Wants",      color: "#F59E0B", bg: "rgba(245,158,11,0.10)"  },
  savings:    { label: "Savings",    color: "#8B5CF6", bg: "rgba(139,92,246,0.10)"  },
  transfer:   { label: "Transfer",   color: "#94A3B8", bg: "rgba(148,163,184,0.10)" },
  investment: { label: "Investment", color: "#7CB342", bg: "rgba(124,179,66,0.10)"  },
}

function getTxGroup(tx) {
  // tx here is a UI-convention tx (spend = negative, income = positive)
  if (tx.is_transfer || tx.category === "Transfer" || tx.category === "Credit Card Payment") return "transfer"
  if (tx.amount > 0) return "income"
  return CATEGORY_MAP[tx.category] || "wants" // default uncategorised spend to wants
}

// ── THEME CONTEXT ──────────────────────────────────────────
const ThemeContext = createContext()
const useTheme = () => useContext(ThemeContext)

const darkTheme = {
  bg: "#0d1117", surface: "#111827", border: "#1e2532",
  text: "#e2e8f0", textMuted: "#64748b", textFaint: "#475569",
  cardBg: "#111827", navBg: "#0d1117", inputBg: "#0d1117",
  hover: "#1a2234", accent: "#2F855A", accent2: "#7CB342",
  isDark: true,
}
const lightTheme = {
  bg: "#F8FAFC", surface: "#FFFFFF", border: "#E2E8F0",
  text: "#0F172A", textMuted: "#64748B", textFaint: "#94A3B8",
  cardBg: "#FFFFFF", navBg: "#FFFFFF", inputBg: "#F8FAFC",
  hover: "#F1F5F9",
  accent: "#2F855A",
  accent2: "#7CB342",
  accentOrange: "#F59E0B",
  success: "#22C55E",
  warning: "#F59E0B",
  error: "#EF4444",
  isDark: false,
}

// ── GLOBAL STYLE HELPERS ───────────────────────────────────
const makeCard = (t) => ({ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" })

function formatTimeAgo(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000)
  if (diff < 60) return "just now"
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}
const makeBtnPrimary = (t) => ({ padding: "10px 22px", borderRadius: 10, border: "none", background: t.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" })
const makeBtnSecondary = (t) => ({ padding: "10px 22px", borderRadius: 10, border: `1px solid ${t.accent}`, background: "transparent", color: t.accent, fontSize: 13, fontWeight: 700, cursor: "pointer" })
const makeBtnOrange = (t) => ({ padding: "10px 22px", borderRadius: 10, border: "none", background: t.accentOrange, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" })
const makeInput = (t) => ({ width: "100%", padding: "11px 14px", background: t.inputBg, border: `1px solid ${t.border}`, borderRadius: 10, color: t.text, fontSize: 14, outline: "none" })

// ── PAYCHECK HELPER ────────────────────────────────────────
const FREQ_TO_MONTHLY = { weekly: 4.333, biweekly: 2.167, "semi-monthly": 2, monthly: 1 }
function calc401k(p, gross) {
  const contrib = p.contrib401kType === "percent"
    ? gross * (p.contrib401kPct || 0) / 100
    : (p.contrib401k || 0)
  const match = gross * (p.match401kPct || 0) / 100
  return { contrib, match }
}

function getPaycheckMonthly(paychecks) {
  return (paychecks || []).reduce((acc, p) => {
    const m = FREQ_TO_MONTHLY[p.frequency] || 2.167
    const gross = p.payType === "hourly" ? (p.hourlyRate||0) * (p.typicalHours||0) : (p.gross||0)
    const { contrib: contrib401k, match: match401k } = calc401k(p, gross)
    const preTaxDed = contrib401k + (p.hsa||0) + (p.healthInsurance||0) + (p.dental||0) + (p.vision||0) + (p.preTaxOther||[]).reduce((s,r)=>s+(r.amount||0),0)
    const taxes     = (p.federalTax||0)+(p.stateTax||0)+(p.socialSecurity||0)+(p.medicare||0)
    const roth401k  = (p.contribRoth401k||0)
    const postTaxDed= roth401k+(p.postTaxOther||[]).reduce((s,r)=>s+(r.amount||0),0)
    const net = gross - preTaxDed - taxes - postTaxDed
    return {
      gross:        acc.gross        + gross        * m,
      contrib401k:  acc.contrib401k  + contrib401k  * m,
      match401k:    acc.match401k    + match401k    * m,
      hsa:          acc.hsa          + (p.hsa||0)   * m,
      benefits:     (acc.benefits||0) + ((p.healthInsurance||0)+(p.dental||0)+(p.vision||0)) * m,
      preTaxOther:  acc.preTaxOther  + (preTaxDed - contrib401k - (p.hsa||0) - (p.healthInsurance||0) - (p.dental||0) - (p.vision||0)) * m,
      taxes:        acc.taxes        + taxes        * m,
      roth401k:     acc.roth401k     + roth401k     * m,
      postTaxDed:   acc.postTaxDed   + postTaxDed   * m,
      net:          acc.net          + net          * m,
    }
  }, { gross:0, contrib401k:0, match401k:0, hsa:0, benefits:0, preTaxOther:0, taxes:0, roth401k:0, postTaxDed:0, net:0 })
}

// US household net worth percentiles by age group (Federal Reserve SCF 2022)
const US_WEALTH_PERCENTILES = [
  { ageMin: 18, ageMax: 34, p10:  1000, p25:   9000, p50:   39000, p75:  152000, p90:   420000 },
  { ageMin: 35, ageMax: 44, p10:  5000, p25:  35000, p50:  135600, p75:  428000, p90:  1100000 },
  { ageMin: 45, ageMax: 54, p10: 14000, p25:  72000, p50:  247200, p75:  790000, p90:  2000000 },
  { ageMin: 55, ageMax: 64, p10: 30000, p25: 100000, p50:  364500, p75: 1180000, p90:  3000000 },
  { ageMin: 65, ageMax: 74, p10: 40000, p25: 130000, p50:  409900, p75: 1360000, p90:  3300000 },
  { ageMin: 75, ageMax: 99, p10: 38000, p25:  95000, p50:  335600, p75: 1100000, p90:  2800000 },
]
function getUsPercentile(netWorth, age) {
  const bracket = US_WEALTH_PERCENTILES.find(b => age >= b.ageMin && age <= b.ageMax) || US_WEALTH_PERCENTILES[0]
  const { p10, p25, p50, p75, p90 } = bracket
  const points = [{ pct: 0, val: 0 }, { pct: 10, val: p10 }, { pct: 25, val: p25 }, { pct: 50, val: p50 }, { pct: 75, val: p75 }, { pct: 90, val: p90 }, { pct: 99, val: p90 * 4 }]
  for (let i = 1; i < points.length; i++) {
    if (netWorth <= points[i].val) {
      const lo = points[i - 1], hi = points[i]
      const frac = hi.val > lo.val ? (netWorth - lo.val) / (hi.val - lo.val) : 0
      return { percentile: Math.round(lo.pct + frac * (hi.pct - lo.pct)), bracket, medianForAge: p50 }
    }
  }
  return { percentile: 99, bracket, medianForAge: p50 }
}

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
]

const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "CAD", symbol: "CA$", label: "Canadian Dollar" },
  { code: "CHF", symbol: "CHF", label: "Swiss Franc" },
  { code: "JPY", symbol: "¥", label: "Japanese Yen" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
  { code: "BRL", symbol: "R$", label: "Brazilian Real" },
  { code: "MXN", symbol: "MX$", label: "Mexican Peso" },
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
]

// ── LEVELS & XP ───────────────────────────────────────────────────────────────
const LEVELS = [
  { level: 1, name: "Seed",          emoji: "🌰", xp: 0    },
  { level: 2, name: "Sprout",        emoji: "🌱", xp: 100  },
  { level: 3, name: "Sapling",       emoji: "🪴", xp: 300  },
  { level: 4, name: "Young Tree",    emoji: "🌿", xp: 600  },
  { level: 5, name: "Tree",          emoji: "🌳", xp: 1000 },
  { level: 6, name: "Grove",         emoji: "🌲", xp: 1750 },
  { level: 7, name: "Forest",        emoji: "🌴", xp: 2750 },
  { level: 8, name: "Ancient Forest",emoji: "🎋", xp: 4000 },
  { level: 9, name: "Ecosystem",     emoji: "🌏", xp: 6000 },
]

const XP_ACTIONS = {
  connect_first_account:  { xp: 50,  label: "Connected first account",  emoji: "🏦" },
  sync_daily:             { xp: 10,  label: "Daily sync",               emoji: "🔄" },
  categorize_10:          { xp: 20,  label: "Categorized 10 transactions", emoji: "🏷️" },
  budget_set:             { xp: 15,  label: "Set a budget category",    emoji: "📊" },
  budget_under_month:     { xp: 50,  label: "Stayed under budget",      emoji: "✅" },
  net_worth_up:           { xp: 30,  label: "Net worth increased",      emoji: "📈" },
  retirement_goal_set:    { xp: 40,  label: "Set retirement goal",      emoji: "🎯" },
  emergency_fund_full:    { xp: 75,  label: "Emergency fund fully funded", emoji: "🛡️" },
  streak_7:               { xp: 25,  label: "7-day login streak",       emoji: "🔥" },
}

function getLevelInfo(totalXp) {
  let current = LEVELS[0]
  let next = LEVELS[1]
  for (let i = 0; i < LEVELS.length; i++) {
    if (totalXp >= LEVELS[i].xp) {
      current = LEVELS[i]
      next = LEVELS[i + 1] || null
    }
  }
  const progress = next
    ? Math.min(100, Math.round(((totalXp - current.xp) / (next.xp - current.xp)) * 100))
    : 100
  return { current, next, progress, totalXp }
}

async function awardXP(userId, actionKey) {
  const action = XP_ACTIONS[actionKey]
  if (!action) return { awarded: false }
  const { error } = await supabase.from("xp_events").insert({
    user_id: userId, action: actionKey, xp: action.xp,
  })
  if (error) return { awarded: false } // duplicate key = already awarded, silently skip
  await supabase.rpc("increment_xp", { p_user_id: userId, p_xp: action.xp })
  return { awarded: true, xp: action.xp, label: action.label }
}

// ── FUND FEE DATABASE ─────────────────────────────────────────────────────────
// expense_ratio is annual % (e.g. 0.03 = 0.03%). Thresholds by category.
const FUND_FEE_DB = {
  // US Large Cap
  SPY:   { er: 0.09, cat: "US Large Cap",      alts: [{ t: "VOO", er: 0.03, n: "Vanguard S&P 500 ETF" }, { t: "IVV", er: 0.03, n: "iShares Core S&P 500 ETF" }, { t: "SPLG", er: 0.02, n: "SPDR Portfolio S&P 500" }] },
  VOO:   { er: 0.03, cat: "US Large Cap",      alts: [{ t: "SPLG", er: 0.02, n: "SPDR Portfolio S&P 500" }] },
  IVV:   { er: 0.03, cat: "US Large Cap",      alts: [{ t: "SPLG", er: 0.02, n: "SPDR Portfolio S&P 500" }] },
  SPLG:  { er: 0.02, cat: "US Large Cap",      alts: [] },
  // US Total Market
  VTI:   { er: 0.03, cat: "US Total Market",   alts: [{ t: "ITOT", er: 0.03, n: "iShares Core S&P Total US Stock Mkt" }, { t: "SCHB", er: 0.03, n: "Schwab US Broad Market ETF" }] },
  ITOT:  { er: 0.03, cat: "US Total Market",   alts: [] },
  SCHB:  { er: 0.03, cat: "US Total Market",   alts: [] },
  VTSAX: { er: 0.04, cat: "US Total Market",   alts: [{ t: "VTI", er: 0.03, n: "Vanguard Total Stock Market ETF" }] },
  FSKAX: { er: 0.015,cat: "US Total Market",   alts: [] },
  FZROX: { er: 0.00, cat: "US Total Market",   alts: [] },
  SWTSX: { er: 0.03, cat: "US Total Market",   alts: [] },
  // US Tech / Growth
  QQQ:   { er: 0.20, cat: "US Tech/Growth",    alts: [{ t: "QQQM", er: 0.15, n: "Invesco Nasdaq 100 ETF" }] },
  QQQM:  { er: 0.15, cat: "US Tech/Growth",    alts: [] },
  // International Developed
  EFA:   { er: 0.32, cat: "Intl Developed",    alts: [{ t: "IEFA", er: 0.07, n: "iShares Core MSCI EAFE ETF" }, { t: "SCHF", er: 0.06, n: "Schwab International Equity ETF" }] },
  IEFA:  { er: 0.07, cat: "Intl Developed",    alts: [{ t: "SCHF", er: 0.06, n: "Schwab International Equity ETF" }] },
  SCHF:  { er: 0.06, cat: "Intl Developed",    alts: [] },
  VXUS:  { er: 0.07, cat: "Intl Total Market", alts: [{ t: "IXUS", er: 0.07, n: "iShares Core MSCI Total Intl" }] },
  IXUS:  { er: 0.07, cat: "Intl Total Market", alts: [] },
  // Emerging Markets
  EEM:   { er: 0.70, cat: "Emerging Markets",  alts: [{ t: "IEMG", er: 0.09, n: "iShares Core MSCI EM ETF" }, { t: "VWO", er: 0.08, n: "Vanguard FTSE Emerging Markets" }, { t: "SCHE", er: 0.11, n: "Schwab Emerging Markets ETF" }] },
  IEMG:  { er: 0.09, cat: "Emerging Markets",  alts: [{ t: "VWO", er: 0.08, n: "Vanguard FTSE Emerging Markets" }] },
  VWO:   { er: 0.08, cat: "Emerging Markets",  alts: [] },
  SCHE:  { er: 0.11, cat: "Emerging Markets",  alts: [] },
  // US Bonds
  AGG:   { er: 0.03, cat: "US Total Bond",     alts: [{ t: "BND", er: 0.03, n: "Vanguard Total Bond Market ETF" }] },
  BND:   { er: 0.03, cat: "US Total Bond",     alts: [] },
  SCHZ:  { er: 0.03, cat: "US Total Bond",     alts: [] },
  TLT:   { er: 0.15, cat: "Long-Term Treasury",alts: [{ t: "VGLT", er: 0.04, n: "Vanguard Long-Term Treasury ETF" }, { t: "EDV", er: 0.06, n: "Vanguard Ext Duration Treasury" }] },
  VGLT:  { er: 0.04, cat: "Long-Term Treasury",alts: [] },
  TIPS:  { er: 0.19, cat: "Inflation-Protected",alts:[{ t: "SCHP", er: 0.03, n: "Schwab US TIPS ETF" }, { t: "VTIP", er: 0.04, n: "Vanguard Short-Term Inflation ETF" }] },
  // Real Estate
  VNQ:   { er: 0.13, cat: "US REITs",          alts: [{ t: "SCHH", er: 0.07, n: "Schwab US REIT ETF" }, { t: "USRT", er: 0.08, n: "iShares Core US REIT ETF" }] },
  SCHH:  { er: 0.07, cat: "US REITs",          alts: [] },
  // Dividends
  SCHD:  { er: 0.06, cat: "US Dividend",       alts: [] },
  VYM:   { er: 0.06, cat: "US Dividend",       alts: [] },
  // Sector
  XLK:   { er: 0.09, cat: "Tech Sector",       alts: [] },
  XLF:   { er: 0.09, cat: "Financials Sector", alts: [] },
  XLE:   { er: 0.09, cat: "Energy Sector",     alts: [] },
  // Gold / Commodities
  GLD:   { er: 0.40, cat: "Gold",              alts: [{ t: "GLDM", er: 0.10, n: "SPDR Gold MiniShares" }, { t: "IAUM", er: 0.09, n: "iShares Gold Trust Micro" }] },
  IAU:   { er: 0.25, cat: "Gold",              alts: [{ t: "GLDM", er: 0.10, n: "SPDR Gold MiniShares" }, { t: "IAUM", er: 0.09, n: "iShares Gold Trust Micro" }] },
  GLDM:  { er: 0.10, cat: "Gold",              alts: [{ t: "IAUM", er: 0.09, n: "iShares Gold Trust Micro" }] },
  // Active / Thematic (high-cost, flag always)
  ARKK:  { er: 0.75, cat: "Active Thematic",   alts: [{ t: "QQQ", er: 0.20, n: "Invesco QQQ (Nasdaq-100)" }, { t: "QQQM", er: 0.15, n: "Invesco Nasdaq 100 ETF" }] },
  JEPI:  { er: 0.35, cat: "Covered Call",      alts: [{ t: "SCHD", er: 0.06, n: "Schwab US Dividend Equity ETF" }] },
  JEPQ:  { er: 0.35, cat: "Covered Call",      alts: [{ t: "QQQM", er: 0.15, n: "Invesco Nasdaq 100 ETF" }] },
  // S&P 500 Mutual Funds
  VFIAX: { er: 0.04, cat: "S&P 500 Mutual Fund",alts:[{ t: "VOO", er: 0.03, n: "Vanguard S&P 500 ETF" }, { t: "SPLG", er: 0.02, n: "SPDR Portfolio S&P 500" }] },
  // Money Market / Cash
  VMFXX: { er: 0.11, cat: "Money Market",      alts: [] },
  SGOV:  { er: 0.09, cat: "T-Bill ETF",        alts: [{ t: "BIL", er: 0.14, n: "SPDR 1-3 Month T-Bill ETF" }] },
}
// Flag thresholds by category (above = flagged)
const FEE_THRESHOLDS = {
  "US Large Cap": 0.10, "US Total Market": 0.10, "US Tech/Growth": 0.25,
  "Intl Developed": 0.20, "Intl Total Market": 0.15, "Emerging Markets": 0.20,
  "US Total Bond": 0.10, "Long-Term Treasury": 0.20, "Inflation-Protected": 0.15,
  "US REITs": 0.20, "US Dividend": 0.15, "Tech Sector": 0.15,
  "Financials Sector": 0.15, "Energy Sector": 0.15,
  "Gold": 0.20, "Active Thematic": 0.50, "Covered Call": 0.50,
  "S&P 500 Mutual Fund": 0.10, "Money Market": 0.20, "T-Bill ETF": 0.15,
}

const navItems = [
  { id: "overview",     label: "Overview",     icon: "🏠" },
  { id: "accounts",     label: "Accounts",     icon: "🏦" },
  { id: "transactions", label: "Transactions", icon: "📋" },
  { id: "budget",       label: "Budget",       icon: "🎯" },
  { id: "cashflow",     label: "Cash Flow",    icon: "💸" },
  { id: "investments",  label: "Investments",  icon: "📈" },
  { id: "retirement",   label: "Retirement",   icon: "🏖️" },
  { id: "simulators",   label: "Simulators",   icon: "🧮" },
  { id: "paycheck",     label: "Paycheck",     icon: "💼" },
  { id: "settings",     label: "Settings",     icon: "⚙️" },
  { id: "report",       label: "Report an Issue",    icon: "🚩" },
  { id: "feature",      label: "Request a Feature",  icon: "💡" },
  { id: "waitlist",     label: "Waitlist",     icon: "📋", adminOnly: true },
]
function getAccountCategory(acc) {
  const type = (acc.account_type || "").toLowerCase()
  const sub  = (acc.subtype || "").toLowerCase()
  if (["401k","403b","roth 401k","457b","pension","profit sharing"].some(x => sub.includes(x))) return "401k / Pension"
  if (["ira","roth","sep ira","simple ira","rollover"].some(x => sub.includes(x))) return "IRA / Roth"
  if (type === "depository") return "Cash & Bank"
  if (type === "investment" || sub.includes("brokerage") || sub.includes("mutual fund")) return "Brokerage"
  if (type === "credit") return "Credit Cards"
  if (type === "loan") return "Loans"
  return "Other"
}

function getDefaultReturnRate(acc) {
  const cat = getAccountCategory(acc)
  if (cat === "Cash & Bank") return 3.5
  if (cat === "401k / Pension" || cat === "IRA / Roth") return 10
  if (cat === "Brokerage") return 10
  return null // blank — user must set
}

function AcctInputs({ accId, acctSettings, setSetting, saveSettings, t }) {
  const s = acctSettings[accId] || {}
  const [rate, setRate] = useState(s.return_rate ?? "")
  const [contrib, setContrib] = useState(s.monthly_contribution ?? 0)
  useEffect(() => {
    setRate(s.return_rate ?? "")
    setContrib(s.monthly_contribution ?? 0)
  }, [s.return_rate, s.monthly_contribution]) // eslint-disable-line
  const inp = { padding: "2px 4px", borderRadius: 5, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 10, outline: "none" }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <input type="number" min="0" max="30" step="0.1" value={rate} placeholder="0"
          style={{ ...inp, width: 42, textAlign: "right" }}
          onChange={e => setRate(e.target.value)}
          onBlur={() => { const v = parseFloat(rate) || 0; setSetting(accId, "return_rate", v); saveSettings() }} />
        <span style={{ fontSize: 10, color: t.textFaint }}>%</span>
      </div>
      <span style={{ fontSize: 10, color: t.border }}>·</span>
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span style={{ fontSize: 10, color: t.textFaint }}>$</span>
        <input type="number" min="0" step="50" value={contrib}
          style={{ ...inp, width: 56 }}
          onChange={e => setContrib(e.target.value)}
          onBlur={() => { const v = parseFloat(contrib) || 0; setSetting(accId, "monthly_contribution", v); saveSettings() }} />
        <span style={{ fontSize: 10, color: t.textFaint }}>/mo</span>
      </div>
    </div>
  )
}

function YearByYearProjection({ theme, card, accounts, inputs, acctSettings = {}, settingsLoaded = false, householdMembers = [], retirementBalance = null }) {
  const t = theme
  const fmt$ = v => `$${Math.round(Math.abs(v)).toLocaleString()}`

  const [expandedYears, setExpandedYears] = useState(new Set())
  const [expandedCats, setExpandedCats] = useState(new Set())

  // ── Projection engine ──────────────────────────────────────────────────────
  const projection = useMemo(() => {
    if (!settingsLoaded || !inputs || !accounts.length) return []
    const { retirement_age, desired_annual_spend, social_security_monthly } = inputs
    const inf = (inputs.inflation_pct || 3) / 100
    const drawdownRate = (inputs.drawdown_return_pct != null ? inputs.drawdown_return_pct : 5) / 100
    const currentYear = new Date().getFullYear()
    const retirementYear = currentYear + Math.max(0, retirement_age - (inputs.current_age || 30))
    const endYear = retirementYear + Math.max(20, 90 - retirement_age)
    const included = accounts.filter(a => acctSettings[a.id]?.include !== false && !["credit","loan"].includes(a.account_type))
    if (!included.length) return []

    let balances = {}
    included.forEach(a => { balances[a.id] = a.balance || 0 })

    const rows = []
    let cumulativeContribs = 0, cumulativeGrowth = 0
    // Withdrawal grows with inflation each drawdown year; initialized at retirement
    let annualWithdrawal = null
    let withdrawalFallback = false

    for (let year = currentYear; year <= endYear; year++) {
      const isAccum = year < retirementYear
      const yearAccts = []
      let totStart = 0, totContribs = 0, totGrowth = 0, totWithdrawals = 0, totEnd = 0

      if (isAccum) {
        included.forEach(a => {
          const s = acctSettings[a.id] || { return_rate: 7, monthly_contribution: 0 }
          const rate = (Number(s.return_rate) || inputs.expected_return_pct || 7) / 100
          const annualContrib = (Number(s.monthly_contribution) || 0) * 12
          const start = balances[a.id]
          const growth = start * rate + annualContrib * (rate / 2)
          const end = start + annualContrib + growth
          yearAccts.push({ id: a.id, name: a.account_name, institution: a.institution_name, category: getAccountCategory(a), owner: householdMembers.find(m => m.id === a.assigned_to)?.name || null, start, contributions: annualContrib, growth, withdrawals: 0, end })
          totStart += start; totContribs += annualContrib; totGrowth += growth; totEnd += end
          balances[a.id] = end
        })
      } else {
        // First drawdown year: convert today's-dollar spend to nominal dollars at retirement
        if (annualWithdrawal === null) {
          const ssAnnual = (social_security_monthly || 0) * 12
          if (desired_annual_spend && desired_annual_spend > 0) {
            const yearsToRetirement = Math.max(0, retirementYear - currentYear)
            const nominalSpend = desired_annual_spend * Math.pow(1 + inf, yearsToRetirement)
            annualWithdrawal = Math.max(0, nominalSpend - ssAnnual)
          } else {
            // No spending target — fall back to 4% of portfolio at retirement
            const retirementBal = included.reduce((s, a) => s + (balances[a.id] || 0), 0)
            annualWithdrawal = Math.max(retirementBal * 0.04 - ssAnnual, 0)
            withdrawalFallback = true
          }
        }
        const totalBal = included.reduce((s, a) => s + (balances[a.id] || 0), 0)
        included.forEach(a => {
          const start = balances[a.id]
          const growth = start * drawdownRate
          const withdrawal = totalBal > 0 ? (start / totalBal) * annualWithdrawal : 0
          const end = Math.max(0, start + growth - withdrawal)
          yearAccts.push({ id: a.id, name: a.account_name, institution: a.institution_name, category: getAccountCategory(a), owner: householdMembers.find(m => m.id === a.assigned_to)?.name || null, start, contributions: 0, growth, withdrawals: withdrawal, end })
          totStart += start; totGrowth += growth; totWithdrawals += withdrawal; totEnd += end
          balances[a.id] = end
        })
        // Grow withdrawal by inflation for next year
        annualWithdrawal *= (1 + inf)
      }

      cumulativeContribs += totContribs
      cumulativeGrowth += totGrowth
      rows.push({ year, phase: isAccum ? "accumulation" : "drawdown", accounts: yearAccts, totals: { start: totStart, contributions: totContribs, growth: totGrowth, withdrawals: totWithdrawals, end: totEnd, cumulativeContribs, cumulativeGrowth } })
      if (!isAccum && totEnd <= 0) break
    }
    rows._withdrawalFallback = withdrawalFallback
    return rows
  }, [accounts, acctSettings, inputs, settingsLoaded, householdMembers])

  const retirementYear = inputs ? new Date().getFullYear() + Math.max(0, (inputs.retirement_age || 65) - (inputs.current_age || 30)) : null

  const chartData = projection.map(r => ({
    year: r.year,
    Contributions: r.phase === "accumulation" ? Math.round(r.totals.contributions) : 0,
    Growth: Math.round(r.totals.growth),
    Withdrawals: r.phase === "drawdown" ? Math.round(r.totals.withdrawals) : 0,
    Portfolio: Math.round(r.totals.end),
  }))

  // ── Grouped accounts for settings panel ───────────────────────────────────
  const settingsGroups = useMemo(() => {
    const map = {}
    accounts.forEach(a => {
      const cat = getAccountCategory(a)
      if (!map[cat]) map[cat] = []
      map[cat].push(a)
    })
    return Object.entries(map)
  }, [accounts])

  if (!settingsLoaded) return <div style={{ color: t.textMuted, padding: 20 }}>Loading projection...</div>
  if (!inputs) return <div style={{ color: t.textMuted, padding: 20 }}>Set up your plan first.</div>

  // KPI values for the chart header
  const portfolioNow = projection.length ? projection[0].totals.end : 0
  const portfolioAtRetirement = projection.find(r => r.year === retirementYear)?.totals.start || 0
  const portfolioAtEnd = projection.length ? projection[projection.length - 1].totals.end : 0
  const fmtM = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(1)}K` : `$${Math.round(v)}`

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0 }}>Year-by-Year Projection</h2>
        <p style={{ fontSize: 13, color: t.textMuted, marginTop: 4 }}>
          {retirementYear ? `Accumulation → ${retirementYear} · Drawdown after · Return rate and contributions from My Plan above — override per account in the table` : ""}
        </p>
      </div>

      {/* Withdrawal fallback warning */}
      {projection._withdrawalFallback && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", borderRadius: 10, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 16 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#F59E0B", margin: "0 0 2px" }}>No spending target set</p>
            <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Withdrawals are estimated using the <strong>4% rule</strong> on your portfolio at retirement. For a more accurate projection, set your <em>Annual Spending in Retirement</em> in Plan Settings.</p>
          </div>
        </div>
      )}

      {/* Chart — clean line with KPI tiles */}
      {chartData.length > 0 && (
        <div style={{ ...card, padding: "28px 28px 20px", marginBottom: 20 }}>
          {/* KPI row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, marginBottom: 28, borderBottom: `1px solid ${t.border}`, paddingBottom: 24 }}>
            {(() => {
              const endYear = projection.length ? projection[projection.length - 1].year : null
              const endAge = endYear && inputs ? inputs.retirement_age + (endYear - retirementYear) : null
              const lastRow = projection.length ? projection[projection.length - 1] : null
              const depleted = lastRow && lastRow.totals.end <= 0
              const endColor = depleted ? "#EF4444" : portfolioAtEnd < portfolioAtRetirement ? "#F59E0B" : t.accent
              return [
                { label: "Portfolio today", value: fmtM(portfolioNow), sub: null, color: t.text },
                { label: "At retirement", value: fmtM(portfolioAtRetirement), sub: retirementYear ? `Year ${retirementYear}` : null, color: t.text },
                { label: "Retirement year", value: retirementYear || "—", sub: inputs ? `Age ${inputs.retirement_age}` : null, color: t.text },
                { label: endAge ? `End of projection (age ${endAge})` : "End of projection", value: depleted ? "Depleted" : fmtM(portfolioAtEnd), sub: depleted ? `Runs out ${endYear}` : null, color: endColor },
              ].map(({ label, value, sub, color }, i) => (
                <div key={i} style={{ borderLeft: i > 0 ? `1px solid ${t.border}` : "none", paddingLeft: i > 0 ? 24 : 0 }}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 500, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: "-0.5px" }}>{value}</div>
                  {sub && <div style={{ fontSize: 11, color: t.textFaint, marginTop: 3 }}>{sub}</div>}
                </div>
              ))
            })()}
          </div>
          {/* Panel 1 — Portfolio value line */}
          <p style={{ fontSize: 11, fontWeight: 600, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Portfolio Value</p>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={chartData} margin={{ top: 6, right: 16, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="pgGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={t.accent} stopOpacity={0.12} />
                  <stop offset="95%" stopColor={t.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.floor(chartData.length / 10)} />
              <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} width={52}
                tickFormatter={v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : `$${(v/1e3).toFixed(0)}k`} />
              <Tooltip formatter={(val) => [`$${Math.round(val).toLocaleString()}`, "Portfolio"]}
                labelStyle={{ color: t.text, fontWeight: 700 }}
                contentStyle={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 12 }} />
              <ReferenceLine x={retirementYear} stroke={t.accent} strokeDasharray="5 4" strokeWidth={1.5}
                label={{ value: "Retirement", fill: t.accent, fontSize: 10, position: "insideTopRight" }} />
              <Area type="monotone" dataKey="Portfolio" stroke={t.accent} strokeWidth={2.5} fill="url(#pgGrad)" dot={false} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Panel 2 — Annual flows */}
          <div style={{ borderTop: `1px solid ${t.border}`, marginTop: 16, paddingTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 600, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Annual Flows</p>
                <p style={{ fontSize: 10, color: t.textFaint, margin: 0 }}>After retirement: contributions stop — portfolio changes from market returns minus withdrawals</p>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                {[["#10b981","Contributions"],["#6366f1","Market returns"],["#f43f5e","Withdrawals"]].map(([color, label]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: t.textMuted }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />{label}
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={130}>
              <BarChart data={chartData} margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.floor(chartData.length / 10)} />
                <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} width={52}
                  tickFormatter={v => v >= 1e3 ? `$${(v/1e3).toFixed(0)}k` : `$${v}`} />
                <Tooltip
                  formatter={(val, name) => [`$${Math.round(val).toLocaleString()}`, name === "Growth" ? "Market returns" : name]}
                  labelStyle={{ color: t.text, fontWeight: 700 }}
                  contentStyle={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 12 }} />
                <ReferenceLine x={retirementYear} stroke={t.accent} strokeDasharray="5 4" strokeWidth={1} />
                <Bar dataKey="Contributions" stackId="a" fill="#10b981" fillOpacity={0.85} />
                <Bar dataKey="Growth" stackId="a" fill="#6366f1" fillOpacity={0.75} radius={[2,2,0,0]} />
                <Bar dataKey="Withdrawals" stackId="b" fill="#f43f5e" fillOpacity={0.8} radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Year-by-year table — years as columns, accounts as rows */}
      {projection.length > 0 && (() => {
        // Build per-account rows using index position (guaranteed match, no ID comparison)
        const firstYear = projection[0].accounts
        const acctMeta = Object.fromEntries(accounts.map(a => [a.id, a]))
        // Attach all year data by index (same order across all projection rows)
        const acctRows = firstYear.map((a, idx) => ({
          ...(acctMeta[a.id] || {}),
          id: a.id,
          account_name: a.name || (acctMeta[a.id] || {}).account_name,
          institution_name: a.institution || (acctMeta[a.id] || {}).institution_name,
          assigned_to: (acctMeta[a.id] || {}).assigned_to,
          category: a.category,
          yearData: projection.map(row => row.accounts[idx]),
        }))
        const byCategory = {}
        acctRows.forEach(a => {
          if (!byCategory[a.category]) byCategory[a.category] = []
          byCategory[a.category].push(a)
        })
        const colW = 110
        const labelW = 240
        const hdrBg = t.isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"
        const cell = (extra = {}) => ({ width: colW, minWidth: colW, padding: "8px 10px", textAlign: "right", fontSize: 12, borderLeft: `1px solid ${t.border}`, boxSizing: "border-box", whiteSpace: "nowrap", ...extra })
        const label = (extra = {}) => ({ width: labelW, minWidth: labelW, padding: "8px 14px", position: "sticky", left: 0, backgroundColor: t.cardBg, zIndex: 2, boxSizing: "border-box", boxShadow: `2px 0 6px ${t.isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.08)"}`, ...extra })

        return (
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 560 }}>
              <table style={{ borderCollapse: "collapse", width: "max-content" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 3, backgroundColor: t.cardBg }}>
                  <tr style={{ background: hdrBg, borderBottom: `2px solid ${t.border}` }}>
                    <th style={{ ...label({ background: hdrBg, zIndex: 4 }), fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left" }}>Account</th>
                    {projection.map(row => (
                      <th key={row.year} style={{ ...cell({ background: row.year === retirementYear ? `${t.accent}18` : hdrBg, fontWeight: row.year === retirementYear ? 800 : 700, color: row.year === retirementYear ? t.accent : t.textFaint, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }) }}>
                        {row.year}
                        {row.year === retirementYear && <div style={{ fontSize: 8, letterSpacing: "0.05em" }}>RETIRE</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(byCategory).map(([cat, accts]) => {
                    const isCatExp = expandedCats.has(cat)
                    return (
                    <>
                      <tr key={`cat-${cat}`} onClick={() => setExpandedCats(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n })}
                        style={{ borderTop: `1px solid ${t.border}`, cursor: "pointer" }}
                        onMouseEnter={e => { for (const td of e.currentTarget.cells) td.style.backgroundColor = t.isDark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.07)" }}
                        onMouseLeave={e => { const cs = e.currentTarget.cells; cs[0].style.backgroundColor = t.cardBg; for (let i = 1; i < cs.length; i++) cs[i].style.backgroundColor = "" }}>
                        <td style={{ padding: "6px 14px", fontSize: 10, fontWeight: 800, color: t.accent, textTransform: "uppercase", letterSpacing: "0.08em", position: "sticky", left: 0, backgroundColor: t.cardBg, zIndex: 2, whiteSpace: "nowrap", borderLeft: `3px solid ${t.accent}`, boxShadow: `2px 0 6px ${t.isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.08)"}` }}>
                          <span style={{ marginRight: 6 }}>{isCatExp ? "▾" : "▸"}</span>{cat}
                          <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 500, color: t.textFaint, textTransform: "none" }}>({accts.length})</span>
                        </td>
                        {projection.map((row, yi) => {
                          const total = accts.reduce((sum, a) => sum + (a.yearData[yi]?.end || 0), 0)
                          return <td key={row.year} style={cell({ fontWeight: 700, color: t.accent, fontSize: 11, background: row.year === retirementYear ? `${t.accent}10` : t.isDark ? "rgba(99,102,241,0.04)" : "rgba(99,102,241,0.03)" })}>{fmt$(total)}</td>
                        })}
                      </tr>
                      {isCatExp && accts.map(acc => {
                        const isExp = expandedYears.has(acc.id)
                        const member = householdMembers.find(m => m.id === acc.assigned_to)
                        return (
                          <>
                            <tr key={acc.id}
                              onClick={() => setExpandedYears(prev => { const n = new Set(prev); n.has(acc.id) ? n.delete(acc.id) : n.add(acc.id); return n })}
                              style={{ borderBottom: `1px solid ${t.border}`, cursor: "pointer" }}
                              onMouseEnter={e => { for (const td of e.currentTarget.cells) td.style.backgroundColor = t.hover }}
                              onMouseLeave={e => { const cs = e.currentTarget.cells; cs[0].style.backgroundColor = t.cardBg; for (let i = 1; i < cs.length; i++) cs[i].style.backgroundColor = "" }}>
                              <td style={label({ borderBottom: `1px solid ${t.border}` })}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 10, color: t.textFaint, flexShrink: 0 }}>{isExp ? "▾" : "▸"}</span>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{acc.account_name}</div>
                                    <div style={{ fontSize: 10, color: t.textFaint }}>
                                      {acc.institution_name}
                                      {member && <span style={{ color: member.color, fontWeight: 700, marginLeft: 4 }}>{member.name}</span>}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              {acc.yearData.map((d, yi) => {
                                const yr = projection[yi].year
                                return <td key={yr} style={cell({ color: t.text, fontWeight: 500, background: yr === retirementYear ? `${t.accent}08` : "" })}>{d ? fmt$(d.end) : "—"}</td>
                              })}
                            </tr>
                            {isExp && ["contributions","growth","withdrawals"].map(metric => (
                              <tr key={`${acc.id}-${metric}`} style={{ borderBottom: `1px solid ${t.border}`, background: t.isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)" }}>
                                <td style={label({ paddingLeft: 30 })}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: metric === "growth" ? "#6366f1" : metric === "contributions" ? "#10b981" : "#f43f5e", textTransform: "capitalize" }}>{metric === "withdrawals" ? "Withdrawals" : metric === "contributions" ? "Contributions" : "Growth"}</span>
                                </td>
                                {acc.yearData.map((d, yi) => {
                                  const yr = projection[yi].year
                                  const val = d ? d[metric] : 0
                                  const color = metric === "growth" ? "#6366f1" : metric === "contributions" ? "#10b981" : "#f43f5e"
                                  return <td key={yr} style={cell({ fontSize: 11, color: val > 0.5 ? color : t.textFaint, background: yr === retirementYear ? `${t.accent}08` : "" })}>{val > 0.5 ? (metric === "withdrawals" ? `−${fmt$(val)}` : `+${fmt$(val)}`) : "—"}</td>
                                })}
                              </tr>
                            ))}
                          </>
                        )
                      })}
                    </>
                  )})}

                  {/* Total row */}
                  <tr style={{ borderTop: `2px solid ${t.border}` }}>
                    <td style={label({ fontSize: 13, fontWeight: 800, color: t.text })}>Total Portfolio</td>
                    {projection.map(row => (
                      <td key={row.year} style={cell({ fontSize: 13, fontWeight: 800, color: t.accent, background: row.year === retirementYear ? `${t.accent}15` : t.isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" })}>{fmt$(row.totals.end)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}

      {projection.length === 0 && (
        <div style={{ ...card, padding: "48px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
          <p style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 6 }}>No projection yet</p>
          <p style={{ fontSize: 13, color: t.textMuted }}>Make sure accounts are connected and your plan is set up under My Plan.</p>
        </div>
      )}
    </div>
  )
}

const retirementSubNav = [
  { id: "overview", label: "Overview", icon: "📊" },
  { id: "plan", label: "My Plan", icon: "🎯" },
  { id: "fire", label: "FIRE", icon: "🔥" },
  { id: "accounts", label: "My Accounts", icon: "🏦" },
]
const incomeBreakdown = [
  { label: "Paychecks", value: 7800, filterKey: "Paycheck" },
  { label: "Dividends", value: 250, filterKey: "Dividends" },
  { label: "Interest", value: 50, filterKey: "Interest" },
  { label: "Other", value: 100, filterKey: "income_other" },
]
const incomeTotal = incomeBreakdown.reduce((sum, r) => sum + Number(r.value || 0), 0)
const monthlyIncomeTotal = incomeTotal

const expenseSplit = [
  { label: "Needs", value: 5000 },
  { label: "Wants", value: 2900 },
]
const monthlyExpensesTotal = expenseSplit.reduce((sum, r) => sum + Number(r.value || 0), 0)

const savingsParts = {
  pretax: 500,     // 401k / HSA etc (usually payroll, not transactions)
  employerMatch: 150,
  posttax: 250,     // Roth IRA, brokerage deposits, cash savings transfers
  cashSaved: 100,   // cash savings added this month
}

const totalSaved = ["pretax", "employerMatch", "posttax", "cashSaved"]
  .reduce((sum, k) => sum + Number(savingsParts?.[k] || 0), 0)
const savingsRatePct = incomeTotal
  ? Math.round((totalSaved / incomeTotal) * 1000) / 10
  : 0

const pctOfIncome = (amount) => {
  const inc = Number(monthlyIncomeTotal || 0)
  if (!inc) return "0%"
  return `${Math.round((Number(amount || 0) / inc) * 100)}%`
}
// S&P 500 annual total returns 1928–2023 (nominal, source: Damodaran)
const SP500_RETURNS = [
  0.4381, -0.0830, -0.2512, -0.4384, -0.0864,  0.4998, -0.0119,  0.4674,  0.3194, -0.3534, // 1928–1937
  0.2928, -0.0110, -0.1067, -0.1277,  0.1917,  0.2506,  0.1903,  0.3582, -0.0843,  0.0520, // 1938–1947
  0.0570,  0.1830,  0.3081,  0.2368,  0.1815, -0.0121,  0.5256,  0.3156,  0.0656, -0.1078, // 1948–1957
  0.4336,  0.1196,  0.0047,  0.2689, -0.0873,  0.2280,  0.1648,  0.1245, -0.1006,  0.2398, // 1958–1967
  0.1106, -0.0850,  0.0356,  0.1431,  0.1898, -0.1466, -0.2647,  0.3720,  0.2384, -0.0718, // 1968–1977
  0.0656,  0.1844,  0.3242, -0.0491,  0.2042,  0.2251,  0.0627,  0.3173,  0.1867,  0.0525, // 1978–1987
  0.1661,  0.3169, -0.0310,  0.3047,  0.0762,  0.1008,  0.0132,  0.3758,  0.2296,  0.3336, // 1988–1997
  0.2858,  0.2104, -0.0910, -0.1189, -0.2210,  0.2869,  0.1088,  0.0491,  0.1579,  0.0549, // 1998–2007
 -0.3700,  0.2646,  0.1506,  0.0211,  0.1600,  0.3239,  0.1369,  0.0138,  0.1196,  0.2183, // 2008–2017
 -0.0438,  0.3149,  0.1840,  0.2871, -0.1811,  0.2629                                       // 2018–2023
]

function runHistoricalSim(currentSavings, inputs, retirementBalance = null) {
  const { current_age, retirement_age, monthly_contribution,
    expected_return_pct, inflation_pct, desired_annual_spend,
    social_security_monthly, goal_amount } = inputs
  const yearsToRetire = retirement_age - current_age
  if (yearsToRetire <= 0) return null

  const r = expected_return_pct / 100
  const inf = inflation_pct / 100
  const windowSize = Math.max(20, 90 - retirement_age)
  const n = SP500_RETURNS.length

  // Use pre-computed balance if provided (matches Year by Year exactly), else fall back to formula
  const annuityFactor = r > 0 ? (Math.pow(1 + r, yearsToRetire) - 1) / r : yearsToRetire
  const p50 = retirementBalance !== null
    ? retirementBalance
    : currentSavings * Math.pow(1 + r, yearsToRetire) + monthly_contribution * 12 * annuityFactor

  // Helper: run all historical drawdown windows for a given starting balance
  function historicalSuccess(startBalance) {
    const annualSS = (social_security_monthly || 0) * 12
    let survived = 0
    const total = n - windowSize + 1
    for (let start = 0; start < total; start++) {
      let bal = startBalance
      let withdrawal = Math.max(0, desired_annual_spend - annualSS)
      let ok = true
      for (let yr = 0; yr < windowSize; yr++) {
        bal = bal * (1 + SP500_RETURNS[start + yr]) - withdrawal
        withdrawal *= (1 + inf)
        if (bal <= 0) { ok = false; break }
      }
      if (ok) survived++
    }
    return { rate: Math.round((survived / total) * 100), total }
  }

  const { rate: successRate, total: totalPeriods } = historicalSuccess(p50)

  // Years money lasts (at median balance, worst-case window)
  const annualSS = (social_security_monthly || 0) * 12
  const annualDraw = Math.max(0, desired_annual_spend - annualSS)
  const yearsMoneyLasts = annualDraw > 0
    ? Math.min(99, Math.round(p50 / annualDraw))
    : 99

  // Monthly contribution needed for 80% historical success (binary search on extra $/mo)
  let monthlyNeeded = 0
  if (successRate < 80) {
    let lo = 0, hi = 20000
    for (let i = 0; i < 22; i++) {
      const mid = (lo + hi) / 2
      const testBalance = p50 + mid * 12 * annuityFactor
      const { rate: testRate } = historicalSuccess(testBalance)
      if (testRate >= 80) hi = mid; else lo = mid
    }
    monthlyNeeded = Math.round(hi)
  }

  return { p50, successRate, totalPeriods, chartData: [], monthlyNeeded, yearsMoneyLasts }
}

// ✅ Normalize a DB/Plaid tx row into the UI convention:
//   - expenses are negative
//   - income is positive
function formatTxForUI(tx) {
  const name = tx?.merchant_name || tx?.name || "Transaction"
  const category = tx?.category || (Array.isArray(tx?.category) && tx.category.length ? tx.category[0] : "Other") || "Other"
  const date = tx?.date || ""
  const amt = Number(tx?.amount || 0) // Plaid native: spend positive, income negative
  const amount = -amt // convert to UI convention: spend negative, income positive
  return { name, category, date, amount, icon: "💳", source: tx?.source || "automatic", plaid_account_id: tx?.plaid_account_id || null }
}
function isoDate(d) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function parseMaybeISODate(s) {
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

function rangeStartDate(range) {
  const now = new Date()
  const d = new Date(now)

  if (range === "1W") {
    d.setDate(d.getDate() - 7)
    return d
  }
  if (range === "1M") {
    d.setMonth(d.getMonth() - 1)
    return d
  }
  if (range === "3M") {
    d.setMonth(d.getMonth() - 3)
    return d
  }
  if (range === "6M") {
    d.setMonth(d.getMonth() - 6)
    return d
  }
  if (range === "YTD") {
    return new Date(d.getFullYear(), 0, 1) // Jan 1
  }
  if (range === "1Y") {
    d.setFullYear(d.getFullYear() - 1)
    return d
  }
  if (range === "5Y") {
    d.setFullYear(d.getFullYear() - 5)
    return d
  }

  return null // MAX (show everything)
}

function downsampleByMaxPoints(points, maxPoints) {
  if (!Array.isArray(points) || points.length <= maxPoints) return points
  const step = Math.ceil(points.length / maxPoints)
  const out = []
  for (let i = 0; i < points.length; i += step) out.push(points[i])
  if (out[out.length - 1] !== points[points.length - 1]) out.push(points[points.length - 1])
  return out
}

// ── MEMBER TOGGLE ───────────────────────────────────────────
function MemberToggle({ householdMembers, selected, onSelect, theme }) {
  const t = theme
  if (!householdMembers || householdMembers.length < 2) return null
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
      {[{ id: null, name: "All" }, ...householdMembers].map(m => {
        const isActive = selected === m.id
        return (
          <button key={m.id ?? "all"} onClick={() => onSelect(m.id)}
            style={{ padding: "5px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
              background: isActive ? (m.color || t.accent) : t.hover,
              color: isActive ? "#fff" : t.textMuted, transition: "all 0.15s" }}>
            {m.name}
          </button>
        )
      })}
    </div>
  )
}

// ── SETTINGS PANEL ─────────────────────────────────────────
function SettingsPanel({ session, isOpen, onClose, theme, settings, onSettingsChange, userAvatar }) {
  const t = theme
  const [activeSection, setActiveSection] = useState("profile")
  const [profile, setProfile] = useState({
    full_name: session.user.user_metadata?.full_name || '',
    email: session.user.email || '',
  })
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' })
  const [saveMsg, setSaveMsg] = useState('')
  const [exchangeRates, setExchangeRates] = useState(null)

  useEffect(() => {
    if (isOpen) {
      fetch('https://api.exchangerate-api.com/v4/latest/USD')
        .then(r => r.json())
        .then(d => setExchangeRates(d.rates))
        .catch(() => { })
    }
  }, [isOpen])

  async function saveProfile() {
    const { error } = await supabase.auth.updateUser({
      data: { full_name: profile.full_name }
    })
    setSaveMsg(error ? '❌ ' + error.message : '✅ Profile saved!')
    setTimeout(() => setSaveMsg(''), 3000)
  }

  async function changePassword() {
    if (passwords.newPass !== passwords.confirm) {
      setSaveMsg('❌ Passwords do not match')
      setTimeout(() => setSaveMsg(''), 3000)
      return
    }
    const { error } = await supabase.auth.updateUser({ password: passwords.newPass })
    setSaveMsg(error ? '❌ ' + error.message : '✅ Password updated!')
    setPasswords({ current: '', newPass: '', confirm: '' })
    setTimeout(() => setSaveMsg(''), 3000)
  }

  const sections = [
    { id: "profile", label: "Profile", icon: "👤" },
    { id: "currency", label: "Currency", icon: "💱" },
    { id: "language", label: "Language", icon: "🌐" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "privacy", label: "Privacy", icon: "🔒" },
  ]

  const inp = {
    width: '100%', padding: '10px 12px',
    background: t.inputBg, border: `1px solid ${t.border}`,
    borderRadius: 8, color: t.text, fontSize: 13,
    outline: 'none', boxSizing: 'border-box'
  }

  // NOTE: kept for display; actual number formatting is handled in Dashboard helper
  const convertedRate = exchangeRates && settings.currency !== 'USD'
    ? exchangeRates[settings.currency] : 1

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }} />

      {/* Panel */}
      <div style={{ position: 'fixed', left: 220, top: 0, width: 580, height: '100vh', background: t.surface, borderRight: `1px solid ${t.border}`, zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '4px 0 24px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: t.text, marginBottom: 2 }}>Settings</h2>
            <p style={{ fontSize: 12, color: t.textMuted }}>Manage your Grove Money preferences</p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: t.hover, border: 'none', color: t.textMuted, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Section nav */}
          <div style={{ width: 160, borderRight: `1px solid ${t.border}`, padding: '16px 0', flexShrink: 0 }}>
            {sections.map(s => (
              <button key={s.id} onClick={() => setActiveSection(s.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: activeSection === s.id ? t.hover : 'transparent', border: 'none', cursor: 'pointer', color: activeSection === s.id ? t.text : t.textMuted, fontSize: 13, fontWeight: activeSection === s.id ? 600 : 400, textAlign: 'left', borderLeft: activeSection === s.id ? `2px solid ${t.accent}` : '2px solid transparent' }}>
                <span>{s.icon}</span>{s.label}
              </button>
            ))}
          </div>

          {/* Section content */}
          <div style={{ flex: 1, padding: '24px 28px', overflowY: 'auto' }}>
            {saveMsg && (
              <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: saveMsg.startsWith('✅') ? 'rgba(110,231,183,0.1)' : 'rgba(244,114,182,0.1)', color: saveMsg.startsWith('✅') ? '#2F855A' : '#EF4444', fontSize: 13 }}>{saveMsg}</div>
            )}

            {/* PROFILE */}
            {activeSection === 'profile' && (
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Profile</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Update your personal information</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, padding: '20px', background: t.hover, borderRadius: 12 }}>
                  <div
  style={{
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: userAvatar ? `url(${userAvatar}) center/cover no-repeat` : `linear-gradient(135deg, ${t.accent2}, ${t.accent})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 800,
    color: "#fff",
    border: `1px solid ${t.border}`,
  }}
>
  {!userAvatar && (profile.full_name || profile.email).slice(0, 2).toUpperCase()}
</div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{profile.full_name || 'Your Name'}</p>
                    <p style={{ fontSize: 13, color: t.textMuted }}>{profile.email}</p>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: t.textMuted, display: 'block', marginBottom: 6 }}>Full Name</label>
                  <input value={profile.full_name} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} style={inp} placeholder="Your full name" />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 12, color: t.textMuted, display: 'block', marginBottom: 6 }}>Email</label>
                  <input value={profile.email} disabled style={{ ...inp, opacity: 0.5 }} />
                  <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>Email cannot be changed here</p>
                </div>
                <button onClick={saveProfile} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${t.accent}, ${t.accent2})`, color: '#0d1117', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Save Profile</button>

                <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${t.border}` }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 16 }}>Change Password</h4>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, display: 'block', marginBottom: 6 }}>New Password</label>
                    <input type="password" value={passwords.newPass} onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))} style={inp} placeholder="••••••••" />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, display: 'block', marginBottom: 6 }}>Confirm New Password</label>
                    <input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} style={inp} placeholder="••••••••" />
                  </div>
                  <button onClick={changePassword} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Update Password</button>
                </div>
              </div>
            )}

            {/* CURRENCY */}
            {activeSection === 'currency' && (
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Currency</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>All amounts will be displayed in your selected currency</p>

                {exchangeRates && settings.currency !== 'USD' && (
                  <div style={{ padding: '12px 16px', background: t.hover, borderRadius: 8, marginBottom: 20, fontSize: 13, color: t.textMuted }}>
                    📡 Live rate: 1 USD = <strong style={{ color: t.text }}>{exchangeRates[settings.currency]?.toFixed(4)} {settings.currency}</strong>
                    <span style={{ fontSize: 11, marginLeft: 8, color: t.textFaint }}>updated daily</span>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {CURRENCIES.map(c => (
                    <div key={c.code} onClick={() => onSettingsChange('currency', c.code)} style={{ padding: '14px 16px', borderRadius: 10, border: `2px solid ${settings.currency === c.code ? t.accent : t.border}`, cursor: 'pointer', background: settings.currency === c.code ? (t.isDark ? 'rgba(47,133,90,0.06)' : 'rgba(47,133,90,0.06)') : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{c.code}</p>
                        <p style={{ fontSize: 11, color: t.textMuted }}>{c.label}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 18, color: t.textMuted }}>{c.symbol}</p>
                        {exchangeRates && c.code !== 'USD' && <p style={{ fontSize: 10, color: t.textFaint }}>{exchangeRates[c.code]?.toFixed(2)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LANGUAGE */}
            {activeSection === 'language' && (
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Language</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Choose your preferred language</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {LANGUAGES.map(l => (
                    <div key={l.code} onClick={() => onSettingsChange('language', l.code)} style={{ padding: '16px 20px', borderRadius: 10, border: `2px solid ${settings.language === l.code ? t.accent : t.border}`, cursor: 'pointer', background: settings.language === l.code ? (t.isDark ? 'rgba(47,133,90,0.06)' : 'rgba(47,133,90,0.06)') : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{l.label}</p>
                      {settings.language === l.code && <span style={{ fontSize: 12, color: t.accent, fontWeight: 600 }}>✓ Active</span>}
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: t.textFaint, marginTop: 16 }}>More languages coming soon</p>
              </div>
            )}

            {/* NOTIFICATIONS */}
            {activeSection === 'notifications' && (
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Notifications</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Choose what alerts you receive</p>

                {[
                  { key: 'notif_budget', label: 'Budget alerts', desc: 'When you exceed a budget category' },
                  { key: 'notif_unusual', label: 'Unusual spending', desc: 'Transactions that look out of the ordinary' },
                  { key: 'notif_monthly', label: 'Monthly summary', desc: 'Your financial recap every month' },
                  { key: 'notif_retirement', label: 'Retirement nudges', desc: 'Tips to improve your retirement score' },
                  { key: 'notif_rates', label: 'Rate changes', desc: 'When exchange rates shift significantly' },
                ].map(n => (
                  <div key={n.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: `1px solid ${t.border}` }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 2 }}>{n.label}</p>
                      <p style={{ fontSize: 12, color: t.textMuted }}>{n.desc}</p>
                    </div>
                    <div onClick={() => onSettingsChange(n.key, !settings[n.key])} style={{ width: 44, height: 24, borderRadius: 12, background: settings[n.key] ? t.accent : t.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: 2, left: settings[n.key] ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* PRIVACY */}
            {activeSection === 'privacy' && (
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Privacy</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Control your data and privacy preferences</p>

                {[
                  { key: 'privacy_analytics', label: 'Usage analytics', desc: 'Help improve Grove by sharing anonymous usage data' },
                  { key: 'privacy_personalization', label: 'Personalization', desc: 'Allow Grove to personalize insights based on your data' },
                ].map(n => (
                  <div key={n.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: `1px solid ${t.border}` }}>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 2 }}>{n.label}</p>
                      <p style={{ fontSize: 12, color: t.textMuted }}>{n.desc}</p>
                    </div>
                    <div onClick={() => onSettingsChange(n.key, !settings[n.key])} style={{ width: 44, height: 24, borderRadius: 12, background: settings[n.key] ? t.accent : t.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: 2, left: settings[n.key] ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: 32, padding: '20px', background: 'rgba(239,68,68,0.06)', borderRadius: 10, border: '1px solid rgba(244,114,182,0.2)' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', marginBottom: 6 }}>Danger Zone</h4>
                  <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 14 }}>These actions are permanent and cannot be undone.</p>
                  <button onClick={() => supabase.auth.signOut()} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(244,114,182,0.4)', background: 'transparent', color: '#EF4444', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Sign Out All Devices
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── AUTH ───────────────────────────────────────────────────
function SetNewPasswordPage({ onDone }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  async function handleSubmit() {
    if (!password || password.length < 6) { setMessage('❌ Password must be at least 6 characters.'); return }
    if (password !== confirm) { setMessage('❌ Passwords do not match.'); return }
    setLoading(true)
    setMessage('')
    const { error } = await supabase.auth.updateUser({ password })
    if (error) { setMessage('❌ ' + error.message); setLoading(false); return }
    setMessage('✅ Password updated!')
    setTimeout(() => onDone(), 1500)
    setLoading(false)
  }

  const inp = { width: '100%', padding: '11px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, color: '#0F172A', fontSize: 14, outline: 'none', boxSizing: 'border-box' }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
      <div style={{ width: 400, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 16, padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <span style={{ fontSize: 20, fontWeight: 700, color: '#0F172A' }}>Grove Money</span>
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>Set a new password</h2>
        <p style={{ color: '#64748B', fontSize: 14, marginBottom: 28 }}>Choose a strong password for your account.</p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#64748B', display: 'block', marginBottom: 6 }}>New Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={inp} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 13, color: '#64748B', display: 'block', marginBottom: 6 }}>Confirm New Password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" style={inp} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>
        {message && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: message.startsWith('✅') ? 'rgba(47,133,90,0.08)' : 'rgba(239,68,68,0.08)', color: message.startsWith('✅') ? '#2F855A' : '#EF4444', fontSize: 13 }}>{message}</div>
        )}
        <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', background: '#2F855A', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Saving...' : 'Update Password'}
        </button>
      </div>
    </div>
  )
}

const US_STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming","Washington D.C."]
const NET_WORTH_RANGES = ["Under $10,000","$10,000 – $50,000","$50,000 – $100,000","$100,000 – $250,000","$250,000 – $500,000","$500,000 – $1,000,000","Over $1,000,000","Prefer not to say"]
const GOALS = ["Save more money","Track my spending","Plan for retirement","Invest smarter","Get out of debt","Understand my finances better","Other"]

function WaitlistForm({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", goal: "", state: "", net_worth_range: "" })
  const [status, setStatus] = useState(null)

  async function submit(e) {
    e.preventDefault()
    if (!form.name || !form.email || !form.goal || !form.state || !form.net_worth_range) return
    setStatus("sending")
    try {
      const res = await fetch(`${API_BASE}/api/waitlist/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      const s = data.duplicate ? "duplicate" : data.ok ? "done" : "error"
      setStatus(s)
      if (s === "done" || s === "duplicate") onDone?.()
    } catch { setStatus("error") }
  }

  const inp = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #D1FAE5", background: "#F9FAFB", fontSize: 13, color: "#0F172A", outline: "none", boxSizing: "border-box" }
  const sel = { ...inp, appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 32 }

  if (status === "done" || status === "duplicate") return (
    <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🌿</div>
      <h3 style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>
        {status === "duplicate" ? "You're already on the list!" : "You're on the list!"}
      </h3>
      <p style={{ fontSize: 14, color: "#475569", marginBottom: 20 }}>
        {status === "duplicate" ? "We already have your email — we'll reach out soon." : `We'll send your invite to ${form.email} when your spot opens.`}
      </p>
      <button onClick={onClose} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#2F855A", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Done</button>
    </div>
  )

  return (
    <form onSubmit={submit}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" required style={inp} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Email</label>
          <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" placeholder="you@email.com" required style={inp} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>State <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 400 }}>US only</span></label>
          <select value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} required style={{ ...sel, color: form.state ? "#0F172A" : "#9CA3AF" }}>
            <option value="" disabled>Select state…</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Net worth range</label>
          <select value={form.net_worth_range} onChange={e => setForm(f => ({ ...f, net_worth_range: e.target.value }))} required style={{ ...sel, color: form.net_worth_range ? "#0F172A" : "#9CA3AF" }}>
            <option value="" disabled>Select range…</option>
            {NET_WORTH_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Main financial goal</label>
        <select value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} required style={{ ...sel, color: form.goal ? "#0F172A" : "#9CA3AF" }}>
          <option value="" disabled>Select a goal…</option>
          {GOALS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {status === "error" && <p style={{ fontSize: 13, color: "#EF4444", marginBottom: 10 }}>Something went wrong. Please try again.</p>}

      <button type="submit" disabled={status === "sending"} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 16px rgba(34,197,94,0.25)" }}>
        {status === "sending" ? "Joining…" : "Request Beta Access →"}
      </button>
      <p style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", margin: "10px 0 0" }}>🇺🇸 Currently available in the US only · No spam · Invite only</p>
    </form>
  )
}

function WaitlistModal({ onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1001, background: "#fff", borderRadius: 20, padding: "32px 28px", width: 520, maxWidth: "calc(100vw - 32px)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ display: "inline-block", background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)", borderRadius: 99, padding: "3px 12px", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#065F46", letterSpacing: "0.05em" }}>BETA ACCESS</span>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0F172A", margin: 0 }}>Join the waitlist</h2>
            <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>Be among the first to try Grove Money.</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, color: "#94A3B8", cursor: "pointer", padding: "0 0 0 12px", lineHeight: 1 }}>✕</button>
        </div>
        <WaitlistForm onClose={onClose} />
      </div>
    </>
  )
}

function WaitlistSection({ onOpenModal }) {
  return (
    <section id="waitlist" style={{ padding: "64px 5vw", background: "#F0FDF4", textAlign: "center" }}>
      <div style={{ maxWidth: 540, margin: "0 auto" }}>
        <div style={{ display: "inline-block", background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)", borderRadius: 99, padding: "5px 18px", marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#065F46", letterSpacing: "0.05em" }}>BETA ACCESS</span>
        </div>
        <h2 style={{ fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, color: "#0F172A", margin: "0 0 12px" }}>Join the waitlist</h2>
        <p style={{ fontSize: 16, color: "#475569", margin: "0 0 28px" }}>Be among the first to get access. We're onboarding beta testers now — US only.</p>
        <button onClick={onOpenModal} style={{ padding: "14px 36px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 16px rgba(34,197,94,0.3)" }}>
          Request Beta Access →
        </button>
      </div>
    </section>
  )
}

function WaitlistAdminPage({ session, theme: t }) {
  const card = makeCard(t)
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviting, setInviting] = useState(null)
  const [msg, setMsg] = useState("")
  const [resendEmail, setResendEmail] = useState("")
  const [resending, setResending] = useState(false)

  async function resendConfirmation() {
    if (!resendEmail.trim()) return
    setResending(true)
    const { data: { session: s } } = await supabase.auth.getSession()
    const res = await fetch(`${API_BASE}/api/admin/resend-confirmation`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${s?.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ email: resendEmail.trim() }),
    })
    const data = await res.json()
    setMsg(data.ok ? `Confirmation email resent to ${resendEmail.trim()}` : `Error: ${data.error}`)
    setTimeout(() => setMsg(""), 4000)
    if (data.ok) setResendEmail("")
    setResending(false)
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      fetch(`${API_BASE}/api/waitlist`, {
        headers: { "Authorization": `Bearer ${s?.access_token}` },
      })
        .then(r => r.json())
        .then(data => { setList(Array.isArray(data) ? data : []); setLoading(false) })
        .catch(() => setLoading(false))
    })
  }, [])

  async function sendInvite(entry) {
    setInviting(entry.id)
    const { data: { session: s } } = await supabase.auth.getSession()
    const res = await fetch(`${API_BASE}/api/waitlist/invite`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${s?.access_token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ id: entry.id, email: entry.email, name: entry.name }),
    })
    const data = await res.json()
    if (data.ok) {
      setList(prev => prev.map(x => x.id === entry.id ? { ...x, invited_at: new Date().toISOString() } : x))
      setMsg(`Invite sent to ${entry.email}`)
      setTimeout(() => setMsg(""), 3000)
    }
    setInviting(null)
  }

  const pending = list.filter(x => !x.invited_at)
  const invited = list.filter(x => x.invited_at)

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: "0 0 4px" }}>Waitlist</h1>
        <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>{list.length} total · {pending.length} pending · {invited.length} invited</p>
      </div>

      {/* Resend confirmation */}
      <div style={{ ...card, padding: "16px 20px", marginBottom: 20, display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>📨</span>
        <input
          type="email"
          value={resendEmail}
          onChange={e => setResendEmail(e.target.value)}
          onKeyDown={e => e.key === "Enter" && resendConfirmation()}
          placeholder="Resend confirmation email to…"
          style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13 }}
        />
        <button onClick={resendConfirmation} disabled={resending || !resendEmail.trim()}
          style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: resending || !resendEmail.trim() ? 0.5 : 1, flexShrink: 0 }}>
          {resending ? "Sending…" : "Resend"}
        </button>
      </div>

      {msg && <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(5,150,105,0.1)", border: "1px solid rgba(5,150,105,0.2)", fontSize: 13, color: "#059669", fontWeight: 600 }}>{msg}</div>}

      {loading ? (
        <p style={{ color: t.textMuted, fontSize: 14 }}>Loading…</p>
      ) : list.length === 0 ? (
        <div style={{ ...card, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>No signups yet. Share your waitlist link to get started.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map(entry => (
            <div key={entry.id} style={{ ...card, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: entry.invited_at ? "rgba(5,150,105,0.1)" : "rgba(47,133,90,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                {entry.invited_at ? "✅" : "⏳"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{entry.name}</span>
                  {entry.invited_at && <span style={{ fontSize: 11, fontWeight: 600, color: "#059669", background: "rgba(5,150,105,0.1)", padding: "2px 8px", borderRadius: 99 }}>Invited</span>}
                </div>
                <div style={{ fontSize: 12, color: t.textMuted }}>{entry.email}</div>
                <div style={{ fontSize: 12, color: t.textFaint, marginTop: 2 }}>
                  {entry.state && <span>{entry.state} · </span>}
                  {entry.net_worth_range && <span>{entry.net_worth_range} · </span>}
                  Goal: {entry.goal} · Joined {new Date(entry.created_at).toLocaleDateString()}
                </div>
              </div>
              {!entry.invited_at && (
                <button
                  onClick={() => sendInvite(entry)}
                  disabled={inviting === entry.id}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: inviting === entry.id ? "default" : "pointer", opacity: inviting === entry.id ? 0.6 : 1, flexShrink: 0 }}
                >
                  {inviting === entry.id ? "Sending…" : "Send Invite"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AppScreenshots() {
  const [active, setActive] = useState(0)
  const tabs = [
    { label: "📊 Overview", file: "screenshot-overview.png", desc: "Your full financial picture at a glance — net worth, health score, cash, debt, and investments." },
    { label: "📈 Investments", file: "screenshot-investments.png", desc: "Portfolio performance, dividend income, allocation breakdown, and an investment score across 6 metrics." },
    { label: "🏖️ Retirement", file: "screenshot-retirement.png", desc: "Know if you're on track. Wealth accumulation projections tested against 66 historical market periods." },
  ]
  return (
    <section style={{ padding: "80px 5vw", background: "#F8FAFC" }}>
      <div style={{ maxWidth: 1300, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>See it in action.</h2>
          <p style={{ fontSize: 16, color: "#64748B" }}>Everything you need to know about your money — in one place.</p>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 28, flexWrap: "wrap" }}>
          {tabs.map((tab, i) => (
            <button key={i} onClick={() => setActive(i)} style={{
              padding: "9px 20px", borderRadius: 99, fontSize: 14, fontWeight: 600, cursor: "pointer", border: "none",
              background: active === i ? "#2F855A" : "#E2E8F0",
              color: active === i ? "#fff" : "#475569",
              transition: "all 0.15s",
            }}>{tab.label}</button>
          ))}
        </div>
        <p style={{ textAlign: "center", fontSize: 14, color: "#64748B", marginBottom: 24, minHeight: 20 }}>{tabs[active].desc}</p>
        <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.15)", border: "1px solid #E2E8F0" }}>
          <img
            key={tabs[active].file}
            src={`${process.env.PUBLIC_URL}/${tabs[active].file}`}
            alt={tabs[active].label}
            style={{ width: "100%", display: "block", imageRendering: "high-quality" }}
          />
        </div>
      </div>
    </section>
  )
}

function FAQ() {
  const [open, setOpen] = useState(null)
  const items = [
    {
      q: "Is Grove Money really free?",
      a: "As of now, yes — Grove is completely free during the beta. At some point we'll introduce a paid plan to keep the service running and growing, but we'll always be transparent about it before anything changes. No credit card required to get started.",
    },
    {
      q: "Is my financial data secure?",
      a: "Yes. Grove uses Plaid — the same technology trusted by Venmo, Coinbase, and thousands of apps — to connect your accounts. Grove never sees or stores your bank login credentials. All data is encrypted at rest and in transit using 256-bit AES encryption.",
    },
    {
      q: "What is Plaid and why do you use it?",
      a: "Plaid is a secure financial data network that connects apps to banks. It's used by millions of people across thousands of apps. When you connect your bank through Grove, you log in through Plaid's interface — Grove only receives read-only account and transaction data.",
    },
    {
      q: "Which banks are supported?",
      a: "Plaid supports over 12,000 financial institutions in the US including Chase, Bank of America, Wells Fargo, Citi, Capital One, Fidelity, Vanguard, and most credit unions. If your bank isn't supported, you can still add accounts manually.",
    },
    {
      q: "Can Grove move money or make transactions?",
      a: "No. Grove is strictly read-only. It connects to your accounts to read balances and transactions — it cannot initiate transfers, make payments, or modify anything at your bank.",
    },
    {
      q: "What's the difference between Grove and YNAB or Mint?",
      a: "Mint shut down in 2024. YNAB is great for budgeting but has no retirement planning or investment tracking. Grove combines budgeting, net worth tracking, investment monitoring, retirement projections, and financial simulators — all in one place, built for people who want the full picture.",
    },
    {
      q: "How is this a beta? What does that mean for me?",
      a: "Beta means Grove is actively being built. You'll get early access, your feedback directly shapes the product, and you'll pay nothing while we work out the kinks. Some features may change or improve based on what you tell us.",
    },
    {
      q: "How do I get access?",
      a: "Grove is invite-only during beta. Join the waitlist and we'll send you an invite as spots open up. We're onboarding small batches to make sure every user has a great experience.",
    },
  ]

  return (
    <section id="faq" style={{ padding: "80px 5vw", background: "#fff" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>Frequently asked questions</h2>
          <p style={{ fontSize: 16, color: "#64748B" }}>Everything you need to know before getting started.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((item, i) => (
            <div key={i} style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", background: open === i ? "#F0FDF4" : "#fff", border: "none", cursor: "pointer", textAlign: "left", gap: 16 }}
              >
                <span style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", lineHeight: 1.4 }}>{item.q}</span>
                <span style={{ fontSize: 18, color: "#2F855A", flexShrink: 0, transform: open === i ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding: "0 20px 18px", background: "#F0FDF4" }}>
                  <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, margin: 0 }}>{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function LandingPage() {
  const [showAuth, setShowAuth] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showWaitlistModal, setShowWaitlistModal] = useState(false)


  function openModal() { setShowWaitlistModal(true) }
  function closeModal() {
    setShowWaitlistModal(false)
    localStorage.setItem("grove_waitlist_seen", "1")
  }

  if (showAuth) return <AuthPage />
  if (showPrivacy) return <PrivacyPage onBack={() => setShowPrivacy(false)} />
  if (showTerms) return <TermsPage onBack={() => setShowTerms(false)} />

  const features = [
    { icon: "🏦", title: "All Your Accounts", desc: "Connect every bank, credit card, investment, and loan in one place. No more switching between apps." },
    { icon: "📊", title: "Smart Budgeting", desc: "Set budgets by category, track spending in real time, and see exactly where every dollar goes." },
    { icon: "📈", title: "Investment Tracking", desc: "See your portfolio performance, holdings, and fund fees — with suggestions to save more." },
    { icon: "🏖️", title: "Retirement Planning", desc: "Track your 401k, IRA, and FIRE number. Know if you're on track before it's too late." },
    { icon: "🛡️", title: "Financial Simulators", desc: "Model your emergency fund, debt payoff, compound growth, house affordability and more." },
    { icon: "🌱", title: "Grow As You Go", desc: "Earn points as you budget, save, and invest. Level up from Seedling to Forest." },
  ]

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: "#0F172A", background: "#fff" }}>

      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "#2F855A", padding: "0 5vw" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#fff", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={`${process.env.PUBLIC_URL}/grove_money_logo_1024x1024.png`} alt="" style={{ width: 34, height: 34, objectFit: "cover", objectPosition: "top" }} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>Grove Money</span>
          </a>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <a href="#faq" style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 14, fontWeight: 500, cursor: "pointer", textDecoration: "none" }}>FAQ</a>
            <button onClick={() => setShowAuth(true)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Sign in</button>
            <button onClick={openModal} style={{ padding: "9px 20px", borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Request Beta Access</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ background: "linear-gradient(160deg, #F0FDF4 0%, #DCFCE7 50%, #fff 100%)", padding: "96px 5vw 80px" }}>
        <div style={{ maxWidth: 780, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)", borderRadius: 99, padding: "5px 18px", marginBottom: 24 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#065F46", letterSpacing: "0.05em" }}>YOUR FINANCES. FINALLY CLEAR.</span>
          </div>
          <h1 style={{ fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 24px", color: "#0F172A" }}>
            The financial app<br/>built for <span style={{ color: "#2F855A" }}>real people.</span>
          </h1>
          <p style={{ fontSize: "clamp(16px, 2vw, 20px)", color: "#475569", lineHeight: 1.7, margin: "0 0 40px", maxWidth: 600, marginLeft: "auto", marginRight: "auto" }}>
            Track every account, budget smarter, plan your retirement, and grow your wealth — all without the overwhelm.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={openModal} style={{ padding: "16px 36px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(34,197,94,0.35)" }}>
              Request Beta Access →
            </button>
            <a href="#how" style={{ padding: "16px 28px", borderRadius: 10, border: "1.5px solid #BBF7D0", background: "transparent", color: "#2F855A", fontSize: 16, fontWeight: 700, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              See how it works
            </a>
          </div>
          <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 16 }}>Free to use · No credit card required · Bank-level security</p>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: "#0F172A", padding: "18px 5vw" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "12px 40px" }}>
          {[
            { icon: "🏦", label: "10,000+ account types supported" },
            { icon: "🔒", label: "Bank-level 256-bit encryption" },
            { icon: "🌱", label: "Built for all financial levels" },
            { icon: "💸", label: "Free during beta" },
          ].map(s => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15 }}>{s.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Why Grove — comparison */}
      <section style={{ padding: "80px 5vw", background: "#fff" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>Why Grove and not the rest?</h2>
            <p style={{ fontSize: 16, color: "#64748B" }}>Most tools do one thing well. Grove does everything — in one place.</p>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#64748B", fontWeight: 600, borderBottom: "2px solid #E2E8F0" }}>Feature</th>
                  {["Grove Money", "YNAB", "Mint (dead)", "Spreadsheets"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700, borderBottom: "2px solid #E2E8F0", color: h === "Grove Money" ? "#2F855A" : "#0F172A", background: h === "Grove Money" ? "#F0FDF4" : "transparent", borderRadius: h === "Grove Money" ? "8px 8px 0 0" : 0 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Budget tracking",         "✅", "✅", "✅", "⚠️ Manual"],
                  ["Account sync via Plaid",  "✅", "✅", "✅", "❌"],
                  ["Retirement planner",      "✅", "❌", "❌", "⚠️ DIY"],
                  ["Investment tracking",     "✅", "❌", "✅", "⚠️ DIY"],
                  ["Financial simulators",    "✅", "❌", "❌", "⚠️ DIY"],
                  ["Net worth history",       "✅", "❌", "✅", "⚠️ DIY"],
                  ["Gamification / levels",   "✅", "❌", "❌", "❌"],
                ].map(([feat, ...vals], i) => (
                  <tr key={feat} style={{ background: i % 2 === 0 ? "#F8FAFC" : "#fff" }}>
                    <td style={{ padding: "12px 16px", color: "#374151", fontWeight: 500 }}>{feat}</td>
                    {vals.map((v, j) => (
                      <td key={j} style={{ padding: "12px 16px", textAlign: "center", background: j === 0 ? "rgba(240,253,244,0.6)" : "transparent", fontWeight: j === 0 ? 700 : 400 }}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>† Mint shut down in 2024. Data shown reflects its last known feature set.</p>
          </div>
        </div>
      </section>

      {/* Built from real life */}
      <section style={{ padding: "80px 5vw", background: "#F8FAFC" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 48, alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <div style={{ width: 180, height: 180, borderRadius: 20, background: "linear-gradient(160deg, #D1FAE5, #6EE7B7)", border: "3px solid #A7F3D0", overflow: "hidden", position: "relative" }}>
                <svg viewBox="0 0 180 180" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
                  {/* Background */}
                  <rect width="180" height="180" fill="url(#bgGrad)" />
                  <defs>
                    <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D1FAE5" />
                      <stop offset="100%" stopColor="#6EE7B7" />
                    </linearGradient>
                  </defs>
                  {/* Shirt / body */}
                  <path d="M30 180 Q30 140 50 130 Q65 124 90 122 Q115 124 130 130 Q150 140 150 180 Z" fill="#2F855A" />
                  {/* Collar */}
                  <path d="M90 122 L80 145 L90 138 L100 145 Z" fill="#fff" opacity="0.9" />
                  {/* Neck */}
                  <rect x="79" y="108" width="22" height="20" rx="8" fill="#D4956A" />
                  {/* Head */}
                  <ellipse cx="90" cy="88" rx="38" ry="42" fill="#D4956A" />
                  {/* Ear left */}
                  <ellipse cx="53" cy="90" rx="7" ry="9" fill="#C4855A" />
                  {/* Ear right */}
                  <ellipse cx="127" cy="90" rx="7" ry="9" fill="#C4855A" />
                  {/* Hair */}
                  <path d="M52 75 Q55 42 90 40 Q125 42 128 75 Q120 55 90 54 Q60 55 52 75 Z" fill="#3D2B1F" />
                  {/* Hair top detail */}
                  <path d="M60 68 Q65 48 90 46 Q115 48 120 68 Q110 58 90 57 Q70 58 60 68 Z" fill="#2A1A10" />
                  {/* Eyes */}
                  <ellipse cx="74" cy="88" rx="7" ry="7.5" fill="#fff" />
                  <ellipse cx="106" cy="88" rx="7" ry="7.5" fill="#fff" />
                  <ellipse cx="75" cy="89" rx="4" ry="4.5" fill="#3D2B1F" />
                  <ellipse cx="107" cy="89" rx="4" ry="4.5" fill="#3D2B1F" />
                  <circle cx="76" cy="87" r="1.2" fill="#fff" />
                  <circle cx="108" cy="87" r="1.2" fill="#fff" />
                  {/* Eyebrows */}
                  <path d="M66 80 Q74 76 82 79" stroke="#3D2B1F" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                  <path d="M98 79 Q106 76 114 80" stroke="#3D2B1F" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                  {/* Nose */}
                  <path d="M88 96 Q86 105 82 108 Q90 110 98 108 Q94 105 92 96 Z" fill="#C4855A" />
                  {/* Smile */}
                  <path d="M76 118 Q90 128 104 118" stroke="#8B4513" strokeWidth="2.2" strokeLinecap="round" fill="none" />
                  {/* Cheeks */}
                  <ellipse cx="67" cy="108" rx="10" ry="6" fill="#E8A07A" opacity="0.4" />
                  <ellipse cx="113" cy="108" rx="10" ry="6" fill="#E8A07A" opacity="0.4" />
                </svg>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Flavien</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Founder, Grove Money</div>
              </div>
            </div>
            <div>
              <h2 style={{ fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, margin: "0 0 16px" }}>Built from real life.</h2>
              <p style={{ fontSize: 16, color: "#475569", lineHeight: 1.8, margin: "0 0 14px" }}>
                Most people in the US are figuring out finances alone. No guide, no mentor — just trial and error with real consequences.
              </p>
              <p style={{ fontSize: 16, color: "#475569", lineHeight: 1.8, margin: "0 0 14px" }}>
                As a French immigrant, I felt that gap more than most. So after 12 years of learning, I built the tool I wished existed from day one.
              </p>
              <p style={{ fontSize: 16, color: "#2F855A", fontWeight: 700, lineHeight: 1.8, margin: 0 }}>
                Grove Money is for anyone who has ever felt like the financial system wasn't built for them. It was built for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Screenshot */}
      <AppScreenshots />

      {/* Features */}
      <section style={{ padding: "80px 5vw", background: "#F8FAFC" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>Everything you need. Nothing you don't.</h2>
            <p style={{ fontSize: 16, color: "#64748B" }}>One app that covers the full picture of your financial life.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            {features.map(f => (
              <div key={f.title} style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 8px" }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" style={{ padding: "80px 5vw", background: "#fff" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>Up and running in minutes.</h2>
          <p style={{ fontSize: 16, color: "#64748B", marginBottom: 56 }}>No spreadsheets. No complexity. Just clarity.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 32 }}>
            {[
              { step: "1", icon: "✉️", title: "Create your account", desc: "Sign up free in 30 seconds. No credit card needed." },
              { step: "2", icon: "🔗", title: "Connect your accounts", desc: "Securely link your banks, cards, and investments via Plaid." },
              { step: "3", icon: "🌱", title: "Start growing", desc: "Get instant insights and start building better financial habits." },
            ].map(s => (
              <div key={s.step} style={{ textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: 99, background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 20, fontWeight: 800, color: "#065F46" }}>{s.step}</div>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{s.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: "80px 5vw", background: "#F8FAFC" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, margin: "0 0 12px" }}>What early users are saying.</h2>
            <p style={{ fontSize: 16, color: "#64748B" }}>Real feedback from our beta community.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {[
              {
                quote: "I had five different Excel sheets just to track my accounts and spending. Grove replaced all of them in one afternoon. Everything is in one place and actually updates automatically — this is exactly what I was missing.",
                name: "Ryan",
                title: "Miami, FL",
                initials: "R",
                color: "#2F855A",
              },
              {
                quote: "I was $18k in debt and had no idea where to even start. Grove showed me exactly where my money was going and helped me build a real plan. Three weeks in and I already had my first solid month. It gave me control I didn't think I could have.",
                name: "Camille",
                title: "Miami, FL",
                initials: "C",
                color: "#7C3AED",
              },
              {
                quote: "I'm 18 and honestly had no clue about budgeting. Grove made it super easy to set up — I connected my bank, set my categories, and within 10 minutes I could see exactly what I was spending. Wish they taught this in school.",
                name: "Luca",
                title: "Miami, FL",
                initials: "L",
                color: "#0369A1",
              },
            ].map(t => (
              <div key={t.name} style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ fontSize: 32, color: "#BBF7D0", lineHeight: 1 }}>"</div>
                <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.8, margin: 0, flex: 1 }}>{t.quote}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: t.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0 }}>{t.initials}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{t.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Waitlist */}
      <WaitlistSection onOpenModal={openModal} />
      {showWaitlistModal && <WaitlistModal onClose={closeModal} />}

      {/* Final CTA */}
      <section style={{ padding: "80px 5vw", background: "linear-gradient(135deg, #2F855A, #166534)" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🌿</div>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 800, color: "#fff", margin: "0 0 16px" }}>Your financial glow-up starts today.</h2>
          <p style={{ fontSize: 16, color: "#A7F3D0", lineHeight: 1.7, margin: "0 0 36px" }}>
            Join people who decided to stop guessing and start knowing. It's free, it's simple, and it might just change how you think about money.
          </p>
          <button onClick={openModal} style={{ padding: "18px 48px", borderRadius: 12, border: "none", background: "#fff", color: "#2F855A", fontSize: 17, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}>
            Request Beta Access →
          </button>
          <p style={{ fontSize: 12, color: "#6EE7B7", marginTop: 16 }}>Free · Secure · No credit card required</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: "32px 5vw", background: "#0F172A", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: "#1E3A2F", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
            </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Grove Money</span>
        </div>
        <p style={{ fontSize: 12, color: "#94A3B8", margin: "0 0 10px" }}>© {new Date().getFullYear()} Grove Money · Built with ❤️ for people who deserve better.</p>
        <div style={{ display: "flex", gap: 20, justifyContent: "center" }}>
          <button onClick={() => setShowPrivacy(true)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", padding: 0 }}>Privacy Policy</button>
          <button onClick={() => setShowTerms(true)} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", padding: 0 }}>Terms of Service</button>
          <a href="#faq" style={{ color: "#64748B", fontSize: 12, textDecoration: "none" }}>FAQ</a>
          <a href="mailto:support@grove-money.com" style={{ color: "#64748B", fontSize: 12, textDecoration: "none" }}>Contact</a>
        </div>
      </footer>
    </div>
  )
}

function AuthPage() {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [showReset, setShowReset] = useState(false)

  async function handleSubmit() {
    setLoading(true)
    setMessage('')
    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setMessage('❌ ' + error.message)
    } else {
      const { error } = await supabase.auth.signUp({
        email, password, options: { data: { full_name: fullName } }
      })
      if (error) setMessage('❌ ' + error.message)
      else setMessage('✅ Check your email to confirm your account!')
    }
    setLoading(false)
  }

  async function handleReset() {
    if (!email) { setMessage('❌ Enter your email above first'); return }
    setLoading(true)
    setMessage('')
    try {
      const res = await fetch(`${API_BASE}/api/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const { exists } = await res.json()
      if (!exists) {
        setMessage('❌ No account found with that email.')
        setLoading(false)
        return
      }
    } catch {
      // Server unreachable — fall through and attempt reset anyway
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    if (error) setMessage('❌ ' + error.message)
    else setMessage('✅ Password reset email sent! Check your inbox.')
    setLoading(false)
  }

  const inp = {
    width: '100%', padding: '11px 14px', background: '#F8FAFC',
    border: '1px solid #E2E8F0', borderRadius: 10, color: '#0F172A',
    fontSize: 14, outline: 'none', boxSizing: 'border-box'
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
      <div style={{ width: 400, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <span style={{ fontSize: 20, fontWeight: 700, color: '#0F172A' }}>Grove Money</span>
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>Welcome back 👋</h2>
        <p style={{ color: '#64748B', fontSize: 14, marginBottom: 28 }}>Sign in to your Grove account</p>
        {!isLogin && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#64748B', display: 'block', marginBottom: 6 }}>Full Name</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Doe" style={inp} />
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#64748B', display: 'block', marginBottom: 6 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inp} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontSize: 13, color: '#64748B' }}>Password</label>
            {isLogin && (
              <button onClick={handleReset} disabled={loading} style={{ background: 'none', border: 'none', color: '#2F855A', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                Forgot password?
              </button>
            )}
          </div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={inp} />
        </div>
        {message && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: message.startsWith('✅') ? 'rgba(47,133,90,0.08)' : 'rgba(239,68,68,0.08)', color: message.startsWith('✅') ? '#2F855A' : '#EF4444', fontSize: 13 }}>{message}</div>
        )}
        <button onClick={handleSubmit} disabled={loading} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: '#2F855A', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
        </button>
        {!isLogin && (
          <p style={{ textAlign: 'center', marginTop: 14, color: '#94A3B8', fontSize: 11, lineHeight: 1.6 }}>
            By creating an account you agree to our{' '}
            <a href="https://grove-money.com" onClick={e => { e.preventDefault(); window.location.href = '/?terms=1' }} style={{ color: '#2F855A', textDecoration: 'none', fontWeight: 600 }}>Terms of Service</a>
            {' '}and{' '}
            <a href="https://grove-money.com" onClick={e => { e.preventDefault(); window.location.href = '/?privacy=1' }} style={{ color: '#2F855A', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</a>.
          </p>
        )}
        <p style={{ textAlign: 'center', marginTop: 20, color: '#64748B', fontSize: 14 }}>
          Don't have an account?{' '}
          <a href="/" style={{ color: '#2F855A', fontWeight: 600, textDecoration: 'none' }}>Join the waitlist →</a>
        </p>
      </div>
    </div>
  )
}

function PrivacyPage({ onBack }) {
  return (
    <div style={{ fontFamily: "system-ui", color: "#0F172A", background: "#fff", minHeight: "100vh" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#2F855A", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 32, padding: 0 }}>← Back</button>
        <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ color: "#64748B", fontSize: 14, marginBottom: 40 }}>Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

        {[
          { title: "Who we are", body: `Grove Money is operated by Flavien Raveau, Miami, Florida, USA ("we", "us", or "our"). You can reach us at privacy@grove-money.com.` },
          { title: "What we collect", body: `We collect the following information when you use Grove Money:\n\n• Account information: your name and email address when you register.\n• Financial data: bank account balances, transaction history, and account metadata retrieved via Plaid when you connect your financial institutions.\n• Paycheck information: gross income, tax withholdings, and benefit deductions you enter manually.\n• Household information: names and preferences you enter for household members.\n• Usage data: pages visited and features used, to improve the product.` },
          { title: "How we use your data", body: `We use your data solely to provide and improve the Grove Money service:\n\n• To display your financial picture across accounts, budgets, and projections.\n• To calculate retirement projections, savings rates, and cash flow summaries.\n• We do not sell your data to any third party. Ever.` },
          { title: "Third-party services", body: `Grove Money uses the following third-party services:\n\n• Plaid (plaid.com) — to securely connect your bank accounts. Plaid's privacy policy applies to data you share with them.\n• Supabase (supabase.com) — to store your account and financial data securely in the cloud.\n\nWe do not use advertising networks or share your data with data brokers.` },
          { title: "Data security", body: `Your data is stored in Supabase with row-level security, meaning only your account can access your data. All connections are encrypted in transit via HTTPS. Bank credentials are never stored by Grove Money — Plaid handles authentication directly.` },
          { title: "Data retention", body: `We retain your data for as long as your account is active. If you request deletion of your account, we will permanently delete all your data within 30 days. To request deletion, email privacy@grove-money.com.` },
          { title: "Your rights", body: `You have the right to:\n\n• Access the personal data we hold about you.\n• Request correction of inaccurate data.\n• Request deletion of your data.\n• Disconnect your bank accounts at any time via the Accounts page.\n\nTo exercise these rights, contact us at privacy@grove-money.com.` },
          { title: "Financial disclaimer", body: `Grove Money is a personal finance tracking and planning tool. Nothing in the app constitutes financial, investment, or tax advice. Projections (including retirement estimates) are for informational purposes only and are not guarantees of future results. Always consult a qualified financial professional before making financial decisions.` },
          { title: "Changes to this policy", body: `We may update this policy from time to time. We will notify you of material changes by posting the updated policy at grove-money.com/privacy with a new "last updated" date.` },
          { title: "Contact", body: `Questions about this policy? Email us at privacy@grove-money.com.` },
        ].map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: "#0F172A" }}>{title}</h2>
            {body.split("\n").map((line, i) => line ? <p key={i} style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, margin: "0 0 6px" }}>{line}</p> : <br key={i} />)}
          </div>
        ))}
      </div>
    </div>
  )
}

function TermsPage({ onBack }) {
  return (
    <div style={{ fontFamily: "system-ui", color: "#0F172A", background: "#fff", minHeight: "100vh" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#2F855A", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 32, padding: 0 }}>← Back</button>
        <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8 }}>Terms of Service</h1>
        <p style={{ color: "#64748B", fontSize: 14, marginBottom: 40 }}>Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

        {[
          { title: "Acceptance of terms", body: `By accessing or using Grove Money at grove-money.com, you agree to be bound by these Terms of Service. If you do not agree, do not use the service.` },
          { title: "Description of service", body: `Grove Money is a personal finance tracking and planning application that helps you monitor accounts, track spending, budget, and plan for retirement. It is provided "as is" for personal, non-commercial use.` },
          { title: "Not financial advice", body: `Grove Money is a tool, not a financial adviser. Nothing in the app — including retirement projections, savings rate calculations, budget suggestions, or cash flow summaries — constitutes financial, investment, legal, or tax advice. You should not make financial decisions based solely on information from Grove Money. Consult a qualified financial professional for advice specific to your situation.` },
          { title: "Your account", body: `You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You must be at least 18 years old to use Grove Money. You agree to provide accurate information and to notify us immediately of any unauthorized use of your account.` },
          { title: "Acceptable use", body: `You agree not to:\n\n• Use Grove Money for any unlawful purpose.\n• Attempt to gain unauthorized access to other users' data.\n• Reverse engineer, copy, or redistribute the Grove Money application.\n• Use the service in any way that could harm, disable, or impair it.` },
          { title: "Third-party services", body: `Grove Money integrates with Plaid to connect your financial accounts. By connecting your accounts, you also agree to Plaid's End User Privacy Policy (plaid.com/legal). We are not responsible for the availability or accuracy of third-party services.` },
          { title: "Data accuracy", body: `Financial data displayed in Grove Money is retrieved from your financial institutions via Plaid and may not always be up to date or accurate. Grove Money makes no warranty regarding the accuracy, completeness, or timeliness of any financial data shown.` },
          { title: "Limitation of liability", body: `To the fullest extent permitted by law, Flavien Raveau and Grove Money shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service, including but not limited to financial losses resulting from reliance on projections or data shown in the app.` },
          { title: "Termination", body: `We reserve the right to suspend or terminate your access to Grove Money at any time, for any reason, with or without notice. You may delete your account at any time by contacting privacy@grove-money.com.` },
          { title: "Governing law", body: `These terms are governed by the laws of the State of Florida, USA, without regard to its conflict of law provisions.` },
          { title: "Changes to terms", body: `We may update these terms from time to time. Continued use of Grove Money after changes are posted constitutes your acceptance of the updated terms.` },
          { title: "Contact", body: `Questions about these terms? Email us at privacy@grove-money.com.` },
        ].map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: "#0F172A" }}>{title}</h2>
            {body.split("\n").map((line, i) => line ? <p key={i} style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, margin: "0 0 6px" }}>{line}</p> : <br key={i} />)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── PLAID BUTTON ───────────────────────────────────────────
function PlaidLinkButton({ linkToken, onSuccess, connecting, theme, label }) {
  const t = theme
  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess })
  return (
    <button
      onClick={() => open()}
      disabled={!ready || connecting}
      style={{
        padding: "10px 20px",
        borderRadius: 10,
        border: `1.5px solid ${t.accent}`,
        background: "transparent",
        color: t.accent,
        fontSize: 13,
        fontWeight: 700,
        cursor: !ready || connecting ? "not-allowed" : "pointer",
        opacity: !ready || connecting ? 0.5 : 1,
        letterSpacing: 0.2,
        transition: "opacity 0.15s"
      }}
    >
      {connecting ? "Connecting..." : (label || "+ Connect Account")}
    </button>
  )
}

// ── ACCOUNTS PAGE ──────────────────────────────────────────
function initials(name) {
  const s = (name || "").trim()
  if (!s) return "?"
  const parts = s.split(" ").filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}
function CsvDropZone({ theme, onFile }) {
  const t = theme
  const [dragging, setDragging] = useState(false)
  return (
    <label
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files[0]) }}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 12, padding: "40px 24px", borderRadius: 14,
        border: `2px dashed ${dragging ? t.accent : t.border}`,
        background: dragging ? (t.isDark ? "rgba(110,231,183,0.05)" : "rgba(47,133,90,0.04)") : t.hover,
        cursor: "pointer", transition: "all 0.15s", marginBottom: 16,
      }}>
      <div style={{ fontSize: 36 }}>📂</div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>Drop your CSV file here</div>
        <div style={{ fontSize: 13, color: t.textMuted }}>or <span style={{ color: t.accent, fontWeight: 600 }}>click to browse</span></div>
      </div>
      <input type="file" accept=".csv" onChange={e => onFile(e.target.files[0])} style={{ display: "none" }} />
    </label>
  )
}

function CategoryPicker({ theme, currentCategory, onSelect, onClose, anchorEl, zIndex = 1000, merchantName, merchantHistory, communityMap, hiddenCats = new Set() }) {
  const t = theme
  const [search, setSearch] = useState("")
  const [activeGroup, setActiveGroup] = useState(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const groups = ["income", "needs", "wants", "savings", "transfer"]

  // Build suggestions: history first, then community, then pattern, deduplicated and excluding current
  const suggestions = useMemo(() => {
    const results = []
    const seen = new Set()
    const addSuggestion = (s) => {
      if (!s || s.category === currentCategory || seen.has(s.category)) return
      seen.add(s.category)
      results.push(s)
    }
    if (merchantName && merchantHistory) {
      const key = merchantName.toLowerCase().trim()
      const hist = merchantHistory[key]
      if (hist) addSuggestion(hist)
    }
    if (merchantName && communityMap) {
      const key = merchantName.toLowerCase().trim()
      const comm = communityMap[key]
      if (comm) {
        const catObj = CATEGORIES.find(c => c.label === comm.category)
        addSuggestion({ category: comm.category, group: comm.group, emoji: catObj?.emoji || "💳", source: "community" })
      }
    }
    if (merchantName) addSuggestion(getPatternSuggestion(merchantName))
    return results.slice(0, 3)
  }, [merchantName, merchantHistory, communityMap, currentCategory])

  useEffect(() => {
    if (!anchorEl) return
    const r = anchorEl.getBoundingClientRect()
    const pickerW = 260
    const spaceRight = window.innerWidth - r.left
    const left = spaceRight < pickerW ? Math.max(8, r.right - pickerW) : r.left
    const spaceBelow = window.innerHeight - r.bottom
    const top = spaceBelow < 320 ? Math.max(8, r.top - 320) : r.bottom + 4
    setPos({ top, left })
  }, [anchorEl])

  const filtered = CATEGORIES.filter(c => {
    const matchGroup = !activeGroup || c.group === activeGroup
    const matchSearch = !search || c.label.toLowerCase().includes(search.toLowerCase())
    return matchGroup && matchSearch && !hiddenCats.has(c.label)
  })

  const grouped = groups.map(grp => ({
    grp,
    items: filtered.filter(c => c.group === grp),
  })).filter(g => g.items.length > 0)

  return (
    <div style={{ position: "fixed", top: pos.top, left: pos.left, zIndex, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", padding: 8, width: 260 }}>
      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5, paddingLeft: 2 }}>Suggested</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {suggestions.map(s => (
              <button key={s.category} onClick={() => onSelect(s.category, s.group)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, border: `1px solid ${t.accent}22`, background: t.isDark ? "rgba(110,231,183,0.07)" : "rgba(47,133,90,0.06)", color: t.text, fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", width: "100%" }}>
                <span style={{ fontSize: 16 }}>{s.emoji}</span>
                <span style={{ flex: 1 }}>{s.category}</span>
                <span style={{ fontSize: 10, color: t.textFaint, fontWeight: 400 }}>{s.source === "history" ? "past" : s.source === "community" ? "community" : "match"}</span>
              </button>
            ))}
          </div>
          <div style={{ height: 1, background: t.border, margin: "8px 0" }} />
        </div>
      )}
      <input
        autoFocus
        placeholder="Search categories..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, outline: "none", marginBottom: 8, boxSizing: "border-box" }}
      />
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
        <button onClick={() => setActiveGroup(null)} style={{ padding: "3px 9px", borderRadius: 999, border: `1px solid ${!activeGroup ? t.accent : t.border}`, background: !activeGroup ? (t.isDark ? "rgba(110,231,183,0.15)" : "rgba(47,133,90,0.1)") : "transparent", color: !activeGroup ? t.accent : t.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>All</button>
        {groups.map(grp => (
          <button key={grp} onClick={() => setActiveGroup(activeGroup === grp ? null : grp)}
            style={{ padding: "3px 9px", borderRadius: 999, border: `1px solid ${activeGroup === grp ? GROUP_STYLE[grp]?.color : t.border}`, background: activeGroup === grp ? GROUP_STYLE[grp]?.bg : "transparent", color: activeGroup === grp ? GROUP_STYLE[grp]?.color : t.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}
          >{grp}</button>
        ))}
      </div>
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {grouped.length === 0 && (
          <div style={{ padding: "12px 8px", color: t.textMuted, fontSize: 13, textAlign: "center" }}>No results</div>
        )}
        {grouped.map(({ grp, items }) => (
          <div key={grp}>
            <div style={{ fontSize: 10, fontWeight: 700, color: GROUP_STYLE[grp]?.color, textTransform: "uppercase", letterSpacing: "0.08em", padding: "6px 8px 2px" }}>{grp}</div>
            {items.map(c => (
              <button key={c.label} onClick={() => { onSelect(c.label, c.group); onClose() }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 6, border: "none", background: currentCategory === c.label ? t.hover : "transparent", color: currentCategory === c.label ? t.text : t.textMuted, fontSize: 12, cursor: "pointer", textAlign: "left", fontWeight: currentCategory === c.label ? 700 : 400 }}
              >
                <span>{c.emoji}</span>{c.label}
                {currentCategory === c.label && <span style={{ marginLeft: "auto", color: t.accent }}>✓</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function InvestmentScoreCard({ score, theme, card }) {
  const t = theme
  const { total, metrics, grade } = score
  const [expandedMetric, setExpandedMetric] = useState(null)
  const [showInfo, setShowInfo] = useState(false)
  const circumference = 2 * Math.PI * 38
  const strokeDash = (total / 100) * circumference
  return (
    <div style={{ ...card, padding: "22px 24px", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Investment Score</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Based on {metrics.length} portfolio metrics</p>
            <button onClick={() => setShowInfo(true)} style={{ width: 16, height: 16, borderRadius: "50%", border: `1px solid ${t.border}`, background: t.hover, color: t.textMuted, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0 }}>?</button>
          </div>
        </div>
        <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
          <svg width="80" height="80" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="40" cy="40" r="38" fill="none" stroke={t.border} strokeWidth="5" />
            <circle cx="40" cy="40" r="38" fill="none" stroke={grade.color} strokeWidth="5"
              strokeDasharray={`${strokeDash} ${circumference}`} strokeLinecap="round"
              style={{ transition: "stroke-dasharray 0.8s ease" }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: grade.color, lineHeight: 1 }}>{total}</span>
            <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600 }}>/ 100</span>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: grade.color, background: `${grade.color}18`, padding: "3px 12px", borderRadius: 99 }}>{grade.label}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {metrics.map(m => {
          const pct = Math.round((m.score / m.max) * 100)
          const isOpen = expandedMetric === m.label
          const barColor = pct >= 80 ? "#2F855A" : pct >= 50 ? "#F59E0B" : "#EF4444"
          return (
            <div key={m.label} style={{ cursor: "pointer" }} onClick={() => setExpandedMetric(isOpen ? null : m.label)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: t.text, fontWeight: 600, flex: 1 }}>{m.label}</span>
                <span style={{ fontSize: 10, color: t.textMuted }}>{m.score}/{m.max}</span>
                <span style={{ fontSize: 10, color: t.textFaint, transition: "transform 0.15s", display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: t.hover, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: barColor, transition: "width 0.5s ease" }} />
              </div>
              {isOpen && (
                <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 7, background: t.hover }}>
                  <p style={{ fontSize: 11, color: t.text, fontWeight: 600, margin: "0 0 3px" }}>{m.detail}</p>
                  <p style={{ fontSize: 11, color: t.textMuted, margin: 0, lineHeight: 1.4 }}>{m.tip}</p>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {showInfo && (
        <div onClick={() => setShowInfo(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderRadius: 16, padding: "28px 28px 24px", width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: 0 }}>How the score works</h3>
              <button onClick={() => setShowInfo(false)} style={{ background: "none", border: "none", fontSize: 20, color: t.textMuted, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
            </div>
            {metrics.map(m => (
              <div key={m.label} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>{m.emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{m.label}</span>
                  <span style={{ fontSize: 11, color: t.textFaint, marginLeft: "auto" }}>max {m.max} pts</span>
                </div>
                {m.rows.map(([cond, pts]) => (
                  <div key={cond} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 12, color: t.textMuted }}>{cond}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{pts}</span>
                  </div>
                ))}
                {m.note && <p style={{ fontSize: 11, color: t.textFaint, marginTop: 6, marginBottom: 0, lineHeight: 1.4 }}>{m.note}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function HealthScoreCard({ healthScore, theme, card, loading }) {
  const t = theme
  const { total, metrics, grade } = healthScore
  const [expandedMetric, setExpandedMetric] = useState(null)
  const [showScoringInfo, setShowScoringInfo] = useState(false)
  const circumference = 2 * Math.PI * 38
  const strokeDash = (total / 100) * circumference

  if (loading) {
    const pulse = { background: t.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", borderRadius: 6, animation: "pulse 1.4s ease-in-out infinite" }
    if (!document.getElementById("grove-pulse")) {
      const s = document.createElement("style"); s.id = "grove-pulse"
      s.textContent = `@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`
      document.head.appendChild(s)
    }
    return (
      <div style={{ ...card, padding: "22px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ ...pulse, width: 100, height: 14 }} />
            <div style={{ ...pulse, width: 140, height: 11 }} />
          </div>
          <div style={{ ...pulse, width: 72, height: 72, borderRadius: "50%" }} />
        </div>
        <div style={{ ...pulse, width: 80, height: 20, borderRadius: 99, alignSelf: "center" }} />
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ ...pulse, width: `${60 + i * 8}%`, height: 11 }} />
            <div style={{ ...pulse, width: "100%", height: 5, borderRadius: 3 }} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ ...card, padding: "22px 24px", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Financial Health</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Based on 5 key metrics</p>
            <button onClick={() => setShowScoringInfo(true)} style={{ width: 16, height: 16, borderRadius: "50%", border: `1px solid ${t.border}`, background: t.hover, color: t.textMuted, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0 }}>?</button>
          </div>
        </div>
        <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
          <svg width="80" height="80" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="40" cy="40" r="38" fill="none" stroke={t.border} strokeWidth="5" />
            <circle cx="40" cy="40" r="38" fill="none" stroke={grade.color} strokeWidth="5"
              strokeDasharray={`${strokeDash} ${circumference}`} strokeLinecap="round"
              style={{ transition: "stroke-dasharray 0.8s ease" }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: grade.color, lineHeight: 1 }}>{total}</span>
            <span style={{ fontSize: 9, color: t.textMuted, fontWeight: 600 }}>/ 100</span>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: grade.color, background: `${grade.color}18`, padding: "3px 12px", borderRadius: 99 }}>{grade.label}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        {metrics.map(m => {
          const pct = Math.round((m.score / m.max) * 100)
          const isOpen = expandedMetric === m.label
          const barColor = pct >= 80 ? "#2F855A" : pct >= 50 ? "#F59E0B" : "#EF4444"
          return (
            <div key={m.label} style={{ cursor: "pointer" }} onClick={() => setExpandedMetric(isOpen ? null : m.label)}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: t.text, fontWeight: 600, flex: 1 }}>{m.label}</span>
                <span style={{ fontSize: 10, color: t.textMuted }}>{m.score}/{m.max}</span>
                <span style={{ fontSize: 10, color: t.textFaint, transition: "transform 0.15s", display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: t.hover, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: barColor, transition: "width 0.5s ease" }} />
              </div>
              {isOpen && (
                <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 7, background: t.hover }}>
                  <p style={{ fontSize: 11, color: t.text, fontWeight: 600, margin: "0 0 3px" }}>{m.detail}</p>
                  <p style={{ fontSize: 11, color: t.textMuted, margin: 0, lineHeight: 1.4 }}>{m.tip}</p>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Scoring info modal */}
      {showScoringInfo && (
        <div onClick={() => setShowScoringInfo(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderRadius: 16, padding: "28px 28px 24px", width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: 0 }}>How scoring works</h3>
              <button onClick={() => setShowScoringInfo(false)} style={{ background: "none", border: "none", fontSize: 20, color: t.textMuted, cursor: "pointer", lineHeight: 1, padding: 0 }}>×</button>
            </div>
            {[
              { label: "Emergency Fund", max: 25, emoji: "🛡️",
                rows: [["≥ 6 months of expenses","25 pts"],["3–6 months","18 pts"],["1–3 months","10 pts"],["< 1 month","4 pts"],["None","0 pts"]],
                note: "Based on liquid savings ÷ monthly needs spending." },
              { label: "Savings Rate", max: 25, emoji: "💰",
                rows: [["≥ 20% of income saved","25 pts"],["10–20%","18 pts"],["5–10%","10 pts"],["0–5%","4 pts"],["Negative / no data","0 pts"]],
                note: "(Income − expenses) ÷ income, from recent transactions." },
              { label: "Debt Load", max: 20, emoji: "💳",
                rows: [["No debt","20 pts"],["Debt < 20% of annual income","20 pts"],["20–36% of annual income","14 pts"],["36–50%","6 pts"],["> 50%","0 pts"]],
                note: "Total loan & credit balances compared to annual income." },
              { label: "Investing", max: 15, emoji: "📈",
                rows: [["Investments ≥ 15% of annual income","15 pts"],["5–15%","10 pts"],["Any investments < 5%","6 pts"],["No investments","0 pts"]],
                note: "Total investment balance compared to annual income." },
              { label: "Net Worth Trend", max: 15, emoji: "📊",
                rows: [["Growing over time","15 pts"],["Flat","8 pts"],["Declining","0 pts"],["Not enough history","8 pts"]],
                note: "Compares your oldest and most recent net worth snapshots." },
            ].map(({ label, max, emoji, rows, note }) => (
              <div key={label} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>{emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{label}</span>
                  <span style={{ fontSize: 11, color: t.textFaint, marginLeft: "auto" }}>max {max} pts</span>
                </div>
                {rows.map(([cond, pts]) => (
                  <div key={cond} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 12, color: t.textMuted }}>{cond}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{pts}</span>
                  </div>
                ))}
                <p style={{ fontSize: 11, color: t.textFaint, margin: "6px 0 0", fontStyle: "italic" }}>{note}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function LevelCard({ userXp, theme, card, onNavigate, session }) {
  const t = theme
  const lvl = getLevelInfo(userXp)
  const [recentEvents, setRecentEvents] = useState([])

  useEffect(() => {
    supabase.from("xp_events")
      .select("action, xp, created_at")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false })
      .limit(4)
      .then(({ data }) => setRecentEvents(data || []))
  }, [session.user.id, userXp])

  return (
    <div style={{ ...card, padding: "22px 24px", display: "flex", flexDirection: "column", cursor: "pointer" }} onClick={onNavigate}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Your Level</h3>
          <p style={{ fontSize: 12, color: t.textMuted, margin: "4px 0 0" }}>Financial growth journey</p>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>{lvl.current.emoji}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, marginTop: 4 }}>{lvl.current.name}</div>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{userXp} XP</span>
          {lvl.next && <span style={{ fontSize: 11, color: t.textFaint }}>{lvl.next.xp} XP → {lvl.next.emoji} {lvl.next.name}</span>}
        </div>
        <div style={{ height: 7, borderRadius: 9999, background: t.border, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${lvl.progress}%`, borderRadius: 9999, background: `linear-gradient(90deg, ${t.accent}, #34d399)`, transition: "width 0.6s ease" }} />
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
        {recentEvents.length === 0 ? (
          <p style={{ fontSize: 12, color: t.textFaint, margin: 0, fontStyle: "italic" }}>Complete actions to earn XP</p>
        ) : recentEvents.map((ev, i) => {
          const action = XP_ACTIONS[ev.action]
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14 }}>{action?.emoji || "⭐"}</span>
              <span style={{ fontSize: 11, color: t.textMuted, flex: 1 }}>{action?.label || ev.action}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.accent }}>+{ev.xp}</span>
            </div>
          )
        })}
      </div>
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${t.border}` }}>
        <span style={{ fontSize: 12, color: t.accent, fontWeight: 600 }}>View all levels →</span>
      </div>
    </div>
  )
}

function InstitutionLogo({ acc, theme, onLogoUpdate }) {
  const [hover, setHover] = useState(false)
  const inputRef = useRef()
  const url = acc.institution_logo_url || acc.institution_logo || null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result
      await supabase.from("accounts").update({ institution_logo_url: base64 }).eq("id", acc.id)
      onLogoUpdate?.()
    }
    reader.readAsDataURL(file)
  }

  return (
    <div
      style={{ position: "relative", width: 34, height: 34, cursor: "pointer", flexShrink: 0 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => inputRef.current?.click()}
      title="Click to upload logo"
    >
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
      {url
        ? <img src={url} alt={acc.institution_name || "Institution"} style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", border: `1px solid ${theme.border}` }} />
        : <div style={{ width: 34, height: 34, borderRadius: 10, background: theme.hover, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: theme.textMuted, border: `1px solid ${theme.border}` }}>
            {initials(acc.institution_name || acc.account_name)}
          </div>
      }
      {hover && (
        <div style={{ position: "absolute", inset: 0, borderRadius: 10, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          📷
        </div>
      )}
    </div>
  )
}

const MANUAL_ACCOUNT_TYPES = [
  { label: "Checking",        account_type: "depository",  subtype: "checking" },
  { label: "Savings",         account_type: "depository",  subtype: "savings" },
  { label: "401k / 403b",     account_type: "investment",  subtype: "401k" },
  { label: "Roth IRA",        account_type: "investment",  subtype: "roth" },
  { label: "Traditional IRA", account_type: "investment",  subtype: "ira" },
  { label: "Brokerage",       account_type: "investment",  subtype: "brokerage" },
  { label: "HSA",             account_type: "investment",  subtype: "hsa" },
  { label: "529 College",     account_type: "investment",  subtype: "529" },
  { label: "Credit Card",     account_type: "credit",      subtype: "credit card" },
  { label: "Mortgage",        account_type: "loan",        subtype: "mortgage" },
  { label: "Auto Loan",       account_type: "loan",        subtype: "auto" },
  { label: "Student Loan",    account_type: "loan",        subtype: "student" },
  { label: "Other",           account_type: "other",       subtype: "other" },
]

function AccountsPage({ session, theme, onSync, onShowOnboarding, onXPAward, householdMembers = [], onAccountsChange }) {
  const t = theme
  const card = makeCard(t)
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [linkToken, setLinkToken] = useState(null)
  const [connecting, setConnecting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [showManualForm, setShowManualForm] = useState(false)
  const [manualForm, setManualForm] = useState({ name: "", institution: "", typeKey: "Checking", balance: "" })
  const [savingManual, setSavingManual] = useState(false)
  const [editingBalance, setEditingBalance] = useState(null) // { id, value }

  // Real estate
  const [realEstateProps, setRealEstateProps] = useState([])
  const [showREForm, setShowREForm] = useState(false)
  const emptyREForm = { nickname: "", address: "", purchase_price: "", purchase_date: "", estimated_value: "", mortgage_account_id: "" }
  const [reForm, setReForm] = useState(emptyREForm)
  const [savingRE, setSavingRE] = useState(false)
  const [rentcastLoading, setRentcastLoading] = useState(null)
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [addressSearching, setAddressSearching] = useState(false)
  const addressDebounceRef = useRef(null)

  // Account detail modal
  const [accountModal, setAccountModal] = useState(null) // account object
  const [accountModalTab, setAccountModalTab] = useState("history")
  const [historyEntries, setHistoryEntries] = useState([])
  const [manualTxEntries, setManualTxEntries] = useState([])
  const today = new Date().toISOString().split("T")[0]
  const [historyForm, setHistoryForm] = useState({ date: today, balance: "", note: "" })
  const [txForm, setTxForm] = useState({ date: today, name: "", ticker: "", shares: "", price: "", type: "buy" })
  const [txEntryMode, setTxEntryMode] = useState("single")
  const emptyTxRow = () => ({ date: today, name: "", ticker: "", shares: "", price: "", type: "buy" })
  const [multiRows, setMultiRows] = useState([emptyTxRow()])
  const [csvRows, setCsvRows] = useState([])
  const [csvError, setCsvError] = useState("")
  const [dbError, setDbError] = useState("")

  function downloadTxTemplate() {
    const csv = "Ticker,Date,Type,Quantity,Share Cost\nVOO,2024-01-15,buy,10,450.00\nAAPL,2024-02-01,buy,5,185.50\nVOO,2024-03-01,buy,5,460.00"
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = "transactions_template.csv"; a.click()
    URL.revokeObjectURL(url)
  }

  const [csvHeaders, setCsvHeaders] = useState([])

  function handleCsvFile(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        // Strip BOM if present
        const text = e.target.result.replace(/^\uFEFF/, "")
        const lines = text.trim().split(/\r?\n/)
        const rawHeaders = lines[0].split(",").map(h => h.trim())
        const headers = rawHeaders.map(h => h.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""))
        setCsvHeaders(rawHeaders)
        const rows = lines.slice(1).filter(l => l.trim()).map(line => {
          const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""))
          const obj = {}
          headers.forEach((h, i) => { obj[h] = vals[i] || "" })
          // Flexible field matching
          const ticker = obj.ticker || obj.symbol || obj.stock || ""
          const date = obj.date || obj.transaction_date || obj.trade_date || ""
          const type = (obj.type || obj.transaction_type || obj.action || "buy").toLowerCase()
          const shares = obj.quantity || obj.shares || obj.qty || obj.units || ""
          // Accept any numeric-looking column for price if named variants don't match
          const price = obj.share_cost || obj.price || obj.price_per_share || obj.cost
            || obj.unit_price || obj.share_price || obj.cost_per_share || ""
          const sharesNum = parseFloat(shares.replace(/[^0-9.-]/g, "")) || 0
          const priceNum = parseFloat(price.replace(/[^0-9.-]/g, "")) || 0
          const amount = sharesNum && priceNum ? sharesNum * priceNum : priceNum
          return { ticker, date, type, shares, price, amount, name: ticker, valid: !!(ticker && date && (priceNum > 0)) }
        })
        setCsvRows(rows)
        if (rows.length > 0) {
        }
        setCsvError(rows.length === 0 ? "No rows found in file" : rows[0] ? `First row — ticker:"${rows[0].ticker}" qty:"${rows[0].shares}" price:"${rows[0].price}" → total:${rows[0].amount}` : "")
      } catch {
        setCsvError("Could not parse file — make sure it's a valid CSV")
        setCsvRows([])
        setCsvHeaders([])
      }
    }
    reader.readAsText(file)
  }

  async function importCsvRows() {
    const valid = csvRows.filter(r => r.valid)
    if (!valid.length) return
    const rows = valid.map(r => ({
      user_id: session.user.id,
      account_id: accountModal.id,
      transaction_date: r.date,
      name: r.ticker,
      ticker: r.ticker || null,
      shares: parseFloat(r.shares) || null,
      price_per_share: parseFloat(r.price) || null,
      amount: r.amount || 0,
      transaction_type: r.type,
    }))
    const { error: insErr } = await supabase.from("manual_transactions").insert(rows)
    if (insErr) { setDbError("Insert failed: " + insErr.message); return }
    setDbError("")
    await recalcBalanceFromTxs(accountModal.id)
    setCsvRows([])
    await loadManualTxEntries(accountModal.id)
  }

  async function openAccountModal(acc) {
    setAccountModal(acc)
    setAccountModalTab("history")
    await Promise.all([loadHistoryEntries(acc.id), loadManualTxEntries(acc.id)])
  }

  async function loadHistoryEntries(accountId) {
    const { data } = await supabase.from("account_balance_history")
      .select("*").eq("account_id", accountId).eq("user_id", session.user.id)
      .order("balance_date", { ascending: false })
    setHistoryEntries(data || [])
  }

  async function loadManualTxEntries(accountId) {
    const { data } = await supabase.from("manual_transactions")
      .select("*").eq("account_id", accountId).eq("user_id", session.user.id)
      .order("transaction_date", { ascending: false })
    setManualTxEntries(data || [])
  }

  async function addBalanceEntry() {
    if (!historyForm.balance || !historyForm.date) return
    const balance = parseFloat(String(historyForm.balance).replace(/,/g, "")) || 0
    await supabase.from("account_balance_history").insert({
      user_id: session.user.id, account_id: accountModal.id,
      balance_date: historyForm.date, balance, note: historyForm.note || null,
    })
    // Update account balance if this is the most recent entry
    const allEntries = [...historyEntries, { balance_date: historyForm.date, balance }]
    const mostRecent = allEntries.sort((a, b) => b.balance_date.localeCompare(a.balance_date))[0]
    if (mostRecent.balance_date === historyForm.date) {
      await supabase.from("accounts").update({ balance, updated_at: new Date().toISOString() })
        .eq("id", accountModal.id).eq("user_id", session.user.id)
      setAccountModal(prev => ({ ...prev, balance }))
      await loadAccounts()
    }
    setHistoryForm({ date: today, balance: "", note: "" })
    await loadHistoryEntries(accountModal.id)
  }

  async function deleteBalanceEntry(entryId) {
    await supabase.from("account_balance_history").delete().eq("id", entryId).eq("user_id", session.user.id)
    const remaining = historyEntries.filter(e => e.id !== entryId)
    if (remaining.length > 0) {
      const mostRecent = [...remaining].sort((a, b) => b.balance_date.localeCompare(a.balance_date))[0]
      await supabase.from("accounts").update({ balance: mostRecent.balance, updated_at: new Date().toISOString() })
        .eq("id", accountModal.id).eq("user_id", session.user.id)
      await loadAccounts()
    }
    await loadHistoryEntries(accountModal.id)
  }

  async function addManualTx() {
    if (!txForm.name || !txForm.date) return
    const shares = parseFloat(txForm.shares) || null
    const price = parseFloat(txForm.price) || null
    const amount = (shares && price) ? shares * price : (price || 0)
    await supabase.from("manual_transactions").insert({
      user_id: session.user.id, account_id: accountModal.id,
      transaction_date: txForm.date, name: txForm.name,
      ticker: txForm.ticker || null, shares, price_per_share: price,
      amount, transaction_type: txForm.type,
    })
    setTxForm({ date: today, name: "", ticker: "", shares: "", price: "", type: "buy" })
    await recalcBalanceFromTxs(accountModal.id)
    await loadManualTxEntries(accountModal.id)
  }

  async function deleteManualTx(txId) {
    await supabase.from("manual_transactions").delete().eq("id", txId).eq("user_id", session.user.id)
    await loadManualTxEntries(accountModal.id)
  }

  async function recalcBalanceFromTxs(accountId) {
    const { data: allTxs, error: txErr } = await supabase.from("manual_transactions")
      .select("transaction_date, amount, transaction_type")
      .eq("account_id", accountId).eq("user_id", session.user.id)
    if (txErr) { setDbError("manual_transactions: " + txErr.message); return }
    if (!allTxs || allTxs.length === 0) return
    const ADDS = new Set(["buy", "contribution", "dividend"])
    const SUBS = new Set(["sell", "fee"])
    const total = allTxs.reduce((sum, r) => {
      if (ADDS.has(r.transaction_type)) return sum + (r.amount || 0)
      if (SUBS.has(r.transaction_type)) return sum - (r.amount || 0)
      return sum
    }, 0)
    const mostRecentDate = allTxs.map(r => r.transaction_date).sort().reverse()[0] || today
    const { error: histErr } = await supabase.from("account_balance_history").upsert(
      { user_id: session.user.id, account_id: accountId, balance_date: mostRecentDate, balance: total, note: "Auto-set from transactions (cost basis)" },
      { onConflict: "user_id,account_id,balance_date" }
    )
    if (histErr) { setDbError("account_balance_history: " + histErr.message); return }
    await supabase.from("accounts").update({ balance: total, updated_at: new Date().toISOString() })
      .eq("id", accountId).eq("user_id", session.user.id)
    setAccountModal(prev => prev ? { ...prev, balance: total } : prev)
    await loadAccounts()
    await loadHistoryEntries(accountId)
    onAccountsChange?.()
  }

  async function saveMultiRows() {
    const valid = multiRows.filter(r => r.name.trim() && r.date)
    if (!valid.length) return
    const rows = valid.map(r => {
      const shares = parseFloat(r.shares) || null
      const price = parseFloat(r.price) || null
      const amount = (shares && price) ? shares * price : (price || 0)
      return { user_id: session.user.id, account_id: accountModal.id, transaction_date: r.date, name: r.name.trim(), ticker: r.ticker || null, shares, price_per_share: price, amount, transaction_type: r.type }
    })
    await supabase.from("manual_transactions").insert(rows)
    setMultiRows([emptyTxRow()])
    await recalcBalanceFromTxs(accountModal.id)
    await loadManualTxEntries(accountModal.id)
  }

  // group expand/collapse state
  const [openGroups, setOpenGroups] = useState({})

  useEffect(() => { loadAccounts(); fetchLinkToken() }, []) // eslint-disable-line

  async function loadAccounts() {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('user_id', session.user.id)
      .order('balance', { ascending: false })

    if (error) fireToast("Failed to load accounts. Please refresh.")
    if (data) {
      setAccounts(data)
      const onboardingDone = localStorage.getItem(`grove_onboarded_${session.user.id}`)
      if (!onboardingDone && data.length === 0) onShowOnboarding?.()
    }
    setLoading(false)
  }

  async function assignAccount(accountId, memberId) {
    await supabase.from("accounts").update({ assigned_to: memberId }).eq("id", accountId)
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, assigned_to: memberId } : a))
  }

 async function removeAccount(accountId) {
  try {
    // Optional: also delete related transactions for that user/account if you store plaid_account_id there
    // await supabase.from("transactions").delete().eq("user_id", session.user.id).eq("plaid_account_id", plaidAccountId)

    const { error } = await supabase
      .from("accounts")
      .delete()
      .eq("user_id", session.user.id)
      .eq("id", accountId)

    if (error) throw error
    await loadAccounts()
  } catch (e) {
    fireToast("Failed to remove account. Please try again.")
  }
}
  
  async function addManualAccount() {
    const typeObj = MANUAL_ACCOUNT_TYPES.find(x => x.label === manualForm.typeKey) || MANUAL_ACCOUNT_TYPES[0]
    const balance = parseFloat(String(manualForm.balance).replace(/,/g, "")) || 0
    if (!manualForm.name.trim()) return
    setSavingManual(true)
    const { error } = await supabase.from("accounts").insert({
      user_id: session.user.id,
      account_name: manualForm.name.trim(),
      institution_name: manualForm.institution.trim() || manualForm.name.trim(),
      account_type: typeObj.account_type,
      subtype: typeObj.subtype,
      balance,
      updated_at: new Date().toISOString(),
    })
    if (error) { fireToast("Failed to save account. Please try again."); setSavingManual(false); return }
    setManualForm({ name: "", institution: "", typeKey: "Checking", balance: "" })
    setShowManualForm(false)
    setSavingManual(false)
    await loadAccounts()
  }

  async function updateManualBalance(id, newBalance) {
    const val = parseFloat(String(newBalance).replace(/,/g, "")) || 0
    await supabase.from("accounts").update({ balance: val, updated_at: new Date().toISOString() }).eq("id", id).eq("user_id", session.user.id)
    setEditingBalance(null)
    await loadAccounts()
  }

  async function fetchLinkToken() {
    try {
      const res = await apiPost('/api/create-link-token', { userId: session.user.id }, session)
      const data = await res.json()
      setLinkToken(data.link_token)
    } catch (err) { console.error('Could not fetch link token', err) }
  }

  async function refreshAccounts() {
    setRefreshing(true)
    try {
      const res = await apiPost("/api/accounts", { userId: session.user.id }, session)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to refresh")

      const now = new Date().toISOString()
      await Promise.all(
        (data.accounts || []).map((a) =>
          supabase.from("accounts")
            .update({ balance: a.balances.current, updated_at: now })
            .eq("user_id", session.user.id)
            .eq("plaid_account_id", a.account_id)
        )
      )
      await loadAccounts()
    } catch (e) {
      console.error("Refresh error:", e)
    }
    setRefreshing(false)
  }

  async function onPlaidSuccess(publicToken, metadata) {
    setConnecting(true);

    try {
      const connectRes = await apiPost("/api/connect", {
        userId: session.user.id,
        public_token: publicToken,
        institution_id: metadata.institution?.institution_id || null,
      }, session);

      const connectData = await connectRes.json();
      if (!connectRes.ok) {
        throw new Error(connectData.error || "Failed to connect account");
      }

      const accountsData = connectData.accounts;

      await Promise.all(
        accountsData.map((account) =>
          supabase.from("accounts").upsert({
            user_id: session.user.id,
            account_name: account.name,
            account_type: account.type,
            subtype: account.subtype,
            updated_at: new Date().toISOString(),
            balance: account.balances.current,
            plaid_account_id: account.account_id,
            institution_name: metadata.institution?.name || "Unknown",
            institution_logo_url: connectData.institution_logo_url || null,
          }, { onConflict: "user_id,plaid_account_id" })
        )
      );

      await loadAccounts();
      // Award XP for first Plaid connection
      if (accounts.length === 0) onXPAward?.("connect_first_account")
      onSync && onSync();
    } catch (err) {
      console.error("Plaid connection error", err);
    }

    setConnecting(false);
  }

  const totalBalance = accounts.reduce((sum, a) => {
    if (a.account_type === 'credit' || a.account_type === 'loan') return sum - (a.balance || 0)
    return sum + (a.balance || 0)
  }, 0)

  // ---- real estate ----
  async function loadRealEstate() {
    const { data } = await supabase.from("real_estate").select("*").eq("user_id", session.user.id).order("created_at")
    if (data) setRealEstateProps(data)
  }
  useEffect(() => { loadRealEstate() }, []) // eslint-disable-line

  async function fetchPropertyValue(address) {
    try {
      const res = await apiPost("/api/property-value", { address }, session)
      const data = await res.json()
      if (!res.ok) return null
      return { price: data.price || null, rent: data.rent || null }
    } catch { return null }
  }

  async function addRealEstate() {
    setSavingRE(true)
    const address = reForm.address.trim()
    let estimatedValue = Number(String(reForm.estimated_value).replace(/,/g, "")) || 0
    let estimatedRent = null
    // Auto-fetch from Rentcast if address provided and no manual value entered
    if (address && !estimatedValue) {
      const fetched = await fetchPropertyValue(address)
      if (fetched?.price) estimatedValue = Math.round(fetched.price)
      if (fetched?.rent) estimatedRent = Math.round(fetched.rent)
    }
    const { error } = await supabase.from("real_estate").insert({
      user_id: session.user.id,
      nickname: reForm.nickname.trim() || address || "Property",
      address,
      purchase_price: Number(String(reForm.purchase_price).replace(/,/g, "")) || 0,
      purchase_date: reForm.purchase_date || null,
      estimated_value: estimatedValue,
      estimated_rent: estimatedRent,
      last_refreshed: estimatedValue && !reForm.estimated_value ? new Date().toISOString() : null,
      mortgage_account_id: reForm.mortgage_account_id || null,
    })
    if (!error) {
      await loadRealEstate()
      setShowREForm(false)
      setReForm(emptyREForm)
      if (onAccountsChange) onAccountsChange()
    } else { fireToast("Could not save property") }
    setSavingRE(false)
  }

  async function deleteRealEstate(id) {
    if (!window.confirm("Remove this property?")) return
    await supabase.from("real_estate").delete().eq("id", id).eq("user_id", session.user.id)
    await loadRealEstate()
    if (onAccountsChange) onAccountsChange()
  }

  async function refreshRentcast(prop) {
    if (!prop.address) { fireToast("This property has no address set"); return }
    setRentcastLoading(prop.id)
    try {
      const result = await fetchPropertyValue(prop.address)
      if (result?.price) {
        await supabase.from("real_estate").update({
          estimated_value: Math.round(result.price),
          estimated_rent: result.rent ? Math.round(result.rent) : prop.estimated_rent,
          last_refreshed: new Date().toISOString()
        }).eq("id", prop.id)
        await loadRealEstate()
        if (onAccountsChange) onAccountsChange()
        fireToast(`Value updated to $${Math.round(result.price).toLocaleString()}`, "success")
      } else { fireToast("Could not get an estimate for this address") }
    } catch (e) { fireToast(`Error: ${e.message}`) }
    setRentcastLoading(null)
  }

  // ---- grouping helpers ----
  function categoryLabel(acc) {
    const type = (acc.account_type || "").toLowerCase()
    const sub = (acc.subtype || "").toLowerCase()

    if (sub === "401k" || sub === "ira" || sub === "roth" || sub === "roth_ira") return "Retirement"
    if (type === "depository") return "Cash & Bank"
    if (type === "investment" || type === "brokerage") return "Investments"
    if (type === "credit") return "Credit Cards"
    if (type === "loan") return "Loans"
    return "Other"
  }

  function signedBalance(acc) {
    const b = Number(acc.balance || 0)
    // Treat credit/loan as negative contribution to net worth
    return (acc.account_type === 'credit' || acc.account_type === 'loan') ? -b : b
  }

  const grouped = accounts.reduce((map, acc) => {
    const key = categoryLabel(acc)
    if (!map[key]) map[key] = []
    map[key].push(acc)
    return map
  }, {})

  const groupOrder = ["Cash & Bank", "Investments", "Retirement", "Credit Cards", "Loans", "Other"]
  const groups = groupOrder
    .filter(g => grouped[g]?.length)
    .map(g => ({
      name: g,
      accounts: grouped[g],
      count: grouped[g].length,
      subtotal: grouped[g].reduce((s, a) => s + signedBalance(a), 0),
    }))

  // initialize open groups once when we first have groups
  useEffect(() => {
    if (!groups.length) return
    setOpenGroups(prev => {
      // keep existing state, default new groups to open
      const next = { ...prev }
      for (const g of groups) {
        if (typeof next[g.name] === "undefined") next[g.name] = true
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length])

  function toggleGroup(name) {
    setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }))
  }

  function accountIcon(acc) {
    if (acc.subtype === '401k' || acc.subtype === 'ira') return '📈'
    if (acc.account_type === 'depository' && acc.subtype === 'checking') return '🏦'
    if (acc.account_type === 'depository' && acc.subtype === 'savings') return '💰'
    if (acc.account_type === 'investment') return '📊'
    if (acc.account_type === 'credit') return '💳'
    if (acc.account_type === 'loan') return '🧾'
    return '🏦'
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
  <div>
    <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6, color: t.text }}>Accounts</h1>
    <p style={{ color: t.textMuted, fontSize: 14 }}>
      {accounts.length > 0
        ? (() => { const plaidCount = accounts.filter(a => a.plaid_account_id).length; const manualCount = accounts.length - plaidCount; return <>{plaidCount > 0 ? `${plaidCount} via Plaid` : ""}{plaidCount > 0 && manualCount > 0 ? " · " : ""}{manualCount > 0 ? `${manualCount} manual` : ""} · <span style={{ color: t.accent, fontWeight: 700 }}>${Math.abs(totalBalance).toLocaleString()} net</span></> })()

        : "Connect your first account to get started"}
    </p>
  </div>

  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
    {accounts.length > 0 && (
      <button onClick={refreshAccounts} disabled={refreshing}
        style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: refreshing ? t.textFaint : t.text, fontSize: 13, fontWeight: 600, cursor: refreshing ? "not-allowed" : "pointer" }}>
        {refreshing ? "Refreshing..." : "↻ Refresh"}
      </button>
    )}
    <button onClick={() => setShowManualForm(true)}
      style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
      + Add Manual
    </button>
    {linkToken && (
      <PlaidLinkButton linkToken={linkToken} onSuccess={onPlaidSuccess} connecting={connecting} theme={t} label="+ Connect via Plaid" />
    )}
  </div>
</div>

      {loading ? (
        <p style={{ color: t.textMuted }}>Loading accounts...</p>
      ) : accounts.length === 0 ? (
        <div style={{ ...card, padding: "48px 32px", textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏦</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: t.text }}>Connect your first account</h2>
          <p style={{ color: t.textMuted, fontSize: 14, marginBottom: 24 }}>Link your bank, brokerage, or retirement accounts</p>
          {linkToken && <PlaidLinkButton linkToken={linkToken} onSuccess={onPlaidSuccess} connecting={connecting} theme={t} />}
        </div>
      ) : (
        <>
          {/* Grouped list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            {groups.map(group => {
              const open = !!openGroups[group.name]
              const subtotalColor =
                group.name === "Credit Cards" || group.name === "Loans"
                  ? "#EF4444"
                  : t.text

              return (
                <div key={group.name} style={{ ...card, overflow: "hidden" }}>
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group.name)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "16px 18px",
                      background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 14, color: t.textMuted }}>{open ? "▾" : "▸"}</span>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>{group.name}</div>
                        <div style={{ fontSize: 11, color: t.textFaint }}>
                          {group.count} account{group.count > 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: subtotalColor }}>
                        ${Math.abs(group.subtotal).toLocaleString()}
                        {(group.name === "Credit Cards" || group.name === "Loans") ? " owed" : ""}
                      </div>
                    </div>
                  </button>

                  {/* Accounts in group */}
                  {open && (
                    <div style={{ padding: "10px 14px 14px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                        {group.accounts.map((acc) => (
                          <div key={acc.id} style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: "16px 16px", background: t.cardBg }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InstitutionLogo acc={acc} theme={t} onLogoUpdate={loadAccounts} />
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: t.hover, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                                  {accountIcon(acc)}
                                </div>
                              </div>

                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 15, fontWeight: 800, color: (acc.account_type === 'credit' || acc.account_type === 'loan') ? "#EF4444" : t.text }}>
                                  {(acc.account_type === 'credit' || acc.account_type === 'loan') ? '-' : ''}${(acc.balance || 0).toLocaleString()}
                                </div>
                                <div style={{ fontSize: 11, color: t.textFaint }}>
                                  {acc.updated_at ? `Synced ${formatTimeAgo(acc.updated_at)}` : "Not yet synced"}
                                </div>
                              </div>
                            </div>

                            <div style={{ marginTop: 10, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 2 }}>{acc.account_name}</div>
    <div style={{ fontSize: 11, color: t.textMuted, marginBottom: householdMembers.length > 0 ? 8 : 0 }}>
      {acc.institution_name} · {acc.subtype || acc.account_type}
    </div>
    {householdMembers.length > 0 && (
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
        {householdMembers.map(m => {
          const active = acc.assigned_to === m.id
          return (
            <button key={m.id} onClick={() => assignAccount(acc.id, active ? null : m.id)}
              style={{ padding: "2px 9px", borderRadius: 99, border: `1px solid ${active ? m.color : t.border}`, background: active ? m.color + "22" : "transparent", color: active ? m.color : t.textFaint, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {m.name}
            </button>
          )
        })}
      </div>
    )}
  </div>

  <div style={{ display: "flex", gap: 6 }}>
    {!acc.plaid_account_id && (
      <button onClick={e => { e.stopPropagation(); openAccountModal(acc) }}
        style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
        History
      </button>
    )}
    <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove "${acc.account_name}"?`)) removeAccount(acc.id) }}
      style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
      Remove
    </button>
  </div>
</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Real Estate section */}
          <div style={{ ...card, overflow: "hidden", marginTop: 8 }}>
            <div style={{ padding: "16px 18px", background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Real Estate</div>
                <div style={{ fontSize: 11, color: t.textFaint }}>{realEstateProps.length} propert{realEstateProps.length !== 1 ? "ies" : "y"} · {(() => {
                  const eq = realEstateProps.reduce((s, p) => {
                    const m = accounts.find(a => a.id === p.mortgage_account_id)
                    return s + Math.max(0, (p.estimated_value || 0) - Math.abs(m?.balance || 0))
                  }, 0)
                  return eq > 0 ? `$${eq.toLocaleString()} equity` : "Track home equity"
                })()}</div>
              </div>
              <button onClick={() => setShowREForm(true)} style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add Property</button>
            </div>
            {realEstateProps.length === 0 ? (
              <div style={{ padding: "24px 18px", textAlign: "center", color: t.textFaint, fontSize: 13 }}>Add your home to include equity in your net worth</div>
            ) : (
              <div>
                {realEstateProps.map((prop, i) => {
                  const mortgageAcc = accounts.find(a => a.id === prop.mortgage_account_id)
                  const mortgage = Math.abs(mortgageAcc?.balance || 0)
                  const equity = Math.max(0, (prop.estimated_value || 0) - mortgage)
                  const gain = prop.purchase_price ? prop.estimated_value - prop.purchase_price : null
                  return (
                    <div key={prop.id} style={{ padding: "14px 18px", borderTop: i === 0 ? "none" : `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(124,179,66,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🏠</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{prop.nickname || prop.address || "Property"}</div>
                        {prop.address && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prop.address}</div>}
                        <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                          {prop.estimated_value > 0 && <span style={{ fontSize: 11, color: t.textMuted }}>Est. value: <strong style={{ color: t.text }}>${prop.estimated_value.toLocaleString()}</strong></span>}
                          {mortgage > 0 && <span style={{ fontSize: 11, color: t.textMuted }}>Mortgage: <strong style={{ color: "#EF4444" }}>-${mortgage.toLocaleString()}</strong></span>}
                          {equity > 0 && <span style={{ fontSize: 11, color: t.textMuted }}>Equity: <strong style={{ color: "#7CB342" }}>${equity.toLocaleString()}</strong></span>}
                          {gain != null && <span style={{ fontSize: 11, color: gain >= 0 ? "#7CB342" : "#EF4444" }}>{gain >= 0 ? "+" : ""}${gain.toLocaleString()} vs purchase</span>}
                          {prop.estimated_rent > 0 && <span style={{ fontSize: 11, color: t.textMuted }}>Est. rent: <strong style={{ color: t.text }}>${prop.estimated_rent.toLocaleString()}/mo</strong></span>}
                          {prop.last_refreshed && <span style={{ fontSize: 11, color: t.textFaint }}>Updated {new Date(prop.last_refreshed).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => refreshRentcast(prop)} disabled={rentcastLoading === prop.id}
                          style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                          {rentcastLoading === prop.id ? "…" : "↻ Rentcast"}
                        </button>
                        <button onClick={() => deleteRealEstate(prop.id)}
                          style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <div style={{ padding: "8px 18px", borderTop: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 11, color: t.textFaint }}>Values estimated via Rentcast · refreshed on demand</span>
            </div>
          </div>

        </>
      )}

      {/* Add Property modal */}
      {showREForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowREForm(false) }}>
          <div style={{ ...card, width: 440, padding: "28px 28px 24px", borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: t.text, margin: 0 }}>Add Property</h2>
              <button onClick={() => setShowREForm(false)} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Nickname */}
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Nickname</label>
                <input type="text" value={reForm.nickname} onChange={e => setReForm(p => ({ ...p, nickname: e.target.value }))}
                  placeholder="e.g. Primary Home"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, boxSizing: "border-box" }} />
              </div>

              {/* Address with autocomplete */}
              <div style={{ position: "relative" }}>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>
                  Address {addressSearching && <span style={{ color: t.textFaint, fontWeight: 400 }}>Searching…</span>}
                </label>
                <input type="text" value={reForm.address}
                  onChange={e => {
                    const val = e.target.value
                    setReForm(p => ({ ...p, address: val }))
                    setAddressSuggestions([])
                    clearTimeout(addressDebounceRef.current)
                    if (val.length < 4) return
                    addressDebounceRef.current = setTimeout(async () => {
                      setAddressSearching(true)
                      try {
                        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&countrycodes=us&addressdetails=1&limit=6`, {
                          headers: { "Accept-Language": "en-US", "User-Agent": "GroveMoneyApp/1.0" }
                        })
                        const data = await res.json()
                        setAddressSuggestions(data.filter(r => r.address?.house_number && r.address?.road).map(r => {
                          const a = r.address
                          const street = `${a.house_number} ${a.road}`
                          const city = a.city || a.town || a.village || a.hamlet || ""
                          const state = a.state || ""
                          const zip = a.postcode || ""
                          return [street, city, `${state} ${zip}`.trim()].filter(Boolean).join(", ")
                        }))
                      } catch {}
                      setAddressSearching(false)
                    }, 400)
                  }}
                  onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                  placeholder="123 Main St, City, ST 12345"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, boxSizing: "border-box" }} />
                {addressSuggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", overflow: "hidden", marginTop: 2 }}>
                    {addressSuggestions.map((s, i) => (
                      <div key={i} onMouseDown={() => { setReForm(p => ({ ...p, address: s })); setAddressSuggestions([]) }}
                        style={{ padding: "9px 12px", fontSize: 12, color: t.text, cursor: "pointer", borderBottom: i < addressSuggestions.length - 1 ? `1px solid ${t.border}` : "none" }}
                        onMouseEnter={e => e.currentTarget.style.background = t.hover}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        📍 {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dollar fields */}
              {[
                { label: "Purchase Price ($)", key: "purchase_price", placeholder: "500,000" },
                { label: "Current Estimated Value ($) — leave blank to auto-fetch", key: "estimated_value", placeholder: "Auto-fetched from Rentcast if blank" },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>{f.label}</label>
                  <input type="text" inputMode="numeric"
                    value={reForm[f.key] ? Number(String(reForm[f.key]).replace(/,/g, "")).toLocaleString() : ""}
                    onChange={e => setReForm(p => ({ ...p, [f.key]: String(Number(e.target.value.replace(/,/g, "")) || "") }))}
                    placeholder={f.placeholder}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, boxSizing: "border-box" }} />
                </div>
              ))}

              {/* Purchase date */}
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Purchase Date</label>
                <input type="date" value={reForm.purchase_date} onChange={e => setReForm(p => ({ ...p, purchase_date: e.target.value }))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Linked Mortgage Account (optional)</label>
                <select value={reForm.mortgage_account_id} onChange={e => setReForm(p => ({ ...p, mortgage_account_id: e.target.value }))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 13, boxSizing: "border-box" }}>
                  <option value="">— None —</option>
                  {accounts.filter(a => a.account_type === "loan").map(a => (
                    <option key={a.id} value={a.id}>{a.account_name} (${Math.abs(a.balance || 0).toLocaleString()})</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button onClick={() => setShowREForm(false)}
                style={{ flex: 1, padding: "11px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={addRealEstate} disabled={savingRE}
                style={{ flex: 2, padding: "11px", borderRadius: 8, border: "none", background: `linear-gradient(135deg, ${t.accent}, ${t.accent2})`, color: "#0d1117", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                {savingRE ? "Saving..." : "Add Property"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual account modal */}
      {showManualForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowManualForm(false) }}>
          <div style={{ ...card, width: 420, padding: "28px 28px 24px", borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: t.text, margin: 0 }}>Add Account Manually</h2>
              <button onClick={() => setShowManualForm(false)} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Account Name *</label>
                <input type="text" value={manualForm.name} onChange={e => setManualForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. ADP 401k"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Institution</label>
                <input type="text" value={manualForm.institution} onChange={e => setManualForm(p => ({ ...p, institution: e.target.value }))}
                  placeholder="e.g. ADP, Fidelity, Vanguard"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Account Type</label>
                <select value={manualForm.typeKey} onChange={e => setManualForm(p => ({ ...p, typeKey: e.target.value }))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 14, boxSizing: "border-box" }}>
                  {MANUAL_ACCOUNT_TYPES.map(x => <option key={x.label} value={x.label}>{x.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 5 }}>Current Balance ($)</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: t.textMuted, fontSize: 14 }}>$</span>
                  <input type="text" inputMode="numeric" value={manualForm.balance} onChange={e => setManualForm(p => ({ ...p, balance: e.target.value }))}
                    placeholder="0"
                    style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
                </div>
                <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>You can update the balance anytime from the Accounts page.</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button onClick={() => setShowManualForm(false)}
                style={{ flex: 1, padding: "11px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={addManualAccount} disabled={!manualForm.name.trim() || savingManual}
                style={{ flex: 2, padding: "11px", borderRadius: 8, border: "none", background: manualForm.name.trim() ? `linear-gradient(135deg, ${t.accent}, ${t.accent2})` : t.border, color: manualForm.name.trim() ? "#0d1117" : t.textMuted, fontSize: 14, fontWeight: 700, cursor: manualForm.name.trim() ? "pointer" : "default" }}>
                {savingManual ? "Saving..." : "Add Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account History Modal */}
      {accountModal && (() => {
        const isInvestment = accountModal.account_type === "investment"
        const inpStyle = { padding: "8px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none" }
        const txTotal = txForm.shares && txForm.price ? (parseFloat(txForm.shares) * parseFloat(txForm.price)).toFixed(2) : null

        return (
          <div onClick={e => { if (e.target === e.currentTarget) setAccountModal(null) }}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ ...card, width: "100%", maxWidth: 560, maxHeight: "85vh", display: "flex", flexDirection: "column", borderRadius: 16, overflow: "hidden" }}>

              {/* Header */}
              <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>{accountModal.account_name}</div>
                  <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{accountModal.institution_name} · {accountModal.subtype}</div>
                </div>
                <button onClick={() => setAccountModal(null)} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>

              {/* DB error banner */}
              {dbError && (
                <div style={{ margin: "0 24px", marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#EF4444", fontSize: 12 }}>
                  ⚠️ {dbError}
                </div>
              )}

              {/* Tab pills */}
              {isInvestment && (
                <div style={{ display: "flex", gap: 6, padding: "12px 24px 0" }}>
                  {[{ id: "history", label: "Balance History" }, { id: "transactions", label: "Transactions" }].map(tb => (
                    <button key={tb.id} onClick={() => setAccountModalTab(tb.id)} style={{
                      padding: "7px 16px", borderRadius: 999, fontSize: 13, fontWeight: accountModalTab === tb.id ? 700 : 500,
                      border: `1px solid ${accountModalTab === tb.id ? t.accent : t.border}`,
                      background: accountModalTab === tb.id ? (t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.08)") : "transparent",
                      color: accountModalTab === tb.id ? t.accent : t.textMuted, cursor: "pointer",
                    }}>{tb.label}</button>
                  ))}
                </div>
              )}

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 24px" }}>

                {/* ── Balance History tab ── */}
                {accountModalTab === "history" && (
                  <div>
                    {/* Add entry form */}
                    <div style={{ background: t.hover, borderRadius: 10, padding: 14, marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Add Balance Entry</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Date</label>
                          <input type="date" value={historyForm.date} onChange={e => setHistoryForm(p => ({ ...p, date: e.target.value }))} style={{ ...inpStyle, width: 140 }} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 100 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Balance ($)</label>
                          <input type="text" inputMode="numeric" placeholder="45,200" value={historyForm.balance} onChange={e => setHistoryForm(p => ({ ...p, balance: e.target.value }))} onKeyDown={e => e.key === "Enter" && addBalanceEntry()} style={{ ...inpStyle, width: "100%" }} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 100 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Note (optional)</label>
                          <input type="text" placeholder="Q1 statement" value={historyForm.note} onChange={e => setHistoryForm(p => ({ ...p, note: e.target.value }))} onKeyDown={e => e.key === "Enter" && addBalanceEntry()} style={{ ...inpStyle, width: "100%" }} />
                        </div>
                        <button onClick={addBalanceEntry} disabled={!historyForm.balance}
                          style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: historyForm.balance ? t.accent : t.border, color: historyForm.balance ? (t.isDark ? "#000" : "#fff") : t.textFaint, fontSize: 13, fontWeight: 700, cursor: historyForm.balance ? "pointer" : "default", whiteSpace: "nowrap" }}>
                          Add
                        </button>
                      </div>
                    </div>

                    {/* History list */}
                    {historyEntries.length === 0 ? (
                      <div style={{ textAlign: "center", color: t.textFaint, fontSize: 13, padding: "24px 0" }}>No entries yet — add your first balance above</div>
                    ) : (
                      <div style={{ ...card, overflow: "hidden" }}>
                        {historyEntries.map((e, i) => {
                          const prev = historyEntries[i + 1]
                          const delta = prev ? e.balance - prev.balance : null
                          return (
                            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i < historyEntries.length - 1 ? `1px solid ${t.border}` : "none" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>${Number(e.balance).toLocaleString()}</div>
                                {e.note && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>{e.note}</div>}
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 12, color: t.textMuted }}>{e.balance_date}</div>
                                {delta !== null && (
                                  <div style={{ fontSize: 11, color: delta >= 0 ? "#2F855A" : "#EF4444", fontWeight: 600 }}>
                                    {delta >= 0 ? "+" : ""}${Math.abs(delta).toLocaleString()}
                                  </div>
                                )}
                              </div>
                              {i === 0 && <span style={{ fontSize: 10, fontWeight: 700, color: t.accent, background: t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.08)", padding: "2px 7px", borderRadius: 99 }}>current</span>}
                              <button onClick={() => deleteBalanceEntry(e.id)} style={{ background: "none", border: "none", color: t.textFaint, fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}>×</button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Transactions tab ── */}
                {accountModalTab === "transactions" && isInvestment && (
                  <div>
                    {/* Mode toggle */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                      {[{ id: "single", label: "Single entry" }, { id: "multi", label: "Multi entry" }, { id: "csv", label: "CSV import" }].map(m => (
                        <button key={m.id} onClick={() => setTxEntryMode(m.id)} style={{
                          padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: txEntryMode === m.id ? 700 : 500,
                          border: `1px solid ${txEntryMode === m.id ? t.accent : t.border}`,
                          background: txEntryMode === m.id ? (t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.08)") : "transparent",
                          color: txEntryMode === m.id ? t.accent : t.textMuted, cursor: "pointer",
                        }}>{m.label}</button>
                      ))}
                    </div>

                    {/* ── Single mode form ── */}
                    {txEntryMode === "single" && (
                    <div style={{ background: t.hover, borderRadius: 10, padding: 14, marginBottom: 16 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Date</label>
                          <input type="date" value={txForm.date} onChange={e => setTxForm(p => ({ ...p, date: e.target.value }))} style={inpStyle} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Type</label>
                          <select value={txForm.type} onChange={e => setTxForm(p => ({ ...p, type: e.target.value }))} style={{ ...inpStyle, cursor: "pointer" }}>
                            {["buy", "sell", "contribution", "dividend", "fee"].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Description</label>
                          <input type="text" placeholder="e.g. Vanguard S&P 500" value={txForm.name} onChange={e => setTxForm(p => ({ ...p, name: e.target.value }))} style={inpStyle} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Ticker (optional)</label>
                          <input type="text" placeholder="e.g. VOO" value={txForm.ticker} onChange={e => setTxForm(p => ({ ...p, ticker: e.target.value.toUpperCase() }))} style={inpStyle} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>Shares (optional)</label>
                          <input type="text" inputMode="numeric" placeholder="10.5" value={txForm.shares} onChange={e => setTxForm(p => ({ ...p, shares: e.target.value }))} style={inpStyle} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: 11, color: t.textFaint }}>{txForm.shares ? "Price / share ($)" : "Total value ($)"}</label>
                          <input type="text" inputMode="numeric" placeholder={txForm.shares ? "450.00" : "1,200.00"} value={txForm.price} onChange={e => setTxForm(p => ({ ...p, price: e.target.value }))} style={inpStyle} />
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                        {txTotal ? <span style={{ fontSize: 13, color: t.textMuted }}>Total: <strong style={{ color: t.text }}>${Number(txTotal).toLocaleString()}</strong></span> : <span />}
                        <button onClick={addManualTx} disabled={!txForm.name || !txForm.date}
                          style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: (txForm.name && txForm.date) ? t.accent : t.border, color: (txForm.name && txForm.date) ? (t.isDark ? "#000" : "#fff") : t.textFaint, fontSize: 13, fontWeight: 700, cursor: (txForm.name && txForm.date) ? "pointer" : "default" }}>
                          Add Transaction
                        </button>
                      </div>
                    </div>
                    )}

                    {/* ── Multi mode ── */}
                    {txEntryMode === "multi" && (
                    <div style={{ background: t.hover, borderRadius: 10, padding: 14, marginBottom: 16 }}>
                      {/* Column headers */}
                      <div style={{ display: "grid", gridTemplateColumns: "110px 80px 1fr 58px 58px 72px 20px", gap: 4, marginBottom: 4 }}>
                        {["Date","Type","Description","Ticker","Shares","Price ($)",""].map((h, i) => (
                          <div key={i} style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", paddingLeft: 2 }}>{h}</div>
                        ))}
                      </div>
                      {/* Rows */}
                      {multiRows.map((row, ri) => {
                        const rowTotal = row.shares && row.price ? (parseFloat(row.shares) * parseFloat(row.price)) : null
                        const upd = (field, val) => setMultiRows(prev => prev.map((r, i) => i === ri ? { ...r, [field]: val } : r))
                        const compact = { padding: "6px 7px", borderRadius: 6, border: `1px solid ${t.border}`, background: t.surface, color: t.text, fontSize: 12, outline: "none", width: "100%" }
                        return (
                          <div key={ri} style={{ display: "grid", gridTemplateColumns: "110px 80px 1fr 58px 58px 72px 20px", gap: 4, marginBottom: 4, alignItems: "center" }}>
                            <input type="date" value={row.date} onChange={e => upd("date", e.target.value)} style={compact} />
                            <select value={row.type} onChange={e => upd("type", e.target.value)} style={{ ...compact, cursor: "pointer" }}>
                              {["buy","sell","contribution","dividend","fee"].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
                            </select>
                            <input type="text" placeholder="Vanguard S&P 500" value={row.name} onChange={e => upd("name", e.target.value)} style={compact} />
                            <input type="text" placeholder="VOO" value={row.ticker} onChange={e => upd("ticker", e.target.value.toUpperCase())} style={compact} />
                            <input type="text" inputMode="numeric" placeholder="10.5" value={row.shares} onChange={e => upd("shares", e.target.value)} style={compact} />
                            <input type="text" inputMode="numeric" placeholder={row.shares ? "450.00" : "total"} value={row.price} onChange={e => upd("price", e.target.value)}
                              style={{ ...compact, color: rowTotal ? t.text : t.textMuted }} />
                            <button onClick={() => setMultiRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== ri) : prev)}
                              style={{ background: "none", border: "none", color: t.textFaint, fontSize: 15, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
                          </div>
                        )
                      })}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                        <button onClick={() => setMultiRows(prev => [...prev, emptyTxRow()])}
                          style={{ padding: "5px 12px", borderRadius: 7, border: `1px dashed ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 12, cursor: "pointer" }}>
                          + Add row
                        </button>
                        <button onClick={saveMultiRows} disabled={!multiRows.some(r => r.name.trim() && r.date)}
                          style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: multiRows.some(r => r.name.trim() && r.date) ? t.accent : t.border, color: multiRows.some(r => r.name.trim() && r.date) ? (t.isDark ? "#000" : "#fff") : t.textFaint, fontSize: 13, fontWeight: 700, cursor: multiRows.some(r => r.name.trim() && r.date) ? "pointer" : "default" }}>
                          Save {multiRows.filter(r => r.name.trim() && r.date).length} {multiRows.filter(r => r.name.trim() && r.date).length === 1 ? "entry" : "entries"}
                        </button>
                      </div>
                    </div>
                    )}

                    {/* ── CSV mode ── */}
                    {txEntryMode === "csv" && (
                    <div style={{ background: t.hover, borderRadius: 10, padding: 14, marginBottom: 16 }}>
                      {/* Template download */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontSize: 13, color: t.textMuted }}>Upload a CSV file with your transactions.</span>
                        <button onClick={downloadTxTemplate} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${t.border}`, background: "transparent", color: t.accent, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          Download template
                        </button>
                      </div>
                      {/* File drop zone */}
                      <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 16px", borderRadius: 10, border: `2px dashed ${t.border}`, cursor: "pointer", background: t.surface }}>
                        <span style={{ fontSize: 28 }}>📂</span>
                        <span style={{ fontSize: 13, color: t.textMuted }}>Click to select CSV <span style={{ color: t.accent, fontWeight: 600 }}>or drag & drop</span></span>
                        <input type="file" accept=".csv" style={{ display: "none" }}
                          onChange={e => { handleCsvFile(e.target.files[0]); e.target.value = "" }}
                          onDrop={e => { e.preventDefault(); handleCsvFile(e.dataTransfer.files[0]) }} />
                      </label>
                      {csvError && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 8 }}>{csvError}</div>}
                      {/* Preview table */}
                      {csvRows.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 4 }}>{csvRows.filter(r => r.valid).length} of {csvRows.length} rows valid</div>
                          {csvHeaders.length > 0 && <div style={{ fontSize: 11, color: t.textFaint, marginBottom: 8 }}>Detected columns: <strong style={{ color: t.textMuted }}>{csvHeaders.join(", ")}</strong></div>}
                          <div style={{ ...card, overflow: "hidden", maxHeight: 220, overflowY: "auto" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "58px 100px 70px 50px 70px 80px", gap: 4, padding: "6px 12px", background: t.hover, fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {["Ticker","Date","Type","Qty","Price","Total"].map(h => <div key={h}>{h}</div>)}
                            </div>
                            {csvRows.map((r, i) => (
                              <div key={i} style={{ display: "grid", gridTemplateColumns: "58px 100px 70px 50px 70px 80px", gap: 4, padding: "6px 12px", borderTop: `1px solid ${t.border}`, opacity: r.valid ? 1 : 0.4, fontSize: 12, color: t.text, alignItems: "center" }}>
                                <span style={{ fontWeight: 700 }}>{r.ticker || "—"}</span>
                                <span>{r.date || "—"}</span>
                                <span style={{ color: r.type === "sell" ? "#EF4444" : t.textMuted }}>{r.type}</span>
                                <span>{r.shares || "—"}</span>
                                <span>{r.price ? `$${r.price}` : "—"}</span>
                                <span style={{ fontWeight: 600, color: r.amount > 0 ? t.text : "#EF4444" }}>{r.amount > 0 ? `$${r.amount.toLocaleString()}` : "—"}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                            <button onClick={importCsvRows} disabled={!csvRows.some(r => r.valid)}
                              style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: csvRows.some(r => r.valid) ? t.accent : t.border, color: csvRows.some(r => r.valid) ? (t.isDark ? "#000" : "#fff") : t.textFaint, fontSize: 13, fontWeight: 700, cursor: csvRows.some(r => r.valid) ? "pointer" : "default" }}>
                              Import {csvRows.filter(r => r.valid).length} {csvRows.filter(r => r.valid).length === 1 ? "row" : "rows"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Transaction list */}
                    {manualTxEntries.length === 0 ? (
                      <div style={{ textAlign: "center", color: t.textFaint, fontSize: 13, padding: "24px 0" }}>No transactions yet</div>
                    ) : (
                      <div style={{ ...card, overflow: "hidden" }}>
                        {manualTxEntries.map((tx, i) => (
                          <div key={tx.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i < manualTxEntries.length - 1 ? `1px solid ${t.border}` : "none" }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: tx.transaction_type === "sell" ? "rgba(239,68,68,0.12)" : "rgba(110,231,183,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                              {tx.transaction_type === "sell" ? "↗" : tx.transaction_type === "dividend" ? "💰" : tx.transaction_type === "fee" ? "💸" : "↙"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: t.text, display: "flex", alignItems: "center", gap: 6 }}>
                                {tx.name}
                                {tx.ticker && <span style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, background: t.hover, padding: "1px 6px", borderRadius: 4 }}>{tx.ticker}</span>}
                              </div>
                              {tx.shares && tx.price_per_share && (
                                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>{tx.shares} shares @ ${tx.price_per_share}</div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>${Number(tx.amount).toLocaleString()}</div>
                              <div style={{ fontSize: 11, color: t.textMuted }}>{tx.transaction_date}</div>
                            </div>
                            <button onClick={() => deleteManualTx(tx.id)} style={{ background: "none", border: "none", color: t.textFaint, fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

const RETIREMENT_SLIDER_FIELDS = [
  { label: "Current Age", key: "current_age", min: 18, max: 80 },
  { label: "Retirement Age", key: "retirement_age", min: 40, max: 80 },
  { label: "Monthly Contribution ($)", key: "monthly_contribution", min: 0, max: 20000, step: 100 },
  { label: "Expected Return (%)", key: "expected_return_pct", min: 1, max: 15, step: 0.5 },
  { label: "Inflation Rate (%)", key: "inflation_pct", min: 0, max: 7, step: 0.1, tooltip: "The US historical average inflation rate since 1926 is ~3% per year (source: BLS CPI data). This means $100 today will cost ~$181 in 20 years. Using 3% is a conservative, historically-grounded assumption." },
  { label: "Annual Spend in Retirement ($)", key: "desired_annual_spend", min: 0, max: 500000, step: 5000 },
  { label: "Social Security / mo ($)", key: "social_security_monthly", min: 0, max: 5000, step: 100 },
  { label: "Goal Amount ($)", key: "goal_amount", min: 100000, max: 20000000, step: 50000 },
]
function fmtVal(key, val) {
  return key.includes('pct') ? val + '%' : (key === 'current_age' || key === 'retirement_age') ? val : '$' + Number(val).toLocaleString()
}

function InfoBubble({ text, theme: t }) {
  const [show, setShow] = useState(false)
  return (
    <span style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: t.hover, border: `1px solid ${t.border}`, color: t.textFaint, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "default", userSelect: "none" }}>i</span>
      {show && (
        <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", zIndex: 300, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", width: 280, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>
          {text}
        </div>
      )}
    </span>
  )
}

function SuccessBadge({ rate }) {
  return <span style={{ fontSize: 28, fontWeight: 800, color: rate >= 80 ? "#2F855A" : rate >= 60 ? "#F59E0B" : "#EF4444" }}>{rate}%</span>
}
// ── XP TOAST NOTIFICATION ─────────────────────────────────────────────────────
function XPToast({ toast, onDismiss }) {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false)
      setTimeout(() => onDismiss(toast.id), 400)
    }, 3800)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  return (
    <div
      onClick={() => { setVisible(false); setTimeout(() => onDismiss(toast.id), 400) }}
      style={{
        transform: visible ? "translateX(0)" : "translateX(130%)",
        opacity: visible ? 1 : 0,
        transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease",
        background: "linear-gradient(135deg, #0f2418 0%, #1a3a28 100%)",
        border: "1px solid rgba(110,231,183,0.35)",
        borderRadius: 14,
        padding: "14px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        minWidth: 270,
        boxShadow: "0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(110,231,183,0.1)",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div style={{ fontSize: 32, lineHeight: 1 }}>{toast.emoji}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: "#6ee7b7", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 3 }}>
          Achievement Unlocked
        </div>
        <div style={{ fontSize: 13, color: "#e2fdf0", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {toast.label}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fbbf24", lineHeight: 1 }}>+{toast.xp}</div>
        <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, marginTop: 2 }}>XP</div>
      </div>
    </div>
  )
}

function XPToastContainer({ toasts, onDismiss }) {
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999, display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none" }}>
      {toasts.map(toast => (
        <div key={toast.id} style={{ pointerEvents: "auto" }}>
          <XPToast toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  )
}

function ChartTooltip({ active, payload, label, theme, formatValue }) {
  if (!active || !payload?.length) return null
  const fmt = formatValue || (v => `$${v?.toLocaleString()}`)
  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <p style={{ color: theme.textMuted, fontSize: 11, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color, fontSize: 13, fontWeight: 600 }}>{p.name}: {fmt(p.value)}</p>)}
    </div>
  )
}
function FanChart({ results, height = 240, theme, goalAmount }) {
  if (!results) return null
  const t = theme
  const balance = results.p50
  const goal = goalAmount || 0
  const pct = goal > 0 ? Math.min(100, Math.round((balance / goal) * 100)) : 100
  const color = results.successRate >= 80 ? t.accent : results.successRate >= 55 ? "#F59E0B" : "#EF4444"
  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: t.textFaint }}>Projected at retirement</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>${(balance / 1000000).toFixed(2)}M</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: t.hover, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: `linear-gradient(90deg, ${color}, ${color}aa)`, transition: "width 0.4s" }} />
      </div>
      {goal > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 10, color: t.textFaint }}>{pct}% of ${(goal/1000000).toFixed(1)}M goal</span>
          <span style={{ fontSize: 10, color: t.textFaint }}>{results.successRate}% historical success</span>
        </div>
      )}
    </div>
  )
}

// Computes, at each age, the minimum balance needed to still hit goal_amount by retirement
function computeRequiredPath(inputs) {
  const { current_age, retirement_age, monthly_contribution, expected_return_pct, goal_amount } = inputs
  const years = retirement_age - current_age
  if (years <= 0) return []
  const r = expected_return_pct / 100
  const path = []
  for (let age = current_age; age <= retirement_age; age++) {
    const remaining = retirement_age - age
    const fvFactor = Math.pow(1 + r, remaining)
    const contribFV = r > 0
      ? monthly_contribution * 12 * (fvFactor - 1) / r
      : monthly_contribution * 12 * remaining
    const required = Math.max(0, (goal_amount - contribFV) / fvFactor)
    path.push({ age, required: Math.round(required) })
  }
  return path
}

// ── RETIREMENT PLANNER ─────────────────────────────────────
function RetirementPlanner({ session, theme, paychecks, transactions = [], onXPAward, householdMembers = [] }) {
  const t = theme
  const card = makeCard(t)
  const [subNav, setSubNav] = useState(() => localStorage.getItem("retirement_subnav") || "overview")
  function setSubNavPersist(v) { setSubNav(v); localStorage.setItem("retirement_subnav", v) }
  const [inputs, setInputs] = useState({
    current_age: 29, retirement_age: 55, monthly_contribution: 1500,
    expected_return_pct: 7, inflation_pct: 3, desired_annual_spend: 80000,
    social_security_monthly: 0, goal_amount: 3000000,
  })
  const [simInputs, setSimInputs] = useState(null)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [accounts, setAccounts] = useState([])
  const [includedAccounts, setIncludedAccounts] = useState([])
  const [chartAccountFilter, setChartAccountFilter] = useState("all") // kept for legacy, unused
  const [fireInputs, setFireInputs] = useState({ leanSpend: 40000, fatSpend: 120000, baristaSpend: 70000, baristaIncome: 20000 })
  const setFireField = (k, v) => setFireInputs(p => ({ ...p, [k]: Number(v) }))
  const [planTab, setPlanTab] = useState(() => localStorage.getItem("retirement_plan_tab") || "projection")
  function setPlanTabPersist(v) { setPlanTab(v); localStorage.setItem("retirement_plan_tab", v) }
  const [ssOpen, setSsOpen] = useState(false)
  const [spendOpen, setSpendOpen] = useState(false)
  const [spendFactor, setSpendFactor] = useState(85)
  const [howOpen, setHowOpen] = useState(true)
  const [ssAnnualIncome, setSsAnnualIncome] = useState(0)
  const [ssAIME, setSsAIME] = useState(0)
  const [contribBreakdown, setContribBreakdown] = useState({ k401: 0, ira: 0, brokerage: 0, other: 0 })
  const setContrib = (key, val) => setContribBreakdown(prev => ({ ...prev, [key]: Number(val) || 0 }))
  const [acctSettings, setAcctSettings] = useState({})
  const [acctSettingsLoaded, setAcctSettingsLoaded] = useState(false)
  const [acctSaveMsg, setAcctSaveMsg] = useState("")
  const [collapsedCats, setCollapsedCats] = useState({ "Cash & Bank": true, "Credit Cards": true, "Loans": true })

  function setAcctSetting(accId, field, value) {
    setAcctSettings(prev => ({ ...prev, [accId]: { ...prev[accId], [field]: value } }))
    // Keep includedAccounts in sync so currentSavings matches year-by-year
    if (field === "include") {
      setIncludedAccounts(prev => value ? [...new Set([...prev, accId])] : prev.filter(x => x !== accId))
    }
  }

  async function saveAcctSettings() {
    for (const [accountId, s] of Object.entries(acctSettings)) {
      await supabase.from("retirement_account_settings").upsert(
        { user_id: session.user.id, account_id: accountId, expected_return_pct: s.return_rate, monthly_contribution: s.monthly_contribution, include_in_plan: s.include !== false },
        { onConflict: "user_id,account_id" }
      )
    }
    setAcctSaveMsg("✅ Saved")
    setTimeout(() => setAcctSaveMsg(""), 2000)
  }

  // Keep monthly_contribution in sync with breakdown total
  useEffect(() => {
    const total = contribBreakdown.k401 + contribBreakdown.ira + contribBreakdown.brokerage + contribBreakdown.other
    setInputs(prev => ({ ...prev, monthly_contribution: total }))
  }, [contribBreakdown])

  // Classify account into one of 4 retirement buckets
  const classifyAccount = (a) => {
    const s = (a.subtype || '').toLowerCase()
    if (['401k','403b','roth 401k','457b','pension','profit sharing'].some(x => s.includes(x))) return 'k401'
    if (['ira','roth','sep ira','simple ira','rollover'].some(x => s.includes(x))) return 'ira'
    if (['brokerage','mutual fund','etf','ugma','utma'].some(x => s.includes(x)) || a.account_type === 'investment') return 'brokerage'
    return 'other'
  }

  useEffect(() => {
    async function loadData() {
      const { data: goal } = await supabase.from('retirement_goals').select('*').eq('user_id', session.user.id).single()
      if (goal) {
        const savedDrawdownRate = localStorage.getItem(`retirement_drawdown_rate_${session.user.id}`)
        const loaded = {
          current_age: goal.current_age ?? 29, retirement_age: goal.target_age ?? 55,
          monthly_contribution: goal.monthly_contribution ?? 1500,
          expected_return_pct: goal.expected_return_pct ?? 7, inflation_pct: goal.inflation_pct ?? 3,
          desired_annual_spend: goal.desired_annual_spend ?? 80000,
          social_security_monthly: goal.social_security_monthly ?? 0, goal_amount: goal.target_amount ?? 3000000,
          drawdown_return_pct: savedDrawdownRate ? Number(savedDrawdownRate) : 6,
        }
        setInputs(loaded); setSimInputs(loaded)
        if (goal.included_account_ids) setIncludedAccounts(goal.included_account_ids)
      }
      const { data: accs } = await supabase.from('accounts').select('*').eq('user_id', session.user.id).not('account_type', 'in', '("loan","credit")')
      if (accs) {
        setAccounts(accs)
        // Default: include retirement + brokerage accounts if nothing saved yet
        if (!goal?.included_account_ids) {
          const retirementSubtypes = ['401k','403b','roth 401k','457b','pension','ira','roth','traditional ira','roth ira','sep ira','simple ira','rollover ira','brokerage','mutual fund']
          setIncludedAccounts(accs.filter(a => retirementSubtypes.some(s => (a.subtype || '').toLowerCase().includes(s)) || a.account_type === 'investment').map(a => a.id))
        }
      }
      setLoading(false)
    }
    loadData()
  }, [session])

  useEffect(() => { if (!loading && simInputs === null) setSimInputs({ ...inputs }) }, [loading, inputs, simInputs])

  // Load per-account settings — runs once when accounts load (not on every plan-setting save)
  useEffect(() => {
    if (!accounts.length) return
    const globalRate = inputs?.expected_return_pct || 7
    const currentPaychecks = paychecks || []
    supabase.from("retirement_account_settings").select("*").eq("user_id", session.user.id)
      .then(({ data }) => {
        const map = {}
        accounts.forEach(acc => {
          const saved = (data || []).find(r => r.account_id === acc.id)
          const savedContrib = saved?.monthly_contribution != null ? Number(saved.monthly_contribution) : null

          // Compute paycheck-derived contribution for this account
          let paycheckContrib = null
          const linkedPaycheck = currentPaychecks.find(p => p.k401AccountId === acc.id)
          if (linkedPaycheck) {
            const gross = linkedPaycheck.payType === "hourly"
              ? (linkedPaycheck.hourlyRate||0)*(linkedPaycheck.typicalHours||0)
              : (linkedPaycheck.gross||0)
            const freq = FREQ_TO_MONTHLY[linkedPaycheck.frequency] || 2.167
            const { contrib, match } = calc401k(linkedPaycheck, gross)
            const monthly = Math.round((contrib + match) * freq)
            if (monthly > 0) paycheckContrib = monthly
          }

          // Use DB value if user explicitly saved non-zero, otherwise use paycheck-derived
          const monthly_contribution = (savedContrib > 0) ? savedContrib : (paycheckContrib ?? savedContrib ?? 0)

          map[acc.id] = {
            return_rate: saved?.expected_return_pct != null ? Number(saved.expected_return_pct) : globalRate,
            monthly_contribution,
            include: saved ? saved.include_in_plan !== false : !["credit","loan"].includes(acc.account_type),
            _fromDb: !!saved,
          }
        })
        setAcctSettings(map)
        setAcctSettingsLoaded(true)
      })
  }, [session.user.id, accounts]) // eslint-disable-line

  // Pre-fill 401k contribution from paycheck data (only if not yet set)
  useEffect(() => {
    if (paychecks?.length > 0) {
      const pm = getPaycheckMonthly(paychecks)
      const auto = Math.round(pm.contrib401k + pm.match401k)
      if (auto > 0) setContribBreakdown(prev => prev.k401 === 0 ? { ...prev, k401: auto } : prev)
    }
  }, [paychecks])

  const coreAccounts = useMemo(
    () => accounts.filter(a => a.subtype === 'ira' || a.subtype === '401k'),
    [accounts]
  )
  const optionalAccounts = useMemo(
    () => accounts.filter(a => a.subtype !== 'ira' && a.subtype !== '401k'),
    [accounts]
  )
  const currentSavings = useMemo(() => {
    // Once acctSettings loaded, use the same filter as Year by Year for consistency
    if (acctSettingsLoaded) {
      return accounts
        .filter(a => acctSettings[a.id]?.include !== false && !["credit","loan"].includes(a.account_type))
        .reduce((sum, a) => sum + (a.balance || 0), 0)
    }
    return accounts.filter(a => includedAccounts.includes(a.id)).reduce((sum, a) => sum + (a.balance || 0), 0)
  }, [accounts, includedAccounts, acctSettings, acctSettingsLoaded])

  // Single unified projection: produces both planRetirementBalance and chart trajectory in one loop
  const retirementProjection = useMemo(() => {
    if (!acctSettingsLoaded || !inputs || !accounts.length) return { balance: 0, trajectory: [] }
    const { retirement_age, current_age } = inputs
    const curAge = current_age || 30
    const yearsToRetire = Math.max(0, retirement_age - curAge)
    const included = accounts.filter(a => acctSettings[a.id]?.include !== false && !["credit","loan"].includes(a.account_type))
    if (!included.length) return { balance: 0, trajectory: [] }
    const balances = {}
    included.forEach(a => { balances[a.id] = a.balance || 0 })
    const startBal = included.reduce((s, a) => s + (balances[a.id] || 0), 0)
    let cumContribs = 0
    const trajectory = [{ age: curAge, contributions: Math.round(startBal), growth: 0, total: Math.round(startBal) }]
    for (let y = 0; y < yearsToRetire; y++) {
      included.forEach(a => {
        const s = acctSettings[a.id] || {}
        const rate = (Number(s.return_rate) || inputs.expected_return_pct || 7) / 100
        const annualContrib = (Number(s.monthly_contribution) || 0) * 12
        const start = balances[a.id]
        balances[a.id] = start * (1 + rate) + annualContrib * (1 + rate / 2)
        cumContribs += annualContrib
      })
      const total = included.reduce((sum, a) => sum + (balances[a.id] || 0), 0)
      const invested = startBal + cumContribs
      trajectory.push({ age: curAge + y + 1, contributions: Math.round(invested), growth: Math.round(Math.max(0, total - invested)), total: Math.round(total) })
    }
    return { balance: included.reduce((sum, a) => sum + (balances[a.id] || 0), 0), trajectory }
  }, [accounts, acctSettings, acctSettingsLoaded, inputs])

  const planRetirementBalance = retirementProjection.balance

  const totalAcctMonthlyContrib = useMemo(() => {
    if (!acctSettingsLoaded) return inputs?.monthly_contribution || 0
    return accounts
      .filter(a => acctSettings[a.id]?.include !== false && !["credit","loan"].includes(a.account_type))
      .reduce((sum, a) => sum + (Number(acctSettings[a.id]?.monthly_contribution) || 0), 0)
  }, [accounts, acctSettings, acctSettingsLoaded, inputs?.monthly_contribution])

  const weightedAcctReturn = useMemo(() => {
    if (!acctSettingsLoaded) return inputs?.expected_return_pct || 7
    const globalRate = inputs?.expected_return_pct || 7
    const includedAccts = accounts.filter(a => acctSettings[a.id]?.include !== false && !["credit","loan"].includes(a.account_type))
    const totalBal = includedAccts.reduce((s, a) => s + (a.balance || 0), 0)
    if (!totalBal) return globalRate
    return includedAccts.reduce((s, a) => s + (acctSettings[a.id]?.return_rate ?? globalRate) * (a.balance || 0), 0) / totalBal
  }, [accounts, acctSettings, acctSettingsLoaded, inputs?.expected_return_pct])

  function toggleAccount(id) {
    setIncludedAccounts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  // Wealth Accumulation chart — directly use retirementProjection trajectory (same loop as planRetirementBalance)
  const wealthAccumChartData = retirementProjection.trajectory

  const mainResults = useMemo(
    () => !loading && inputs && planRetirementBalance > 0
      ? runHistoricalSim(currentSavings, { ...inputs, monthly_contribution: totalAcctMonthlyContrib, expected_return_pct: weightedAcctReturn }, planRetirementBalance)
      : null,
    [loading, inputs, currentSavings, totalAcctMonthlyContrib, weightedAcctReturn, planRetirementBalance]
  )
  const simResults = useMemo(
    () => !loading && simInputs && planRetirementBalance > 0
      ? runHistoricalSim(currentSavings, { ...simInputs, monthly_contribution: totalAcctMonthlyContrib, expected_return_pct: weightedAcctReturn }, planRetirementBalance)
      : null,
    [loading, simInputs, currentSavings, totalAcctMonthlyContrib, weightedAcctReturn, planRetirementBalance]
  )

  async function saveInputs() {
    setSaved(false)
    const payload = {
      user_id: session.user.id, current_age: inputs.current_age, target_age: inputs.retirement_age,
      monthly_contribution: inputs.monthly_contribution, expected_return_pct: inputs.expected_return_pct,
      inflation_pct: inputs.inflation_pct, desired_annual_spend: inputs.desired_annual_spend,
      social_security_monthly: inputs.social_security_monthly, target_amount: inputs.goal_amount,
      included_account_ids: includedAccounts,
    }
    const { data: existing } = await supabase.from('retirement_goals').select('id').eq('user_id', session.user.id).single()
    if (existing) await supabase.from('retirement_goals').update(payload).eq('user_id', session.user.id)
    else await supabase.from('retirement_goals').insert(payload)
    onXPAward?.("retirement_goal_set") // dedup handled by unique index on xp_events
    setSaved(true); setTimeout(() => setSaved(false), 2500)
  }

  const setInput = (key, val) => {
    setInputs(p => ({ ...p, [key]: Number(val) }))
    if (key === "drawdown_return_pct") localStorage.setItem(`retirement_drawdown_rate_${session.user.id}`, String(val))
  }
  const setSimInput = (key, val) => setSimInputs(p => ({ ...p, [key]: Number(val) }))
  const sliderFields = RETIREMENT_SLIDER_FIELDS

  if (loading) return <div style={{ color: t.textMuted, padding: 40 }}>Loading your plan...</div>

  return (
    <div style={{ display: "flex", gap: 0, height: "100%" }}>
      <div style={{ width: 180, borderRight: `1px solid ${t.border}`, paddingTop: 4, flexShrink: 0 }}>
        <p style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, paddingLeft: 4 }}>Retirement</p>
        {retirementSubNav.map(item => (
          <button key={item.id} onClick={() => setSubNavPersist(item.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: subNav === item.id ? t.hover : "transparent", border: "none", cursor: "pointer", color: subNav === item.id ? t.text : t.textMuted, fontSize: 13, fontWeight: subNav === item.id ? 600 : 400, borderRadius: 8, marginBottom: 2, textAlign: "left", borderLeft: subNav === item.id ? `2px solid ${t.accent}` : "2px solid transparent" }}>
            <span>{item.icon}</span>{item.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, paddingLeft: 28, overflowY: "auto", paddingBottom: 32 }}>
        {subNav === "overview" && !mainResults && (
          <div style={{ textAlign: "center", padding: "60px 32px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏖️</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 8 }}>Set up your retirement plan</h2>
            <p style={{ fontSize: 14, color: t.textMuted, marginBottom: 24, maxWidth: 360, margin: "0 auto 24px" }}>
              Fill in your age, goal, and contributions in "My Plan" — we'll model your future and tell you if you're on track.
            </p>
            <button
              onClick={() => setSubNav("plan")}
              style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, ${t.accent}, ${t.accent2})`, color: "#0d1117", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
            >
              Set Up My Plan →
            </button>
          </div>
        )}
        {subNav === "overview" && mainResults && (() => {
          const yearsLeft = inputs.retirement_age - inputs.current_age
          const requiredPath = computeRequiredPath(inputs)
          const requiredNow = requiredPath[0]?.required ?? 0
          const isOnTrack = currentSavings >= requiredNow
          const goalProgress = Math.min(100, Math.round((currentSavings / requiredNow) * 100))

          // Chart data comes directly from retirementProjection (guaranteed same as planRetirementBalance)
          const growthData = wealthAccumChartData

          // Status tier
          const rate = mainResults.successRate
          const statusColor  = rate >= 80 ? t.accent : rate >= 55 ? "#F59E0B" : "#EF4444"
          const statusBg     = rate >= 80
            ? (t.isDark ? "rgba(110,231,183,0.08)" : "rgba(47,133,90,0.07)")
            : rate >= 55
            ? "rgba(245,158,11,0.08)"
            : "rgba(244,114,182,0.08)"
          const statusIcon   = rate >= 80 ? "🟢" : rate >= 55 ? "🟡" : "🔴"
          const statusLabel  = rate >= 80 ? "On Track" : rate >= 55 ? "Getting There" : "Needs Attention"
          const totalPeriods = mainResults.totalPeriods
          const statusMsg    = rate >= 80
            ? `Great work! Your plan survived ${rate}% of all historical market periods — including the Great Depression and 2008. Keep contributing ${fmtVal('monthly_contribution', inputs.monthly_contribution)}/mo and you're on track to retire at ${inputs.retirement_age}.`
            : rate >= 55
            ? `Your plan survived ${rate} of ${totalPeriods} historical periods. A modest boost to your contributions should get you well above 80%.`
            : `Your plan only survived ${rate} of ${totalPeriods} historical periods. You're likely behind the required savings pace — adjusting now makes a big difference.`

          // Annual spend gap at retirement
          const annualSpendInflated = inputs.desired_annual_spend * Math.pow(1 + inputs.inflation_pct / 100, yearsLeft)
          const medianCanSustain = mainResults.p50 * 0.04 // 4% rule
          const spendCovered = medianCanSustain >= annualSpendInflated

          return (
            <div>
              {/* Status Hero */}
              {(() => {
                // Build specific tips based on what's actually off
                const tips = []
                if (currentSavings < requiredNow) {
                  const gap = requiredNow - currentSavings
                  tips.push({ icon: "💰", field: "current_savings", text: `You're ~$${gap.toLocaleString()} behind the required savings pace for your goal` })
                }
                if (mainResults.monthlyNeeded > 0) {
                  tips.push({ icon: "📈", field: "monthly_contribution", text: `Saving $${mainResults.monthlyNeeded.toLocaleString()}/mo more would push your plan to 80% historical success` })
                } else if (rate < 80) {
                  tips.push({ icon: "📈", field: "monthly_contribution", text: `Your balance looks good but only ${rate}% of historical periods survived — adding $100–200/mo builds a stronger buffer` })
                }
                if (annualSpendInflated > medianCanSustain) {
                  tips.push({ icon: "💸", field: "desired_annual_spend", text: `Your $${Math.round(inputs.desired_annual_spend/1000)}k/yr spending plan (≈$${Math.round(annualSpendInflated/1000)}k in future dollars) exceeds the $${Math.round(medianCanSustain/1000)}k/yr your portfolio can safely sustain` })
                }
                const laterRetireRate = runHistoricalSim(currentSavings, { ...inputs, retirement_age: inputs.retirement_age + 2 }).successRate
                const rateBump = laterRetireRate - rate
                tips.push({ icon: "📅", field: "retirement_age", text: `Retiring at ${inputs.retirement_age + 2} instead of ${inputs.retirement_age} would survive ${laterRetireRate}% of historical periods vs ${rate}% now (+${rateBump}pts) — one of the easiest levers` })

                return (
                  <div style={{
                    ...card, padding: "24px 28px", marginBottom: 20,
                    borderLeft: `4px solid ${statusColor}`,
                    background: statusBg,
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
                      {/* Left — status + message */}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <span style={{ fontSize: 22 }}>{statusIcon}</span>
                          <span style={{ fontSize: 13, fontWeight: 800, color: statusColor, textTransform: "uppercase", letterSpacing: "0.08em" }}>{statusLabel}</span>
                        </div>
                        <div style={{ fontSize: 48, fontWeight: 900, color: statusColor, lineHeight: 1, marginBottom: 8 }}>
                          {rate}%
                        </div>
                        <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 12 }}>of {totalPeriods} historical market periods survived with your ${(inputs.goal_amount / 1000000).toFixed(1)}M goal</p>
                        <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5 }}>{statusMsg}</p>
                      </div>

                      {/* Right — tips + CTA */}
                      <div style={{ minWidth: 260, maxWidth: 340 }}>
                        {rate < 80 ? (
                          <>
                            <p style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
                              What to adjust:
                            </p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                              {tips.slice(0, 3).map((tip, i) => (
                                <div key={i} style={{
                                  display: "flex", alignItems: "flex-start", gap: 10,
                                  padding: "10px 12px", borderRadius: 8,
                                  background: t.isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                                  border: `1px solid ${t.border}`,
                                }}>
                                  <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{tip.icon}</span>
                                  <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.45, margin: 0 }}>{tip.text}</p>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ textAlign: "right", marginBottom: 14 }}>
                              <div style={{ fontSize: 11, color: t.textFaint }}>Your goal</div>
                              <div style={{ fontSize: 24, fontWeight: 800, color: t.accent }}>${(inputs.goal_amount / 1000000).toFixed(1)}M</div>
                              <div style={{ fontSize: 11, color: t.textFaint }}>by age {inputs.retirement_age}</div>
                            </div>
                            <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(47,133,90,0.06)", border: "1px solid rgba(47,133,90,0.15)" }}>
                              <p style={{ fontSize: 12, color: t.accent, lineHeight: 1.45, margin: 0 }}>
                                🎉 On pace to exceed by ${((mainResults.p50 - inputs.goal_amount)/1000000).toFixed(2)}M. Scroll down for options.
                              </p>
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => setSubNav("plan")}
                          style={{
                            width: "100%", padding: "11px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                            background: `linear-gradient(135deg, ${t.accent}, ${t.accent2})`,
                            color: "#0d1117", fontSize: 13, fontWeight: 700,
                          }}
                        >
                          {rate < 80 ? "Adjust My Plan to Fix These →" : "Fine-tune My Plan →"}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* 3 Key metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
                {[
                  {
                    label: "Current Savings",
                    value: `$${currentSavings.toLocaleString()}`,
                    sub: rate >= 80
                      ? `Survived ${rate}% of historical periods ✓`
                      : rate >= 60
                      ? `Close — need ~$${requiredNow.toLocaleString()} to be fully on pace`
                      : `Behind schedule — need ~$${requiredNow.toLocaleString()} to catch up`,
                    color: rate >= 80 ? t.accent : rate >= 60 ? "#F59E0B" : "#EF4444",
                    topColor: rate >= 80 ? t.accent : rate >= 60 ? "#F59E0B" : "#EF4444",
                  },
                  {
                    label: "Monthly Contribution",
                    value: `$${inputs.monthly_contribution.toLocaleString()}`,
                    sub: rate >= 80
                      ? "Well positioned to hit your goal 🎉"
                      : mainResults.monthlyNeeded > 0
                      ? `+$${mainResults.monthlyNeeded.toLocaleString()}/mo would close the gap`
                      : `Only ${rate}% of historical periods survived — consider saving more`,
                    color: rate >= 80 ? t.accent : rate >= 60 ? "#F59E0B" : "#EF4444",
                    topColor: t.accent2,
                  },
                  {
                    label: "Time to Retirement",
                    value: `${yearsLeft} years`,
                    sub: `Age ${inputs.current_age} → ${inputs.retirement_age}`,
                    color: t.accent2,
                    topColor: t.accent2,
                  },
                ].map((m, i) => (
                  <div key={i} style={{ ...card, padding: "18px 20px", borderTop: `3px solid ${m.topColor}` }}>
                    <p style={{ fontSize: 11, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{m.label}</p>
                    <p style={{ fontSize: 22, fontWeight: 800, color: m.color, marginBottom: 4 }}>{m.value}</p>
                    <p style={{ fontSize: 11, color: t.textMuted }}>{m.sub}</p>
                  </div>
                ))}
              </div>

              {/* 401k note from paychecks */}
              {paychecks && paychecks.length > 0 && (() => {
                const pm = getPaycheckMonthly(paychecks)
                if (pm.contrib401k <= 0 && pm.match401k <= 0) return null
                const fmt = (n) => `$${Math.round(n).toLocaleString()}`
                return (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", borderRadius: 10, marginBottom: 20, background: `rgba(47,133,90,0.08)`, border: `1px solid rgba(47,133,90,0.2)` }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                    <p style={{ margin: 0, fontSize: 13, color: t.text, lineHeight: 1.5 }}>
                      Your paycheck setup shows{" "}
                      {pm.contrib401k > 0 && <><strong style={{ color: t.accent }}>{fmt(pm.contrib401k)}/mo</strong> going to 401k</>}
                      {pm.contrib401k > 0 && pm.match401k > 0 && " + "}
                      {pm.match401k > 0 && <><strong style={{ color: t.accent2 }}>{fmt(pm.match401k)}/mo</strong> employer match</>}
                      {pm.contrib401k > 0 && pm.match401k > 0 && <> = <strong style={{ color: t.accent }}>{fmt(pm.contrib401k + pm.match401k)}/mo</strong> total toward retirement</>}
                    </p>
                  </div>
                )
              })()}

              {/* Projection Chart */}
              <div style={{ ...card, padding: "22px 24px", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 4 }}>Wealth Accumulation</h3>
                    <p style={{ fontSize: 12, color: t.textFaint }}>
                      What you put in vs. what the market adds — up to retirement.
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, alignItems: "flex-end" }}>
                    {[
                      { label: "Contributions", color: t.accent2, fill: true },
                      { label: "Investment growth", color: t.accent, fill: true },
                      { label: `Goal: $${(inputs.goal_amount/1000000).toFixed(1)}M`, color: "#EF4444", dash: true },
                    ].map(l => (
                      <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        {l.fill
                          ? <svg width="14" height="10"><rect x="0" y="1" width="14" height="8" rx="2" fill={l.color} fillOpacity={0.7} /></svg>
                          : <svg width="18" height="10"><line x1="0" y1="5" x2="18" y2="5" stroke={l.color} strokeWidth="2" strokeDasharray="4 2" /></svg>}
                        <span style={{ fontSize: 11, color: t.textMuted }}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={290}>
                  <AreaChart data={growthData} margin={{ bottom: 16 }} stackOffset="none">
                    <defs>
                      <linearGradient id="gradContribs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={t.accent2} stopOpacity={0.55} />
                        <stop offset="95%" stopColor={t.accent2} stopOpacity={0.25} />
                      </linearGradient>
                      <linearGradient id="gradGrowth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={t.accent} stopOpacity={0.75} />
                        <stop offset="95%" stopColor={t.accent} stopOpacity={0.35} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="age" tick={{ fill: t.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} label={{ value: "Age →", position: "insideBottomRight", offset: 0, fill: t.textFaint, fontSize: 11 }} />
                    <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${Math.round(v/1000)}k`} width={55} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null
                        const contrib = payload.find(p => p.dataKey === "contributions")?.value ?? 0
                        const growth  = payload.find(p => p.dataKey === "growth")?.value ?? 0
                        const total   = contrib + growth
                        const fmt = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : `$${Math.round(v/1000)}k`
                        return (
                          <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                            <p style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>Age {label}</p>
                            <p style={{ color: t.accent2, marginBottom: 3 }}>Capital invested: {fmt(contrib)}</p>
                            <p style={{ color: t.accent, marginBottom: 3 }}>Market gains: {fmt(growth)}</p>
                            <p style={{ color: t.text, fontWeight: 700, borderTop: `1px solid ${t.border}`, paddingTop: 4, marginTop: 4 }}>Portfolio: {fmt(total)}</p>
                          </div>
                        )
                      }}
                    />
                    {/* Stacked areas */}
                    <Area type="monotone" dataKey="contributions" stackId="1" stroke={t.accent2} strokeWidth={1.5} fill="url(#gradContribs)" name="Contributions" dot={false} />
                    <Area type="monotone" dataKey="growth"        stackId="1" stroke={t.accent}  strokeWidth={1.5} fill="url(#gradGrowth)"   name="Investment growth" dot={false} />
                    {/* Goal horizontal line */}
                    <ReferenceLine y={inputs.goal_amount} stroke="#EF4444" strokeDasharray="4 2" strokeWidth={1.5}
                      label={{ value: "Goal", position: "insideTopRight", fill: "#EF4444", fontSize: 10, fontWeight: 700 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Path to Your Goal — roadmap */}
              <div style={{ ...card, padding: "22px 26px", marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>The Path to Your Goal</h3>
                <p style={{ fontSize: 12, color: t.textFaint, marginBottom: 20 }}>Here's the story of your retirement in plain numbers</p>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 0 }}>
                  {[
                    {
                      icon: "📍",
                      title: "Today",
                      color: t.accent,
                      lines: [
                        { label: "Age", value: `${inputs.current_age}` },
                        { label: "Saved so far", value: `$${currentSavings.toLocaleString()}`, info: "Total balance across the accounts selected under the 'My Accounts' subtab" },
                        { label: "Contributing", value: `$${inputs.monthly_contribution.toLocaleString()}/mo` },
                      ],
                    },
                    {
                      icon: "⚙️",
                      title: `Over ${yearsLeft} years`,
                      color: t.accent2,
                      lines: [
                        { label: "Avg annual return", value: `${inputs.expected_return_pct}%` },
                        { label: "Inflation assumed", value: `${inputs.inflation_pct}%` },
                        { label: "Total contributions", value: `$${(inputs.monthly_contribution * 12 * yearsLeft / 1000).toFixed(0)}k` },
                      ],
                    },
                    {
                      icon: "🏖️",
                      title: `At Retirement (age ${inputs.retirement_age})`,
                      color: mainResults.p50 >= inputs.goal_amount ? t.accent : "#EF4444",
                      lines: [
                        { label: "Projected (median)", value: `$${(mainResults.p50 / 1000000).toFixed(2)}M`, bold: true },
                        { label: "Your goal", value: `$${(inputs.goal_amount / 1000000).toFixed(1)}M` },
                        { label: mainResults.p50 >= inputs.goal_amount ? "Surplus" : "Gap", value: `${mainResults.p50 >= inputs.goal_amount ? "+" : "-"}$${(Math.abs(mainResults.p50 - inputs.goal_amount) / 1000000).toFixed(2)}M`, bold: true, color: mainResults.p50 >= inputs.goal_amount ? t.accent : "#EF4444" },
                      ],
                    },
                    {
                      icon: "💸",
                      title: "In Retirement",
                      color: spendCovered ? t.accent : "#F59E0B",
                      lines: [
                        { label: "Annual income (4% rule)", value: `$${Math.round(medianCanSustain / 1000)}k/yr` },
                        { label: "You want to spend", value: `$${Math.round(inputs.desired_annual_spend / 1000)}k/yr` },
                        { label: "Inflation-adjusted at retirement", value: `≈$${Math.round(annualSpendInflated / 1000)}k/yr` },
                        { label: "Money lasts", value: mainResults.yearsMoneyLasts >= 99 ? "Forever 🎉" : `${mainResults.yearsMoneyLasts} yrs`, bold: true, color: mainResults.yearsMoneyLasts >= 30 ? t.accent : "#F59E0B" },
                      ],
                    },
                  ].map((step, i, arr) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Step header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${step.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                            {step.icon}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: step.color }}>{step.title}</span>
                        </div>
                        {/* Step data */}
                        <div style={{ paddingLeft: 4 }}>
                          {step.lines.map(ln => (
                            <div key={ln.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: t.textFaint }}>
                                {ln.label}
                                {ln.info && (
                                  <span style={{ position: "relative", display: "inline-flex" }}
                                    onMouseEnter={e => { const tip = e.currentTarget.querySelector('.tip'); if (tip) tip.style.display = "block" }}
                                    onMouseLeave={e => { const tip = e.currentTarget.querySelector('.tip'); if (tip) tip.style.display = "none" }}
                                  >
                                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", background: t.hover, border: `1px solid ${t.border}`, fontSize: 9, fontWeight: 800, color: t.textMuted, cursor: "default", flexShrink: 0 }}>i</span>
                                    <span className="tip" style={{ display: "none", position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: t.isDark ? "#1e293b" : "#0f172a", color: "#fff", fontSize: 11, lineHeight: 1.5, padding: "7px 10px", borderRadius: 7, whiteSpace: "nowrap", maxWidth: 240, zIndex: 999, pointerEvents: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.3)", whiteSpace: "normal" }}>
                                      {ln.info}
                                    </span>
                                  </span>
                                )}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: ln.bold ? 800 : 600, color: ln.color || t.text }}>{ln.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Arrow connector */}
                      {i < arr.length - 1 && (
                        <div style={{ display: "flex", alignItems: "center", padding: "0 10px", marginTop: 10, flexShrink: 0 }}>
                          <svg width="20" height="16"><path d="M0 8 L14 8 M8 2 L14 8 L8 14" stroke={t.border} strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action card — gated on success rate, not monthlyNeeded */}
              <div style={{ ...card, padding: "22px 26px" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  {rate >= 80 ? "You're On Track — Here's What's Possible" : "How to Improve Your Odds"}
                </h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 18, lineHeight: 1.6 }}>
                  {rate >= 80
                    ? <>Your plan survived <strong style={{ color: t.accent }}>{rate}% of {totalPeriods} historical market periods</strong> — including crashes like 1929, 1987, and 2008. Your projected <strong style={{ color: t.accent }}>${(mainResults.p50/1000000).toFixed(2)}M</strong> beats your ${(inputs.goal_amount/1000000).toFixed(1)}M goal by <strong style={{ color: t.accent }}>${((mainResults.p50 - inputs.goal_amount)/1000000).toFixed(2)}M</strong>. Here's what that opens up:</>
                    : <>Your plan survived <strong style={{ color: rate >= 60 ? "#F59E0B" : "#EF4444" }}>{rate} of {totalPeriods} historical market periods</strong> — it struggles in years following major crashes. Here are three levers that can improve your odds:</>}
                </p>
                {rate < 80 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    {[
                      {
                        icon: "💰", color: t.accent,
                        title: mainResults.monthlyNeeded > 0
                          ? `Save $${mainResults.monthlyNeeded.toLocaleString()}/mo more`
                          : `Increase your contribution`,
                        desc: mainResults.monthlyNeeded > 0
                          ? `Bump from $${inputs.monthly_contribution.toLocaleString()} to $${(inputs.monthly_contribution + mainResults.monthlyNeeded).toLocaleString()}/mo and your median outcome hits the goal. Every extra dollar compounds hard over ${yearsLeft} years.`
                          : `Your balance looks good but some historical sequences still fail. Adding even $100–200/mo builds a buffer against bad market timing.`,
                      },
                      {
                        icon: "📅", color: t.accent2,
                        title: `Retire 2–3 years later`,
                        desc: `Retiring at ${inputs.retirement_age + 2} instead of ${inputs.retirement_age} gives your money 2 extra years to compound and reduces how long it needs to last. Often the easiest lever to pull.`,
                      },
                      {
                        icon: "📉", color: "#F59E0B",
                        title: `Lower your spending target`,
                        desc: `Reducing your target from $${Math.round(inputs.desired_annual_spend/1000)}k to $${Math.round(inputs.desired_annual_spend*0.9/1000)}k/yr in retirement means you need a smaller nest egg — and raises your success rate without saving more.`,
                      },
                    ].map(opt => (
                      <div key={opt.title} style={{ padding: "16px", borderRadius: 10, border: `1px solid ${t.border}`, background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                        <div style={{ fontSize: 22, marginBottom: 8 }}>{opt.icon}</div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: opt.color, marginBottom: 6 }}>{opt.title}</p>
                        <p style={{ fontSize: 12, color: t.textFaint, lineHeight: 1.5 }}>{opt.desc}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                    {[
                      {
                        icon: "⏰", color: t.accent,
                        title: "Retire earlier",
                        desc: `At your current pace you could potentially retire before ${inputs.retirement_age}. Use the Simulator to find your earliest possible retirement age.`,
                      },
                      {
                        icon: "🌴", color: t.accent2,
                        title: "Spend more in retirement",
                        desc: `With $${Math.round(medianCanSustain/1000)}k/yr from the 4% rule, you have room to spend more than your current $${Math.round(inputs.desired_annual_spend/1000)}k/yr plan.`,
                      },
                      {
                        icon: "🎁", color: "#F59E0B",
                        title: "Leave a legacy",
                        desc: `Your surplus of $${((mainResults.p50 - inputs.goal_amount)/1000000).toFixed(2)}M could fund children, charities, or a family trust. Your future looks bright.`,
                      },
                    ].map(opt => (
                      <div key={opt.title} style={{ padding: "16px", borderRadius: 10, border: `1px solid ${t.border}`, background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                        <div style={{ fontSize: 22, marginBottom: 8 }}>{opt.icon}</div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: opt.color, marginBottom: 6 }}>{opt.title}</p>
                        <p style={{ fontSize: 12, color: t.textFaint, lineHeight: 1.5 }}>{opt.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* How is this calculated */}
              <div style={{ ...card, padding: "20px 22px", marginBottom: 20 }}>
                <button onClick={() => setHowOpen(o => !o)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>📐 How is this calculated?</p>
                  <span style={{ fontSize: 12, color: t.textMuted }}>{howOpen ? "▲" : "▼"}</span>
                </button>
                {howOpen && (
                  <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                      { icon: "📜", title: "Historical Simulation", text: "We run your plan through every 30-year window in US market history from 1928 to present (~70+ overlapping periods). If your money lasts through each window, that period counts as a success. A 90%+ rate means your plan survived nearly every historical crash — including 1929, 1973, and 2008." },
                      { icon: "📊", title: "4% Withdrawal Rule", text: "A classic retirement guideline: withdraw 4% of your portfolio in year one, then adjust for inflation annually. Research shows this historically sustains a 30-year retirement. The withdrawal amounts shown use your projected median portfolio balance at retirement." },
                      { icon: "📈", title: "Median (p50) Balance", text: "Your projected balance is the median outcome across all historical simulations — half did better, half did worse. The fan chart shows the range: p10 (worst 10% of scenarios) to p90 (best 10%)." },
                      { icon: "💹", title: "Inflation Adjustment", text: "Your annual spending target is adjusted for inflation over your working years. If you want $80k/yr in today's dollars with 3% inflation and 20 years to retire, you'll actually need ~$144k/yr in future dollars. The plan accounts for this." },
                      { icon: "🔐", title: "Social Security", text: "If you enter a Social Security benefit, it's added to your monthly income in retirement and reduces how much your portfolio needs to sustain. The SS estimator uses the SSA's PIA formula with 2025 bend points." },
                    ].map(item => (
                      <div key={item.title} style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 8, background: t.hover }}>
                        <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 3 }}>{item.title}</p>
                          <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.55, margin: 0 }}>{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        {subNav === "accounts" && (() => {
          // classifyAccount maps to 'k401'|'ira'|'brokerage'|'other'
          // accounts tab uses '401k' as display id — remap for byGroup
          const classifyForTab = a => classifyAccount(a) === 'k401' ? '401k' : classifyAccount(a)

          const groups = [
            { id: '401k',     label: '401k / 403b',      emoji: '📈', color: '#8B5CF6', desc: 'Employer-sponsored retirement accounts' },
            { id: 'ira',      label: 'Roth IRA / IRA',   emoji: '🏦', color: t.accent,  desc: 'Individual retirement accounts' },
            { id: 'brokerage',label: 'Brokerage',         emoji: '📊', color: t.accent2, desc: 'Taxable investment accounts' },
            { id: 'other',    label: 'Other',             emoji: '💰', color: '#F59E0B', desc: 'Savings, HSA, 529, and other accounts' },
          ]

          const byGroup = {}
          groups.forEach(g => { byGroup[g.id] = [] })
          accounts.forEach(a => { byGroup[classifyForTab(a)].push(a) })

          const groupTotal = (gid) => byGroup[gid]
            .filter(a => includedAccounts.includes(a.id))
            .reduce((s, a) => s + (a.balance || 0), 0)

          const AccountRow = ({ a }) => {
            const included = includedAccounts.includes(a.id)
            return (
              <div onClick={() => toggleAccount(a.id)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, border: `1px solid ${included ? "rgba(47,133,90,0.20)" : t.border}`, background: included ? "rgba(47,133,90,0.05)" : "transparent", cursor: "pointer", transition: "all 0.15s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${included ? t.accent : t.border}`, background: included ? t.accent : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {included && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: 0 }}>{a.account_name}</p>
                    <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{a.institution_name} · {(a.subtype || a.account_type || '').toUpperCase()}</p>
                  </div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: included ? t.text : t.textMuted }}>${(a.balance || 0).toLocaleString()}</span>
              </div>
            )
          }

          return (
            <div>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: t.text }}>My Retirement Accounts</h2>
                <p style={{ color: t.textMuted, fontSize: 13 }}>Select which accounts count toward your retirement total</p>
              </div>

              {/* Total bar */}
              <div style={{ ...card, padding: "18px 22px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", borderLeft: `4px solid ${t.accent}` }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>Total Included</p>
                  <p style={{ fontSize: 11, color: t.textFaint, margin: "2px 0 0" }}>Used in your Monte Carlo simulation</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontSize: 28, fontWeight: 900, color: t.accent, margin: 0 }}>${currentSavings.toLocaleString()}</p>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                    {groups.filter(g => groupTotal(g.id) > 0).map(g => (
                      <span key={g.id} style={{ fontSize: 11, color: t.textMuted }}>{g.emoji} ${groupTotal(g.id).toLocaleString()}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* 4 groups */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                {groups.map(g => {
                  const accs = byGroup[g.id]
                  const total = groupTotal(g.id)
                  return (
                    <div key={g.id} style={{ ...card, padding: "18px 20px", borderTop: `3px solid ${g.color}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 18 }}>{g.emoji}</span>
                            <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0 }}>{g.label}</p>
                          </div>
                          <p style={{ fontSize: 11, color: t.textFaint, margin: "3px 0 0" }}>{g.desc}</p>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <p style={{ fontSize: 16, fontWeight: 800, color: total > 0 ? g.color : t.textMuted, margin: 0 }}>${total.toLocaleString()}</p>
                          {accs.length > 0 && <p style={{ fontSize: 10, color: t.textFaint, margin: "2px 0 0" }}>{accs.filter(a => includedAccounts.includes(a.id)).length}/{accs.length} included</p>}
                        </div>
                      </div>
                      {accs.length === 0 ? (
                        <p style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "8px 0" }}>No accounts in this category</p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {accs.map(a => <AccountRow key={a.id} a={a} />)}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <button onClick={saveInputs} style={{ padding: "12px 28px", borderRadius: 8, border: "none", background: saved ? t.hover : `linear-gradient(135deg, ${t.accent}, ${t.accent2})`, color: saved ? t.accent : "#0d1117", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                {saved ? "✓ Saved!" : "Save Account Selection"}
              </button>
            </div>
          )
        })()}


        {subNav === "fire" && (() => {
          const fmtMoney = (n) => "$" + Math.abs(Math.round(n)).toLocaleString()
          const r = inputs.expected_return_pct / 100
          const yearsLeft = inputs.retirement_age - inputs.current_age
          const coastTarget = inputs.goal_amount / Math.pow(1 + r, yearsLeft)
          const leanTarget  = fireInputs.leanSpend * 25
          const fatTarget   = fireInputs.fatSpend * 25
          const barTarget   = Math.max(0, (fireInputs.baristaSpend - fireInputs.baristaIncome)) * 25
          const saved = currentSavings

          const fireTypes = [
            {
              id: "lean",
              emoji: "🌿",
              name: "Lean FIRE",
              color: "#10B981",
              bg: "rgba(16,185,129,0.08)",
              border: "rgba(16,185,129,0.25)",
              target: leanTarget,
              desc: "Retire early on a minimal budget. Covers essentials with little room for extras. Best for those who can live frugally.",
              inputs: [
                { label: "Annual spending in retirement ($)", key: "leanSpend", min: 10000, max: 100000, step: 1000 },
              ],
            },
            {
              id: "fat",
              emoji: "🏆",
              name: "Fat FIRE",
              color: "#F59E0B",
              bg: "rgba(245,158,11,0.08)",
              border: "rgba(245,158,11,0.25)",
              target: fatTarget,
              desc: "Retire with a comfortable, high-spending lifestyle. Full financial freedom with no compromises on quality of life.",
              inputs: [
                { label: "Annual spending in retirement ($)", key: "fatSpend", min: 50000, max: 500000, step: 5000 },
              ],
            },
            {
              id: "barista",
              emoji: "☕",
              name: "Barista FIRE",
              color: "#8B5CF6",
              bg: "rgba(139,92,246,0.08)",
              border: "rgba(139,92,246,0.25)",
              target: barTarget,
              desc: "Semi-retire with part-time work covering some expenses (e.g. health insurance). Your portfolio only needs to cover the gap.",
              inputs: [
                { label: "Annual spending ($)", key: "baristaSpend", min: 20000, max: 200000, step: 1000 },
                { label: "Part-time income ($/yr)", key: "baristaIncome", min: 0, max: 80000, step: 1000 },
              ],
            },
            {
              id: "coast",
              emoji: "🌊",
              name: "Coast FIRE",
              color: "#3B82F6",
              bg: "rgba(59,130,246,0.08)",
              border: "rgba(59,130,246,0.25)",
              target: coastTarget,
              desc: saved >= coastTarget
                ? `You've hit your Coast FIRE number. Your current ${fmtMoney(saved)} will grow to ${fmtMoney(inputs.goal_amount)} by age ${inputs.retirement_age} at ${inputs.expected_return_pct}%/yr — even if you never contribute another dollar.`
                : `You need ${fmtMoney(coastTarget - saved)} more to reach Coast FIRE. Once there, your savings will grow to ${fmtMoney(inputs.goal_amount)} by age ${inputs.retirement_age} on their own — no more contributions required.`,
              inputs: [],
            },
          ]

          return (
            <div>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: t.text }}>🔥 FIRE Calculator</h2>
                <p style={{ color: t.textMuted, fontSize: 13 }}>Financial Independence, Retire Early — find your number across four strategies</p>
              </div>

              {/* Summary bar */}
              <div style={{ ...card, padding: "20px 24px", marginBottom: 24, display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Current Savings</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: t.accent }}>{fmtMoney(saved)}</div>
                </div>
                <div style={{ width: 1, height: 40, background: t.border }} />
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Using plan assumptions</div>
                  <div style={{ fontSize: 13, color: t.text }}>{inputs.expected_return_pct}% return · retire at {inputs.retirement_age} · {yearsLeft} yrs away</div>
                </div>
              </div>

              {/* FIRE type cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                {fireTypes.map(ft => {
                  const pct = ft.target > 0 ? Math.min(100, (saved / ft.target) * 100) : 100
                  const reached = saved >= ft.target
                  const gap = ft.target - saved
                  const curYear = new Date().getFullYear()
                  const firePoint = ft.target > 0 ? retirementProjection.trajectory.find(p => p.total >= ft.target) : null
                  const fireAge = firePoint?.age ?? null
                  const fireYear = fireAge != null ? curYear + (fireAge - inputs.current_age) : null
                  const yearsToFire = fireAge != null ? fireAge - inputs.current_age : null
                  return (
                    <div key={ft.id} style={{ ...card, padding: "24px 26px", borderTop: `3px solid ${ft.color}`, background: ft.bg }}>
                      {/* Header */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 24 }}>{ft.emoji}</span>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: t.text }}>{ft.name}</div>
                            {reached && <div style={{ fontSize: 11, fontWeight: 700, color: ft.color }}>✓ Reached!</div>}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: ft.color }}>{fmtMoney(ft.target)}</div>
                          <div style={{ fontSize: 11, color: t.textFaint }}>target</div>
                        </div>
                      </div>

                      {/* When you'll hit it */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: reached ? `${ft.color}18` : t.hover }}>
                        <span style={{ fontSize: 16 }}>{reached ? "🎉" : fireYear ? "📅" : "⚠️"}</span>
                        <div>
                          {reached
                            ? <span style={{ fontSize: 12, fontWeight: 700, color: ft.color }}>Already reached with your current plan!</span>
                            : fireYear
                              ? <><span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Age {fireAge} · {fireYear}</span><span style={{ fontSize: 11, color: t.textMuted }}> — {yearsToFire} year{yearsToFire !== 1 ? "s" : ""} away</span></>
                              : <span style={{ fontSize: 12, color: t.textMuted }}>Not reached within your retirement horizon</span>}
                        </div>
                      </div>

                      {/* Description */}
                      <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, marginBottom: 16 }}>{ft.desc}</p>

                      {/* Progress bar */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 11, color: t.textFaint }}>Progress</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: ft.color }}>{pct.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 999, background: t.hover, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, background: `linear-gradient(90deg, ${ft.color}99, ${ft.color})`, transition: "width 0.4s" }} />
                        </div>
                        <div style={{ fontSize: 11, color: t.textFaint, marginTop: 6 }}>
                          {reached ? `You're ${fmtMoney(saved - ft.target)} past your ${ft.name} number 🎉` : `${fmtMoney(gap)} remaining`}
                        </div>
                      </div>

                      {/* Adjustable inputs */}
                      {ft.inputs.length > 0 && (
                        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                          {ft.inputs.map(inp => (
                            <div key={inp.key}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <label style={{ fontSize: 11, color: t.textMuted }}>{inp.label}</label>
                                <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{fmtMoney(fireInputs[inp.key])}</span>
                              </div>
                              <input type="range" min={inp.min} max={inp.max} step={inp.step} value={fireInputs[inp.key]}
                                onChange={e => setFireField(inp.key, e.target.value)}
                                style={{ width: "100%", accentColor: ft.color, cursor: "pointer" }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Educational section */}
              <div style={{ ...card, padding: "26px 28px", marginTop: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 4 }}>What is FIRE?</h3>
                <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.6, marginBottom: 24 }}>
                  FIRE stands for <strong style={{ color: t.text }}>Financial Independence, Retire Early</strong>. The core idea: save and invest aggressively enough that your portfolio generates enough passive income to cover your living expenses — forever. There are several flavors, each suited to different lifestyles and goals.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {[
                    {
                      emoji: "🌿", name: "Lean FIRE", color: "#10B981",
                      text: "The minimalist path. You retire early but keep spending tight — think ~$30–40k/yr. No frills, but full freedom. Works best if you're willing to live simply, potentially abroad or in a low cost-of-living area. The trade-off: little margin for unexpected expenses.",
                    },
                    {
                      emoji: "🏆", name: "Fat FIRE", color: "#F59E0B",
                      text: "Full financial freedom without lifestyle sacrifices. You target a large enough portfolio to sustain $100k+/yr in spending — vacations, dining out, whatever you want. Requires a higher savings rate and usually a higher income, but you don't have to cut anything out.",
                    },
                    {
                      emoji: "☕", name: "Barista FIRE", color: "#8B5CF6",
                      text: "A middle ground: you semi-retire and work a low-stress part-time job (famously, a barista) that covers health insurance and small expenses. Your portfolio only needs to cover the gap. This reduces the target significantly and gives you structure, social connection, and flexibility.",
                    },
                    {
                      emoji: "🌊", name: "Coast FIRE", color: "#3B82F6",
                      text: "You've saved enough that compound growth alone will get you to your retirement goal — no more contributions needed. You still work, but the financial pressure is off. You can coast: take a less stressful job, work fewer hours, or just stop worrying about maxing out every account.",
                    },
                  ].map(item => (
                    <div key={item.name} style={{ display: "flex", gap: 16, padding: "16px 18px", borderRadius: 10, background: t.hover, border: `1px solid ${t.border}` }}>
                      <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{item.emoji}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: item.color, marginBottom: 4 }}>{item.name}</div>
                        <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.6, margin: 0 }}>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        })()}

        {subNav === "plan" && (() => {
          const pm = getPaycheckMonthly(paychecks || [])
          const paycheck401k = Math.round(pm.contrib401k + pm.match401k)
          const fmtComma = n => Number(n) > 0 ? Number(n).toLocaleString() : ""
          const parseComma = s => Number(String(s).replace(/,/g, "")) || 0
          const isDollar = opts => opts.prefix === "$"
          const numInput = (key, label, opts = {}) => {
            const val = inputs[key]
            const displayVal = isDollar(opts) ? fmtComma(val) : val
            return (
              <div key={key}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 13, color: t.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
                    {label}
                    {opts.tooltip && <InfoBubble text={opts.tooltip} theme={t} />}
                  </label>
                  {opts.badge && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: `${t.accent}18`, color: t.accent, fontWeight: 600 }}>{opts.badge}</span>}
                </div>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  {opts.prefix && <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>{opts.prefix}</span>}
                  <input
                    type="text" inputMode={isDollar(opts) ? "numeric" : "decimal"}
                    value={displayVal}
                    onChange={e => {
                      const raw = isDollar(opts) ? parseComma(e.target.value) : Number(e.target.value) || 0
                      setInput(key, raw)
                    }}
                    style={{ width: "100%", padding: opts.prefix ? "10px 12px 10px 24px" : opts.suffix ? "10px 32px 10px 12px" : "10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                  {opts.suffix && <span style={{ position: "absolute", right: 12, fontSize: 13, color: t.textMuted, pointerEvents: "none" }}>{opts.suffix}</span>}
                </div>
                {opts.hint && <p style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{opts.hint}</p>}
              </div>
            )
          }

          // SS estimator state (local to this render via closure — lifted to component state below)
          const annualWithdrawal = mainResults ? Math.round(mainResults.p50 * 0.04) : 0
          const monthlyWithdrawal = Math.round(annualWithdrawal / 12)

          return (
            <div>
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: t.text }}>My Plan</h2>
                <p style={{ color: t.textMuted, fontSize: 13 }}>Enter your retirement assumptions — we'll run the numbers</p>
              </div>

              {/* ── Plan sub-tabs ── */}
              <div style={{ display: "flex", gap: 6, marginBottom: 24 }}>
                {[["projection", "📊 Plan Overview"], ["yearby", "📅 Year by Year"], ["accounts", "⚙️ Plan Settings"]].map(([id, label]) => (
                  <button key={id} onClick={() => setPlanTabPersist(id)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${planTab === id ? t.accent : t.border}`, background: planTab === id ? (t.isDark ? "rgba(110,231,183,0.1)" : "rgba(47,133,90,0.08)") : "transparent", color: planTab === id ? t.accent : t.textMuted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* ── Row 1: Plan Projection (full width) ── */}
              {planTab === "projection" && <div>
              <div style={{ ...card, padding: "22px 24px", marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 18, color: t.text }}>Plan Projection</h3>
                {mainResults && (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
                      {[
                        { label: "Historical Success Rate", value: mainResults.successRate + "%", color: mainResults.successRate >= 80 ? t.accent : "#F59E0B", big: true },
                        { label: "Projected Balance (median)", value: "$" + (mainResults.p50 / 1000000).toFixed(2) + "M", color: t.text, big: true },
                        { label: "Periods Tested", value: mainResults.totalPeriods, color: t.accent2 },
                        { label: "Money Lasts", value: mainResults.yearsMoneyLasts >= 99 ? "Forever 🎉" : mainResults.yearsMoneyLasts + " yrs", color: mainResults.yearsMoneyLasts >= 30 ? t.accent : "#F59E0B" },
                      ].map(s => (
                        <div key={s.label} style={{ background: t.hover, borderRadius: 8, padding: "12px 14px" }}>
                          <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{s.label}</p>
                          <p style={{ fontSize: s.big ? 20 : 17, fontWeight: 700, color: s.color }}>{s.value}</p>
                        </div>
                      ))}
                    </div>
                    {/* ── Growth chart (Recharts, interactive) ── */}
                    {(() => {
                      const curYear = new Date().getFullYear()
                      const curAge = inputs.current_age || 30
                      // Use retirementProjection directly — same loop as planRetirementBalance and Wealth Accumulation
                      const startBal = retirementProjection.trajectory[0]?.contributions ?? Math.round(currentSavings)
                      const chartPts = retirementProjection.trajectory.map(pt => ({
                        year: curYear + (pt.age - curAge),
                        balance: pt.total,
                        startingBalance: startBal,
                        contributions: Math.max(0, pt.contributions - startBal),
                        growth: pt.growth,
                      }))
                      const fmtM = v => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${Math.round(v / 1000)}k`
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Portfolio Growth Path</p>
                            <div style={{ display: "flex", gap: 16 }}>
                              {[{ label: "Today", val: fmtM(currentSavings), c: t.textMuted }, { label: "At retirement", val: fmtM(chartPts[chartPts.length - 1]?.balance || 0), c: t.accent }].map(s => (
                                <div key={s.label} style={{ textAlign: "right" }}>
                                  <p style={{ fontSize: 10, color: t.textFaint, marginBottom: 1 }}>{s.label}</p>
                                  <p style={{ fontSize: 14, fontWeight: 700, color: s.c, margin: 0 }}>{s.val}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={chartPts} margin={{ top: 4, right: 8, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={t.accent} stopOpacity={0.45} />
                                  <stop offset="95%" stopColor={t.accent} stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="contribGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={t.accent2} stopOpacity={0.5} />
                                  <stop offset="95%" stopColor={t.accent2} stopOpacity={0.15} />
                                </linearGradient>
                                <linearGradient id="startGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={t.isDark ? "#ffffff" : "#000000"} stopOpacity={0.08} />
                                  <stop offset="95%" stopColor={t.isDark ? "#ffffff" : "#000000"} stopOpacity={0.02} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} interval={Math.floor(chartPts.length / 6)} />
                              <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} width={54}
                                tickFormatter={v => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`} />
                              <Tooltip
                                formatter={(val, name) => [fmtM(val), name]}
                                labelFormatter={label => `Year ${label}`}
                                contentStyle={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 12 }}
                                labelStyle={{ color: t.text, fontWeight: 700 }} />
                              <Area type="monotone" dataKey="startingBalance" stackId="1" stroke="none" fill="url(#startGrad)" name="Starting balance" />
                              <Area type="monotone" dataKey="contributions" stackId="1" stroke="none" fill="url(#contribGrad)" name="Contributions" />
                              <Area type="monotone" dataKey="growth" stackId="1" stroke={t.accent} strokeWidth={2} fill="url(#growthGrad)" name="Market growth" />
                            </AreaChart>
                          </ResponsiveContainer>
                          <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                            {[
                              { color: t.accent, label: "Market growth" },
                              { color: t.accent2, label: "Contributions" },
                              { color: t.textFaint, label: "Starting balance" },
                            ].map(l => (
                              <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color, opacity: 0.6 }} />
                                <span style={{ fontSize: 10, color: t.textFaint }}>{l.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }}>
                      <div>
                        <div style={{ padding: "14px 16px", borderRadius: 10, background: `rgba(47,133,90,0.07)`, border: `1px solid rgba(47,133,90,0.18)`, marginBottom: 12 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Estimated Withdrawal (4% rule)</p>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            {[
                              { label: "Per Year", value: "$" + annualWithdrawal.toLocaleString() },
                              { label: "Per Month", value: "$" + monthlyWithdrawal.toLocaleString() },
                            ].map(w => (
                              <div key={w.label}>
                                <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 2 }}>{w.label}</p>
                                <p style={{ fontSize: 20, fontWeight: 800, color: t.accent }}>{w.value}</p>
                              </div>
                            ))}
                          </div>
                          {inputs.social_security_monthly > 0 && (
                            <p style={{ fontSize: 11, color: t.textMuted, marginTop: 8, borderTop: `1px solid ${t.border}`, paddingTop: 8 }}>
                              + Social Security: <strong style={{ color: t.accent }}>${inputs.social_security_monthly.toLocaleString()}/mo</strong> = <strong style={{ color: t.accent }}>${(monthlyWithdrawal + inputs.social_security_monthly).toLocaleString()}/mo total</strong>
                            </p>
                          )}
                        </div>
                      </div>
                      <FanChart results={mainResults} height={180} theme={t} goalAmount={inputs.goal_amount} />
                    </div>
                  </>
                )}
                {!mainResults && <p style={{ fontSize: 13, color: t.textMuted }}>Enter your plan details to see projections.</p>}
              </div></div>}

              {/* ── Plan Settings tab ── */}
              {planTab === "accounts" && <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20, alignItems: "start" }}>

                  {/* Timeline */}
                  <div style={{ ...card, padding: "20px 22px" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Timeline</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {numInput("current_age", "Current Age", { min: 18, max: 80 })}
                      {numInput("retirement_age", "Target Retirement Age", { min: 40, max: 80 })}
                    </div>
                  </div>

                  {/* Return & Inflation */}
                  <div style={{ ...card, padding: "20px 22px" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Market Assumptions</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {numInput("drawdown_return_pct", "Return in Retirement", { suffix: "%", step: 0.5, max: 15,
                        tooltip: "Expected annual return after you retire. Most people shift to a conservative portfolio (bonds, stable assets). A 60/40 portfolio historically returns ~5–6% nominal. Defaults to 5%." })}
                      {numInput("inflation_pct", "Inflation Rate", { suffix: "%", step: 0.1, max: 7,
                        tooltip: "US historical average inflation since 1926 is ~3%/yr (BLS CPI). This means $100 today ≈ $181 in 20 years." })}
                    </div>
                  </div>

                  {/* Retirement Income */}
                  <div style={{ ...card, padding: "20px 22px" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Retirement Income</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <label style={{ fontSize: 13, color: t.textMuted }}>Annual Spending in Retirement</label>
                          <button onClick={() => setSpendOpen(o => !o)} style={{ fontSize: 11, color: t.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                            {spendOpen ? "▲ Hide estimator" : "▼ Estimate mine"}
                          </button>
                        </div>
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                          <input type="text" inputMode="numeric"
                            value={inputs.desired_annual_spend ? Number(inputs.desired_annual_spend).toLocaleString() : ""}
                            placeholder="e.g. 80,000"
                            onChange={e => setInput("desired_annual_spend", Number(e.target.value.replace(/,/g, "")) || 0)}
                            style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                        </div>

                        {/* Spending Estimator */}
                        {spendOpen && (() => {
                          const MONTHS_BACK = 6
                          const cutoff = new Date()
                          cutoff.setMonth(cutoff.getMonth() - MONTHS_BACK)
                          const cutoffStr = cutoff.toISOString().split("T")[0]

                          const recentTx = transactions.filter(tx => tx.date >= cutoffStr && !tx.is_transfer)
                          const expenses = recentTx.filter(tx => tx.amount > 0)
                          const savingsCats = new Set(["Cash Savings","Emergency Fund","Investments","Retirement","Savings Goal"])
                          const realExpenses = expenses.filter(tx => !savingsCats.has(tx.user_category) && tx.user_group !== "savings")
                          const avgMonthly = realExpenses.reduce((s, tx) => s + tx.amount, 0) / MONTHS_BACK
                          const annualCurrent = Math.round(avgMonthly * 12)
                          const annualAdjusted = Math.round(annualCurrent * spendFactor / 100)

                          const hasTxData = transactions.length > 0

                          // Category breakdown for top spending areas
                          const catTotals = {}
                          realExpenses.forEach(tx => {
                            const cat = tx.user_category || tx.category || "Other"
                            catTotals[cat] = (catTotals[cat] || 0) + tx.amount
                          })
                          const topCats = Object.entries(catTotals)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 5)
                            .map(([label, total]) => ({ label, monthly: Math.round(total / MONTHS_BACK) }))

                          return (
                            <div style={{ marginTop: 12, padding: "16px", borderRadius: 10, background: t.hover, border: `1px solid ${t.border}` }}>
                              <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 4 }}>Spending Estimator</p>
                              <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
                                {hasTxData
                                  ? `Based on your last ${MONTHS_BACK} months of actual spending (excluding savings & transfers). Adjust the retirement factor to account for lifestyle changes — most people spend 80–90% of their pre-retirement budget in retirement.`
                                  : "Connect your accounts to get a personalized estimate from your actual spending."}
                              </p>

                              {hasTxData && avgMonthly > 0 ? (
                                <>
                                  {/* Numbers */}
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                                    {[
                                      { label: "Avg monthly spend (today)", value: `$${Math.round(avgMonthly).toLocaleString()}` },
                                      { label: "Annual equivalent", value: `$${annualCurrent.toLocaleString()}` },
                                    ].map(s => (
                                      <div key={s.label} style={{ padding: "10px 12px", borderRadius: 8, background: t.cardBg }}>
                                        <p style={{ fontSize: 10, color: t.textFaint, marginBottom: 2 }}>{s.label}</p>
                                        <p style={{ fontSize: 16, fontWeight: 700, color: t.text }}>{s.value}</p>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Top categories */}
                                  {topCats.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                      <p style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Top spending categories (avg/mo)</p>
                                      {topCats.map(c => (
                                        <div key={c.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                                          <span style={{ fontSize: 12, color: t.textMuted }}>{c.label}</span>
                                          <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>${c.monthly.toLocaleString()}/mo</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Retirement factor slider */}
                                  <div style={{ marginBottom: 14 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                      <label style={{ fontSize: 12, color: t.textMuted }}>Retirement spending factor</label>
                                      <span style={{ fontSize: 12, fontWeight: 700, color: t.accent }}>{spendFactor}%</span>
                                    </div>
                                    <input type="range" min={50} max={120} step={5} value={spendFactor}
                                      onChange={e => setSpendFactor(Number(e.target.value))}
                                      style={{ width: "100%", accentColor: t.accent }} />
                                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                                      <span style={{ fontSize: 10, color: t.textFaint }}>50% (frugal)</span>
                                      <span style={{ fontSize: 10, color: t.textFaint }}>100% (same)</span>
                                      <span style={{ fontSize: 10, color: t.textFaint }}>120% (more)</span>
                                    </div>
                                    <p style={{ fontSize: 11, color: t.textFaint, marginTop: 6, lineHeight: 1.4 }}>
                                      Most retirees spend 80–90% of pre-retirement. Housing costs often drop (mortgage paid off), but healthcare rises. Travel and leisure may increase early in retirement, then slow.
                                    </p>
                                  </div>

                                  {/* Result */}
                                  <div style={{ padding: "12px 14px", borderRadius: 8, background: `rgba(47,133,90,0.08)`, border: `1px solid rgba(47,133,90,0.2)`, marginBottom: 12 }}>
                                    <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>Estimated annual retirement spending</p>
                                    <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>
                                      ${annualAdjusted.toLocaleString()}
                                      <span style={{ fontSize: 12, fontWeight: 400, color: t.textMuted }}>/yr</span>
                                    </p>
                                    <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>
                                      ${Math.round(annualAdjusted / 12).toLocaleString()}/mo · {spendFactor}% of your current ${Math.round(avgMonthly).toLocaleString()}/mo
                                    </p>
                                  </div>

                                  <button onClick={() => { setInput("desired_annual_spend", annualAdjusted); setSpendOpen(false) }}
                                    style={{ width: "100%", padding: "8px 0", borderRadius: 6, border: "none", background: t.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                                    Use ${annualAdjusted.toLocaleString()}/yr →
                                  </button>
                                </>
                              ) : (
                                <p style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No spending data found. Connect your bank accounts to enable this estimate.</p>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <label style={{ fontSize: 13, color: t.textMuted }}>Social Security (monthly)</label>
                          <button onClick={() => setSsOpen(o => !o)} style={{ fontSize: 11, color: t.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                            {ssOpen ? "▲ Hide estimator" : "▼ Estimate mine"}
                          </button>
                        </div>
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                          <input type="text" inputMode="numeric" value={inputs.social_security_monthly ? inputs.social_security_monthly.toLocaleString() : ""}
                            placeholder="0"
                            onChange={e => setInput("social_security_monthly", Number(e.target.value.replace(/,/g, "")) || 0)}
                            style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                        </div>
                        {/* Social Security Estimator */}
                        {ssOpen && (() => {
                          const fra = 67 // full retirement age for born after 1960
                          const retAge = inputs.retirement_age
                          const ssEst = (() => {
                            if (!ssAIME || ssAIME <= 0) return 0
                            const aime = ssAIME
                            // 2025 SSA bend points
                            const bp1 = 1226, bp2 = 7391
                            const pia = aime <= bp1
                              ? aime * 0.90
                              : aime <= bp2
                              ? bp1 * 0.90 + (aime - bp1) * 0.32
                              : bp1 * 0.90 + (bp2 - bp1) * 0.32 + (aime - bp2) * 0.15
                            // Age adjustment
                            const adjFactor = retAge >= 70 ? 1.24
                              : retAge >= fra ? 1 + (retAge - fra) * 0.08
                              : retAge >= 62 ? 1 - (fra - retAge) * 0.067
                              : 0.7
                            return Math.round(pia * adjFactor)
                          })()
                          return (
                            <div style={{ marginTop: 12, padding: "16px", borderRadius: 10, background: t.hover, border: `1px solid ${t.border}` }}>
                              <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 12 }}>SS Benefit Estimator</p>
                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                  <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Average annual income (career avg, pre-tax)</label>
                                  <div style={{ position: "relative" }}>
                                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: t.textMuted }}>$</span>
                                    <input type="number" min={0} max={300000} step={5000} value={ssAnnualIncome}
                                      onChange={e => { setSsAnnualIncome(Number(e.target.value)); setSsAIME(Math.round(Number(e.target.value) / 12)) }}
                                      placeholder="e.g. 85000"
                                      style={{ width: "100%", padding: "8px 10px 8px 22px", borderRadius: 7, border: `1px solid ${t.border}`, background: t.cardBg, color: t.text, fontSize: 13, boxSizing: "border-box" }} />
                                  </div>
                                  {paychecks?.length > 0 && (() => {
                                    const annualGross = Math.round(getPaycheckMonthly(paychecks).gross * 12)
                                    return (
                                      <button onClick={() => { setSsAnnualIncome(annualGross); setSsAIME(Math.round(annualGross / 12)) }}
                                        style={{ marginTop: 4, fontSize: 11, color: t.accent, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                                        Use paycheck gross (${annualGross.toLocaleString()}/yr) →
                                      </button>
                                    )
                                  })()}
                                </div>
                                {ssEst > 0 && (
                                  <div style={{ padding: "12px 14px", borderRadius: 8, background: `rgba(47,133,90,0.08)`, border: `1px solid rgba(47,133,90,0.2)` }}>
                                    <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>
                                      Estimated monthly benefit at age {retAge}
                                      {retAge < fra ? ` (early — ${Math.round((1 - (fra - retAge) * 0.067) * 100)}% of full)` : retAge > fra ? ` (+${Math.round((retAge - fra) * 8)}% delayed credits)` : " (full retirement age)"}
                                    </p>
                                    <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>${ssEst.toLocaleString()}<span style={{ fontSize: 12, fontWeight: 400, color: t.textMuted }}>/mo</span></p>
                                    <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>Based on SSA bend-point formula with AIME of ${Math.round(ssAnnualIncome / 12).toLocaleString()}/mo. Your actual amount depends on your full earnings history at SSA.gov.</p>
                                    <button onClick={() => setInput("social_security_monthly", ssEst)}
                                      style={{ marginTop: 8, padding: "6px 14px", borderRadius: 6, border: "none", background: t.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                                      Use ${ssEst.toLocaleString()}/mo →
                                    </button>
                                  </div>
                                )}
                                <p style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.5 }}>
                                  Uses the SSA Primary Insurance Amount (PIA) formula. Claiming at 62 reduces benefits ~30%; at 70 increases them ~24% via delayed credits. Full retirement age is 67 for those born after 1960.
                                </p>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>

              </div>
              <button onClick={saveInputs} style={{ width: "100%", padding: "13px", borderRadius: 8, border: "none", background: saved ? "rgba(47,133,90,0.1)" : "linear-gradient(135deg, #2F855A, #22C55E)", color: saved ? "#2F855A" : "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 20, boxShadow: saved ? "none" : "0 4px 14px rgba(34,197,94,0.3)", transition: "all 0.2s" }}>
                {saved ? "✓ Plan Saved" : "Save My Plan"}
              </button>
              {/* ── Accounts & Contributions ── */}
              <div style={{ ...card, padding: "20px 22px", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: "0 0 2px" }}>Accounts & Contributions</p>
                    <p style={{ fontSize: 11, color: t.textFaint, margin: 0 }}>Set return rate and monthly contribution per account</p>
                  </div>
                  <button onClick={saveAcctSettings} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                    {acctSaveMsg || "Save"}
                  </button>
                </div>
                {!acctSettingsLoaded ? (
                  <p style={{ fontSize: 12, color: t.textMuted }}>Loading accounts...</p>
                ) : (() => {
                  const grouped = {}
                  accounts.forEach(a => {
                    const cat = getAccountCategory(a)
                    if (!grouped[cat]) grouped[cat] = []
                    grouped[cat].push(a)
                  })
                  const CAT_ORDER = ["401k / Pension", "IRA / Roth", "Brokerage", "Other", "Cash & Bank", "Credit Cards", "Loans"]
                  const sortedEntries = Object.entries(grouped).sort(([a], [b]) => {
                    const ai = CAT_ORDER.indexOf(a); const bi = CAT_ORDER.indexOf(b)
                    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
                  })
                  return sortedEntries.map(([cat, accts]) => {
                    const isCollapsed = !!collapsedCats[cat]
                    const totalContrib = accts.reduce((s, a) => s + (acctSettings[a.id]?.monthly_contribution || 0), 0)
                    const includedCount = accts.filter(a => acctSettings[a.id]?.include !== false).length
                    return (
                      <div key={cat} style={{ marginBottom: 16 }}>
                        <button onClick={() => setCollapsedCats(prev => ({ ...prev, [cat]: !prev[cat] }))}
                          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", borderBottom: `1px solid ${t.border}`, padding: "0 0 8px", marginBottom: isCollapsed ? 0 : 12, cursor: "pointer" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: t.accent, textTransform: "uppercase", letterSpacing: "0.07em" }}>{cat}</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {isCollapsed && <span style={{ fontSize: 11, color: t.textFaint }}>{includedCount}/{accts.length} accts{totalContrib > 0 ? ` · $${Math.round(totalContrib)}/mo` : ""}</span>}
                            <span style={{ fontSize: 11, color: t.textFaint }}>{isCollapsed ? "▶" : "▼"}</span>
                          </span>
                        </button>
                        {!isCollapsed && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                            {accts.map(a => {
                              const s = acctSettings[a.id] || {}
                              const isIncluded = s.include !== false
                              const inpStyle = { padding: "6px 8px", borderRadius: 7, border: `1px solid ${t.border}`, background: t.cardBg || t.hover, color: t.text, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }
                              return (
                                <div key={a.id} style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${isIncluded ? t.accent + "35" : t.border}`, background: isIncluded ? (t.isDark ? "rgba(110,231,183,0.03)" : "rgba(47,133,90,0.025)") : t.hover, opacity: isIncluded ? 1 : 0.6, transition: "all 0.15s" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                                    <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: isIncluded ? t.text : t.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.account_name}</div>
                                      <div style={{ fontSize: 11, color: t.textFaint, marginTop: 1 }}>{a.institution_name}{a.subtype ? ` · ${a.subtype}` : ""}</div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                      <span style={{ fontSize: 14, fontWeight: 700, color: isIncluded ? t.accent : t.textMuted, flexShrink: 0 }}>
                                        ${(a.balance || 0) >= 1000000 ? ((a.balance || 0) / 1e6).toFixed(2) + "M" : Math.round((a.balance || 0) / 1000) + "k"}
                                      </span>
                                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
                                        <input type="checkbox" checked={isIncluded}
                                          onChange={e => {
                                            const checked = e.target.checked
                                            setAcctSetting(a.id, "include", checked)
                                            const cur = acctSettings[a.id] || {}
                                            supabase.from("retirement_account_settings").upsert({ user_id: session.user.id, account_id: a.id, expected_return_pct: cur.return_rate, monthly_contribution: cur.monthly_contribution, include_in_plan: checked }, { onConflict: "user_id,account_id" })
                                          }}
                                          style={{ width: 0, height: 0, opacity: 0, position: "absolute" }} />
                                        <div style={{ width: 34, height: 18, borderRadius: 9, background: isIncluded ? t.accent : t.border, transition: "background 0.2s", position: "relative", flexShrink: 0 }}>
                                          <div style={{ position: "absolute", top: 2, left: isIncluded ? 18 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                                        </div>
                                      </label>
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 12, marginTop: 2 }}>
                                    <div>
                                      <label style={{ fontSize: 10, color: t.textFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Return</label>
                                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                        <input type="number" min="0" max="30" step="0.1"
                                          key={`rate-${a.id}-${acctSettingsLoaded}`}
                                          defaultValue={s.return_rate ?? ""}
                                          placeholder={String(inputs?.expected_return_pct || 7)}
                                          onBlur={e => {
                                            const v = parseFloat(e.target.value) || 0
                                            setAcctSetting(a.id, "return_rate", v)
                                            const cur = acctSettings[a.id] || {}
                                            supabase.from("retirement_account_settings").upsert({ user_id: session.user.id, account_id: a.id, expected_return_pct: v, monthly_contribution: cur.monthly_contribution, include_in_plan: cur.include !== false }, { onConflict: "user_id,account_id" })
                                          }}
                                          style={{ ...inpStyle, width: 52 }} />
                                        <span style={{ fontSize: 11, color: t.textMuted }}>%</span>
                                      </div>
                                    </div>
                                    <div>
                                      <label style={{ fontSize: 10, color: t.textFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>$/mo</label>
                                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                        <span style={{ fontSize: 11, color: t.textMuted }}>$</span>
                                        <input type="number" min="0" step="50"
                                          key={`contrib-${a.id}-${acctSettingsLoaded}`}
                                          defaultValue={s.monthly_contribution ?? 0}
                                          onBlur={e => {
                                            const v = parseFloat(e.target.value) || 0
                                            setAcctSetting(a.id, "monthly_contribution", v)
                                            const cur = acctSettings[a.id] || {}
                                            supabase.from("retirement_account_settings").upsert({ user_id: session.user.id, account_id: a.id, expected_return_pct: cur.return_rate, monthly_contribution: v, include_in_plan: cur.include !== false }, { onConflict: "user_id,account_id" })
                                          }}
                                          style={{ ...inpStyle, width: 70 }} />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })
                })()}
              </div>
            </>}

              {/* ── Year by Year tab ── */}
              {planTab === "yearby" && (
                <YearByYearProjection
                  theme={t} card={card}
                  accounts={accounts}
                  inputs={inputs}
                  acctSettings={acctSettings}
                  settingsLoaded={acctSettingsLoaded}
                  householdMembers={householdMembers}
                  retirementBalance={planRetirementBalance}
                />
              )}
            </div>
          )
        })()}

      </div>
    </div>
  )
}

// ── SIMULATORS PAGE ────────────────────────────────────────
function SimulatorsPage({ session, theme, transactions = [] }) {
  const t = theme
  const card = makeCard(t)
  const [simType, setSimType] = useState("emergency")

  // ── Retirement sim state ───────────────────────────────
  const [retBase, setRetBase] = useState({
    current_age: 29, retirement_age: 55, monthly_contribution: 1500,
    expected_return_pct: 7, inflation_pct: 3, desired_annual_spend: 80000,
    social_security_monthly: 0, goal_amount: 3000000,
  })
  const [retScenario, setRetScenario] = useState(null)
  const [retLoading, setRetLoading] = useState(true)
  const [currentSavings, setCurrentSavings] = useState(0)
  const [finances, setFinances] = useState({ monthlyIncome: 0, monthlyExpenses: 0, liquidSavings: 0, loaded: false })
  const [liquidAccounts, setLiquidAccounts] = useState([])
  const [emergencyMonths, setEmergencyMonths] = useState(3)

  useEffect(() => {
    async function load() {
      const { data: goal } = await supabase.from('retirement_goals').select('*').eq('user_id', session.user.id).single()
      if (goal) {
        const loaded = {
          current_age: goal.current_age ?? 29, retirement_age: goal.target_age ?? 55,
          monthly_contribution: goal.monthly_contribution ?? 1500,
          expected_return_pct: goal.expected_return_pct ?? 7, inflation_pct: goal.inflation_pct ?? 3,
          desired_annual_spend: goal.desired_annual_spend ?? 80000,
          social_security_monthly: goal.social_security_monthly ?? 0, goal_amount: goal.target_amount ?? 3000000,
        }
        setRetBase(loaded); setRetScenario({ ...loaded })
      }
      const { data: accs } = await supabase.from('accounts').select('*').eq('user_id', session.user.id)
      if (accs) {
        setCurrentSavings(accs.filter(a => a.subtype === 'ira' || a.subtype === '401k').reduce((s, a) => s + (a.balance || 0), 0))
        const liquidAccs = accs.filter(a => a.account_type === 'depository')
        setLiquidAccounts(liquidAccs)
        const liquid = liquidAccs.reduce((s, a) => s + (a.balance || 0), 0)
        const MONTHS_BACK = 6
        const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - MONTHS_BACK)
        const cutoffStr = cutoff.toISOString().split('T')[0]
        const needsCats = new Set(CATEGORIES.filter(c => c.group === "needs").map(c => c.label))
        const recentTx = transactions.filter(tx => tx.date >= cutoffStr)
        const incomeTxns = recentTx.filter(t => t.amount < 0 && !t.is_transfer)
        // Monthly expenses = avg needs spending over 6 months (use user_group or user_category)
        const needsTxns = recentTx.filter(t => t.amount > 0 && !t.is_transfer && (t.user_group === "needs" || needsCats.has(t.user_category)))
        const monthlyIncome   = Math.round(incomeTxns.reduce((s, t) => s + (-t.amount), 0) / MONTHS_BACK)
        const monthlyExpenses = Math.round(needsTxns.reduce((s, t) => s + t.amount, 0) / MONTHS_BACK)
        setFinances({ monthlyIncome, monthlyExpenses, liquidSavings: Math.round(liquid), loaded: true })
      }
      setRetLoading(false)
    }
    load()
  }, [session, transactions])

  useEffect(() => { if (!retLoading && !retScenario) setRetScenario({ ...retBase }) }, [retLoading, retBase, retScenario])

  const baseResults = useMemo(
    () => !retLoading ? runHistoricalSim(currentSavings, retBase) : null,
    [retLoading, retBase, currentSavings]
  )
  const scenarioResults = useMemo(
    () => !retLoading && retScenario ? runHistoricalSim(currentSavings, retScenario) : null,
    [retLoading, retScenario, currentSavings]
  )

  // ── Car loan state ─────────────────────────────────────
  const [car, setCar] = useState({ price: 35000, down: 5000, rate: 6.5, months: 60 })
  const setCarField = (k, v) => setCar(p => ({ ...p, [k]: Number(v) }))
  const carLoan = car.price - car.down
  const carMonthlyRate = car.rate / 100 / 12
  const carPayment = carMonthlyRate > 0
    ? carLoan * carMonthlyRate / (1 - Math.pow(1 + carMonthlyRate, -car.months))
    : carLoan / car.months
  const carTotalPaid = carPayment * car.months
  const carTotalInterest = carTotalPaid - carLoan
  const carAmort = []
  let carBal = carLoan
  for (let m = 1; m <= car.months; m++) {
    const interest = carBal * carMonthlyRate
    const principal = carPayment - interest
    carBal = Math.max(0, carBal - principal)
    if (m % 12 === 0 || m === car.months) carAmort.push({ year: Math.ceil(m / 12), balance: Math.round(carBal), paid: Math.round(carPayment * m) })
  }

  // ── Home/mortgage state ────────────────────────────────
  const [home, setHome] = useState({ price: 500000, down: 100000, rate: 6.8, years: 30, taxRate: 1.2, insuranceRate: 0.5 })
  const [excludedAccounts, setExcludedAccounts] = useState(new Set())
  const toggleExcludeAccount = id => setExcludedAccounts(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  const setHomeField = (k, v) => setHome(p => ({ ...p, [k]: Number(v) }))
  const [mortExtra, setMortExtra] = useState(0)
  const [mortBalanceOverride, setMortBalanceOverride] = useState("")
  const homeLoan = home.price - home.down
  const homeMonthlyRate = home.rate / 100 / 12
  const homePI = homeMonthlyRate > 0
    ? homeLoan * homeMonthlyRate / (1 - Math.pow(1 + homeMonthlyRate, -(home.years * 12)))
    : homeLoan / (home.years * 12)
  const homeTax = home.price * home.taxRate / 100 / 12
  const homeInsurance = home.price * home.insuranceRate / 100 / 12
  const homePMI = home.down / home.price < 0.20 ? homeLoan * 0.005 / 12 : 0
  const homeMonthly = homePI + homeTax + homeInsurance + homePMI
  const homeTotalInterest = homePI * home.years * 12 - homeLoan
  const homeEquityData = []
  let homeBal = homeLoan
  for (let yr = 1; yr <= home.years; yr++) {
    for (let m = 0; m < 12; m++) {
      const interest = homeBal * homeMonthlyRate
      homeBal = Math.max(0, homeBal - (homePI - interest))
    }
    const equity = home.price - homeBal
    homeEquityData.push({ year: yr, equity: Math.round(equity), balance: Math.round(homeBal) })
  }

  // ── Debt payoff state ──────────────────────────────────
  const [debtAccounts, setDebtAccounts] = useState([])
  const [debtMethod, setDebtMethod] = useState("avalanche") // avalanche | snowball
  const [debtExtra, setDebtExtra] = useState(0)
  useEffect(() => {
    supabase.from("accounts").select("*").eq("user_id", session.user.id)
      .in("account_type", ["credit", "loan"])
      .then(({ data }) => {
        if (data?.length) {
          setDebtAccounts(data.map(a => ({
            id: a.id, name: a.account_name || a.name,
            balance: Math.abs(a.balance || 0),
            rate: a.interest_rate || 18,
            minPayment: Math.max(25, Math.round(Math.abs(a.balance || 0) * 0.02)),
          })))
        }
      })
  }, [session])

  const debtPayoffCalc = useMemo(() => {
    if (!debtAccounts.length) return null
    const debts = debtAccounts.map(d => ({ ...d }))
    const sorted = debtMethod === "avalanche"
      ? [...debts].sort((a, b) => b.rate - a.rate)
      : [...debts].sort((a, b) => a.balance - b.balance)
    const totalMin = sorted.reduce((s, d) => s + d.minPayment, 0)
    const monthly = totalMin + (debtExtra || 0)
    let month = 0, totalInterest = 0
    const timeline = []
    const working = sorted.map(d => ({ ...d }))
    const startTotal = working.reduce((s, d) => s + d.balance, 0)
    timeline.push({ month: 0, totalDebt: Math.round(startTotal) })
    while (working.some(d => d.balance > 0) && month < 600) {
      month++
      let extra = debtExtra || 0
      let monthInterest = 0
      for (const d of working) {
        if (d.balance <= 0) continue
        const interest = d.balance * (d.rate / 100 / 12)
        d.balance += interest
        totalInterest += interest
        monthInterest += interest
        d.balance -= d.minPayment
        if (d.balance < 0) { extra += Math.abs(d.balance); d.balance = 0 }
      }
      // Apply extra to priority debt
      for (const d of working) {
        if (d.balance <= 0) continue
        d.balance = Math.max(0, d.balance - extra)
        break
      }
      // Record every month if payoff < 24mo, else every 6 months
      const totalMonths = month
      const interval = startTotal < 5000 || totalMonths <= 24 ? 1 : 6
      if (month % interval === 0 || !working.some(d => d.balance > 0))
        timeline.push({ month, totalDebt: Math.round(working.reduce((s, d) => s + d.balance, 0)) })
    }
    return { months: month, totalInterest: Math.round(totalInterest), monthly: Math.round(monthly), timeline }
  }, [debtAccounts, debtMethod, debtExtra])

  // ── Emergency fund state ────────────────────────────────
  const [efMonths, setEfMonths] = useState(6)
  const [efMonthlySaving, setEfMonthlySaving] = useState(500)
  const efAccountsKey = `ef_excluded_${session.user.id}`
  const [excludedEfAccounts, setExcludedEfAccounts] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(efAccountsKey) || "[]")) } catch { return new Set() }
  })
  const [efAccountsDirty, setEfAccountsDirty] = useState(false)
  const toggleExcludeEfAccount = id => {
    setExcludedEfAccounts(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
    setEfAccountsDirty(true)
  }
  const saveEfAccounts = (excluded) => {
    localStorage.setItem(efAccountsKey, JSON.stringify([...excluded]))
    setEfAccountsDirty(false)
  }
  const efTarget = useMemo(() => finances.monthlyExpenses * efMonths, [finances.monthlyExpenses, efMonths])
  const efCurrent = useMemo(
    () => liquidAccounts.filter(a => !excludedEfAccounts.has(a.id)).reduce((s, a) => s + (a.balance || 0), 0),
    [liquidAccounts, excludedEfAccounts]
  )
  const efShortfall = Math.max(0, efTarget - efCurrent)
  const efMonthsToGoal = efMonthlySaving > 0 ? Math.ceil(efShortfall / efMonthlySaving) : null

  // ── Compound interest state ─────────────────────────────
  const [ci, setCi] = useState({ initial: 10000, monthly: 500, rate: 10, years: 30 })
  const setCiField = (k, v) => setCi(p => ({ ...p, [k]: Number(v) }))
  const ciData = useMemo(() => {
    const r = ci.rate / 100 / 12
    const points = []
    let balance = ci.initial
    let contributed = ci.initial
    for (let yr = 1; yr <= ci.years; yr++) {
      for (let m = 0; m < 12; m++) {
        balance = balance * (1 + r) + ci.monthly
        contributed += ci.monthly
      }
      points.push({ year: yr, balance: Math.round(balance), contributed: Math.round(contributed), growth: Math.round(balance - contributed) })
    }
    return points
  }, [ci])

  const simulators = [
    { id: "emergency",  label: "Emergency Fund",      icon: "🛡️" },
    { id: "debt",       label: "Debt Payoff",          icon: "💳" },
    { id: "compound",   label: "Compound Interest",    icon: "📈" },
    { id: "home",       label: "House Affordability",  icon: "🏠" },
    { id: "mortgage",   label: "Mortgage Paydown",     icon: "🏡" },
    { id: "car",        label: "Car Loan",             icon: "🚗" },
  ]

  const sliderStyle = { width: "100%", accentColor: t.accent2, cursor: "pointer" }
  const fieldRow = (label, value) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
      <span style={{ fontSize: 12, color: t.textMuted }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{value}</span>
    </div>
  )
  const statBox = (label, value, color = t.text) => (
    <div style={{ background: t.hover, borderRadius: 8, padding: "14px 16px" }}>
      <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 18, fontWeight: 800, color }}>{value}</p>
    </div>
  )

  return (
    <div style={{ display: "flex", gap: 0, height: "100%" }}>
      {/* Sidebar */}
      <div style={{ width: 180, borderRight: `1px solid ${t.border}`, paddingTop: 4, flexShrink: 0 }}>
        <p style={{ fontSize: 11, color: t.textFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, paddingLeft: 4 }}>Simulators</p>
        {simulators.map(s => (
          <button key={s.id} onClick={() => setSimType(s.id)} style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 10px", borderRadius: 8,
            border: "none", cursor: "pointer", marginBottom: 2, fontSize: 13, fontWeight: simType === s.id ? 700 : 400,
            background: simType === s.id ? `${t.accent}18` : "transparent",
            color: simType === s.id ? t.accent : t.textMuted,
          }}>
            <span>{s.icon}</span>{s.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, paddingLeft: 28, overflowY: "auto", paddingBottom: 32 }}>

        {/* ── RETIREMENT SIMULATOR (removed — lives in My Plan tab) ── */}
        {simType === "retirement_disabled" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>Retirement Simulator</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>Compare your current plan against a what-if scenario</p>
            </div>
            {retLoading ? <div style={{ color: t.textMuted }}>Loading plan...</div> : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Base plan */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.accent }} />
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: t.accent }}>Current Plan</h3>
                    <span style={{ fontSize: 11, color: t.textFaint, background: t.hover, padding: "2px 8px", borderRadius: 4 }}>locked</span>
                  </div>
                  <div style={{ ...card, padding: "20px 22px", marginBottom: 14 }}>
                    {RETIREMENT_SLIDER_FIELDS.map(f => (
                      <div key={f.key} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                        <span style={{ fontSize: 12, color: t.textMuted }}>{f.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{fmtVal(f.key, retBase[f.key])}</span>
                      </div>
                    ))}
                  </div>
                  {baseResults && (
                    <div style={{ ...card, padding: "20px 22px" }}>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Historical Success</p>
                        <SuccessBadge rate={baseResults.successRate} />
                      </div>
                      <FanChart results={baseResults} theme={t} goalAmount={retBase.goal_amount} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                        <div style={{ background: t.hover, borderRadius: 6, padding: "10px 12px" }}>
                          <p style={{ fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Projected Balance</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: t.text }}>${(baseResults.p50 / 1000000).toFixed(1)}M</p>
                        </div>
                        <div style={{ background: t.hover, borderRadius: 6, padding: "10px 12px" }}>
                          <p style={{ fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Money Lasts</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{baseResults.yearsMoneyLasts >= 99 ? "Forever" : baseResults.yearsMoneyLasts + "yr"}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Scenario */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.accent2 }} />
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: t.accent2 }}>What If...</h3>
                    <button onClick={() => setRetScenario({ ...retBase })} style={{ fontSize: 11, color: t.textFaint, background: t.hover, padding: "2px 8px", borderRadius: 4, border: "none", cursor: "pointer" }}>reset</button>
                  </div>
                  <div style={{ ...card, padding: "20px 22px", marginBottom: 14 }}>
                    {RETIREMENT_SLIDER_FIELDS.map(f => (
                      <div key={f.key} style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <label style={{ fontSize: 12, color: t.textMuted }}>{f.label}</label>
                          <span style={{ fontSize: 12, fontWeight: 700, color: retScenario && retScenario[f.key] !== retBase[f.key] ? t.accent2 : t.text }}>{fmtVal(f.key, retScenario?.[f.key] ?? retBase[f.key])}</span>
                        </div>
                        <input type="range" min={f.min} max={f.max} step={f.step || 1}
                          value={retScenario?.[f.key] ?? retBase[f.key]}
                          onChange={e => setRetScenario(p => ({ ...p, [f.key]: Number(e.target.value) }))}
                          style={sliderStyle} />
                      </div>
                    ))}
                  </div>
                  {scenarioResults && (
                    <div style={{ ...card, padding: "20px 22px" }}>
                      <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Historical Success</p>
                        <SuccessBadge rate={scenarioResults.successRate} />
                        {baseResults && (
                          <p style={{ fontSize: 12, color: scenarioResults.successRate > baseResults.successRate ? t.accent : "#EF4444", marginTop: 4 }}>
                            {scenarioResults.successRate > baseResults.successRate ? "▲" : "▼"} {Math.abs(scenarioResults.successRate - baseResults.successRate)}pts vs current plan
                          </p>
                        )}
                      </div>
                      <FanChart results={scenarioResults} theme={t} goalAmount={retScenario?.goal_amount} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                        <div style={{ background: t.hover, borderRadius: 6, padding: "10px 12px" }}>
                          <p style={{ fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Projected Balance</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: baseResults && scenarioResults.p50 > baseResults.p50 ? t.accent : "#EF4444" }}>${(scenarioResults.p50 / 1000000).toFixed(1)}M</p>
                        </div>
                        <div style={{ background: t.hover, borderRadius: 6, padding: "10px 12px" }}>
                          <p style={{ fontSize: 10, color: t.textMuted, marginBottom: 2 }}>Money Lasts</p>
                          <p style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{scenarioResults.yearsMoneyLasts >= 99 ? "Forever" : scenarioResults.yearsMoneyLasts + "yr"}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CAR LOAN SIMULATOR ── */}
        {simType === "car" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>Car Loan Simulator</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>See your real cost of ownership including interest</p>
            </div>
            {/* Car affordability analysis — top */}
            {finances.loaded && (() => {
              const inc = finances.monthlyIncome
              const noIncome = inc === 0
              const paymentPct = inc > 0 ? (carPayment / inc) * 100 : 0
              const allCarCostsPct = inc > 0 ? ((carPayment + 400) / inc) * 100 : 0
              const downPct = (car.down / car.price) * 100
              const emergencyAfter = finances.monthlyExpenses > 0 ? (finances.liquidSavings - car.down) / finances.monthlyExpenses : 99
              const checks = [
                { label: "20% down payment", pass: downPct >= 20, warn: downPct >= 10, detail: `Your down is ${Math.round(downPct)}% — ${downPct >= 20 ? "avoids higher interest rates" : downPct >= 10 ? "consider saving more to reduce interest" : "low down payment increases total cost significantly"}` },
                { label: "Payment ≤ 15% of income", pass: noIncome || paymentPct <= 15, warn: !noIncome && paymentPct <= 20, detail: noIncome ? "Connect your bank to analyze your income" : `Payment is ${Math.round(paymentPct)}% of your monthly income of $${inc.toLocaleString()}` },
                { label: "Total car costs ≤ 20% of income", pass: noIncome || allCarCostsPct <= 20, warn: !noIncome && allCarCostsPct <= 25, detail: noIncome ? "Based on payment + estimated $400/mo insurance, gas & maintenance" : `Est. total car costs ~${Math.round(allCarCostsPct)}% of income (payment + ~$400 running costs)` },
                { label: "3+ months emergency fund after purchase", pass: emergencyAfter >= 3, warn: emergencyAfter >= 1, detail: emergencyAfter >= 99 ? "Connect your bank to check liquid savings" : `After $${car.down.toLocaleString()} down, you'd have ~${Math.round(emergencyAfter * 10) / 10} months of expenses in savings` },
              ]
              const passing = checks.filter(c => c.pass).length
              const verdict = passing === 4 ? "green" : passing >= 2 ? "yellow" : "red"
              const verdictColor = verdict === "green" ? t.accent : verdict === "yellow" ? "#F59E0B" : "#EF4444"
              const verdictBg = verdict === "green" ? "rgba(47,133,90,0.06)" : verdict === "yellow" ? "rgba(245,158,11,0.08)" : "rgba(239,68,68,0.06)"
              const verdictLabel = verdict === "green" ? "Looks affordable for you" : verdict === "yellow" ? "Possible, but tight" : "Not recommended right now"
              const verdictIcon = verdict === "green" ? "✅" : verdict === "yellow" ? "⚠️" : "❌"
              return (
                <div style={{ ...card, padding: "22px 26px", marginBottom: 20, borderLeft: `4px solid ${verdictColor}`, background: verdictBg }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 3 }}>Affordability Assessment</h3>
                      <p style={{ fontSize: 12, color: t.textFaint }}>Based on your connected accounts and transactions</p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: verdictColor }}>{verdictIcon} {verdictLabel}</div>
                      <div style={{ fontSize: 11, color: t.textFaint }}>{passing} of 4 checks passed</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
                    {checks.map(c => (
                      <div key={c.label} style={{ padding: "12px 14px", borderRadius: 10, background: t.cardBg, border: `1px solid ${c.pass ? t.accent + "44" : c.warn ? "#F59E0B44" : "#EF444444"}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 14 }}>{c.pass ? "✅" : c.warn ? "⚠️" : "❌"}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: c.pass ? t.accent : c.warn ? "#F59E0B" : "#EF4444" }}>{c.label}</span>
                        </div>
                        <p style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.4, margin: 0 }}>{c.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* Inputs */}
              <div style={{ ...card, padding: "22px 24px" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 18 }}>Loan Details</h3>
                {[
                  { label: "Car Price", key: "price", prefix: "$", suffix: "", placeholder: "35000" },
                  { label: "Down Payment", key: "down", prefix: "$", suffix: "", placeholder: "5000" },
                  { label: "Interest Rate", key: "rate", prefix: "", suffix: "%", placeholder: "6.5" },
                  { label: "Loan Term (months)", key: "months", prefix: "", suffix: "mo", placeholder: "60" },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>{f.label}</label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      {f.prefix && <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>{f.prefix}</span>}
                      <input
                        type="number"
                        value={car[f.key]}
                        onChange={e => setCarField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        style={{ width: "100%", padding: `10px ${f.suffix ? "36px" : "12px"} 10px ${f.prefix ? "24px" : "12px"}`, borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }}
                      />
                      {f.suffix && <span style={{ position: "absolute", right: 12, fontSize: 13, color: t.textMuted, pointerEvents: "none" }}>{f.suffix}</span>}
                    </div>
                  </div>
                ))}
                {car.months && (
                  <p style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
                    {car.months} months = {Math.floor(car.months / 12)}y {car.months % 12}m
                  </p>
                )}
              </div>
              {/* Results */}
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {statBox("Monthly Payment", `$${Math.round(carPayment).toLocaleString()}`, t.accent)}
                  {statBox("Total Interest", `$${Math.round(carTotalInterest).toLocaleString()}`, "#EF4444")}
                  {statBox("Total Cost", `$${Math.round(carTotalPaid + car.down).toLocaleString()}`, t.text)}
                  {statBox("Loan Amount", `$${Math.round(carLoan).toLocaleString()}`, t.accent2)}
                </div>
                {/* Cost breakdown bar */}
                <div style={{ ...card, padding: "18px 20px", marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 12 }}>Cost Breakdown</p>
                  {[
                    { label: "Car price", val: car.price, color: t.accent2 },
                    { label: "Total interest", val: Math.round(carTotalInterest), color: "#EF4444" },
                  ].map(item => {
                    const pct = Math.round((item.val / (car.price + carTotalInterest)) * 100)
                    return (
                      <div key={item.label} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: t.textMuted }}>{item.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: item.color }}>${item.val.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: t.hover }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: item.color }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
                {/* Remaining balance over time */}
                <div style={{ ...card, padding: "18px 20px" }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 12 }}>Remaining Balance by Year</p>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={carAmort}>
                      <defs>
                        <linearGradient id="carGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={t.accent2} stopOpacity={0.4} />
                          <stop offset="95%" stopColor={t.accent2} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} label={{ value: "Year", position: "insideBottomRight", fill: t.textFaint, fontSize: 10, offset: 0 }} />
                      <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${Math.round(v/1000)}k`} width={45} />
                      <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                        <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                          <p style={{ color: t.text, fontWeight: 700 }}>Year {label}</p>
                          <p style={{ color: t.accent2 }}>Balance: ${payload[0]?.value?.toLocaleString()}</p>
                        </div>
                      ) : null} />
                      <Area type="monotone" dataKey="balance" stroke={t.accent2} strokeWidth={2} fill="url(#carGrad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Financial guidance */}
            {(() => {
              const paymentPct = finances.monthlyIncome > 0 ? (carPayment / finances.monthlyIncome) * 100 : null
              const allCarPct  = finances.monthlyIncome > 0 ? ((carPayment + 400) / finances.monthlyIncome) * 100 : null // +$400 est. insurance+gas
              const downPct    = car.price > 0 ? (car.down / car.price) * 100 : 0
              const rule20     = downPct >= 20
              const rule4      = car.months <= 48
              const rule10     = allCarPct !== null ? allCarPct <= 10 : null

              const checks = [
                {
                  label: "20% down payment",
                  pass: rule20,
                  detail: rule20
                    ? `Your down payment is ${Math.round(downPct)}% — great. You avoid negative equity early in the loan.`
                    : `Your down payment is ${Math.round(downPct)}%. Aim for at least 20% to avoid being underwater if the car's value drops.`,
                },
                {
                  label: "Loan term ≤ 48 months",
                  pass: rule4,
                  detail: rule4
                    ? `${car.months}-month term is solid. Shorter terms mean less interest and less time upside-down.`
                    : `${car.months}-month term is long. Cars depreciate fast — longer loans often mean you owe more than the car is worth. Try to stay under 48 months.`,
                },
                {
                  label: "All car costs ≤ 10% of income",
                  pass: rule10 === true,
                  warn: rule10 === null,
                  detail: rule10 === null
                    ? "Connect your bank to check this against your income."
                    : rule10 === true
                    ? `Estimated all-in car costs (payment + insurance + gas) are ~${Math.round(allCarPct)}% of your income — within the recommended 10%.`
                    : `Estimated all-in car costs are ~${Math.round(allCarPct)}% of your income. The 20/4/10 rule recommends keeping this under 10% to avoid being car-poor.`,
                },
              ]

              return (
                <div style={{ ...card, padding: "20px 24px", marginTop: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>📋</span>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>The 20/4/10 Rule</h3>
                  </div>
                  <p style={{ fontSize: 12, color: t.textMuted, marginBottom: 16 }}>
                    A widely used guideline: 20% down, loan no longer than 4 years, total car costs under 10% of gross income.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {checks.map(c => (
                      <div key={c.label} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: c.warn ? `rgba(245,158,11,0.07)` : c.pass ? `rgba(47,133,90,0.07)` : `rgba(239,68,68,0.07)` }}>
                        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{c.warn ? "⚠️" : c.pass ? "✅" : "❌"}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: c.warn ? "#F59E0B" : c.pass ? t.accent : "#EF4444", marginBottom: 2 }}>{c.label}</div>
                          <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>{c.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* ── HOUSE AFFORDABILITY SIMULATOR ── */}
        {simType === "home" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>House Affordability</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>Understand your full monthly cost and how equity builds over time</p>
            </div>
            {/* Mortgage affordability analysis — top */}
            {finances.loaded && (() => {
              const inc = finances.monthlyIncome
              const noIncome = inc === 0
              const housingPct = inc > 0 ? (homeMonthly / inc) * 100 : 0
              const closingCosts = Math.round(home.price * 0.03)
              const totalCashNeeded = home.down + closingCosts
              const canCoverDown = finances.liquidSavings >= totalCashNeeded
              const emergencyAfter = finances.monthlyExpenses > 0 ? (finances.liquidSavings - totalCashNeeded) / finances.monthlyExpenses : 99
              const downPct = (home.down / home.price) * 100
              const checks = [
                { label: "Housing costs ≤ 28% of income", pass: noIncome || housingPct <= 28, warn: !noIncome && housingPct <= 36, detail: noIncome ? "Connect your bank to check income" : `Your PITI is ${Math.round(housingPct)}% of monthly income ($${inc.toLocaleString()}). Lenders cap approval at 28–36%.` },
                { label: "20% down payment", pass: downPct >= 20, warn: downPct >= 10, detail: `Down is ${Math.round(downPct)}% — ${downPct >= 20 ? "no PMI required" : downPct >= 10 ? `PMI adds ~$${Math.round(homePMI)}/mo until you hit 20% equity` : "low equity increases costs and risk"}` },
                { label: "Can cover down + closing costs", pass: canCoverDown, warn: finances.liquidSavings >= home.down, detail: `Down ($${home.down.toLocaleString()}) + est. closing costs ($${closingCosts.toLocaleString()}) = $${totalCashNeeded.toLocaleString()}. You have $${finances.liquidSavings.toLocaleString()} liquid.` },
                { label: "3+ months emergency fund after closing", pass: emergencyAfter >= 3, warn: emergencyAfter >= 1, detail: emergencyAfter >= 99 ? "Connect your bank to check savings" : `After closing costs, you'd have ~${Math.round(emergencyAfter * 10) / 10} months of expenses left in savings` },
              ]
              const passing = checks.filter(c => c.pass).length
              const verdict = passing === 4 ? "green" : passing >= 2 ? "yellow" : "red"
              const verdictColor = verdict === "green" ? t.accent : verdict === "yellow" ? "#F59E0B" : "#EF4444"
              const verdictBg = verdict === "green" ? "rgba(47,133,90,0.06)" : verdict === "yellow" ? "rgba(245,158,11,0.08)" : "rgba(239,68,68,0.06)"
              const verdictLabel = verdict === "green" ? "Looks affordable for you" : verdict === "yellow" ? "Possible, but tight" : "Not recommended right now"
              const verdictIcon = verdict === "green" ? "✅" : verdict === "yellow" ? "⚠️" : "❌"
              return (
                <div style={{ ...card, padding: "22px 26px", marginBottom: 20, borderLeft: `4px solid ${verdictColor}`, background: verdictBg }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 3 }}>Affordability Assessment</h3>
                      <p style={{ fontSize: 12, color: t.textFaint }}>Based on your connected accounts and transactions</p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: verdictColor }}>{verdictIcon} {verdictLabel}</div>
                      <div style={{ fontSize: 11, color: t.textFaint }}>{passing} of 4 checks passed</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
                    {checks.map(c => (
                      <div key={c.label} style={{ padding: "12px 14px", borderRadius: 10, background: t.cardBg, border: `1px solid ${c.pass ? t.accent + "44" : c.warn ? "#F59E0B44" : "#EF444444"}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 14 }}>{c.pass ? "✅" : c.warn ? "⚠️" : "❌"}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: c.pass ? t.accent : c.warn ? "#F59E0B" : "#EF4444" }}>{c.label}</span>
                        </div>
                        <p style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.4, margin: 0 }}>{c.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
            {/* Cash Available card */}
            {finances.loaded && (() => {
              const closingCosts = Math.round(home.price * 0.03) // ~3% national average (title, escrow, lender fees)
              const totalCashNeeded = home.down + closingCosts
              const emergencyFundAmount = Math.round(emergencyMonths * finances.monthlyExpenses)
              const sortedAccounts = [...liquidAccounts].sort((a, b) => (b.balance || 0) - (a.balance || 0))
              const includedSavings = sortedAccounts.filter(a => !excludedAccounts.has(a.id)).reduce((s, a) => s + (a.balance || 0), 0)
              const netAvailable = includedSavings - emergencyFundAmount
              const canAfford = netAvailable >= totalCashNeeded
              const availPct = includedSavings > 0 ? Math.min(100, (Math.max(0, netAvailable) / includedSavings) * 100) : 0
              const emergPct = includedSavings > 0 ? Math.min(100, (emergencyFundAmount / includedSavings) * 100) : 0
              return (
                <div style={{ ...card, padding: "18px 20px", marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <p style={{ color: t.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>Cash Available</p>
                    <span style={{ fontSize: 22, fontWeight: 700, color: canAfford ? t.accent : "#EF4444" }}>${netAvailable.toLocaleString()}</span>
                  </div>
                  <div style={{ display: "flex", height: 6, borderRadius: 6, overflow: "hidden", background: t.border, margin: "0 0 14px" }}>
                    <div style={{ width: `${availPct}%`, background: t.accent, transition: "width 0.3s" }} />
                    <div style={{ width: `${emergPct}%`, background: "#EF4444", transition: "width 0.3s" }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {sortedAccounts.map(a => {
                      const included = !excludedAccounts.has(a.id)
                      return (
                        <div key={a.id}
                          onClick={() => toggleExcludeAccount(a.id)}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "6px 8px", borderRadius: 7, cursor: "pointer", background: included ? (t.isDark ? "rgba(110,231,183,0.06)" : "rgba(47,133,90,0.05)") : t.hover, opacity: included ? 1 : 0.45, transition: "all 0.15s" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${included ? t.accent : t.border}`, background: included ? t.accent : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {included && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                            </div>
                            <span style={{ color: t.text }}>{a.account_name || a.name || "Account"}</span>
                          </div>
                          <span style={{ fontWeight: 600, color: t.text }}>${(a.balance || 0).toLocaleString()}</span>
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#EF4444", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${t.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>− Emergency reserve</span>
                      <button onClick={() => setEmergencyMonths(m => Math.max(1, m - 1))}
                        style={{ width: 18, height: 18, borderRadius: 4, border: `1px solid ${t.border}`, background: t.cardBg, cursor: "pointer", fontSize: 12, color: t.textMuted, lineHeight: 1 }}>−</button>
                      <span style={{ fontWeight: 700, color: t.textMuted }}>{emergencyMonths}mo</span>
                      <button onClick={() => setEmergencyMonths(m => Math.min(12, m + 1))}
                        style={{ width: 18, height: 18, borderRadius: 4, border: `1px solid ${t.border}`, background: t.cardBg, cursor: "pointer", fontSize: 12, color: t.textMuted, lineHeight: 1 }}>+</button>
                    </div>
                    <span style={{ fontWeight: 600 }}>−${emergencyFundAmount.toLocaleString()}</span>
                  </div>
                  <p style={{ fontSize: 11, color: t.textFaint, margin: "8px 0 0" }}>
                    {canAfford
                      ? `✅ Covers down ($${home.down.toLocaleString()}) + closing costs ($${closingCosts.toLocaleString()})`
                      : `❌ Short $${(totalCashNeeded - netAvailable).toLocaleString()} — need $${totalCashNeeded.toLocaleString()} total (down + closing)`}
                  </p>
                </div>
              )
            })()}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* Inputs */}
              <div style={{ ...card, padding: "22px 24px" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 18 }}>Loan Details</h3>
                {[
                  { label: "Home Price", key: "price", prefix: "$", suffix: "", placeholder: "500,000", commas: true },
                  { label: "Down Payment", key: "down", prefix: "$", suffix: "", placeholder: "100,000", commas: true, hint: home.price > 0 ? `${Math.round(home.down / home.price * 100)}% of price` : "" },
                  { label: "Interest Rate", key: "rate", prefix: "", suffix: "%", placeholder: "6.8" },
                  { label: "Loan Term", key: "years", prefix: "", suffix: "yrs", placeholder: "30" },
                  { label: "Property Tax Rate", key: "taxRate", prefix: "", suffix: "%/yr", placeholder: "1.2", hint: "National avg ~1.1%" },
                  { label: "Home Insurance Rate", key: "insuranceRate", prefix: "", suffix: "%/yr", placeholder: "0.5", hint: <a href="https://www.bankrate.com/insurance/homeowners-insurance/states/" target="_blank" rel="noopener noreferrer" style={{ color: t.accent, textDecoration: "underline", fontSize: 11 }}>Rates by state ↗</a> },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>{f.label}</label>
                      {f.hint && <span style={{ fontSize: 11, color: t.textFaint }}>{f.hint}</span>}
                    </div>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      {f.prefix && <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>{f.prefix}</span>}
                      {f.commas ? (
                        <input
                          type="text" inputMode="numeric"
                          value={home[f.key] ? Number(home[f.key]).toLocaleString() : ""}
                          onChange={e => setHomeField(f.key, Number(e.target.value.replace(/,/g, "")) || 0)}
                          placeholder={f.placeholder}
                          style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }}
                        />
                      ) : (
                        <input
                          type="number"
                          value={home[f.key]}
                          onChange={e => setHomeField(f.key, e.target.value)}
                          placeholder={f.placeholder}
                          style={{ width: "100%", padding: `10px ${f.suffix ? "52px" : "12px"} 10px ${f.prefix ? "24px" : "12px"}`, borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }}
                        />
                      )}
                      {f.suffix && <span style={{ position: "absolute", right: 12, fontSize: 12, color: t.textMuted, pointerEvents: "none" }}>{f.suffix}</span>}
                    </div>
                  </div>
                ))}
                {home.down / home.price < 0.20 && (
                  <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)", marginTop: 4 }}>
                    <p style={{ fontSize: 11, color: "#F59E0B", margin: 0 }}>⚠️ PMI added (~${Math.round(homePMI)}/mo) — down payment under 20%</p>
                  </div>
                )}
              </div>
              {/* Results */}
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {statBox("Monthly Payment", `$${Math.round(homeMonthly).toLocaleString()}`, t.accent)}
                  {statBox("Total Interest", `$${Math.round(homeTotalInterest).toLocaleString()}`, "#EF4444")}
                  {statBox("Loan Amount", `$${Math.round(homeLoan).toLocaleString()}`, t.text)}
                  {statBox("Down Payment", `${Math.round(home.down/home.price*100)}%`, home.down/home.price >= 0.20 ? t.accent : "#F59E0B")}
                </div>
                {/* Monthly breakdown */}
                <div style={{ ...card, padding: "18px 20px", marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 12 }}>Monthly Breakdown</p>
                  {[
                    { label: "Principal & Interest", val: Math.round(homePI), color: t.accent },
                    { label: "Property Tax", val: Math.round(homeTax), color: t.accent2 },
                    { label: "Insurance", val: Math.round(homeInsurance), color: "#F59E0B" },
                    ...(homePMI > 0 ? [{ label: "PMI", val: Math.round(homePMI), color: "#EF4444" }] : []),
                  ].map(item => {
                    const pct = Math.round((item.val / homeMonthly) * 100)
                    return (
                      <div key={item.label} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: t.textMuted }}>{item.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: item.color }}>${item.val.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: t.hover }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: item.color }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
                {/* Equity chart */}
                <div style={{ ...card, padding: "18px 20px" }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 12 }}>Equity Growth Over Time</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={homeEquityData}>
                      <defs>
                        <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={t.accent} stopOpacity={0.5} />
                          <stop offset="95%" stopColor={t.accent} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="debtGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#EF4444" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} label={{ value: "Year", position: "insideBottomRight", fill: t.textFaint, fontSize: 10, offset: 0 }} />
                      <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${Math.round(v/1000)}k`} width={50} />
                      <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                        <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                          <p style={{ color: t.text, fontWeight: 700, marginBottom: 4 }}>Year {label}</p>
                          {payload.map(p => <p key={p.dataKey} style={{ color: p.color }}>{p.name}: ${p.value?.toLocaleString()}</p>)}
                        </div>
                      ) : null} />
                      <Area type="monotone" dataKey="equity" stroke={t.accent} strokeWidth={2} fill="url(#equityGrad)" name="Equity" dot={false} />
                      <Area type="monotone" dataKey="balance" stroke="#EF4444" strokeWidth={1.5} fill="url(#debtGrad)" name="Remaining debt" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {false /*mortgage paydown moved to its own simulator*/ && (() => {
              const mortBal = mortBalanceOverride !== "" ? Number(mortBalanceOverride.replace(/,/g, "")) : homeLoan
              const r = home.rate / 100 / 12
              const n = home.years * 12
              const basePayment = r > 0 ? mortBal * r / (1 - Math.pow(1 + r, -n)) : mortBal / n

              function amortize(principal, monthlyPayment, extraPerMonth) {
                let bal = principal, totalInt = 0, month = 0
                const yearlyData = []
                while (bal > 0.01 && month < 600) {
                  const interest = bal * r
                  const principal_paid = Math.min(bal, monthlyPayment - interest + extraPerMonth)
                  bal = Math.max(0, bal - principal_paid)
                  totalInt += interest
                  month++
                  if (month % 12 === 0) yearlyData.push({ year: month / 12, balance: Math.round(bal) })
                }
                return { months: month, totalInterest: Math.round(totalInt), yearlyData }
              }

              const base = amortize(mortBal, basePayment, 0)
              const extra = amortize(mortBal, basePayment, mortExtra)
              const monthsSaved = base.months - extra.months
              const interestSaved = base.totalInterest - extra.totalInterest
              const fmtYM = m => { const y = Math.floor(m / 12); const mo = m % 12; return y > 0 && mo > 0 ? `${y}yr ${mo}mo` : y > 0 ? `${y} yrs` : `${mo} mo` }
              const fmtM = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : `$${Math.round(v).toLocaleString()}`

              const chartData = base.yearlyData.map((pt, i) => ({
                year: pt.year,
                baseline: pt.balance,
                withExtra: extra.yearlyData[i]?.balance ?? 0,
              }))

              return (
                <div style={{ marginTop: 24 }}>
                  <div style={{ marginBottom: 16 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 4 }}>🏠 Mortgage Paydown Calculator</h2>
                    <p style={{ color: t.textMuted, fontSize: 13 }}>See how extra payments slash your timeline and interest cost</p>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                    {/* Inputs */}
                    <div style={{ ...card, padding: "22px 24px" }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>Loan Details</p>

                      <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 5 }}>Current Balance</label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                          <input type="text" inputMode="numeric"
                            value={mortBalanceOverride !== "" ? mortBalanceOverride : Number(homeLoan).toLocaleString()}
                            onChange={e => setMortBalanceOverride(e.target.value)}
                            placeholder={Number(homeLoan).toLocaleString()}
                            style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                        </div>
                        <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>Defaults to your loan above — override for an existing mortgage</p>
                      </div>

                      <div style={{ padding: "10px 14px", borderRadius: 8, background: t.hover, marginBottom: 20, fontSize: 12, color: t.textMuted, display: "flex", gap: 16 }}>
                        <span>Rate: <b style={{ color: t.text }}>{home.rate}%</b></span>
                        <span>Term: <b style={{ color: t.text }}>{home.years} yrs</b></span>
                        <span>Base payment: <b style={{ color: t.text }}>${Math.round(basePayment).toLocaleString()}/mo</b></span>
                      </div>

                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>Extra Monthly Payment</label>
                          <span style={{ fontSize: 16, fontWeight: 800, color: t.accent }}>${mortExtra.toLocaleString()}/mo</span>
                        </div>
                        <input type="range" min={0} max={5000} step={50} value={mortExtra}
                          onChange={e => setMortExtra(Number(e.target.value))}
                          style={{ width: "100%", accentColor: t.accent, cursor: "pointer", marginBottom: 6 }} />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: t.textFaint }}>
                          <span>$0</span><span>$5,000/mo</span>
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {/* Impact banner */}
                      {mortExtra > 0 && (
                        <div style={{ ...card, padding: "18px 22px", borderLeft: `4px solid ${t.accent}`, background: t.isDark ? "rgba(110,231,183,0.06)" : "rgba(47,133,90,0.05)" }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: t.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>With ${mortExtra.toLocaleString()}/mo extra</p>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 2 }}>Time saved</p>
                              <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>{fmtYM(monthsSaved)}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 2 }}>Interest saved</p>
                              <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>{fmtM(interestSaved)}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Side-by-side comparison */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {[
                          { label: "Without extra", months: base.months, interest: base.totalInterest, color: "#EF4444" },
                          { label: `With $${mortExtra.toLocaleString()}/mo extra`, months: extra.months, interest: extra.totalInterest, color: t.accent },
                        ].map(s => (
                          <div key={s.label} style={{ ...card, padding: "16px 18px", borderTop: `3px solid ${s.color}` }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</p>
                            <div style={{ marginBottom: 8 }}>
                              <p style={{ fontSize: 10, color: t.textFaint, margin: "0 0 2px" }}>Payoff</p>
                              <p style={{ fontSize: 17, fontWeight: 800, color: s.color, margin: 0 }}>{fmtYM(s.months)}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: 10, color: t.textFaint, margin: "0 0 2px" }}>Total interest</p>
                              <p style={{ fontSize: 17, fontWeight: 800, color: t.text, margin: 0 }}>{fmtM(s.interest)}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total cost comparison */}
                      <div style={{ ...card, padding: "14px 18px" }}>
                        {[
                          { label: "Total cost (no extra)", val: mortBal + base.totalInterest, sub: `${fmtM(mortBal)} principal + ${fmtM(base.totalInterest)} interest`, color: t.textMuted },
                          { label: `Total cost (with extra)`, val: mortBal + extra.totalInterest, sub: `${fmtM(mortBal)} principal + ${fmtM(extra.totalInterest)} interest`, color: t.accent },
                        ].map(r => (
                          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${t.border}` }}>
                            <div>
                              <p style={{ fontSize: 12, fontWeight: 600, color: t.text, margin: 0 }}>{r.label}</p>
                              <p style={{ fontSize: 10, color: t.textFaint, margin: 0 }}>{r.sub}</p>
                            </div>
                            <p style={{ fontSize: 15, fontWeight: 800, color: r.color, margin: 0 }}>{fmtM(r.val)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Balance over time chart */}
                  <div style={{ ...card, padding: "20px 24px" }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 4 }}>Balance Over Time</p>
                    <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 16 }}>How fast your mortgage disappears with vs. without extra payments</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="baseGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#EF4444" stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="extraGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={t.accent} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={t.accent} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} label={{ value: "Year", position: "insideBottomRight", fill: t.textFaint, fontSize: 10, offset: 0 }} />
                        <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : `$${Math.round(v/1000)}k`} width={54} />
                        <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                          <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                            <p style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>Year {label}</p>
                            {payload.map(p => <p key={p.dataKey} style={{ color: p.color, margin: "2px 0" }}>{p.name}: ${p.value?.toLocaleString()}</p>)}
                          </div>
                        ) : null} />
                        <Area type="monotone" dataKey="baseline" stroke="#EF4444" strokeWidth={2} fill="url(#baseGrad)" name="No extra payment" dot={false} />
                        {mortExtra > 0 && <Area type="monotone" dataKey="withExtra" stroke={t.accent} strokeWidth={2} fill="url(#extraGrad)" name={`$${mortExtra.toLocaleString()}/mo extra`} dot={false} />}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )
            })()}

            {/* Math explanation card */}
            <div style={{ ...card, padding: "22px 26px", marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 18 }}>📐</span>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>How the numbers are calculated</h3>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Monthly Payment (PITI)</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      ["Principal & Interest (P&I)", `$${Math.round(homePI).toLocaleString()}`, "The core loan repayment, calculated using the standard amortization formula based on your loan amount, rate, and term."],
                      ["Property Tax", `$${Math.round(homeTax).toLocaleString()}`, `${home.taxRate}% of home value ÷ 12. Varies by state — FL avg ~0.9%, TX ~1.7%, NJ ~2.2%.`],
                      ["Home Insurance", `$${Math.round(homeInsurance).toLocaleString()}`, `${home.insuranceRate}% of home value ÷ 12. National average is around 0.5–1%.`],
                      ...(homePMI > 0 ? [["PMI (Private Mortgage Insurance)", `$${Math.round(homePMI).toLocaleString()}`, "Required when down payment is under 20%. Typically 0.5% of the loan per year. Drops off once you reach 20% equity."]] : []),
                      ["Total Monthly", `$${Math.round(homeMonthly).toLocaleString()}`, "Sum of all above. Lenders typically require this to be ≤ 28% of gross monthly income."],
                    ].map(([label, value, desc]) => (
                      <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background: t.hover }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: label === "Total Monthly" ? t.accent : t.text }}>{label}</span>
                          <span style={{ fontSize: 13, fontWeight: 800, color: label === "Total Monthly" ? t.accent : t.text }}>{value}</span>
                        </div>
                        <p style={{ fontSize: 11, color: t.textMuted, margin: 0, lineHeight: 1.4 }}>{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>What is PMI?</p>
                  <div style={{ padding: "14px 16px", borderRadius: 10, background: home.down / home.price < 0.20 ? "rgba(245,158,11,0.08)" : "rgba(47,133,90,0.06)", border: `1px solid ${home.down / home.price < 0.20 ? "rgba(245,158,11,0.3)" : "rgba(47,133,90,0.3)"}`, marginBottom: 14 }}>
                    {home.down / home.price < 0.20 ? (
                      <>
                        <p style={{ fontSize: 13, fontWeight: 700, color: "#F59E0B", marginBottom: 6 }}>⚠️ PMI applies to your scenario</p>
                        <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                          With a {Math.round(home.down / home.price * 100)}% down payment, lenders require PMI — insurance that protects <em>them</em> (not you) if you default. It costs ~$
                          {Math.round(homePMI)}/mo and continues until your equity reaches 20% (~year {homeEquityData.findIndex(d => d.equity >= home.price * 0.20) + 1 || "?"}).
                          To avoid it, increase your down payment to ${Math.round(home.price * 0.20).toLocaleString()} (20%).
                        </p>
                      </>
                    ) : (
                      <>
                        <p style={{ fontSize: 13, fontWeight: 700, color: t.accent, marginBottom: 6 }}>✅ No PMI required</p>
                        <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                          Your {Math.round(home.down / home.price * 100)}% down payment clears the 20% threshold — no PMI added. This saves you ~${Math.round(homeLoan * 0.005 / 12)}/mo compared to a minimal down payment.
                        </p>
                      </>
                    )}
                  </div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Closing Costs</p>
                  <div style={{ padding: "12px 14px", borderRadius: 8, background: t.hover }}>
                    <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                      Estimated at <strong style={{ color: t.text }}>3% of purchase price</strong> (~${Math.round(home.price * 0.03).toLocaleString()}), which is the national average. Includes lender origination fees, title insurance, escrow, appraisal, and prepaid items. Actual costs vary by state and lender — always request a Loan Estimate.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MORTGAGE PAYDOWN SIMULATOR ── */}
        {simType === "mortgage" && (() => {
          const mortBal = mortBalanceOverride !== "" ? Number(mortBalanceOverride.replace(/,/g, "")) : homeLoan
          const r = home.rate / 100 / 12
          const n = home.years * 12
          const basePayment = r > 0 ? mortBal * r / (1 - Math.pow(1 + r, -n)) : mortBal / n

          function amortize(principal, monthlyPayment, extraPerMonth) {
            let bal = principal, totalInt = 0, month = 0
            const yearlyData = []
            while (bal > 0.01 && month < 600) {
              const interest = bal * r
              const principal_paid = Math.min(bal, monthlyPayment - interest + extraPerMonth)
              bal = Math.max(0, bal - principal_paid)
              totalInt += interest
              month++
              if (month % 12 === 0) yearlyData.push({ year: month / 12, balance: Math.round(bal) })
            }
            return { months: month, totalInterest: Math.round(totalInt), yearlyData }
          }

          const base = amortize(mortBal, basePayment, 0)
          const extra = amortize(mortBal, basePayment, mortExtra)
          const monthsSaved = base.months - extra.months
          const interestSaved = base.totalInterest - extra.totalInterest
          const fmtYM = m => { const y = Math.floor(m / 12); const mo = m % 12; return y > 0 && mo > 0 ? `${y}yr ${mo}mo` : y > 0 ? `${y} yrs` : `${mo} mo` }
          const fmtM = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : `$${Math.round(v).toLocaleString()}`

          const chartData = base.yearlyData.map((pt, i) => ({
            year: pt.year,
            baseline: pt.balance,
            withExtra: extra.yearlyData[i]?.balance ?? 0,
          }))

          return (
            <div>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>🏡 Mortgage Paydown</h2>
                <p style={{ color: t.textMuted, fontSize: 13 }}>See how extra payments slash your timeline and interest cost</p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                {/* Inputs */}
                <div style={{ ...card, padding: "22px 24px" }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>Loan Details</p>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 5 }}>Current Balance</label>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                      <input type="text" inputMode="numeric"
                        value={mortBalanceOverride !== "" ? mortBalanceOverride : Number(homeLoan).toLocaleString()}
                        onChange={e => setMortBalanceOverride(e.target.value)}
                        placeholder={Number(homeLoan).toLocaleString()}
                        style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                    </div>
                    <p style={{ fontSize: 11, color: t.textFaint, marginTop: 4 }}>Defaults to your House Affordability loan — override for an existing mortgage</p>
                  </div>

                  {[
                    { label: "Interest Rate", field: "rate", suffix: "%" },
                    { label: "Loan Term", field: "years", suffix: "yrs" },
                  ].map(f => (
                    <div key={f.field} style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 5 }}>{f.label}</label>
                      <div style={{ position: "relative" }}>
                        <input type="number" value={home[f.field]} onChange={e => setHomeField(f.field, e.target.value)}
                          style={{ width: "100%", padding: "10px 40px 10px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: t.textMuted }}>{f.suffix}</span>
                      </div>
                    </div>
                  ))}

                  <div style={{ padding: "10px 14px", borderRadius: 8, background: t.hover, marginBottom: 20, fontSize: 12, color: t.textMuted }}>
                    Base payment: <b style={{ color: t.text }}>${Math.round(basePayment).toLocaleString()}/mo</b>
                  </div>

                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>Extra Monthly Payment</label>
                      <span style={{ fontSize: 16, fontWeight: 800, color: t.accent }}>${mortExtra.toLocaleString()}/mo</span>
                    </div>
                    <input type="range" min={0} max={5000} step={50} value={mortExtra}
                      onChange={e => setMortExtra(Number(e.target.value))}
                      style={{ width: "100%", accentColor: t.accent, cursor: "pointer", marginBottom: 6 }} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: t.textFaint }}>
                      <span>$0</span><span>$5,000/mo</span>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {mortExtra > 0 && (
                    <div style={{ ...card, padding: "18px 22px", borderLeft: `4px solid ${t.accent}`, background: t.isDark ? "rgba(110,231,183,0.06)" : "rgba(47,133,90,0.05)" }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: t.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>With ${mortExtra.toLocaleString()}/mo extra</p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 2 }}>Time saved</p>
                          <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>{fmtYM(monthsSaved)}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 2 }}>Interest saved</p>
                          <p style={{ fontSize: 22, fontWeight: 800, color: t.accent, margin: 0 }}>{fmtM(interestSaved)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {[
                      { label: "Without extra", months: base.months, interest: base.totalInterest, color: "#EF4444" },
                      { label: `+$${mortExtra.toLocaleString()}/mo`, months: extra.months, interest: extra.totalInterest, color: t.accent },
                    ].map(s => (
                      <div key={s.label} style={{ ...card, padding: "16px 18px", borderTop: `3px solid ${s.color}` }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</p>
                        <div style={{ marginBottom: 8 }}>
                          <p style={{ fontSize: 10, color: t.textFaint, margin: "0 0 2px" }}>Payoff</p>
                          <p style={{ fontSize: 17, fontWeight: 800, color: s.color, margin: 0 }}>{fmtYM(s.months)}</p>
                        </div>
                        <div>
                          <p style={{ fontSize: 10, color: t.textFaint, margin: "0 0 2px" }}>Total interest</p>
                          <p style={{ fontSize: 17, fontWeight: 800, color: t.text, margin: 0 }}>{fmtM(s.interest)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ ...card, padding: "14px 18px" }}>
                    {[
                      { label: "Total cost (no extra)", val: mortBal + base.totalInterest, sub: `${fmtM(mortBal)} principal + ${fmtM(base.totalInterest)} interest`, color: t.textMuted },
                      { label: "Total cost (with extra)", val: mortBal + extra.totalInterest, sub: `${fmtM(mortBal)} principal + ${fmtM(extra.totalInterest)} interest`, color: t.accent },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: t.text, margin: 0 }}>{row.label}</p>
                          <p style={{ fontSize: 10, color: t.textFaint, margin: 0 }}>{row.sub}</p>
                        </div>
                        <p style={{ fontSize: 15, fontWeight: 800, color: row.color, margin: 0 }}>{fmtM(row.val)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Balance over time chart */}
              <div style={{ ...card, padding: "20px 24px" }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 4 }}>Balance Over Time</p>
                <p style={{ fontSize: 11, color: t.textFaint, marginBottom: 16 }}>How fast your mortgage disappears with vs. without extra payments</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="baseGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="extraGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={t.accent} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={t.accent} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="year" tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} label={{ value: "Year", position: "insideBottomRight", fill: t.textFaint, fontSize: 10, offset: 0 }} />
                    <YAxis tick={{ fill: t.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : `$${Math.round(v/1000)}k`} width={54} />
                    <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                      <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                        <p style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>Year {label}</p>
                        {payload.map(p => <p key={p.dataKey} style={{ color: p.color, margin: "2px 0" }}>{p.name}: ${p.value?.toLocaleString()}</p>)}
                      </div>
                    ) : null} />
                    <Area type="monotone" dataKey="baseline" stroke="#EF4444" strokeWidth={2} fill="url(#baseGrad)" name="No extra payment" dot={false} />
                    {mortExtra > 0 && <Area type="monotone" dataKey="withExtra" stroke={t.accent} strokeWidth={2} fill="url(#extraGrad)" name={`$${mortExtra.toLocaleString()}/mo extra`} dot={false} />}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )
        })()}

        {/* ── DEBT PAYOFF SIMULATOR ── */}
        {simType === "debt" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>Debt Payoff</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>Compare avalanche vs snowball and see your payoff date</p>
            </div>
            {debtAccounts.length === 0 ? (
              <div style={{ ...card, padding: 32, textAlign: "center", color: t.textMuted }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💳</div>
                <p style={{ fontWeight: 600, marginBottom: 6 }}>No debt accounts found</p>
                <p style={{ fontSize: 13 }}>Credit cards and loans from your linked accounts will appear here automatically.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Controls */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ ...card, padding: 20 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Strategy</p>
                    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                      {[["avalanche", "⬇️ Avalanche", "Highest rate first — saves most interest"], ["snowball", "⚡ Snowball", "Lowest balance first — fastest wins"]].map(([id, label, desc]) => (
                        <button key={id} onClick={() => setDebtMethod(id)} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: `1px solid ${debtMethod === id ? t.accent : t.border}`, background: debtMethod === id ? (t.isDark ? "rgba(110,231,183,0.1)" : "rgba(47,133,90,0.08)") : "transparent", cursor: "pointer", textAlign: "left" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: debtMethod === id ? t.accent : t.text, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 11, color: t.textMuted }}>{desc}</div>
                        </button>
                      ))}
                    </div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Extra monthly payment</label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                      <input type="number" min={0} step={50} value={debtExtra} onChange={e => setDebtExtra(Number(e.target.value))} placeholder="0"
                        style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                    </div>
                  </div>
                  <div style={{ ...card, padding: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Debts</p>
                      <span style={{ fontSize: 11, color: t.textFaint, fontStyle: "italic" }}>Edit APR & min to match your statements</span>
                    </div>
                    {/* Header */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 64px 72px 72px", gap: 6, paddingBottom: 8, borderBottom: `1px solid ${t.border}`, marginBottom: 4 }}>
                      {["Account", "APR %", "Min/mo", "Balance"].map(h => (
                        <span key={h} style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: h === "Account" ? "left" : "right" }}>{h}</span>
                      ))}
                    </div>
                    {[...debtAccounts].sort((a, b) => b.balance - a.balance).map((d, i, arr) => (
                      <div key={d.id} style={{ display: "grid", gridTemplateColumns: "1fr 64px 72px 72px", gap: 6, alignItems: "center", padding: "7px 0", borderBottom: i < arr.length - 1 ? `1px solid ${t.border}` : "none" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                        <input type="number" min={0} max={100} step={0.1} value={d.rate}
                          onChange={e => setDebtAccounts(prev => prev.map(x => x.id === d.id ? { ...x, rate: Number(e.target.value) || 0 } : x))}
                          style={{ width: "100%", padding: "4px 6px", borderRadius: 6, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 12, textAlign: "right", outline: "none", boxSizing: "border-box" }} />
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: t.textMuted, pointerEvents: "none" }}>$</span>
                          <input type="number" min={0} step={1} value={d.minPayment}
                            onChange={e => setDebtAccounts(prev => prev.map(x => x.id === d.id ? { ...x, minPayment: Number(e.target.value) || 0 } : x))}
                            style={{ width: "100%", padding: "4px 6px 4px 16px", borderRadius: 6, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 12, textAlign: "right", outline: "none", boxSizing: "border-box" }} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", textAlign: "right" }}>${d.balance.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Results */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {debtPayoffCalc && (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        {[
                          ["Debt-free in", debtPayoffCalc.months < 12 ? `${debtPayoffCalc.months}mo` : `${Math.floor(debtPayoffCalc.months / 12)}y ${debtPayoffCalc.months % 12}m`, t.accent],
                          ["Total interest", `$${debtPayoffCalc.totalInterest.toLocaleString()}`, "#EF4444"],
                          ["Monthly payment", `$${debtPayoffCalc.monthly.toLocaleString()}`, t.text],
                          ["Payoff date", new Date(Date.now() + debtPayoffCalc.months * 30.5 * 86400000).toLocaleDateString("en-US", { month: "short", year: "numeric" }), t.text],
                        ].map(([label, value, color]) => (
                          <div key={label} style={{ ...card, padding: "14px 16px" }}>
                            <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{label}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ ...card, padding: 20 }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, marginBottom: 12 }}>PAYOFF TIMELINE</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <AreaChart data={debtPayoffCalc.timeline}>
                            <defs>
                              <linearGradient id="debtGrad2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="month" tickFormatter={m => debtPayoffCalc.months < 12 ? `${m}mo` : `${Math.round(m/12)}y`} tick={{ fontSize: 10, fill: t.textMuted }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v}`} tick={{ fontSize: 10, fill: t.textMuted }} axisLine={false} tickLine={false} width={45} />
                            <Tooltip formatter={v => [`$${v.toLocaleString()}`, "Remaining debt"]} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }} />
                            <Area type="monotone" dataKey="totalDebt" stroke="#EF4444" strokeWidth={2} fill="url(#debtGrad2)" dot={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Explanation card */}
            <div style={{ ...card, padding: "22px 26px", marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 18 }}>📐</span>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Avalanche vs Snowball — which is right for you?</h3>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    {
                      icon: "⬇️", title: "Avalanche (highest rate first)", color: "#3B82F6",
                      body: "Pay minimums on all debts, then throw every extra dollar at the highest-interest debt first. Once it's gone, roll that payment into the next highest. Mathematically optimal — you pay the least total interest.",
                      best: "Best if you're motivated by numbers and want to minimize cost.",
                    },
                    {
                      icon: "⚡", title: "Snowball (lowest balance first)", color: "#F59E0B",
                      body: "Pay minimums on all debts, then attack the smallest balance first regardless of rate. Each paid-off debt gives you a psychological win that builds momentum.",
                      best: "Best if you need motivation and quick wins to stay on track.",
                    },
                  ].map(s => (
                    <div key={s.title} style={{ padding: "14px 16px", borderRadius: 10, background: t.hover, borderLeft: `3px solid ${s.color}` }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: s.color, marginBottom: 6 }}>{s.icon} {s.title}</p>
                      <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, marginBottom: 6 }}>{s.body}</p>
                      <p style={{ fontSize: 11, color: t.textFaint, fontStyle: "italic", margin: 0 }}>{s.best}</p>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ padding: "14px 16px", borderRadius: 10, background: t.hover }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 6 }}>💡 The power of extra payments</p>
                    <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                      Every extra dollar above the minimum goes directly to principal — reducing the balance that interest compounds on. Even $50–$100/mo extra can shave months or years off your payoff date and save thousands in interest.
                    </p>
                  </div>
                  <div style={{ padding: "14px 16px", borderRadius: 10, background: t.hover }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 6 }}>📋 How minimum payments are estimated</p>
                    <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                      Minimums are estimated at 2% of the balance (a common credit card standard), with a $25 floor. Your actual minimums may differ — check your statements and update the rate field for accuracy.
                    </p>
                  </div>
                  <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", marginBottom: 6 }}>⚠️ High-interest debt warning</p>
                    <p style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, margin: 0 }}>
                      Credit cards averaging 20%+ APR cost more in annual interest than most investments earn. Paying off high-rate debt is often the highest guaranteed return you can get — better than investing the same money.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── EMERGENCY FUND SIMULATOR ── */}
        {simType === "emergency" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>Emergency Fund</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>How much runway do you have, and how long to reach your target?</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ ...card, padding: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Target months of expenses</p>
                  <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                    {[1, 3, 6, 9, 12].map(m => (
                      <button key={m} onClick={() => setEfMonths(m)} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${efMonths === m ? t.accent : t.border}`, background: efMonths === m ? (t.isDark ? "rgba(110,231,183,0.1)" : "rgba(47,133,90,0.08)") : "transparent", color: efMonths === m ? t.accent : t.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{m}mo</button>
                    ))}
                  </div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Monthly savings toward fund</label>
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>$</span>
                    <input type="number" min={0} step={50} value={efMonthlySaving} onChange={e => setEfMonthlySaving(Number(e.target.value))} placeholder="500"
                      style={{ width: "100%", padding: "10px 12px 10px 24px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                  </div>
                </div>
                <div style={{ ...card, padding: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Eligible accounts</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                    {[...liquidAccounts].sort((a, b) => (b.balance || 0) - (a.balance || 0)).map(a => {
                      const included = !excludedEfAccounts.has(a.id)
                      return (
                        <div key={a.id} onClick={() => toggleExcludeEfAccount(a.id)}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px", borderRadius: 7, cursor: "pointer", background: included ? (t.isDark ? "rgba(110,231,183,0.06)" : "rgba(47,133,90,0.05)") : t.hover, opacity: included ? 1 : 0.45, transition: "all 0.15s" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${included ? t.accent : t.border}`, background: included ? t.accent : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {included && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                            </div>
                            <span style={{ fontSize: 12, color: t.text }}>{a.account_name || a.name || "Account"}</span>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>${(a.balance || 0).toLocaleString()}</span>
                        </div>
                      )
                    })}
                  </div>
                  <button onClick={() => saveEfAccounts(excludedEfAccounts)} disabled={!efAccountsDirty}
                    style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: "none", background: efAccountsDirty ? t.accent : t.border, color: efAccountsDirty ? "#fff" : t.textMuted, fontSize: 13, fontWeight: 700, cursor: efAccountsDirty ? "pointer" : "default", marginBottom: 10, transition: "all 0.2s" }}>
                    {efAccountsDirty ? "Save Account Selection" : "Account Selection Saved"}
                  </button>
                  <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                    {[
                      ["Monthly needs (6mo avg)", `$${finances.monthlyExpenses.toLocaleString()}`],
                      ["Selected savings", `$${efCurrent.toLocaleString()}`],
                      ["Target", `$${efTarget.toLocaleString()}`],
                      ["Shortfall", efShortfall > 0 ? `$${efShortfall.toLocaleString()}` : "✅ Already funded"],
                    ].map(([k, v]) => fieldRow(k, v))}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    ["Current runway", `${finances.monthlyExpenses > 0 ? (efCurrent / finances.monthlyExpenses).toFixed(1) : "—"} months`, efCurrent >= efTarget ? t.accent : "#F59E0B"],
                    ["Target", `${efMonths} months`, t.text],
                    ["Monthly saving", `$${efMonthlySaving.toLocaleString()}`, t.text],
                    ["Time to goal", efShortfall <= 0 ? "Already there! 🎉" : efMonthsToGoal ? `${Math.floor(efMonthsToGoal / 12)}y ${efMonthsToGoal % 12}m` : "—", t.accent],
                  ].map(([label, value, color]) => (
                    <div key={label} style={{ ...card, padding: "14px 16px" }}>
                      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ ...card, padding: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, marginBottom: 12 }}>PROGRESS TO TARGET</p>
                  {(() => {
                    const pct = efTarget > 0 ? Math.min(100, (efCurrent / efTarget) * 100) : 0
                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontSize: 13, color: t.textMuted }}>$0</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: t.accent }}>{pct.toFixed(0)}%</span>
                          <span style={{ fontSize: 13, color: t.textMuted }}>${efTarget.toLocaleString()}</span>
                        </div>
                        <div style={{ height: 16, borderRadius: 8, background: t.hover, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 8, background: pct >= 100 ? t.accent : "#F59E0B", transition: "width 0.4s" }} />
                        </div>
                        <p style={{ fontSize: 12, color: t.textMuted, marginTop: 10 }}>
                          {pct >= 100 ? "Your emergency fund is fully funded! Consider moving excess to investments." : `You have ${(pct).toFixed(0)}% of your ${efMonths}-month emergency fund. ${efMonthsToGoal ? `At $${efMonthlySaving}/mo you'll reach your target in ${efMonthsToGoal} months.` : ""}`}
                        </p>
                      </>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── COMPOUND INTEREST SIMULATOR ── */}
        {simType === "compound" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 4 }}>Compound Interest</h2>
              <p style={{ color: t.textMuted, fontSize: 13 }}>See how your money grows over time with the power of compounding</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ ...card, padding: 20 }}>
                  {[
                    { label: "Initial investment", key: "initial", prefix: "$", suffix: "", placeholder: "10000" },
                    { label: "Monthly contribution", key: "monthly", prefix: "$", suffix: "/mo", placeholder: "500" },
                    { label: "Annual return", key: "rate", prefix: "", suffix: "%", placeholder: "7", hint: "S&P 500 hist. avg ~10%" },
                    { label: "Time horizon", key: "years", prefix: "", suffix: "yrs", placeholder: "30" },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>{f.label}</label>
                        {f.hint && <span style={{ fontSize: 11, color: t.textFaint }}>{f.hint}</span>}
                      </div>
                      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        {f.prefix && <span style={{ position: "absolute", left: 12, fontSize: 14, color: t.textMuted, pointerEvents: "none" }}>{f.prefix}</span>}
                        <input type="number" value={ci[f.key]} onChange={e => setCiField(f.key, e.target.value)} placeholder={f.placeholder}
                          style={{ width: "100%", padding: `10px ${f.suffix ? "52px" : "12px"} 10px ${f.prefix ? "24px" : "12px"}`, borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg || t.hover, color: t.text, fontSize: 14, fontWeight: 600, boxSizing: "border-box", outline: "none" }} />
                        {f.suffix && <span style={{ position: "absolute", right: 12, fontSize: 12, color: t.textMuted, pointerEvents: "none" }}>{f.suffix}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {(() => {
                    const last = ciData[ciData.length - 1] || {}
                    return [
                      ["Final balance", `$${(last.balance || 0).toLocaleString()}`, t.accent],
                      ["Total contributed", `$${(last.contributed || 0).toLocaleString()}`, t.text],
                      ["Growth", `$${(last.growth || 0).toLocaleString()}`, "#3B82F6"],
                      ["Return multiple", last.contributed > 0 ? `${(last.balance / last.contributed).toFixed(1)}x` : "—", t.text],
                    ].map(([label, value, color]) => (
                      <div key={label} style={{ ...card, padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color }}>{value}</div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
              <div style={{ ...card, padding: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, marginBottom: 16 }}>GROWTH OVER TIME</p>
                <ResponsiveContainer width="100%" height={340}>
                  <AreaChart data={ciData}>
                    <defs>
                      <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={t.accent} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={t.accent} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="contGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#94A3B8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="year" tickFormatter={y => `Yr ${y}`} tick={{ fontSize: 10, fill: t.textMuted }} axisLine={false} tickLine={false} interval={Math.floor(ci.years / 6)} />
                    <YAxis tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: t.textMuted }} axisLine={false} tickLine={false} width={55} />
                    <Tooltip formatter={v => [`$${v.toLocaleString()}`, ""]} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="contributed" stroke="#94A3B8" strokeWidth={1.5} fill="url(#contGrad)" name="Contributed" dot={false} />
                    <Area type="monotone" dataKey="balance" stroke={t.accent} strokeWidth={2} fill="url(#balGrad)" name="Balance" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
                <p style={{ fontSize: 12, color: t.textMuted, marginTop: 8, textAlign: "center" }}>
                  The green area above the grey line is pure investment growth — money that works for you without any additional effort.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

// ── BUDGET PAGE ────────────────────────────────────────────
function BudgetPage({ session, theme, transactions, fmtMoney, categories, hiddenCats = new Set(), onTransactionsLoaded, paychecks, onXPAward, notifBudget }) {
  const t = theme
  const card = makeCard(t)

  // ── Period system ──────────────────────────────────────
  const [periodType, setPeriodType]     = useState("month")
  const [periodOffset, setPeriodOffset] = useState(0)
  const [editing, setEditing]           = useState(null)
  const [editVal, setEditVal]           = useState("")
  const [drillDown, setDrillDown]       = useState(null) // { label, txs }
  const [drillModifying, setDrillModifying] = useState(false) // true = show edit page
  const [drillOverrides, setDrillOverrides] = useState({})
  const [drillEditingTxId, setDrillEditingTxId] = useState(null) // which tx row has picker open

  const merchantHistory = useMemo(() => {
    const map = {}
    transactions.forEach(tx => {
      const cat = tx.user_category
      const grp = tx.user_group
      if (!cat) return
      const name = (tx.merchant_name || tx.name || "").toLowerCase().trim()
      if (!name) return
      if (!map[name]) map[name] = {}
      if (!map[name][cat]) map[name][cat] = { category: cat, group: grp, count: 0 }
      map[name][cat].count++
    })
    const result = {}
    Object.entries(map).forEach(([name, cats]) => {
      const top = Object.values(cats).sort((a, b) => b.count - a.count)[0]
      const catObj = CATEGORIES.find(c => c.label === top.category)
      result[name] = { ...top, emoji: catObj?.emoji || "💳", source: "history" }
    })
    return result
  }, [transactions])

  const [communityMap, setCommunityMap] = useState({})
  useEffect(() => {
    const merchantNames = [...new Set(
      transactions.map(tx => (tx.merchant_name || tx.name || "").toLowerCase().trim()).filter(Boolean)
    )]
    if (merchantNames.length === 0) return
    supabase.from("merchant_categories")
      .select("merchant_name, category, grp, votes")
      .in("merchant_name", merchantNames)
      .then(({ data }) => {
        if (!data) return
        const byMerchant = {}
        data.forEach(row => {
          if (!byMerchant[row.merchant_name]) byMerchant[row.merchant_name] = []
          byMerchant[row.merchant_name].push(row)
        })
        const result = {}
        Object.entries(byMerchant).forEach(([name, rows]) => {
          const total = rows.reduce((s, r) => s + r.votes, 0)
          const best = rows.sort((a, b) => b.votes - a.votes)[0]
          if (best.votes >= 5 && best.votes / total >= 0.6) {
            result[name] = { category: best.category, group: best.grp }
          }
        })
        setCommunityMap(result)
      })
  }, [transactions])

  const period = useMemo(() => {
    const now = new Date()
    if (periodType === "month") {
      const d = new Date(now.getFullYear(), now.getMonth() + periodOffset, 1)
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      return { label: d.toLocaleString("default", { month: "long", year: "numeric" }), months: [ym], budgetKey: ym, multiplier: 1 }
    }
    if (periodType === "quarter") {
      const curQ   = Math.floor(now.getMonth() / 3)
      const qStart = new Date(now.getFullYear(), (curQ + periodOffset) * 3, 1)
      const q      = Math.floor(qStart.getMonth() / 3) + 1
      const months = [0, 1, 2].map(i => {
        const md = new Date(qStart.getFullYear(), qStart.getMonth() + i, 1)
        return `${md.getFullYear()}-${String(md.getMonth() + 1).padStart(2, "0")}`
      })
      return { label: `Q${q} ${qStart.getFullYear()}`, months, budgetKey: months[0], multiplier: 3 }
    }
    // year
    const year   = now.getFullYear() + periodOffset
    const months = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`)
    return { label: String(year), months, budgetKey: `${year}-01`, multiplier: 12 }
  }, [periodType, periodOffset])

  // Reset offset when switching period type
  useEffect(() => { setPeriodOffset(0) }, [periodType])

  // ── Budget persistence (stored as monthly amounts) ──────
  const storageKey = `budgets_${session.user.id}_${period.budgetKey}`
  const [budgets, setBudgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}") } catch { return {} }
  })
  useEffect(() => {
    try { setBudgets(JSON.parse(localStorage.getItem(storageKey) || "{}")) }
    catch { setBudgets({}) }
  }, [storageKey])

  function saveBudget(category, val) {
    const next = { ...budgets, [category]: Number(val) || 0 }
    setBudgets(next)
    localStorage.setItem(storageKey, JSON.stringify(next))
    setEditing(null)
    if (Number(val) > 0) onXPAward?.("budget_set") // dedup handled by unique index on xp_events
  }

  // Displayed budget = stored monthly amount × period multiplier
  const budgetDisplay = useCallback(
    (label) => (budgets[label] || 0) * period.multiplier,
    [budgets, period.multiplier]
  )

  // ── Actuals: sum over all months in period ──────────────
  // Category matching: user_category > direct CATEGORIES label match > raw Plaid category
  const actuals = useMemo(() => {
    const out = {}
    transactions.forEach(tx => {
      const d = parseMaybeISODate(tx.date)
      if (!d) return
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      if (!period.months.includes(m)) return
      if (tx.is_transfer || tx.user_group === "transfer" || getTxGroup(tx) === "transfer") return
      const ui  = formatTxForUI(tx)
      if (ui.amount >= 0) return  // skip income
      const raw = tx.user_category || ui.category || "Other"
      // Try to match to a known CATEGORIES label (case-insensitive)
      const matched = categories.find(c => c.label.toLowerCase() === raw.toLowerCase())?.label ?? raw
      out[matched] = (out[matched] || 0) + Math.abs(ui.amount)
    })
    return out
  }, [transactions, period.months, categories])

  // ── Monthly chart data (all transactions, last 12 months) ──
  const chartData = useMemo(() => {
    const byMonth = {}
    transactions.forEach(tx => {
      const d = parseMaybeISODate(tx.date)
      if (!d) return
      if (tx.is_transfer || tx.user_group === "transfer" || getTxGroup(tx) === "transfer") return
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      if (!byMonth[m]) byMonth[m] = { income: 0, expenses: 0 }
      const ui = formatTxForUI(tx)
      if (ui.amount > 0) byMonth[m].income   += ui.amount
      else               byMonth[m].expenses += Math.abs(ui.amount)
    })
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([m, { income, expenses }]) => {
        const [y, mo] = m.split("-")
        const label = new Date(Number(y), Number(mo) - 1, 1).toLocaleString("default", { month: "short" })
        return { month: label, income: Math.round(income), expenses: Math.round(expenses), savings: Math.round(income - expenses) }
      })
  }, [transactions])

  const BUDGET_GROUPS = [
    { id: "income",  label: "Income",  color: GROUP_STYLE.income.color  },
    { id: "needs",   label: "Needs",   color: GROUP_STYLE.needs.color   },
    { id: "wants",   label: "Wants",   color: GROUP_STYLE.wants.color   },
    { id: "savings", label: "Savings", color: GROUP_STYLE.savings.color },
  ]

  const [collapsedSubgroups, setCollapsedSubgroups] = useState(new Set())
  function toggleSubgroup(key) {
    setCollapsedSubgroups(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  // Per-group totals — budgeted already scaled by multiplier via budgetDisplay
  const groupTotals = useMemo(() => {
    const out = {}
    ;["income", "needs", "wants", "savings"].forEach(id => {
      const cats = categories.filter(c => c.group === id && !c.hidden)
      out[id] = {
        budgeted: cats.reduce((s, c) => s + budgetDisplay(c.label), 0),
        actual:   cats.reduce((s, c) => s + (actuals[c.label] || 0), 0),
      }
    })
    return out
  }, [budgets, actuals, categories, budgetDisplay])

  const colW = "2fr 1fr 1fr 1fr"

  // ── Budget alert — email when categories exceed budget ──
  useEffect(() => {
    if (!notifBudget || !transactions.length || !Object.keys(budgets).length) return
    // Only fire once per calendar day per session
    const today = new Date().toISOString().split("T")[0]
    const storageKey = `budget_alert_sent_${session.user.id}_${today}`
    if (sessionStorage.getItem(storageKey)) return

    const expenseCats = categories.filter(c => c.group !== "income" && !c.hidden)
    const overages = expenseCats
      .map(c => ({ category: c.label, budgeted: budgetDisplay(c.label), actual: actuals[c.label] || 0 }))
      .filter(o => o.budgeted > 0 && o.actual > o.budgeted)

    if (!overages.length) return

    sessionStorage.setItem(storageKey, "1")
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s?.access_token) return
      fetch(`${API_BASE}/api/notify/budget-alert`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${s.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ overages }),
      }).catch(() => {}) // silent fail — non-critical
    })
  }, [actuals, budgets, categories, notifBudget]) // eslint-disable-line

  // ── Spending trends (last 6 months by group) ───────────
  const [budgetTab, setBudgetTab] = useState("budget")
  const [sankeyDrill, setSankeyDrill] = useState(null) // null | { id, label, color }
  const [sankeyMode, setSankeyMode] = useState("paycheck") // "paycheck" | "budget" | "actual"

  const spendingTrends = useMemo(() => {
    const months = []
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`)
    }
    return months.map(ym => {
      const txs = transactions.filter(tx => tx.date?.startsWith(ym) && tx.amount > 0)
      const needs  = txs.filter(tx => (tx.user_group || CATEGORY_MAP[tx.category]?.group) === "needs").reduce((s, tx) => s + tx.amount, 0)
      const wants  = txs.filter(tx => (tx.user_group || CATEGORY_MAP[tx.category]?.group) === "wants").reduce((s, tx) => s + tx.amount, 0)
      const savings = txs.filter(tx => (tx.user_group || CATEGORY_MAP[tx.category]?.group) === "savings").reduce((s, tx) => s + tx.amount, 0)
      const income = transactions.filter(tx => tx.date?.startsWith(ym) && tx.amount < 0).reduce((s, tx) => s + (-tx.amount), 0)
      const label = new Date(ym + "-01").toLocaleString("default", { month: "short" })
      return { month: ym, label, needs: Math.round(needs), wants: Math.round(wants), savings: Math.round(savings), income: Math.round(income), total: Math.round(needs + wants + savings) }
    })
  }, [transactions])

  // ── Category row renderer (shared between flat + subgrouped) ──
  const paycheckPm = getPaycheckMonthly(paychecks || [])
  function renderCatRow(cat, i, total, isIncome, groupColor) {
    const budgeted  = budgetDisplay(cat.label)
    const actual    = actuals[cat.label] || 0
    const remaining = isIncome ? actual - budgeted : budgeted - actual
    const isOver    = budgeted > 0 && !isIncome && actual > budgeted
    const isUnder   = budgeted > 0 && isIncome && actual < budgeted
    const isEditing = editing === cat.label
    const paycheckSuggestion = cat.label === "Paycheck" && paycheckPm.net > 0 ? Math.round(paycheckPm.net) : null

    function openDrill() {
      const txs = transactions.filter(tx => {
        const d = parseMaybeISODate(tx.date)
        if (!d) return false
        const m = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`
        if (!period.months.includes(m)) return false
        const ui  = formatTxForUI(tx)
        const raw = tx.user_category || ui.category || "Other"
        const matched = categories.find(c => c.label.toLowerCase() === raw.toLowerCase())?.label ?? raw
        return matched === cat.label
      }).map(tx => {
        const ui = formatTxForUI(tx)
        return { id: tx.id, name: tx.merchant_name || tx.name, amount: Math.abs(ui.amount), rawDate: tx.date, category: tx.user_category || ui.category, group: tx.user_group || ui.group }
      })
      setDrillDown({ label: cat.label, txs })
    }

    return (
      <div key={cat.label} style={{
        display: "grid", gridTemplateColumns: colW,
        padding: "11px 20px",
        borderBottom: i < total - 1 ? `1px solid ${t.border}` : "none",
        alignItems: "center",
        background: isEditing ? (t.isDark ? "rgba(110,231,183,0.04)" : "rgba(47,133,90,0.03)") : "transparent",
        transition: "background 0.15s",
      }}>
        {/* Name */}
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t.text }}>
          <span style={{ fontSize: 16 }}>{cat.emoji}</span>
          {cat.label}
          {paycheckSuggestion && !isEditing && budgeted === 0 && (
            <span style={{ fontSize: 11, color: t.accent, background: t.isDark ? "rgba(110,231,183,0.1)" : "rgba(47,133,90,0.08)", padding: "1px 7px", borderRadius: 99, fontWeight: 600, whiteSpace: "nowrap" }}>
              💡 {fmtMoney(paycheckSuggestion)}/mo
            </span>
          )}
        </span>

        {/* Budgeted */}
        <div style={{ textAlign: "right" }}>
          {isEditing ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
              {paycheckSuggestion && (
                <button
                  onClick={() => setEditVal(String(paycheckSuggestion))}
                  style={{ fontSize: 11, color: t.accent, background: t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.1)", border: "none", padding: "2px 7px", borderRadius: 6, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Use {fmtMoney(paycheckSuggestion)}
                </button>
              )}
              <input
                autoFocus
                type="number"
                value={editVal}
                onChange={e => setEditVal(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") saveBudget(cat.label, editVal)
                  if (e.key === "Escape") setEditing(null)
                }}
                onBlur={() => saveBudget(cat.label, editVal)}
                style={{ width: 90, textAlign: "right", padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.accent}`, background: t.surface, color: t.text, fontSize: 13, outline: "none" }}
              />
            </div>
          ) : (
            <button
              onClick={() => { setEditing(cat.label); setEditVal(budgeted > 0 ? String(Math.round(budgeted / period.multiplier)) : "") }}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: budgeted > 0 ? t.text : t.textFaint, fontWeight: budgeted > 0 ? 600 : 400, padding: "2px 4px", borderRadius: 4, textAlign: "right", width: "100%" }}
            >
              {budgeted > 0 ? fmtMoney(budgeted) : "—"}
            </button>
          )}
        </div>

        {/* Actual */}
        <div style={{ textAlign: "right" }}>
          <button
            onClick={() => actual > 0 && openDrill()}
            style={{ background: "none", border: "none", cursor: actual > 0 ? "pointer" : "default", fontSize: 13, color: actual > 0 ? t.text : t.textFaint, fontWeight: actual > 0 ? 600 : 400, padding: "2px 4px", borderRadius: 4, textAlign: "right", width: "100%" }}
          >
            {actual > 0 ? fmtMoney(actual) : "—"}
          </button>
        </div>

        {/* Remaining */}
        <div style={{ textAlign: "right", fontSize: 13, fontWeight: budgeted > 0 ? 600 : 400, color: budgeted === 0 ? t.textFaint : isOver ? "#EF4444" : isUnder ? "#F59E0B" : t.accent }}>
          {budgeted > 0 ? fmtMoney(remaining) : "—"}
        </div>
      </div>
    )
  }

  return (
    <>
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6, color: t.text }}>Budget</h1>
          <p style={{ color: t.textMuted, fontSize: 14 }}>Planned vs. actual · stay on track 🎯</p>
        </div>

        {/* Period controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Type toggle */}
          <div style={{ display: "flex", background: t.hover, borderRadius: 10, padding: 3, gap: 2 }}>
            {["month", "quarter", "year"].map(pt => (
              <button
                key={pt}
                onClick={() => setPeriodType(pt)}
                style={{
                  padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: periodType === pt ? t.surface : "transparent",
                  color: periodType === pt ? t.text : t.textMuted,
                  fontSize: 12, fontWeight: periodType === pt ? 700 : 500,
                  boxShadow: periodType === pt ? `0 1px 3px rgba(0,0,0,0.15)` : "none",
                  transition: "all 0.15s",
                  textTransform: "capitalize",
                }}
              >
                {pt}
              </button>
            ))}
          </div>

          {/* Prev / label / Next */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => setPeriodOffset(p => p - 1)}
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >‹</button>
            <span style={{ minWidth: 130, textAlign: "center", fontSize: 13, fontWeight: 700, color: t.text }}>{period.label}</span>
            <button
              onClick={() => setPeriodOffset(p => p + 1)}
              disabled={periodOffset >= 0}
              style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: periodOffset >= 0 ? t.textFaint : t.text, fontSize: 16, cursor: periodOffset >= 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >›</button>
          </div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[["budget", "📋 Budget"], ["sankey", "💸 Income Flow (Sankey)"], ["trends", "📈 Spending Trends"]].map(([id, label]) => (
          <button key={id} onClick={() => setBudgetTab(id)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${budgetTab === id ? t.accent : t.border}`, background: budgetTab === id ? (t.isDark ? "rgba(110,231,183,0.1)" : "rgba(47,133,90,0.08)") : "transparent", color: budgetTab === id ? t.accent : t.textMuted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      {budgetTab === "sankey" && (() => {
        const fmt$ = v => `$${Math.round(v).toLocaleString()}`
        const textColor = t.isDark ? "#e2e8f0" : "#1a202c"
        const subColor  = t.isDark ? "#94a3b8" : "#64748b"
        const NW = 14, PAD_V = 40, GAP = 10

        const modePicker = (
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {[["paycheck","💼 Paycheck (gross)"],["budget","📋 Budget (planned)"],["actual","📊 Actual"]].map(([id,lbl]) => (
              <button key={id} onClick={() => { setSankeyMode(id); setSankeyDrill(null) }}
                style={{ padding:"6px 14px", borderRadius:7, border:`1px solid ${sankeyMode===id?t.accent:t.border}`, background:sankeyMode===id?(t.isDark?"rgba(110,231,183,0.1)":"rgba(47,133,90,0.08)"):"transparent", color:sankeyMode===id?t.accent:t.textMuted, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                {lbl}
              </button>
            ))}
          </div>
        )

        // ── BUDGET MODE ──────────────────────────────────────────────────
        if (sankeyMode === "budget") {
          const pm2 = getPaycheckMonthly(paychecks || [])
          const takeHome = pm2.net > 0 ? pm2.net : categories.reduce((s,c) => s + budgetDisplay(c.label), 0)
          const sourceLabel = pm2.net > 0 ? "Take-home" : "Total Budgeted"
          const SPEND_GROUPS = [
            { id:"needs",   label:"Needs",   color:GROUP_STYLE.needs.color   },
            { id:"wants",   label:"Wants",   color:GROUP_STYLE.wants.color   },
            { id:"savings", label:"Savings", color:GROUP_STYLE.savings.color },
          ]
          let col1Defs, col1Header, sourceVal, sourceLabel2
          if (sankeyDrill) {
            col1Defs = categories.filter(c => c.group===sankeyDrill.id && budgetDisplay(c.label)>0)
              .map(c => ({ id:c.label, label:c.label, color:sankeyDrill.color, value:budgetDisplay(c.label) }))
            col1Header = sankeyDrill.label.toUpperCase() + " CATEGORIES"
            sourceVal = groupTotals[sankeyDrill.id]?.budgeted || 0
            sourceLabel2 = sankeyDrill.label
          } else {
            col1Defs = []; let used = 0
            SPEND_GROUPS.forEach(g => { const amt=groupTotals[g.id]?.budgeted||0; if(amt>0){ col1Defs.push({...g,value:amt}); used+=amt } })
            const unalloc = Math.max(0, takeHome - used)
            if (unalloc > 1) col1Defs.push({ id:"unalloc", label:"Unallocated", color:"#6B7280", value:unalloc })
            col1Header = "BUDGET GROUPS"; sourceVal = takeHome; sourceLabel2 = sourceLabel
          }
          if (takeHome <= 0) return (
            <div style={{ ...card, padding:"24px", marginBottom:20 }}>
              {modePicker}
              <div style={{ padding:"40px 20px", textAlign:"center", color:t.textFaint }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
                <p style={{ fontSize:15, fontWeight:600, color:t.text, marginBottom:6 }}>No budget data yet</p>
                <p style={{ fontSize:13 }}>Set budget amounts and a paycheck to see your planned flow.</p>
              </div>
            </div>
          )
          const H = Math.max(280, col1Defs.length*52+80), W=700, C0X=140, C1X=560
          const srcNode = { id:"src", value:sourceVal, color:GROUP_STYLE.income.color, x:C0X-NW/2, y:PAD_V, h:H-PAD_V*2 }
          const total2 = col1Defs.reduce((s,n)=>s+n.value,0), avail2 = H-PAD_V*2-GAP*(col1Defs.length-1)
          let yy=PAD_V
          const cNodes = col1Defs.map(n => { const h=Math.max(18,(n.value/total2)*avail2); const nd={...n,x:C1X-NW/2,y:yy,h}; yy+=h+GAP; return nd })
          const rf={src:0}, lf={}; cNodes.forEach(n=>{ rf[n.id]=0; lf[n.id]=0 })
          const ribbons2 = cNodes.map(tg => {
            if(tg.value<=0) return null
            const sh=Math.min((tg.value/srcNode.value)*srcNode.h, srcNode.h-rf.src)
            const th=Math.min(1*tg.h, tg.h-(lf[tg.id]||0))
            const y0a=srcNode.y+rf.src, y0b=y0a+sh, y1a=tg.y+(lf[tg.id]||0), y1b=y1a+th
            rf.src+=sh; lf[tg.id]=(lf[tg.id]||0)+th
            const x0=srcNode.x+NW, x1=tg.x, mx=x0+(x1-x0)*0.5
            return { d:`M${x0},${y0a} C${mx},${y0a} ${mx},${y1a} ${x1},${y1a} L${x1},${y1b} C${mx},${y1b} ${mx},${y0b} ${x0},${y0b}Z`, color:tg.color }
          }).filter(Boolean)
          return (
            <div style={{ ...card, padding:"24px 24px 16px", marginBottom:20 }}>
              {modePicker}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                <p style={{ fontSize:14, fontWeight:700, color:t.text, margin:0 }}>Budget Flow (Planned)</p>
                {sankeyDrill && <button onClick={()=>setSankeyDrill(null)} style={{ fontSize:12, color:t.accent, background:"none", border:`1px solid ${t.accent}44`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontWeight:600 }}>← Back</button>}
              </div>
              <p style={{ fontSize:12, color:t.textMuted, marginBottom:16 }}>{sankeyDrill?"← click Back to return":"Take-home → planned spending · click any group to drill into categories"}</p>
              <div style={{ overflowX:"auto" }}>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", minWidth:400, display:"block" }}>
                  <defs>{ribbons2.map((r,i)=><linearGradient key={i} id={`bg${i}`} x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={r.color} stopOpacity="0.5"/><stop offset="100%" stopColor={r.color} stopOpacity="0.2"/></linearGradient>)}</defs>
                  {ribbons2.map((r,i)=><path key={i} d={r.d} fill={`url(#bg${i})`} stroke="none"/>)}
                  <rect x={srcNode.x} y={srcNode.y} width={NW} height={srcNode.h} rx={4} fill={srcNode.color}/>
                  <text x={srcNode.x-10} y={srcNode.y+srcNode.h/2-7} textAnchor="end" fill={textColor} fontSize={13} fontWeight={700}>{sourceLabel2}</text>
                  <text x={srcNode.x-10} y={srcNode.y+srcNode.h/2+9} textAnchor="end" fill={subColor} fontSize={12}>{fmt$(sourceVal)}</text>
                  {cNodes.map(n => {
                    const canDrill = !sankeyDrill && ["needs","wants","savings"].includes(n.id)
                    return (
                      <g key={n.id} onClick={canDrill?()=>setSankeyDrill({id:n.id,label:n.label,color:n.color}):undefined} style={{ cursor:canDrill?"pointer":"default" }}>
                        <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill={n.color}/>
                        {canDrill&&<rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill="white" fillOpacity={0.1}/>}
                        <text x={n.x+NW+10} y={n.y+n.h/2-(n.h>28?7:0)} textAnchor="start" fill={textColor} fontSize={12} fontWeight={600}>{n.label}{canDrill?" ›":""}</text>
                        {n.h>22&&<text x={n.x+NW+10} y={n.y+n.h/2+9} textAnchor="start" fill={subColor} fontSize={11}>{fmt$(n.value)}</text>}
                      </g>
                    )
                  })}
                  <text x={C0X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{sourceLabel2.toUpperCase()}</text>
                  <text x={C1X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{col1Header}</text>
                </svg>
              </div>
            </div>
          )
        }

        // ── ACTUAL MODE ──────────────────────────────────────────────────
        if (sankeyMode === "actual") {
          const incomeActual = groupTotals.income?.actual || 0
          const SPEND_GROUPS = [
            { id:"needs",   label:"Needs",   color:GROUP_STYLE.needs.color   },
            { id:"wants",   label:"Wants",   color:GROUP_STYLE.wants.color   },
            { id:"savings", label:"Savings", color:GROUP_STYLE.savings.color },
          ]
          let col1Defs, col1Header, sourceVal, srcLabel
          if (sankeyDrill) {
            col1Defs = categories.filter(c => c.group===sankeyDrill.id && (actuals[c.label]||0)>0)
              .map(c => ({ id:c.label, label:c.label, color:sankeyDrill.color, value:actuals[c.label]||0 }))
            col1Header = sankeyDrill.label.toUpperCase() + " CATEGORIES"
            sourceVal = groupTotals[sankeyDrill.id]?.actual || 0
            srcLabel = sankeyDrill.label
          } else {
            col1Defs = []; let used = 0
            SPEND_GROUPS.forEach(g => { const amt=groupTotals[g.id]?.actual||0; if(amt>0){ col1Defs.push({...g,value:amt}); used+=amt } })
            const unaccounted = Math.max(0, incomeActual - used)
            if (unaccounted > 1) col1Defs.push({ id:"unaccounted", label:"Unaccounted", color:"#6B7280", value:unaccounted })
            col1Header = "SPENDING GROUPS"; sourceVal = incomeActual; srcLabel = "Income"
          }
          if (sourceVal <= 0 || col1Defs.length === 0) return (
            <div style={{ ...card, padding:"24px", marginBottom:20 }}>
              {modePicker}
              <div style={{ padding:"40px 20px", textAlign:"center", color:t.textFaint }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📊</div>
                <p style={{ fontSize:15, fontWeight:600, color:t.text, marginBottom:6 }}>No transaction data yet</p>
                <p style={{ fontSize:13 }}>Sync your accounts to see your actual money flow.</p>
              </div>
            </div>
          )
          const H = Math.max(280, col1Defs.length*52+80), W=700, C0X=140, C1X=560
          const srcNode = { id:"income", value:sourceVal, color:GROUP_STYLE.income.color, x:C0X-NW/2, y:PAD_V, h:H-PAD_V*2 }
          const total3 = col1Defs.reduce((s,n)=>s+n.value,0), avail3 = H-PAD_V*2-GAP*(col1Defs.length-1)
          let yy=PAD_V
          const cNodes = col1Defs.map(n => { const h=Math.max(18,(n.value/total3)*avail3); const nd={...n,x:C1X-NW/2,y:yy,h}; yy+=h+GAP; return nd })
          const rf={income:0}, lf={}; cNodes.forEach(n=>{ lf[n.id]=0 })
          const ribbons3 = cNodes.map(tg => {
            if(tg.value<=0) return null
            const sh=Math.min((tg.value/srcNode.value)*srcNode.h, srcNode.h-rf.income)
            const th=tg.h
            const y0a=srcNode.y+rf.income, y0b=y0a+sh, y1a=tg.y, y1b=y1a+th
            rf.income+=sh
            const x0=srcNode.x+NW, x1=tg.x, mx=x0+(x1-x0)*0.5
            return { d:`M${x0},${y0a} C${mx},${y0a} ${mx},${y1a} ${x1},${y1a} L${x1},${y1b} C${mx},${y1b} ${mx},${y0b} ${x0},${y0b}Z`, color:tg.color }
          }).filter(Boolean)
          return (
            <div style={{ ...card, padding:"24px 24px 16px", marginBottom:20 }}>
              {modePicker}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                <p style={{ fontSize:14, fontWeight:700, color:t.text, margin:0 }}>Actual Flow</p>
                {sankeyDrill && <button onClick={()=>setSankeyDrill(null)} style={{ fontSize:12, color:t.accent, background:"none", border:`1px solid ${t.accent}44`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontWeight:600 }}>← Back</button>}
              </div>
              <p style={{ fontSize:12, color:t.textMuted, marginBottom:16 }}>{sankeyDrill?"← click Back to return":"Actual income → actual spending · click any group to drill into categories"}</p>
              <div style={{ overflowX:"auto" }}>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", minWidth:400, display:"block" }}>
                  <defs>{ribbons3.map((r,i)=><linearGradient key={i} id={`ag${i}`} x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={r.color} stopOpacity="0.5"/><stop offset="100%" stopColor={r.color} stopOpacity="0.2"/></linearGradient>)}</defs>
                  {ribbons3.map((r,i)=><path key={i} d={r.d} fill={`url(#ag${i})`} stroke="none"/>)}
                  <rect x={srcNode.x} y={srcNode.y} width={NW} height={srcNode.h} rx={4} fill={srcNode.color}/>
                  <text x={srcNode.x-10} y={srcNode.y+srcNode.h/2-7} textAnchor="end" fill={textColor} fontSize={13} fontWeight={700}>{srcLabel}</text>
                  <text x={srcNode.x-10} y={srcNode.y+srcNode.h/2+9} textAnchor="end" fill={subColor} fontSize={12}>{fmt$(sourceVal)}</text>
                  {cNodes.map(n => {
                    const canDrill = !sankeyDrill && ["needs","wants","savings"].includes(n.id)
                    return (
                      <g key={n.id} onClick={canDrill?()=>setSankeyDrill({id:n.id,label:n.label,color:n.color}):undefined} style={{ cursor:canDrill?"pointer":"default" }}>
                        <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill={n.color}/>
                        {canDrill&&<rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill="white" fillOpacity={0.1}/>}
                        <text x={n.x+NW+10} y={n.y+n.h/2-(n.h>28?7:0)} textAnchor="start" fill={textColor} fontSize={12} fontWeight={600}>{n.label}{canDrill?" ›":""}</text>
                        {n.h>22&&<text x={n.x+NW+10} y={n.y+n.h/2+9} textAnchor="start" fill={subColor} fontSize={11}>{fmt$(n.value)}</text>}
                      </g>
                    )
                  })}
                  <text x={C0X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{srcLabel.toUpperCase()}</text>
                  <text x={C1X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{col1Header}</text>
                </svg>
              </div>
            </div>
          )
        }

        // ── PAYCHECK MODE (existing) ──────────────────────────────────────
        const pm = getPaycheckMonthly(paychecks || [])
        const gross = pm.gross

        if (gross <= 0) return (
          <div style={{ ...card, padding: "24px", marginBottom: 20 }}>
            {modePicker}
            <div style={{ padding:"40px 20px", textAlign:"center", color:t.textFaint }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>💸</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 6 }}>No paycheck data yet</p>
              <p style={{ fontSize: 13 }}>Set up a paycheck in the Paycheck tab to see your income flow.</p>
            </div>
          </div>
        )

        const takeHome = pm.net

        // ── Build per-item monthly breakdowns from paychecks ──
        const merged = (items) => {
          const m = {}
          items.forEach(d => { m[d.id] = { ...d, value: (m[d.id]?.value||0) + d.value } })
          return Object.values(m).filter(d => d.value > 0)
        }
        const taxBreakdown = merged((paychecks||[]).flatMap(p => {
          const mo = FREQ_TO_MONTHLY[p.frequency]||2.167
          return [
            { id:"federal", label:"Federal Tax",      color:"#EF4444", value:(p.federalTax||0)*mo },
            { id:"state",   label:"State Tax",         color:"#F87171", value:(p.stateTax||0)*mo },
            { id:"ss",      label:"Social Security",   color:"#FCA5A5", value:(p.socialSecurity||0)*mo },
            { id:"medicare",label:"Medicare",          color:"#FECACA", value:(p.medicare||0)*mo },
          ]
        }))
        const k401Breakdown = merged((paychecks||[]).flatMap(p => {
          const mo = FREQ_TO_MONTHLY[p.frequency]||2.167
          const g = p.payType==="hourly" ? (p.hourlyRate||0)*(p.typicalHours||0) : (p.gross||0)
          const { contrib, match } = calc401k(p, g)
          return [
            { id:"contrib", label:"Your Contribution", color:"#8B5CF6", value: contrib*mo },
            { id:"match",   label:"Employer Match",    color:"#A78BFA", value: match*mo },
          ]
        }))
        const benefitsBreakdown = merged((paychecks||[]).flatMap(p => {
          const mo = FREQ_TO_MONTHLY[p.frequency]||2.167
          return [
            { id:"health", label:"Health Insurance", color:"#F59E0B", value:(p.healthInsurance||0)*mo },
            { id:"dental", label:"Dental",           color:"#FCD34D", value:(p.dental||0)*mo },
            { id:"vision", label:"Vision",           color:"#FDE68A", value:(p.vision||0)*mo },
            ...(p.postTaxOther||[]).map((r,i)=>({ id:`ptx${i}`, label:r.label||"Other", color:"#F59E0B", value:(r.amount||0)*mo })),
          ]
        }))

        const col1DrillMap = { taxes: taxBreakdown, k401: k401Breakdown, benefits: benefitsBreakdown }
        const col1Clickable = ["taxes","k401","benefits"]

        // ── Build col1 and col2 defs based on drill state ──
        const isDrillCol1 = sankeyDrill?.col === 1
        const isDrillCol2 = sankeyDrill?.col === 2

        const col1BaseDefs = [
          { id:"takehome", label:"Take-home", color:GROUP_STYLE.income.color, value:takeHome },
          ...(pm.taxes > 0       ? [{ id:"taxes",    label:"Taxes",    color:"#EF4444", value:pm.taxes }] : []),
          ...(pm.contrib401k > 0 ? [{ id:"k401",     label:"401k",     color:"#8B5CF6", value:pm.contrib401k+pm.match401k }] : []),
          ...(pm.hsa > 0         ? [{ id:"hsa",      label:"HSA",      color:"#06B6D4", value:pm.hsa }] : []),
          ...(pm.postTaxDed > 0  ? [{ id:"benefits", label:"Benefits", color:"#F59E0B", value:pm.postTaxDed }] : []),
        ]

        let col1Defs = isDrillCol1 ? col1DrillMap[sankeyDrill.id] || col1BaseDefs : col1BaseDefs
        let col1Source = "gross"
        let col1Header = isDrillCol1 ? sankeyDrill.label.toUpperCase() : "DEDUCTIONS"

        // In col1 drill, gross flows entirely into the drilled node, which then fans out
        // We use a bridge approach: gross→bridge, bridge→sub-items
        const col1BridgeDef = isDrillCol1
          ? col1BaseDefs.find(n => n.id === sankeyDrill.id)
          : null

        let col2Defs = []
        let col2Header = "SPENDING"
        let col2Source = isDrillCol1 ? "takehome" : (isDrillCol2 ? sankeyDrill.id : "takehome")
        let takeHomeUsed = 0

        if (isDrillCol2) {
          categories.filter(c => c.group === sankeyDrill.id).forEach(c => {
            const amt = actuals[c.label] || 0
            if (amt <= 0) return
            col2Defs.push({ id:c.label, label:c.label, color:sankeyDrill.color, value:amt })
            takeHomeUsed += amt
          })
          col2Header = sankeyDrill.label.toUpperCase()
        } else {
          BUDGET_GROUPS.filter(g => g.id !== "income").forEach(({ id, label, color }) => {
            const amt = groupTotals[id]?.actual || 0
            if (amt <= 0) return
            col2Defs.push({ id, label, color, value:amt })
            takeHomeUsed += amt
          })
          const unaccounted = Math.max(0, takeHome - takeHomeUsed)
          if (unaccounted > 1) col2Defs.push({ id:"unspent", label:"Unspent", color:"#6B7280", value:unaccounted })
        }

        // ── SVG layout ──
        const maxNodes = Math.max(col1Defs.length, col2Defs.length)
        const H = Math.max(420, maxNodes * 52 + 80)
        const W = 820
        const C0X = 140, C1X = 370, C2X = 650

        function layoutNodes(defs, cx) {
          const total = defs.reduce((s,n)=>s+n.value, 0)
          const avail = H - PAD_V*2 - GAP*(defs.length-1)
          let y = PAD_V
          return defs.map(n => {
            const h = Math.max(18, (n.value/total)*avail)
            const node = { ...n, x:cx-NW/2, y, h }
            y += h + GAP
            return node
          })
        }

        const grossNode = { id:"gross", color:GROUP_STYLE.income.color, value:gross, x:C0X-NW/2, y:PAD_V, h:H-PAD_V*2 }

        // For col1 drill: we show all base col1 nodes but only the drilled one is expanded in the middle
        // Approach: gross→bridge(same as base col1 node)→sub-items; other base nodes not shown in col1
        // Simpler: col1Defs = sub-items of drilled node; gross links directly to bridge, bridge to sub-items
        const col1Nodes = layoutNodes(col1Defs, C1X)

        // Bridge node for col1 drill (sits between gross and sub-items, invisible — just for routing)
        const bridgeCol1 = isDrillCol1 && col1BridgeDef ? (() => {
          // Place at midpoint, proportional to value
          const midX = (C0X + C1X) / 2
          const ratio = col1BridgeDef.value / gross
          const h = Math.max(18, ratio * (H - PAD_V*2))
          return { ...col1BridgeDef, x: midX - NW/2, y: PAD_V + (H-PAD_V*2-h)/2, h }
        })() : null

        // Bridge node for col2 drill
        const bridgeCol2 = isDrillCol2 ? (() => {
          const thNode = col1Nodes.find(n=>n.id==="takehome")
          if (!thNode) return null
          const ratio = (groupTotals[sankeyDrill.id]?.actual||0) / takeHome
          const h = Math.max(18, ratio * thNode.h)
          return { ...sankeyDrill, x:(C1X+C2X)/2-NW/2, y:thNode.y, h }
        })() : null

        // Constrain col2 (spending) to the vertical range of the Take-home node
        // so ribbons flow straight across without crossing
        const thNode = col1Nodes.find(n => n.id === "takehome")
        const col2Nodes = (() => {
          if (!thNode || col2Defs.length === 0) return layoutNodes(col2Defs, C2X)
          const total = col2Defs.reduce((s, n) => s + n.value, 0)
          const avail = Math.max(0, thNode.h - GAP * (col2Defs.length - 1))
          let y = thNode.y
          return col2Defs.map(n => {
            const h = Math.max(18, (n.value / total) * avail)
            const node = { ...n, x: C2X - NW / 2, y, h }
            y += h + GAP
            return node
          })
        })()
        const allNodes = [grossNode, ...col1Nodes, ...(bridgeCol1?[bridgeCol1]:[]), ...(bridgeCol2?[bridgeCol2]:[]), ...col2Nodes]
        const nodeMap = Object.fromEntries(allNodes.map(n=>[n.id,n]))

        // ── Links ──
        let links = []
        if (isDrillCol1 && bridgeCol1) {
          // gross → bridge → sub-items (also show take-home going to col2)
          links = [
            { source:"gross", target:bridgeCol1.id, value:col1BridgeDef.value, color:col1BridgeDef.color },
            ...col1Defs.map(n=>({ source:bridgeCol1.id, target:n.id, value:n.value, color:n.color })),
            // also show take-home → spending if take-home not drilled
            ...(col1Nodes.find(n=>n.id==="takehome") ? col2Defs.map(n=>({ source:"takehome", target:n.id, value:n.value, color:n.color })) : []),
          ]
          // Add take-home as ghost node in col1 too? No — keep it clean. Just show the drill.
          // Actually the layout already puts sub-items in col1; remove takehome from col1 links
          links = [
            { source:"gross", target:bridgeCol1.id, value:col1BridgeDef.value, color:col1BridgeDef.color },
            ...col1Defs.map(n=>({ source:bridgeCol1.id, target:n.id, value:n.value, color:n.color })),
          ]
        } else {
          links = [
            { source:"gross", target:"takehome", value:takeHome, color:GROUP_STYLE.income.color },
            ...(pm.taxes > 0       ? [{ source:"gross", target:"taxes",    value:pm.taxes,                        color:"#EF4444" }] : []),
            ...(pm.contrib401k > 0 ? [{ source:"gross", target:"k401",     value:pm.contrib401k+pm.match401k,     color:"#8B5CF6" }] : []),
            ...(pm.hsa > 0         ? [{ source:"gross", target:"hsa",      value:pm.hsa,                          color:"#06B6D4" }] : []),
            ...(pm.postTaxDed > 0  ? [{ source:"gross", target:"benefits", value:pm.postTaxDed,                   color:"#F59E0B" }] : []),
            ...(isDrillCol2 && bridgeCol2 ? [{ source:"takehome", target:sankeyDrill.id, value:bridgeCol2.h/col1Nodes.find(n=>n.id==="takehome")?.h*takeHome||0, color:sankeyDrill.color }] : []),
            ...col2Defs.map(n=>({ source:col2Source, target:n.id, value:n.value, color:n.color })),
          ]
        }

        const rightFill = Object.fromEntries(allNodes.map(n=>[n.id,0]))
        const leftFill  = { ...rightFill }

        const ribbons = links.map(lk => {
          const s = nodeMap[lk.source], tg = nodeMap[lk.target]
          if (!s || !tg || lk.value <= 0) return null
          const sh = Math.min((lk.value/s.value)*s.h, s.h - rightFill[s.id])
          const th = Math.min((lk.value/tg.value)*tg.h, tg.h - leftFill[tg.id])
          const y0a = s.y+rightFill[s.id], y0b = y0a+sh
          const y1a = tg.y+leftFill[tg.id], y1b = y1a+th
          rightFill[s.id] += sh
          leftFill[tg.id] += th
          const x0=s.x+NW, x1=tg.x, mx=x0+(x1-x0)*0.45
          return { d:`M${x0},${y0a} C${mx},${y0a} ${mx},${y1a} ${x1},${y1a} L${x1},${y1b} C${mx},${y1b} ${mx},${y0b} ${x0},${y0b}Z`, color:lk.color }
        }).filter(Boolean)

        const drillLabel = sankeyDrill
          ? (sankeyDrill.col===1 ? sankeyDrill.label : sankeyDrill.label) + " breakdown"
          : "Gross → deductions → spending · click any node to drill down"

        return (
          <div style={{ ...card, padding:"24px 24px 16px", marginBottom:20 }}>
            {modePicker}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
              <p style={{ fontSize:14, fontWeight:700, color:t.text, margin:0 }}>Income Flow — Paycheck (Gross)</p>
              {sankeyDrill && (
                <button onClick={() => setSankeyDrill(null)}
                  style={{ fontSize:12, color:t.accent, background:"none", border:`1px solid ${t.accent}44`, borderRadius:6, padding:"4px 10px", cursor:"pointer", fontWeight:600 }}>
                  ← Back
                </button>
              )}
            </div>
            <p style={{ fontSize:12, color:t.textMuted, marginBottom:20 }}>{drillLabel}</p>
            <div style={{ overflowX:"auto" }}>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", minWidth:560, display:"block" }}>
                <defs>
                  {ribbons.map((r,i) => (
                    <linearGradient key={i} id={`rg${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={r.color} stopOpacity="0.5"/>
                      <stop offset="100%" stopColor={r.color} stopOpacity="0.2"/>
                    </linearGradient>
                  ))}
                </defs>

                {ribbons.map((r,i) => <path key={i} d={r.d} fill={`url(#rg${i})`} stroke="none"/>)}

                {/* Gross */}
                <rect x={grossNode.x} y={grossNode.y} width={NW} height={grossNode.h} rx={4} fill={grossNode.color}/>
                <text x={grossNode.x-10} y={grossNode.y+grossNode.h/2-7} textAnchor="end" fill={textColor} fontSize={13} fontWeight={700}>Gross</text>
                <text x={grossNode.x-10} y={grossNode.y+grossNode.h/2+9} textAnchor="end" fill={subColor} fontSize={12}>{fmt$(gross)}</text>

                {/* Col 1 nodes */}
                {col1Nodes.map(n => {
                  const canDrill = !sankeyDrill && col1Clickable.includes(n.id)
                  return (
                    <g key={n.id} onClick={canDrill ? () => setSankeyDrill({ col:1, id:n.id, label:n.label, color:n.color }) : undefined}
                      style={{ cursor:canDrill?"pointer":"default" }}>
                      <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill={n.color}/>
                      {canDrill && <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill="white" fillOpacity={0.1}/>}
                      <text x={n.x+NW+10} y={n.y+n.h/2-(n.h>28?7:0)} textAnchor="start" fill={textColor} fontSize={12} fontWeight={600}>
                        {n.label}{canDrill?" ›":""}
                      </text>
                      {n.h>22 && <text x={n.x+NW+10} y={n.y+n.h/2+9} textAnchor="start" fill={subColor} fontSize={11}>{fmt$(n.value)}</text>}
                    </g>
                  )
                })}

                {/* Col 2 nodes */}
                {col2Nodes.map(n => {
                  const canDrill = !sankeyDrill && ["needs","wants","savings"].includes(n.id)
                  return (
                    <g key={n.id} onClick={canDrill ? () => setSankeyDrill({ col:2, id:n.id, label:n.label, color:n.color }) : undefined}
                      style={{ cursor:canDrill?"pointer":"default" }}>
                      <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill={n.color}/>
                      {canDrill && <rect x={n.x} y={n.y} width={NW} height={n.h} rx={4} fill="white" fillOpacity={0.1}/>}
                      <text x={n.x+NW+10} y={n.y+n.h/2-(n.h>28?7:0)} textAnchor="start" fill={textColor} fontSize={12} fontWeight={600}>
                        {n.label}{canDrill?" ›":""}
                      </text>
                      {n.h>22 && <text x={n.x+NW+10} y={n.y+n.h/2+9} textAnchor="start" fill={subColor} fontSize={11}>{fmt$(n.value)}</text>}
                    </g>
                  )
                })}

                {/* Column headers */}
                <text x={C0X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>GROSS</text>
                <text x={C1X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{col1Header}</text>
                <text x={C2X} y={18} textAnchor="middle" fill={subColor} fontSize={10} fontWeight={700}>{col2Header}</text>
              </svg>
            </div>
          </div>
        )
      })()}

      {budgetTab === "trends" && (() => {
        const prevMonth = spendingTrends[spendingTrends.length - 2] || {}
        const currMonth = spendingTrends[spendingTrends.length - 1] || {}
        const groups = [
          { key: "needs",   label: "Needs",   color: "#3B82F6" },
          { key: "wants",   label: "Wants",   color: "#F59E0B" },
          { key: "savings", label: "Savings", color: "#8B5CF6" },
        ]
        return (
          <div>
            {/* MoM summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
              {groups.map(g => {
                const curr = currMonth[g.key] || 0
                const prev = prevMonth[g.key] || 0
                const delta = curr - prev
                const deltaPct = prev > 0 ? ((delta / prev) * 100).toFixed(0) : null
                return (
                  <div key={g.key} style={{ ...card, padding: "16px 20px", borderTop: `3px solid ${g.color}` }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{g.label}</p>
                    <p style={{ fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 4 }}>{fmtMoney(curr)}</p>
                    {deltaPct !== null && (
                      <p style={{ fontSize: 12, color: delta > 0 ? "#EF4444" : "#2F855A", fontWeight: 600 }}>
                        {delta > 0 ? "▲" : "▼"} {fmtMoney(Math.abs(delta))} ({Math.abs(deltaPct)}%) vs last month
                      </p>
                    )}
                    {deltaPct === null && <p style={{ fontSize: 12, color: t.textFaint }}>No prior month data</p>}
                  </div>
                )
              })}
            </div>

            {/* Stacked bar chart */}
            <div style={{ ...card, padding: "20px 20px 8px", marginBottom: 20 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 16 }}>Spending by Category — Last 6 Months</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={spendingTrends} barCategoryGap="30%">
                  <XAxis dataKey="label" tick={{ fill: t.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={v => `$${(v/1000).toFixed(0)}k`} tick={{ fill: t.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} width={42} />
                  <Tooltip formatter={v => fmtMoney(v)} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="needs"   stackId="a" fill="#3B82F6" name="Needs"   radius={[0,0,0,0]} />
                  <Bar dataKey="wants"   stackId="a" fill="#F59E0B" name="Wants"   radius={[0,0,0,0]} />
                  <Bar dataKey="savings" stackId="a" fill="#8B5CF6" name="Savings" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly breakdown table */}
            <div style={{ ...card, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 1fr 1fr", padding: "10px 20px", borderBottom: `1px solid ${t.border}`, background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                {["Month", "Income", "Needs", "Wants", "Savings", "Total Spent"].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: h === "Month" ? "left" : "right" }}>{h}</div>
                ))}
              </div>
              {spendingTrends.map((row, i) => {
                const isCurrentMonth = i === spendingTrends.length - 1
                return (
                  <div key={row.month} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 1fr 1fr", padding: "12px 20px", borderBottom: i < spendingTrends.length - 1 ? `1px solid ${t.border}` : "none", background: isCurrentMonth ? (t.isDark ? "rgba(110,231,183,0.04)" : "rgba(47,133,90,0.03)") : "transparent" }}>
                    <div style={{ fontSize: 13, fontWeight: isCurrentMonth ? 700 : 400, color: isCurrentMonth ? t.accent : t.text }}>{row.label}{isCurrentMonth ? " ●" : ""}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#2F855A", textAlign: "right" }}>{row.income ? fmtMoney(row.income) : "—"}</div>
                    <div style={{ fontSize: 13, color: t.text, textAlign: "right" }}>{row.needs ? fmtMoney(row.needs) : "—"}</div>
                    <div style={{ fontSize: 13, color: t.text, textAlign: "right" }}>{row.wants ? fmtMoney(row.wants) : "—"}</div>
                    <div style={{ fontSize: 13, color: t.text, textAlign: "right" }}>{row.savings ? fmtMoney(row.savings) : "—"}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text, textAlign: "right" }}>{row.total ? fmtMoney(row.total) : "—"}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {budgetTab === "budget" && <div>
      {/* Bar chart — income / expenses / savings by month */}
      {chartData.length > 0 && (
        <div style={{ ...card, padding: "20px 20px 8px", marginBottom: 24 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 16 }}>Monthly Overview</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barCategoryGap="30%" barGap={2}>
              <XAxis dataKey="month" tick={{ fill: t.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  return (
                    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px" }}>
                      <p style={{ color: t.textMuted, fontSize: 11, marginBottom: 6 }}>{label}</p>
                      {payload.map((p, i) => (
                        <p key={i} style={{ color: p.color, fontSize: 12, fontWeight: 600, margin: "2px 0" }}>
                          {p.name}: {fmtMoney(p.value)}
                        </p>
                      ))}
                    </div>
                  )
                }}
              />
              <Bar dataKey="income"   name="Income"   fill={GROUP_STYLE.income.color} radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill={GROUP_STYLE.needs.color}  radius={[4, 4, 0, 0]} />
              <Bar dataKey="savings"  name="Savings"  fill={t.accent2}               radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 4, paddingBottom: 4 }}>
            {[["Income", GROUP_STYLE.income.color], ["Expenses", GROUP_STYLE.needs.color], ["Savings", t.accent2]].map(([name, color]) => (
              <div key={name} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                <span style={{ fontSize: 11, color: t.textMuted }}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary cards */}
      {(() => {
        // Compute income subcategories directly (positive amounts from income transactions)
        const incomeSubMap = {}
        transactions.forEach(tx => {
          const d = parseMaybeISODate(tx.date)
          if (!d) return
          const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
          if (!period.months.includes(m)) return
          const ui = formatTxForUI(tx)
          if (ui.amount > 0) {
            const cat = tx.user_category || ui.category
            incomeSubMap[cat] = (incomeSubMap[cat] || 0) + ui.amount
          }
        })
        const payrollActual   = incomeSubMap["Paycheck"]  || 0
        const dividendsActual = incomeSubMap["Dividends"] || 0
        const interestActual  = incomeSubMap["Interest"]  || 0
        const incomeActual    = Object.values(incomeSubMap).reduce((s, v) => s + v, 0)
        const incomeBudgeted  = groupTotals.income?.budgeted || 0

        const needsActual      = groupTotals.needs?.actual || 0
        const wantsActual      = groupTotals.wants?.actual || 0
        const expensesActual   = needsActual + wantsActual
        const expensesBudgeted = (groupTotals.needs?.budgeted || 0) + (groupTotals.wants?.budgeted || 0)
        const txInvested       = groupTotals.savings?.actual || 0
        const pm               = getPaycheckMonthly(paychecks || [])
        const contrib401k      = pm.contrib401k + pm.match401k   // pre-tax, never shows as transaction
        const investedActual   = txInvested + contrib401k
        const netSavings       = incomeActual - expensesActual
        const cashSaved        = netSavings - investedActual
        const expensesOver     = expensesBudgeted > 0 && expensesActual > expensesBudgeted
        const savingsPositive  = netSavings >= 0

        // Segmented bar percentages for income (payroll vs dividends vs interest)
        const payrollPct   = incomeActual > 0 ? (payrollActual   / incomeActual) * 100 : 0
        const dividendsPct = incomeActual > 0 ? (dividendsActual / incomeActual) * 100 : 0
        const interestPct  = incomeActual > 0 ? (interestActual  / incomeActual) * 100 : 0

        const labelStyle = { color: t.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0, marginBottom: 10 }
        const bigNumStyle = (color) => ({ fontSize: 22, fontWeight: 700, color })
        const dotStyle = (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 })
        const segLabel = { fontSize: 12, color: t.textMuted }
        const segValue = { fontSize: 13, fontWeight: 600, color: t.text }

        function SegBar({ segments }) {
          // segments: [{ pct, color }]
          return (
            <div style={{ display: "flex", height: 6, borderRadius: 6, overflow: "hidden", background: t.border, margin: "10px 0 12px" }}>
              {segments.map((s, i) => (
                <div key={i} style={{ width: `${s.pct}%`, background: s.color, transition: "width 0.3s" }} />
              ))}
            </div>
          )
        }

        function SegLegend({ items }) {
          return (
            <div style={{ display: "flex", gap: 16 }}>
              {items.map(({ color, label, value }) => (
                <div key={label} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={dotStyle(color)} />
                    <span style={segLabel}>{label}</span>
                  </div>
                  <span style={segValue}>{value}</span>
                </div>
              ))}
            </div>
          )
        }

        // Segmented bar percentages for expenses (needs vs wants)
        const needsPct  = expensesActual > 0 ? (needsActual / expensesActual) * 100 : 0
        const wantsPct  = expensesActual > 0 ? (wantsActual / expensesActual) * 100 : 0

        // Segmented bar percentages for savings (401k vs tx-invested vs cash)
        const savingsTotal   = investedActual + Math.max(0, cashSaved)
        const k401kPct       = savingsTotal > 0 ? (contrib401k  / savingsTotal) * 100 : 0
        const txInvestedPct  = savingsTotal > 0 ? (txInvested   / savingsTotal) * 100 : 0
        const investedPct    = k401kPct + txInvestedPct
        const cashPct        = savingsTotal > 0 ? (Math.max(0, cashSaved) / savingsTotal) * 100 : 0

        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>

            {/* Income */}
            <div style={{ ...card, padding: "18px 20px" }}>
              <p style={labelStyle}>Income</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={bigNumStyle(GROUP_STYLE.income.color)}>{fmtMoney(incomeActual)}</span>
                {incomeBudgeted > 0 && <span style={{ fontSize: 13, color: t.textMuted }}>of {fmtMoney(incomeBudgeted)}</span>}
              </div>
              <SegBar segments={[
                { pct: payrollPct,   color: GROUP_STYLE.income.color },
                { pct: dividendsPct, color: "#F59E0B" },
                { pct: interestPct,  color: "#7DD3FC" },
              ]} />
              <SegLegend items={[
                { color: GROUP_STYLE.income.color, label: "Paychecks", value: fmtMoney(payrollActual)   },
                { color: "#F59E0B",                label: "Dividends", value: fmtMoney(dividendsActual) },
                { color: "#7DD3FC",                label: "Interest",  value: fmtMoney(interestActual)  },
              ]} />
            </div>

            {/* Expenses */}
            <div style={{ ...card, padding: "18px 20px" }}>
              <p style={labelStyle}>Expenses</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={bigNumStyle(expensesOver ? "#EF4444" : GROUP_STYLE.needs.color)}>{fmtMoney(expensesActual)}</span>
                {expensesBudgeted > 0 && <span style={{ fontSize: 13, color: t.textMuted }}>of {fmtMoney(expensesBudgeted)}</span>}
              </div>
              <SegBar segments={[
                { pct: needsPct,  color: GROUP_STYLE.needs.color },
                { pct: wantsPct,  color: GROUP_STYLE.wants.color },
              ]} />
              <SegLegend items={[
                { color: GROUP_STYLE.needs.color, label: "Needs",  value: fmtMoney(needsActual)  },
                { color: GROUP_STYLE.wants.color, label: "Wants",  value: fmtMoney(wantsActual)  },
              ]} />
            </div>

            {/* Savings */}
            <div style={{ ...card, padding: "18px 20px" }}>
              <p style={labelStyle}>Savings</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={bigNumStyle(savingsPositive ? GROUP_STYLE.income.color : "#EF4444")}>{fmtMoney(netSavings)}</span>
                <span style={{ fontSize: 13, color: t.textMuted }}>this month</span>
              </div>
              <SegBar segments={[
                { pct: k401kPct,      color: "#8B5CF6" },
                { pct: txInvestedPct, color: t.accent2 },
                { pct: cashPct,       color: GROUP_STYLE.income.color },
              ]} />
              <SegLegend items={[
                ...(contrib401k > 0 ? [{ color: "#8B5CF6", label: "401k", value: fmtMoney(contrib401k) }] : []),
                ...(txInvested  > 0 ? [{ color: t.accent2, label: "Invested", value: fmtMoney(txInvested) }] : []),
                { color: GROUP_STYLE.income.color, label: "Cash", value: fmtMoney(Math.max(0, cashSaved)) },
              ]} />
            </div>

          </div>
        )
      })()}

      {/* Category tables by group */}
      {/* renderCatRow — shared between flat and subgrouped rendering */}
      {BUDGET_GROUPS.map(({ id, label, color }) => {
        const cats = categories.filter(c => c.group === id && !c.hidden)
        const { budgeted: totalBudgeted, actual: totalActual } = groupTotals[id] || { budgeted: 0, actual: 0 }
        const isIncome = id === "income"

        return (
          <div key={id} style={{ ...card, marginBottom: 20, overflow: "hidden" }}>
            {/* Group header */}
            <div style={{
              display: "grid", gridTemplateColumns: colW,
              padding: "12px 20px", borderBottom: `1px solid ${t.border}`,
              background: `${color}0d`,
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color }}>{label}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 5 }}>
                {period.multiplier > 1 ? `Budget ×${period.multiplier}` : "Budgeted"}
                <span style={{ fontSize: 10, color: t.accent, fontWeight: 500, background: t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.1)", padding: "1px 6px", borderRadius: 99 }}>click to edit</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textAlign: "right" }}>Actual</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textAlign: "right" }}>Remaining</span>
            </div>

            {/* Category rows — grouped by subgroup */}
            {(() => {
              const subgroups = []
              const seen = new Set()
              cats.forEach(c => { if (c.subgroup && !seen.has(c.subgroup)) { seen.add(c.subgroup); subgroups.push(c.subgroup) } })
              const hasSubgroups = subgroups.length > 1 || (subgroups.length === 1 && subgroups[0] !== label)
              if (!hasSubgroups) return cats.map((cat, i) => renderCatRow(cat, i, cats.length, isIncome, color))
              return subgroups.map(sg => {
                const sgCats = cats.filter(c => c.subgroup === sg)
                const sgKey = `${id}:${sg}`
                const collapsed = collapsedSubgroups.has(sgKey)
                const sgBudgeted = sgCats.reduce((s, c) => s + budgetDisplay(c.label), 0)
                const sgActual = sgCats.reduce((s, c) => s + (actuals[c.label] || 0), 0)
                return (
                  <div key={sg}>
                    <button onClick={() => toggleSubgroup(sgKey)} style={{ width: "100%", display: "grid", gridTemplateColumns: colW, padding: "9px 20px 9px 28px", background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", border: "none", borderBottom: `1px solid ${t.border}`, cursor: "pointer", alignItems: "center", textAlign: "left" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: t.textFaint }}>{collapsed ? "▶" : "▼"}</span>
                        {sg}
                      </span>
                      <span style={{ fontSize: 12, color: t.textFaint, textAlign: "right" }}>{sgBudgeted > 0 ? fmtMoney(sgBudgeted) : ""}</span>
                      <span style={{ fontSize: 12, color: t.textFaint, textAlign: "right" }}>{sgActual > 0 ? fmtMoney(sgActual) : ""}</span>
                      <span />
                    </button>
                    {!collapsed && sgCats.map((cat, i) => renderCatRow(cat, i, sgCats.length, isIncome, color))}
                  </div>
                )
              })
            })()}

            {/* Group totals row */}
            <div style={{
              display: "grid", gridTemplateColumns: colW,
              padding: "12px 20px",
              borderTop: `1px solid ${t.border}`,
              background: t.hover,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.textMuted }}>Total {label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.text, textAlign: "right" }}>{fmtMoney(totalBudgeted)}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.text, textAlign: "right" }}>{fmtMoney(totalActual)}</span>
              <span style={{
                fontSize: 13, fontWeight: 700, textAlign: "right",
                color: totalBudgeted > 0 && (isIncome ? totalActual < totalBudgeted : totalActual > totalBudgeted) ? "#EF4444" : t.accent,
              }}>
                {totalBudgeted > 0 ? fmtMoney(isIncome ? totalActual - totalBudgeted : totalBudgeted - totalActual) : "—"}
              </span>
            </div>
          </div>
        )
      })}

      {/* ── Left to Budget ── */}
      {(() => {
        const incomeBudgeted = groupTotals.income?.budgeted || 0
        const expenseBudgeted = (groupTotals.needs?.budgeted || 0) + (groupTotals.wants?.budgeted || 0) + (groupTotals.savings?.budgeted || 0)
        const leftToBudget = incomeBudgeted - expenseBudgeted
        if (incomeBudgeted === 0) return null
        const isOver = leftToBudget < 0
        return (
          <div style={{
            ...card, padding: "18px 24px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            border: `1.5px solid ${isOver ? "rgba(239,68,68,0.4)" : leftToBudget === 0 ? t.border : "rgba(34,197,94,0.4)"}`,
            background: isOver ? "rgba(239,68,68,0.05)" : leftToBudget === 0 ? t.surface : "rgba(34,197,94,0.05)",
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Left to Budget</div>
              <div style={{ fontSize: 12, color: t.textFaint, marginTop: 2 }}>
                {isOver
                  ? "You've allocated more than your budgeted income"
                  : leftToBudget === 0
                  ? "Every dollar is assigned — great job!"
                  : `${fmtMoney(leftToBudget)} of your income budget is still unassigned`}
              </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: isOver ? "#EF4444" : leftToBudget === 0 ? t.accent : "#22C55E" }}>
              {isOver ? `-${fmtMoney(Math.abs(leftToBudget))}` : fmtMoney(leftToBudget)}
            </div>
          </div>
        )
      })()}
      </div>}
    </div>

    {/* ── Drill-down panel (read-only summary) ── */}
    {drillDown && !drillModifying && (
      <>
        <div onClick={() => { setDrillDown(null); setDrillOverrides({}) }} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.4)" }} />
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1101, width: "min(520px, 100vw)", background: t.surface, borderLeft: `1px solid ${t.border}`, display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,0.25)" }}>
          <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${t.border}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => { setDrillDown(null); setDrillOverrides({}) }} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: t.hover, color: t.textMuted, fontSize: 16, cursor: "pointer", flexShrink: 0 }}>←</button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>{drillDown.label}</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{drillDown.txs.length} transaction{drillDown.txs.length !== 1 ? "s" : ""} · {fmtMoney(drillDown.txs.reduce((s, r) => s + Math.abs(r.amount), 0))} total</div>
            </div>
            <button
              onClick={() => { setDrillModifying(true); setDrillEditingTxId(null) }}
              style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
            >Modify</button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {drillDown.txs.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: t.textFaint, fontSize: 14 }}>No transactions found</div>
            ) : drillDown.txs.map((tx, i) => {
              const currentCat = drillOverrides[tx.id]?.category || tx.category
              const catObj = CATEGORIES.find(c => c.label === currentCat)
              const d = parseMaybeISODate(tx.rawDate)
              const dateStr = d ? `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}` : tx.rawDate
              return (
                <div key={tx.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 24px", borderBottom: i < drillDown.txs.length - 1 ? `1px solid ${t.border}` : "none" }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{catObj?.emoji || "💳"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.name}</div>
                    <div style={{ fontSize: 11, color: t.textFaint, marginTop: 1 }}>{dateStr} · {currentCat}</div>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: tx.amount > 0 ? "#2F855A" : t.text, flexShrink: 0 }}>
                    {tx.amount > 0 ? "+" : "-"}{fmtMoney(Math.abs(tx.amount))}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </>
    )}

    {/* ── Modify page (full screen) ── */}
    {drillDown && drillModifying && (
      <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: t.bg, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${t.border}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 12, background: t.surface }}>
          <button onClick={() => { setDrillModifying(false); setDrillEditingTxId(null) }} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: t.hover, color: t.textMuted, fontSize: 16, cursor: "pointer", flexShrink: 0 }}>←</button>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>Edit Categories — {drillDown.label}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>Click a category on any row to reassign it</div>
          </div>
        </div>
        {/* Transaction list — same layout as Transactions tab */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Column header */}
          <div style={{ display: "grid", gridTemplateColumns: "90px 1.4fr 0.9fr 110px 90px", padding: "8px 18px", borderBottom: `1px solid ${t.border}`, background: t.hover }}>
            {["Date", "Merchant", "Category", "Amount", "Group"].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: h === "Amount" ? "right" : "left" }}>{h}</span>
            ))}
          </div>
          {drillDown.txs.map((tx, i) => {
            const currentCat = drillOverrides[tx.id]?.category || tx.category
            const currentGroup = drillOverrides[tx.id]?.group || tx.group
            const catObj = CATEGORIES.find(c => c.label === currentCat)
            const gs = GROUP_STYLE[currentGroup] || GROUP_STYLE.transfer
            const d = parseMaybeISODate(tx.rawDate)
            const dateStr = d ? `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}` : tx.rawDate
            const isEditing = drillEditingTxId === tx.id

            const saveCategory = async (label, group) => {
              setDrillEditingTxId(null)
              const { error } = await supabase.from("transactions")
                .update({ user_category: label, user_group: group })
                .eq("id", tx.id).eq("user_id", session.user.id)
              if (error) { fireToast("Failed to save category change."); return }
              setDrillOverrides(prev => ({ ...prev, [tx.id]: { category: label, group } }))
              const { data: refreshed } = await supabase.from("transactions").select("*").eq("user_id", session.user.id).order("date", { ascending: false })
              if (refreshed) onTransactionsLoaded && onTransactionsLoaded(refreshed)
            }

            return (
              <div key={tx.id || i} style={{
                display: "grid", gridTemplateColumns: "90px 1.4fr 0.9fr 110px 90px",
                padding: "13px 18px", borderBottom: `1px solid ${t.border}`,
                alignItems: "center", background: isEditing ? t.hover : "transparent", transition: "background 0.1s",
              }}>
                {/* Date */}
                <div style={{ fontSize: 12, color: t.textFaint }}>{dateStr}</div>

                {/* Merchant */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{catObj?.emoji || tx.icon || "💳"}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.name}</div>
                    <div style={{ fontSize: 11, color: t.textFaint }}>{currentCat}</div>
                  </div>
                </div>

                {/* Category picker */}
                <div style={{ position: "relative" }}>
                  <button
                    ref={el => { if (isEditing && el) window.__drillPickerAnchor = el }}
                    onClick={e => { window.__drillPickerAnchor = e.currentTarget; setDrillEditingTxId(isEditing ? null : tx.id) }}
                    style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${isEditing ? t.accent : t.border}`, background: isEditing ? (t.isDark ? "rgba(110,231,183,0.08)" : "rgba(47,133,90,0.06)") : t.hover, color: t.text, fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}
                  >{currentCat} ▾</button>
                  {isEditing && (
                    <>
                      <div onClick={() => setDrillEditingTxId(null)} style={{ position: "fixed", inset: 0, zIndex: 1299 }} />
                      <CategoryPicker theme={t} currentCategory={currentCat} anchorEl={window.__drillPickerAnchor} zIndex={1300} onClose={() => setDrillEditingTxId(null)} onSelect={saveCategory} merchantName={tx.name} merchantHistory={merchantHistory} communityMap={communityMap} hiddenCats={hiddenCats} />
                    </>
                  )}
                </div>

                {/* Amount */}
                <div style={{ fontSize: 14, fontWeight: 700, color: tx.amount > 0 ? "#2F855A" : t.text, textAlign: "right" }}>
                  {tx.amount > 0 ? "+" : "-"}{fmtMoney(Math.abs(tx.amount))}
                </div>

                {/* Group pill */}
                <div>
                  <span style={{ padding: "3px 10px", borderRadius: 999, background: gs.bg, color: gs.color, fontSize: 11, fontWeight: 700, textTransform: "capitalize" }}>
                    {currentGroup}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )}
    </>
  )
}

// Detect recurring transactions from history
// Returns array of { key, name, category, group, emoji, avgAmount, monthlyCount, months, lastDate, transactions }
function detectTransferPairs(rows) {
  // Returns { hiddenIds: Set<id>, pairMap: Map<debitId, creditRow> }
  const hiddenIds = new Set()
  const pairMap = new Map()
  const candidates = rows.filter(r => r.plaid_account_id)
  for (let i = 0; i < candidates.length; i++) {
    const r1 = candidates[i]
    if (hiddenIds.has(r1.id)) continue
    for (let j = i + 1; j < candidates.length; j++) {
      const r2 = candidates[j]
      if (hiddenIds.has(r2.id)) continue
      if (r1.plaid_account_id === r2.plaid_account_id) continue
      if (Math.abs(Math.abs(r1.amount) - Math.abs(r2.amount)) > 0.01) continue
      const d1 = new Date(r1.rawDate), d2 = new Date(r2.rawDate)
      if (isNaN(d1) || isNaN(d2)) continue
      if (Math.abs(d1 - d2) / 86400000 > 3) continue
      // Opposite-sign pair: classic debit+credit transfer
      if (Math.sign(r1.amount) !== Math.sign(r2.amount)) {
        const debit  = r1.amount < 0 ? r1 : r2
        const credit = r1.amount < 0 ? r2 : r1
        hiddenIds.add(credit.id)
        pairMap.set(debit.id, credit)
        break
      }
      // Same-sign pair (both negative): some banks report both sides as debits
      // Hide the later one (or r2 if same date), keep the earlier as the visible row
      const earlier = d1 <= d2 ? r1 : r2
      const later   = d1 <= d2 ? r2 : r1
      hiddenIds.add(later.id)
      pairMap.set(earlier.id, later)
      break
    }
  }
  return { hiddenIds, pairMap }
}

function detectRecurring(allRows) {
  // Group by normalized merchant name
  const byMerchant = {}
  allRows.forEach(row => {
    if (row.amount >= 0) return // skip income
    const key = row.name.toLowerCase().trim().replace(/\s+/g, " ")
    if (!byMerchant[key]) byMerchant[key] = { name: row.name, category: row.category, group: row.group, rows: [] }
    byMerchant[key].rows.push(row)
  })

  const recurring = []
  Object.entries(byMerchant).forEach(([key, data]) => {
    const { rows } = data
    // Need at least 2 occurrences in different months
    const monthSet = new Set(rows.map(r => {
      const d = parseMaybeISODate(r.rawDate)
      return d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` : null
    }).filter(Boolean))
    if (monthSet.size < 2) return

    // Check if average interval is roughly monthly (20-45 days)
    const dates = rows.map(r => parseMaybeISODate(r.rawDate)).filter(Boolean).sort((a,b) => a-b)
    if (dates.length < 2) return
    const intervals = []
    for (let i = 1; i < dates.length; i++) {
      intervals.push((dates[i] - dates[i-1]) / (1000 * 60 * 60 * 24))
    }
    const avgInterval = intervals.reduce((s,v)=>s+v,0) / intervals.length
    if (avgInterval < 6 || avgInterval > 95) return // skip daily or > quarterly

    const amounts = rows.map(r => Math.abs(r.amount))
    const avgAmount = amounts.reduce((s,v)=>s+v,0) / amounts.length
    const maxAmt = Math.max(...amounts)
    const minAmt = Math.min(...amounts)
    // Allow up to 20% variance in amount
    if (maxAmt > 0 && (maxAmt - minAmt) / maxAmt > 0.2 && minAmt / maxAmt < 0.5) return

    const catObj = CATEGORIES.find(c => c.label === data.category)
    const lastDate = dates[dates.length - 1]

    // Classify frequency
    let frequency = "monthly"
    if (avgInterval < 10) frequency = "weekly"
    else if (avgInterval < 20) frequency = "biweekly"
    else if (avgInterval < 50) frequency = "monthly"
    else frequency = "quarterly"

    recurring.push({
      key,
      name: data.name,
      category: data.category,
      group: data.group,
      emoji: catObj?.emoji || "🔄",
      avgAmount,
      monthlyCount: monthSet.size,
      frequency,
      lastDate,
      transactions: rows,
    })
  })

  // Sort by avg amount desc
  return recurring.sort((a, b) => b.avgAmount - a.avgAmount)
}

function TransactionsPage({ session, theme, transactions, accounts, onSync, txLoading, fmtMoney, onTransactionsLoaded, syncWarning, hiddenCats = new Set() }) {
  // Build a map: plaid_account_id → account for quick lookup
  const accountMap = useMemo(() => {
    const m = {}
    ;(accounts || []).forEach(a => { if (a.plaid_account_id) m[a.plaid_account_id] = a })
    return m
  }, [accounts])
  const t = theme
  const card = makeCard(t)

  const [txSubNav, setTxSubNav] = useState("all") // "all" | "recurring" | "uncategorized"
  const [groupBy, setGroupBy] = useState("day") // "day" | "category" | "merchant"
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())
  const [dismissedRecurring, setDismissedRecurring] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`dismissed_recurring_${session?.user?.id}`) || "[]") } catch { return [] }
  })

  const [search, setSearch]           = useState("")
  const [groupFilter, setGroupFilter] = useState("all")
  const [txPeriodType, setTxPeriodType]     = useState("month")
  const [txPeriodOffset, setTxPeriodOffset] = useState(0)
  const [uncatFilter, setUncatFilter] = useState(false)

  // Reset offset when switching period type
  useEffect(() => { setTxPeriodOffset(0) }, [txPeriodType])

  const txPeriod = useMemo(() => {
    const now = new Date()
    if (txPeriodType === "quarter") {
      const curQ   = Math.floor(now.getMonth() / 3)
      const qStart = new Date(now.getFullYear(), (curQ + txPeriodOffset) * 3, 1)
      const q      = Math.floor(qStart.getMonth() / 3) + 1
      const keys   = [0,1,2].map(i => { const md = new Date(qStart.getFullYear(), qStart.getMonth()+i, 1); return `${md.getFullYear()}-${String(md.getMonth()+1).padStart(2,'0')}` })
      return { keys, label: `Q${q} ${qStart.getFullYear()}` }
    }
    if (txPeriodType === "year") {
      const year = now.getFullYear() + txPeriodOffset
      const keys  = Array.from({length:12}, (_,i) => `${year}-${String(i+1).padStart(2,'0')}`)
      return { keys, label: String(year) }
    }
    const d    = new Date(now.getFullYear(), now.getMonth() + txPeriodOffset, 1)
    const keys = [`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`]
    return { keys, label: d.toLocaleString('default', { month: 'long', year: 'numeric' }) }
  }, [txPeriodType, txPeriodOffset])
  const [editingId, setEditingId]     = useState(null)
  const [editingGroupId, setEditingGroupId] = useState(null)
  const [overrides, setOverrides]     = useState({})   // { [tx.id]: { category, group } }

  // Manual entry modal
  const [showManual, setShowManual] = useState(false)
  const [manualForm, setManualForm] = useState({ date: new Date().toISOString().split("T")[0], name: "", amount: "", category: "Other", isIncome: false })
  const [manualSaving, setManualSaving] = useState(false)

  async function saveManualTransaction() {
    if (!manualForm.name || !manualForm.amount || !manualForm.date) return
    setManualSaving(true)
    const amt = parseFloat(manualForm.amount)
    const plaidAmt = manualForm.isIncome ? -Math.abs(amt) : Math.abs(amt)
    const catObj = CATEGORIES.find(c => c.label === manualForm.category)
    await supabase.from("transactions").insert({
      user_id: session.user.id,
      plaid_transaction_id: `manual_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      name: manualForm.name,
      merchant_name: manualForm.name,
      category: manualForm.category,
      date: manualForm.date,
      amount: plaidAmt,
      source: "manual",
    })
    setManualSaving(false)
    setShowManual(false)
    setManualForm({ date: new Date().toISOString().split("T")[0], name: "", amount: "", category: "Other", isIncome: false })
    onSync && onSync()
  }

  // CSV import modal
  const [showCsv, setShowCsv] = useState(false)
  const [csvRows, setCsvRows] = useState([])
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvError, setCsvError] = useState("")

  function parseCsv(text) {
    const lines = text.trim().split("\n").filter(Boolean)
    if (lines.length < 2) { setCsvError("CSV must have a header row and at least one data row."); return }
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/"/g, ""))
    const dateIdx   = headers.findIndex(h => h.includes("date"))
    const nameIdx   = headers.findIndex(h => h.includes("desc") || h.includes("name") || h.includes("merchant") || h.includes("payee"))
    const amountIdx = headers.findIndex(h => h.includes("amount") || h.includes("debit") || h.includes("credit"))
    if (dateIdx === -1 || nameIdx === -1 || amountIdx === -1) {
      setCsvError(`Could not detect required columns. Found: ${headers.join(", ")}. Need: date, name/description, amount.`)
      return
    }
    const parsed = lines.slice(1).map((line, i) => {
      const cols = line.split(",").map(c => c.trim().replace(/"/g, ""))
      const raw = parseFloat((cols[amountIdx] || "0").replace(/[$,\s]/g, ""))
      return { id: i, date: cols[dateIdx] || "", name: cols[nameIdx] || "", rawAmount: raw, isIncome: raw > 0, preview: cols }
    }).filter(r => r.name && !isNaN(r.rawAmount))
    setCsvError("")
    setCsvRows(parsed)
  }

  async function importCsvRows() {
    setCsvImporting(true)
    const rows = csvRows.map(r => ({
      user_id: session.user.id,
      plaid_transaction_id: `csv_${Date.now()}_${r.id}_${Math.random().toString(36).slice(2)}`,
      name: r.name,
      merchant_name: r.name,
      category: "Other",
      date: r.date,
      amount: r.isIncome ? -Math.abs(r.rawAmount) : Math.abs(r.rawAmount),
      source: "csv",
    }))
    const { error } = await supabase.from("transactions").insert(rows)
    if (error) {
      setCsvError(`Import failed: ${error.message}`)
      setCsvImporting(false)
      return
    }
    setCsvImporting(false)
    setShowCsv(false)
    setCsvRows([])
    // Reload from DB directly — don't trigger a Plaid sync
    const { data } = await supabase.from("transactions").select("*").eq("user_id", session.user.id).order("date", { ascending: false })
    if (data) onTransactionsLoaded && onTransactionsLoaded(data)
  }

  // Build display rows — apply DB overrides then local overrides
  // Also deduplicate: hide pending transactions when a matching settled one exists
  const allRows = useMemo(() => {
    // Build set of pending IDs to hide (same account + amount + normalized name within 5 days, settled exists)
    const settled = transactions.filter(tx => !tx.pending)
    const pendingHidden = new Set(
      transactions
        .filter(tx => tx.pending)
        .filter(tx => settled.some(s =>
          s.plaid_account_id === tx.plaid_account_id &&
          Math.abs(s.amount - tx.amount) < 0.01 &&
          s.name?.toLowerCase().trim() === tx.name?.toLowerCase().trim() &&
          Math.abs(new Date(s.date) - new Date(tx.date)) / 86400000 <= 5
        ))
        .map(tx => tx.id)
    )

    return transactions
      .filter(tx => !pendingHidden.has(tx.id))
      .map(tx => {
        const lo  = overrides[tx.id] || {}
        const base = formatTxForUI(tx)
        const isTransfer = !!tx.is_transfer
        const rawCategory = lo.category || tx.user_category || base.category
        const category = !lo.category && !tx.user_category && isTransfer && (rawCategory === "Other" || !rawCategory) ? "Transfer" : rawCategory
        const group    = lo.group    || tx.user_group    || getTxGroup({ ...base, category })
        return { ...base, id: tx.id, category, group, rawDate: tx.date, plaid_account_id: tx.plaid_account_id || null, rawName: tx.name || null, merchant_name: tx.merchant_name || null, pending: !!tx.pending, is_transfer: isTransfer }
      })
  }, [transactions, overrides])

  const { hiddenIds, pairMap } = useMemo(() => detectTransferPairs(allRows), [allRows])

  // Build merchant → top category from user's own history
  const merchantHistory = useMemo(() => {
    const map = {}
    transactions.forEach(tx => {
      const cat = tx.user_category
      const grp = tx.user_group
      if (!cat) return
      const name = (tx.merchant_name || tx.name || "").toLowerCase().trim()
      if (!name) return
      if (!map[name]) map[name] = {}
      if (!map[name][cat]) map[name][cat] = { category: cat, group: grp, count: 0 }
      map[name][cat].count++
    })
    const result = {}
    Object.entries(map).forEach(([name, cats]) => {
      const top = Object.values(cats).sort((a, b) => b.count - a.count)[0]
      const catObj = CATEGORIES.find(c => c.label === top.category)
      result[name] = { ...top, emoji: catObj?.emoji || "💳", source: "history" }
    })
    return result
  }, [transactions])
  const [communityMap, setCommunityMap] = useState({})
  useEffect(() => {
    const merchantNames = [...new Set(
      transactions.map(tx => (tx.merchant_name || tx.name || "").toLowerCase().trim()).filter(Boolean)
    )]
    if (merchantNames.length === 0) return
    supabase.from("merchant_categories")
      .select("merchant_name, category, grp, votes")
      .in("merchant_name", merchantNames)
      .then(({ data }) => {
        if (!data) return
        const byMerchant = {}
        data.forEach(row => {
          if (!byMerchant[row.merchant_name]) byMerchant[row.merchant_name] = []
          byMerchant[row.merchant_name].push(row)
        })
        const result = {}
        Object.entries(byMerchant).forEach(([name, rows]) => {
          const total = rows.reduce((s, r) => s + r.votes, 0)
          const best = rows.sort((a, b) => b.votes - a.votes)[0]
          if (best.votes >= 5 && best.votes / total >= 0.6) {
            result[name] = { category: best.category, group: best.grp }
          }
        })
        setCommunityMap(result)
      })
  }, [transactions])

  const recurringCandidates = useMemo(() => detectRecurring(allRows), [allRows])
  const visibleRecurring = useMemo(
    () => recurringCandidates.filter(r => !dismissedRecurring.includes(r.key)),
    [recurringCandidates, dismissedRecurring]
  )

  function dismissRecurring(key) {
    const next = [...dismissedRecurring, key]
    setDismissedRecurring(next)
    localStorage.setItem(`dismissed_recurring_${session?.user?.id}`, JSON.stringify(next))
  }

  // Unique months for the selector
  const months = useMemo(() => [...new Set(
    allRows.map(r => {
      const d = parseMaybeISODate(r.rawDate)
      return d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` : null
    }).filter(Boolean)
  )].sort().reverse(), [allRows])

  const TRANSFER_CATS = new Set(["Transfer", "Credit Card Payment", "Transfers", "Credit Card Payments"])

  // Apply filters — also hide the credit side of matched transfer pairs
  const filtered = useMemo(() => allRows.filter(r => {
    if (hiddenIds.has(r.id)) return false
    if (uncatFilter) {
      if (r.category !== "Other" && r.category !== "" && r.category) return false
      if (r.is_transfer || TRANSFER_CATS.has(r.category)) return false
    } else if (search && !r.name.toLowerCase().includes(search.toLowerCase()) &&
        !r.category.toLowerCase().includes(search.toLowerCase())) return false
    if (groupFilter !== "all" && r.group !== groupFilter) return false
    const d = parseMaybeISODate(r.rawDate)
    if (!d) return false
    const m = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`
    if (!txPeriod.keys.includes(m)) return false
    return true
  }), [allRows, hiddenIds, search, uncatFilter, groupFilter, txPeriod])

  const totalIncome   = useMemo(() => filtered.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0), [filtered])
  const totalExpenses = useMemo(() => filtered.filter(r => r.amount < 0).reduce((s, r) => s + Math.abs(r.amount), 0), [filtered])

  const groupedFiltered = useMemo(() => {
    const map = {}
    filtered.forEach(r => {
      let key
      if (groupBy === "day") key = r.rawDate || "Unknown"
      else if (groupBy === "category") key = r.category || "Other"
      else key = r.name || "Unknown"
      if (!map[key]) map[key] = []
      map[key].push(r)
    })
    const entries = Object.entries(map)
    if (groupBy === "day") {
      entries.sort((a, b) => b[0].localeCompare(a[0]))
    } else {
      // sort by total absolute spend desc
      entries.sort((a, b) =>
        b[1].reduce((s, r) => s + Math.abs(r.amount), 0) -
        a[1].reduce((s, r) => s + Math.abs(r.amount), 0)
      )
    }
    return entries
  }, [filtered, groupBy])

  function toggleGroup(key) {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const [bulkPrompt, setBulkPrompt] = useState(null) // { merchant, category, group, ids }
  const [bulkApplying, setBulkApplying] = useState(false)

  async function applyOverride(txId, category, group) {
    const { error } = await supabase.from("transactions")
      .update({ user_category: category, user_group: group })
      .eq("id", txId).eq("user_id", session.user.id)
    if (error) { fireToast("Failed to save category change."); return }

    // Vote for this merchant→category mapping in the community table
    const thisTx = allRows.find(r => r.id === txId)
    const merchantKey = (thisTx?.merchant_name || thisTx?.rawName || "").toLowerCase().trim()
    if (merchantKey) {
      await supabase.from("merchant_categories")
        .upsert({ merchant_name: merchantKey, category, grp: group, votes: 1 }, {
          onConflict: "merchant_name,category",
          ignoreDuplicates: false,
        })
      // Increment votes via RPC-free approach: fetch current then update
      const { data: existing } = await supabase.from("merchant_categories")
        .select("votes").eq("merchant_name", merchantKey).eq("category", category).single()
      if (existing) {
        await supabase.from("merchant_categories")
          .update({ votes: existing.votes + 1, grp: group })
          .eq("merchant_name", merchantKey).eq("category", category)
      }
    }

    // Refresh the transactions array in Dashboard so user_category persists after navigation
    const { data: refreshed } = await supabase.from("transactions").select("*").eq("user_id", session.user.id).order("date", { ascending: false })
    if (refreshed) onTransactionsLoaded && onTransactionsLoaded(refreshed)
    setOverrides(prev => ({ ...prev, [txId]: { category, group } }))
    setEditingId(null)

    // Find other transactions from same merchant with a different category
    if (!thisTx) return
    const merchantName = thisTx.name?.toLowerCase().trim()
    const others = allRows.filter(r =>
      r.id !== txId &&
      r.name?.toLowerCase().trim() === merchantName &&
      (r.category !== category || r.group !== group)
    )
    if (others.length > 0) {
      setBulkPrompt({ merchant: thisTx.name, category, group, ids: others.map(r => r.id) })
    }
  }

  async function applyBulk() {
    if (!bulkPrompt) return
    setBulkApplying(true)
    const { category, group, ids } = bulkPrompt
    // Update in batches of 50
    for (let i = 0; i < ids.length; i += 50) {
      const { error } = await supabase.from("transactions")
        .update({ user_category: category, user_group: group })
        .in("id", ids.slice(i, i + 50))
        .eq("user_id", session.user.id)
      if (error) fireToast("Failed to save bulk category changes.")
    }
    // Refresh Dashboard transactions state
    const { data: refreshed } = await supabase.from("transactions").select("*").eq("user_id", session.user.id).order("date", { ascending: false })
    if (refreshed) onTransactionsLoaded && onTransactionsLoaded(refreshed)
    setOverrides(prev => {
      const next = { ...prev }
      ids.forEach(id => { next[id] = { category, group } })
      return next
    })
    setBulkApplying(false)
    setBulkPrompt(null)
  }

  async function deleteTransaction(txId) {
    await supabase.from("transactions").delete().eq("id", txId).eq("user_id", session.user.id)
    const { data } = await supabase.from("transactions").select("*").eq("user_id", session.user.id).order("date", { ascending: false })
    if (data) onTransactionsLoaded && onTransactionsLoaded(data)
  }

  return (
    <>
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6, color: t.text }}>Transactions</h1>
          <p style={{ color: t.textMuted, fontSize: 14 }}>
            {allRows.length > 0 ? `${filtered.length} shown · tap any category to reassign` : "Connect a bank to see your transactions"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setCsvRows([]); setCsvError(""); setShowCsv(true) }}
            style={{ padding: "10px 16px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            📂 Import CSV
          </button>
          <button onClick={() => setShowManual(true)}
            style={{ padding: "10px 16px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            + Manual Add
          </button>
          <button onClick={onSync} disabled={txLoading}
            style={{ padding: "10px 16px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: txLoading ? t.textFaint : t.textMuted, fontSize: 13, fontWeight: 600, cursor: txLoading ? "not-allowed" : "pointer" }}>
            {txLoading ? "Refreshing..." : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* Sub-nav pills */}
      {(() => {
        const uncatCount = allRows.filter(r => !hiddenIds.has(r.id) && (r.category === "Other" || !r.category) && !r.is_transfer && !TRANSFER_CATS.has(r.category)).length
        return (
          <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
            {[
              { id: "all",           label: "📋 All" },
              { id: "recurring",     label: `🔄 Recurring${visibleRecurring.length > 0 ? ` · ${visibleRecurring.length}` : ""}` },
              { id: "uncategorized", label: `⚠️ Uncategorized${uncatCount > 0 ? ` · ${uncatCount}` : ""}` },
            ].map(item => {
              const active = txSubNav === item.id
              return (
                <button key={item.id} onClick={() => setTxSubNav(item.id)} style={{
                  padding: "9px 18px", borderRadius: 999, fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer",
                  border: `1px solid ${active ? t.accent : t.border}`,
                  background: active ? (t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.08)") : "transparent",
                  color: active ? t.accent : t.textMuted,
                  transition: "all 0.15s",
                }}>
                  {item.label}
                </button>
              )
            })}
          </div>
        )
      })()}

      {/* Sync warning banner */}
      {syncWarning && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 10, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <p style={{ fontSize: 13, color: "#92400E", margin: 0, flex: 1 }}>{syncWarning}</p>
          <button onClick={onSync} disabled={txLoading} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(245,158,11,0.4)", background: "transparent", color: "#B45309", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      )}

      {/* Bulk apply modal */}
      {bulkPrompt && (
        <div onClick={() => setBulkPrompt(null)} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, width: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.4)" }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: t.isDark ? "rgba(110,231,183,0.12)" : "rgba(47,133,90,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20 }}>🏷</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: t.text, marginBottom: 8 }}>Apply category to all?</h2>
            <p style={{ fontSize: 14, color: t.textMuted, marginBottom: 6, lineHeight: 1.5 }}>
              You set <strong style={{ color: t.text }}>{bulkPrompt.merchant}</strong> → <strong style={{ color: t.accent }}>{bulkPrompt.category}</strong>.
            </p>
            <p style={{ fontSize: 14, color: t.textMuted, marginBottom: 28, lineHeight: 1.5 }}>
              Found <strong style={{ color: t.text }}>{bulkPrompt.ids.length} other transaction{bulkPrompt.ids.length !== 1 ? "s" : ""}</strong> from this merchant with a different category. Apply to all of them?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setBulkPrompt(null)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Skip
              </button>
              <button onClick={applyBulk} disabled={bulkApplying} style={{ flex: 2, padding: "11px 0", borderRadius: 10, border: "none", background: t.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: bulkApplying ? "not-allowed" : "pointer", opacity: bulkApplying ? 0.7 : 1 }}>
                {bulkApplying ? "Applying..." : `Apply to all ${bulkPrompt.ids.length} transactions`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recurring view ── */}
      {txSubNav === "recurring" && (
        <div>
          {visibleRecurring.length === 0 ? (
            <div style={{ ...card, padding: "56px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: t.text }}>No recurring transactions detected</h2>
              <p style={{ color: t.textMuted, fontSize: 14, maxWidth: 360, margin: "0 auto 0" }}>
                Grove automatically detects bills, subscriptions, and other recurring charges once you have a few months of history.
              </p>
            </div>
          ) : (
            <>
              {/* Summary strip */}
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ padding: "9px 16px", borderRadius: 8, background: t.hover, border: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 11, color: t.textFaint }}>Detected </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{visibleRecurring.length} recurring</span>
                </div>
                <div style={{ padding: "9px 16px", borderRadius: 8, background: t.hover, border: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 11, color: t.textFaint }}>Est. monthly total </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#EF4444" }}>
                    -{fmtMoney(visibleRecurring.filter(r=>r.frequency==="monthly").reduce((s,r)=>s+r.avgAmount,0)
                       + visibleRecurring.filter(r=>r.frequency==="weekly").reduce((s,r)=>s+r.avgAmount*4.33,0)
                       + visibleRecurring.filter(r=>r.frequency==="biweekly").reduce((s,r)=>s+r.avgAmount*2.167,0)
                       + visibleRecurring.filter(r=>r.frequency==="quarterly").reduce((s,r)=>s+r.avgAmount/3,0)
                    )}
                  </span>
                </div>
              </div>

              {/* Cards */}
              <div style={{ ...card, overflow: "hidden" }}>
                {/* Header row */}
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 80px", padding: "10px 18px", background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)", borderBottom: `1px solid ${t.border}` }}>
                  {["Merchant", "Category", "Frequency", "Avg Amount", ""].map(h => (
                    <div key={h} style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                  ))}
                </div>

                {visibleRecurring.map((item, i) => {
                  const gs = GROUP_STYLE[item.group] || GROUP_STYLE.wants
                  const freqColor = { weekly: "#EF4444", biweekly: "#F97316", monthly: "#3B82F6", quarterly: "#8B5CF6" }[item.frequency] || t.textMuted
                  const nextEstimate = (() => {
                    if (!item.lastDate) return null
                    const d = new Date(item.lastDate)
                    const days = { weekly: 7, biweekly: 14, monthly: 30, quarterly: 90 }[item.frequency] || 30
                    d.setDate(d.getDate() + days)
                    return d.toLocaleDateString("default", { month: "short", day: "numeric" })
                  })()

                  return (
                    <div key={item.key} style={{
                      display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 80px",
                      padding: "15px 18px", borderBottom: i < visibleRecurring.length - 1 ? `1px solid ${t.border}` : "none",
                      alignItems: "center",
                    }}>
                      {/* Merchant */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 22 }}>{item.emoji}</span>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{item.name}</div>
                          {nextEstimate && <div style={{ fontSize: 11, color: t.textFaint }}>Next est. {nextEstimate}</div>}
                        </div>
                      </div>
                      {/* Category */}
                      <div>
                        <span style={{ padding: "3px 10px", borderRadius: 6, background: gs.bg, color: gs.color, fontSize: 12, fontWeight: 600 }}>
                          {item.category}
                        </span>
                      </div>
                      {/* Frequency */}
                      <div>
                        <span style={{ padding: "3px 10px", borderRadius: 999, background: `${freqColor}18`, color: freqColor, fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>
                          {item.frequency}
                        </span>
                      </div>
                      {/* Amount */}
                      <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>
                        -{fmtMoney(item.avgAmount)}
                        <span style={{ fontSize: 11, color: t.textFaint, marginLeft: 4 }}>/ {item.frequency === "quarterly" ? "qtr" : item.frequency === "biweekly" ? "2wk" : item.frequency === "weekly" ? "wk" : "mo"}</span>
                      </div>
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button
                          onClick={() => dismissRecurring(item.key)}
                          title="Dismiss"
                          style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.borderColor = "#EF4444" }}
                          onMouseLeave={e => { e.currentTarget.style.color = t.textMuted; e.currentTarget.style.borderColor = t.border }}
                        >Dismiss</button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Dismissed hint */}
              {dismissedRecurring.length > 0 && (
                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <button onClick={() => {
                    setDismissedRecurring([])
                    localStorage.removeItem(`dismissed_recurring_${session?.user?.id}`)
                  }} style={{ background: "none", border: "none", color: t.textFaint, fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
                    Restore {dismissedRecurring.length} dismissed
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Uncategorized view ── */}
      {txSubNav === "uncategorized" && (() => {
        const uncatRows = allRows.filter(r => !hiddenIds.has(r.id) && (r.category === "Other" || !r.category) && !r.is_transfer && !TRANSFER_CATS.has(r.category)).sort((a, b) => b.rawDate?.localeCompare(a.rawDate))
        return uncatRows.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 6 }}>All transactions are categorized</p>
            <p style={{ fontSize: 13, color: t.textFaint }}>Nothing left to review.</p>
          </div>
        ) : (
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 140px auto auto", padding: "12px 18px", background: t.hover, borderBottom: `1px solid ${t.border}`, gap: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>Transaction</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>Category</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Amount</span>
            </div>
            {uncatRows.map((r, i) => {
              const isEditing = editingId === r.id
              return (
                <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: "1fr 140px auto auto", padding: "12px 18px", borderBottom: i < uncatRows.length - 1 ? `1px solid ${t.border}` : "none", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>💳</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                  </div>
                  <div style={{ position: "relative" }}>
                    <button
                      ref={el => { if (isEditing && el) window.__catPickerAnchor = el }}
                      onClick={e => { window.__catPickerAnchor = e.currentTarget; setEditingId(isEditing ? null : r.id) }}
                      style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${isEditing ? t.accent : "#F59E0B"}`, background: isEditing ? (t.isDark ? "rgba(110,231,183,0.08)" : "rgba(47,133,90,0.06)") : "rgba(245,158,11,0.08)", color: isEditing ? t.accent : "#B45309", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                      Assign ▾
                    </button>
                    {isEditing && (
                      <>
                        <div onClick={() => setEditingId(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                        <CategoryPicker theme={t} currentCategory={r.category} onSelect={(label, group) => applyOverride(r.id, label, group)} onClose={() => setEditingId(null)} anchorEl={window.__catPickerAnchor} merchantName={r.name} merchantHistory={merchantHistory} communityMap={communityMap} hiddenCats={hiddenCats} />
                      </>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: t.textMuted, whiteSpace: "nowrap" }}>{r.rawDate}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: r.amount > 0 ? t.accent : t.text, textAlign: "right", whiteSpace: "nowrap" }}>
                    {r.amount > 0 ? "+" : ""}{fmtMoney(Math.abs(r.amount))}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })()}

      {/* ── All view ── */}
      {txSubNav === "all" && <>

      {/* Empty state */}
      {allRows.length === 0 && (
        <div style={{ ...card, padding: "56px 32px", textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: t.text }}>No transactions yet</h2>
          <p style={{ color: t.textMuted, fontSize: 14, maxWidth: 360, margin: "0 auto" }}>
            Your transactions will appear here automatically once your accounts are synced.
          </p>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search} onChange={e => { setSearch(e.target.value); setUncatFilter(false) }}
          placeholder="Search merchant or category..."
          style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none", width: 220 }}
        />
        {/* Period controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          <div style={{ display: "flex", background: t.hover, borderRadius: 8, padding: 3, gap: 2 }}>
            {["month","quarter","year"].map(pt => (
              <button key={pt} onClick={() => setTxPeriodType(pt)}
                style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                  background: txPeriodType === pt ? t.surface : "transparent",
                  color: txPeriodType === pt ? t.text : t.textMuted,
                  fontSize: 12, fontWeight: txPeriodType === pt ? 700 : 500,
                  boxShadow: txPeriodType === pt ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                  textTransform: "capitalize" }}>
                {pt}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={() => setTxPeriodOffset(o => o - 1)}
              style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
            <span style={{ minWidth: 120, textAlign: "center", fontSize: 13, fontWeight: 700, color: t.text }}>{txPeriod.label}</span>
            <button onClick={() => setTxPeriodOffset(o => o + 1)} disabled={txPeriodOffset >= 0}
              style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: txPeriodOffset >= 0 ? t.textFaint : t.text, fontSize: 15, cursor: txPeriodOffset >= 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
          </div>
        </div>
      </div>
      {/* Type filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "income", "needs", "wants", "savings", "transfer"].map(g => {
          const gs = GROUP_STYLE[g]
          const active = groupFilter === g
          return (
            <button key={g} onClick={() => setGroupFilter(g)} style={{
              padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer", textTransform: "capitalize",
              border: `1px solid ${active ? (gs?.color || t.accent) : t.border}`,
              background: active ? (gs?.bg || (t.isDark ? "rgba(110,231,183,0.1)" : "rgba(5,150,105,0.1)")) : "transparent",
              color: active ? (gs?.color || t.accent) : t.textMuted,
            }}>
              {g === "all" ? "All" : g}
            </button>
          )
        })}
        {(search || uncatFilter || groupFilter !== "all") && (
          <button onClick={() => { setSearch(""); setUncatFilter(false); setGroupFilter("all") }} style={{
            padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: "pointer",
            border: `1px solid ${t.border}`, background: "transparent", color: t.textFaint,
          }}>✕ Clear</button>
        )}
      </div>

      {/* Summary strip */}
      {(() => {
        const uncategorized = allRows.filter(r => !hiddenIds.has(r.id) && (!r.category || r.category === "Other") && !r.is_transfer).length
        return (
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {[
              { label: "Showing",  value: `${filtered.length} transactions`, color: t.text },
              { label: "Income",   value: `+${fmtMoney(totalIncome)}`,       color: "#2F855A" },
              { label: "Expenses", value: `-${fmtMoney(totalExpenses)}`,     color: "#EF4444" },
              { label: "Net",      value: fmtMoney(totalIncome - totalExpenses), color: totalIncome >= totalExpenses ? "#2F855A" : "#EF4444" },
            ].map(s => (
              <div key={s.label} style={{ padding: "9px 16px", borderRadius: 8, background: t.hover, border: `1px solid ${t.border}` }}>
                <span style={{ fontSize: 11, color: t.textFaint }}>{s.label} </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.value}</span>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Spending breakdown */}
      {allRows.length > 0 && groupFilter !== "income" && (() => {
        const EXCLUDED_CATS = new Set(["Transfer", "Credit Card Payment", "Transfers", "Credit Card Payments"])
        const byCategory = {}
        filtered.filter(r => r.amount < 0 && !EXCLUDED_CATS.has(r.category) && !r.is_transfer).forEach(r => {
          byCategory[r.category] = (byCategory[r.category] || 0) + Math.abs(r.amount)
        })
        const sorted = Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 6)
        if (sorted.length === 0) return null
        const maxVal = sorted[0][1]
        return (
          <div style={{ ...card, padding: "20px 22px", marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 16 }}>Top Spending Categories</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 28px" }}>
              {sorted.map(([cat, val]) => {
                const catObj = CATEGORIES.find(c => c.label === cat)
                const color = GROUP_STYLE[catObj?.group || 'wants']?.color || "#7CB342"
                return (
                  <div key={cat}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: 12, color: t.textMuted }}>{catObj?.emoji || "💳"} {cat}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{fmtMoney(val)}</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: t.hover }}>
                      <div style={{ height: "100%", borderRadius: 3, width: `${(val / maxVal) * 100}%`, background: color, transition: "width 0.4s ease" }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Grouped transaction list */}
      {filtered.length === 0 && allRows.length > 0 && (
        <div style={{ ...card, padding: 40, textAlign: "center", color: t.textMuted }}>No transactions match your filters</div>
      )}

      {groupedFiltered.map(([groupKey, rows]) => {
        const isCollapsed = collapsedGroups.has(groupKey)
        const groupIncome   = rows.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0)
        const groupExpenses = rows.filter(r => r.amount < 0).reduce((s, r) => s + Math.abs(r.amount), 0)
        const groupNet      = groupIncome - groupExpenses

        // Group header label
        let headerLabel = groupKey
        let headerSub = ""
        if (groupBy === "day") {
          const d = parseMaybeISODate(groupKey)
          if (d) {
            const today = new Date(); const yesterday = new Date(); yesterday.setDate(today.getDate()-1)
            const isToday     = d.toDateString() === today.toDateString()
            const isYesterday = d.toDateString() === yesterday.toDateString()
            headerLabel = isToday ? "Today" : isYesterday ? "Yesterday"
              : d.toLocaleDateString("default", { weekday: "long", month: "short", day: "numeric" })
          }
        } else if (groupBy === "category") {
          const catObj = CATEGORIES.find(c => c.label === groupKey)
          headerLabel = `${catObj?.emoji || "•"} ${groupKey}`
          headerSub = `${rows.length} transaction${rows.length !== 1 ? "s" : ""}`
        } else {
          // merchant
          const catObj = CATEGORIES.find(c => c.label === rows[0]?.category)
          headerLabel = `${catObj?.emoji || "🏪"} ${groupKey}`
          headerSub = `${rows.length} transaction${rows.length !== 1 ? "s" : ""}`
        }

        const colW = groupBy === "day"
          ? "1.6fr 0.9fr 110px 110px 90px 36px"
          : "90px 1.4fr 0.9fr 110px 90px 36px"

        return (
          <div key={groupKey} style={{ ...card, marginBottom: 10, overflow: "hidden" }}>
            {/* Group header */}
            <div
              onClick={() => toggleGroup(groupKey)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", cursor: "pointer", borderBottom: isCollapsed ? "none" : `1px solid ${t.border}`, background: t.isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{headerLabel}</span>
                {headerSub && <span style={{ fontSize: 12, color: t.textFaint }}>{headerSub}</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                {groupExpenses > 0 && <span style={{ fontSize: 13, fontWeight: 600, color: "#EF4444" }}>-{fmtMoney(groupExpenses)}</span>}
                {groupIncome > 0 && <span style={{ fontSize: 13, fontWeight: 600, color: "#2F855A" }}>+{fmtMoney(groupIncome)}</span>}
                {groupBy === "day" && groupExpenses > 0 && groupIncome > 0 && (
                  <span style={{ fontSize: 12, color: t.textFaint }}>net {groupNet >= 0 ? "+" : ""}{fmtMoney(groupNet)}</span>
                )}
                <span style={{ fontSize: 12, color: t.textFaint, marginLeft: 4 }}>{isCollapsed ? "▶" : "▼"}</span>
              </div>
            </div>

            {!isCollapsed && rows.map((row, i) => {
              const gs = GROUP_STYLE[row.group] || GROUP_STYLE.transfer
              const catObj = CATEGORIES.find(c => c.label === row.category)
              const isEditing = editingId === row.id

              return (
                <div key={row.id || i} style={{
                  display: "grid", gridTemplateColumns: colW,
                  padding: "13px 18px", borderBottom: i < rows.length - 1 ? `1px solid ${t.border}` : "none",
                  alignItems: "center", background: isEditing ? t.hover : "transparent", transition: "background 0.1s",
                }}>
                  {/* Date (only when not grouping by day) */}
                  {groupBy !== "day" && (
                    <div style={{ fontSize: 12, color: t.textFaint }}>
                      {(() => { const d = parseMaybeISODate(row.rawDate); return d ? `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}` : row.date })()}
                    </div>
                  )}

                  {/* Merchant */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{catObj?.emoji || row.icon || "💳"}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name}</span>
                        {row.pending && <span style={{ fontSize: 10, fontWeight: 700, color: "#F59E0B", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>pending</span>}
                      </div>
                      {(() => {
                        const srcAcct = row.plaid_account_id ? accountMap[row.plaid_account_id] : null
                        const srcLabel = srcAcct ? [srcAcct.institution_name, srcAcct.account_name || srcAcct.subtype].filter(Boolean).join(" · ") : null
                        const isTransfer = row.group === "transfer"

                        // For transfers, use matched pair credit side for destination
                        let dstLabel = null
                        if (isTransfer) {
                          const paired = pairMap.get(row.id)
                          if (paired) {
                            const dstAcct = paired.plaid_account_id ? accountMap[paired.plaid_account_id] : null
                            dstLabel = dstAcct
                              ? [dstAcct.institution_name, dstAcct.account_name || dstAcct.subtype].filter(Boolean).join(" · ")
                              : paired.name
                          } else {
                            // Fallback: try name matching against other accounts
                            const txNameLower = (row.rawName || row.name || "").toLowerCase()
                            for (const acct of Object.values(accountMap)) {
                              if (acct.plaid_account_id === row.plaid_account_id) continue
                              const instLower = (acct.institution_name || "").toLowerCase()
                              const nameLower = (acct.account_name || "").toLowerCase()
                              if ((instLower && txNameLower.includes(instLower)) || (nameLower && txNameLower.includes(nameLower))) {
                                dstLabel = [acct.institution_name, acct.account_name || acct.subtype].filter(Boolean).join(" · ")
                                break
                              }
                            }
                          }
                        }

                        if (isTransfer && (srcLabel || dstLabel)) {
                          const isPaired = pairMap.has(row.id)
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                              {isPaired && <span style={{ fontSize: 10, color: "#94A3B8" }}>↔</span>}
                              {srcLabel && <span style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.25)", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>FROM {srcLabel}</span>}
                              {dstLabel && <span style={{ fontSize: 10, color: t.textFaint }}>→</span>}
                              {dstLabel && <span style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.25)", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>TO {dstLabel}</span>}
                            </div>
                          )
                        }

                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {groupBy === "day" && row.category && row.category !== "Other" && <span style={{ fontSize: 11, color: t.textFaint }}>{row.category}</span>}
                            {srcLabel && <span style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, background: t.hover, border: `1px solid ${t.border}`, borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>{srcLabel}</span>}
                          </div>
                        )
                      })()}
                    </div>
                  </div>

                  {/* Category picker */}
                  <div style={{ position: "relative" }}>
                    <button
                      ref={el => { if (isEditing && el) window.__catPickerAnchor = el }}
                      onClick={e => { window.__catPickerAnchor = e.currentTarget; setEditingId(isEditing ? null : row.id) }}
                      style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${isEditing ? t.accent : t.border}`, background: isEditing ? (t.isDark ? "rgba(110,231,183,0.08)" : "rgba(47,133,90,0.06)") : t.hover, color: t.text, fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}>
                      {row.category} ▾
                    </button>
                    {isEditing && (
                      <>
                        <div onClick={() => setEditingId(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                        <CategoryPicker theme={t} currentCategory={row.category} onSelect={(label, group) => applyOverride(row.id, label, group)} onClose={() => setEditingId(null)} anchorEl={window.__catPickerAnchor} merchantName={row.name} merchantHistory={merchantHistory} communityMap={communityMap} hiddenCats={hiddenCats} />
                      </>
                    )}
                  </div>

                  {/* Amount */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: row.amount > 0 ? "#2F855A" : t.text, textAlign: "right" }}>
                    {row.amount > 0 ? "+" : "-"}{fmtMoney(Math.abs(row.amount))}
                  </div>

                  {/* Group pill */}
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setEditingGroupId(editingGroupId === row.id ? null : row.id)} style={{ padding: "3px 10px", borderRadius: 999, border: `1px solid ${editingGroupId === row.id ? gs.color : "transparent"}`, background: gs.bg, color: gs.color, fontSize: 11, fontWeight: 700, textTransform: "capitalize", cursor: "pointer" }}>
                      {row.group} ▾
                    </button>
                    {editingGroupId === row.id && (
                      <>
                        <div onClick={() => setEditingGroupId(null)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
                        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", padding: 6, width: 150 }}>
                          {Object.entries(GROUP_STYLE).map(([key, gs2]) => (
                            <button key={key} onClick={() => { applyOverride(row.id, row.category, key); setEditingGroupId(null) }}
                              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, border: "none", background: row.group === key ? t.hover : "transparent", color: row.group === key ? gs2.color : t.textMuted, fontSize: 12, fontWeight: row.group === key ? 700 : 400, cursor: "pointer", textAlign: "left", textTransform: "capitalize" }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: gs2.color, flexShrink: 0 }} />
                              {key}
                              {row.group === key && <span style={{ marginLeft: "auto", color: gs2.color }}>✓</span>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Delete */}
                  <button onClick={() => { if (window.confirm("Delete this transaction?")) deleteTransaction(row.id) }} title="Delete"
                    style={{ width: 28, height: 28, borderRadius: 6, border: "none", background: "transparent", color: t.textMuted, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.background = "rgba(239,68,68,0.1)" }}
                    onMouseLeave={e => { e.currentTarget.style.color = t.textMuted; e.currentTarget.style.background = "transparent" }}
                  >🗑</button>
                </div>
              )
            })}
          </div>
        )
      })}

    </>}

    </div>

    {/* ── Manual Entry Modal ── */}
    {showManual && (
      <div onClick={() => setShowManual(false)} style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 28, width: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: t.text, marginBottom: 20 }}>Add Transaction</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Date</label>
              <input type="date" value={manualForm.date} onChange={e => setManualForm(p => ({ ...p, date: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Merchant / Description</label>
              <input value={manualForm.name} onChange={e => setManualForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Starbucks"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Amount ($)</label>
              <input type="number" min="0" step="0.01" value={manualForm.amount} onChange={e => setManualForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 6 }}>Category</label>
              <select value={manualForm.category} onChange={e => setManualForm(p => ({ ...p, category: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 14, boxSizing: "border-box" }}>
                {CATEGORIES.map(c => <option key={c.label} value={c.label}>{c.emoji} {c.label}</option>)}
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={manualForm.isIncome} onChange={e => setManualForm(p => ({ ...p, isIncome: e.target.checked }))} />
              <span style={{ fontSize: 13, color: t.textMuted }}>This is income (money received)</span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
            <button onClick={() => setShowManual(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={saveManualTransaction} disabled={manualSaving || !manualForm.name || !manualForm.amount}
              style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: manualSaving ? "not-allowed" : "pointer", opacity: (!manualForm.name || !manualForm.amount) ? 0.5 : 1 }}>
              {manualSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── CSV Import Modal ── */}
    {showCsv && (
      <div onClick={() => setShowCsv(false)} style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, width: 560, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.4)" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0 }}>Import CSV</h2>
            <button onClick={() => setShowCsv(false)} style={{ background: "none", border: "none", color: t.textFaint, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
          </div>
          {/* Column legend */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Column guide</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {[
                { name: "date", required: true },
                { name: "name / description", required: true },
                { name: "amount", required: true },
                { name: "category", required: false },
                { name: "merchant_name", required: false },
              ].map(col => (
                <span key={col.name} style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: col.required ? "rgba(234,179,8,0.15)" : t.hover,
                  border: `1px solid ${col.required ? "rgba(234,179,8,0.4)" : t.border}`,
                  color: col.required ? "#CA8A04" : t.textMuted,
                }}>
                  {col.name}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: t.textFaint }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(234,179,8,0.3)", border: "1px solid rgba(234,179,8,0.5)", display: "inline-block" }} />
                Required
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: t.hover, border: `1px solid ${t.border}`, display: "inline-block" }} />
                Optional
              </span>
            </div>
          </div>

          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 20 }}>
            <a
              href="#"
              onClick={e => {
                e.preventDefault()
                const csv = "date,name,amount,category,merchant_name\n2024-01-15,Grocery Store,-52.40,Food,Whole Foods\n2024-01-16,Paycheck,2500.00,Income,Employer Inc\n2024-01-17,Electric Bill,-120.00,Utilities,\n# Tip: use negative amounts for expenses, positive for income"
                const blob = new Blob([csv], { type: "text/csv" })
                const url = URL.createObjectURL(blob)
                const a = document.createElement("a")
                a.href = url; a.download = "grove-template.csv"; a.click()
                URL.revokeObjectURL(url)
              }}
              style={{ color: t.accent, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
            >
              Download our template here
            </a>
          </p>

          {/* Drop zone */}
          {csvRows.length === 0 && (
            <CsvDropZone theme={t} onFile={file => {
              if (!file) return
              const reader = new FileReader()
              reader.onload = ev => parseCsv(ev.target.result)
              reader.readAsText(file)
            }} />
          )}

          {csvError && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#EF4444" }}>
              {csvError}
            </div>
          )}

          {csvRows.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: t.textMuted }}><strong style={{ color: t.text }}>{csvRows.length}</strong> transactions detected</span>
                <button onClick={() => setCsvRows([])} style={{ fontSize: 12, color: t.textMuted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Clear</button>
              </div>
              <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 90px", padding: "8px 14px", background: t.hover, borderBottom: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em" }}>Description</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Amount</span>
                </div>
                {csvRows.slice(0, 6).map((r, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "90px 1fr 90px", padding: "10px 14px", borderBottom: i < Math.min(csvRows.length, 6) - 1 ? `1px solid ${t.border}` : "none", fontSize: 13 }}>
                    <span style={{ color: t.textMuted }}>{r.date}</span>
                    <span style={{ color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                    <span style={{ color: r.isIncome ? "#2F855A" : "#EF4444", textAlign: "right", fontWeight: 600 }}>{r.isIncome ? "+" : "-"}${Math.abs(r.rawAmount).toFixed(2)}</span>
                  </div>
                ))}
                {csvRows.length > 6 && <div style={{ padding: "10px 14px", fontSize: 12, color: t.textFaint, background: t.hover }}>+{csvRows.length - 6} more rows</div>}
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setShowCsv(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={importCsvRows} disabled={csvImporting || csvRows.length === 0}
              style={{ flex: 2, padding: "12px 0", borderRadius: 10, border: "none", background: csvRows.length === 0 ? t.border : t.accent, color: csvRows.length === 0 ? t.textFaint : "#fff", fontSize: 14, fontWeight: 700, cursor: (csvImporting || csvRows.length === 0) ? "not-allowed" : "pointer", transition: "background 0.15s" }}>
              {csvImporting ? "Importing..." : csvRows.length > 0 ? `Import ${csvRows.length} transactions` : "Select a file first"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}

// ── DASHBOARD ──────────────────────────────────────────────
function formatBirthdayInput(value) {
  const digits = value.replace(/\D/g, "").slice(0, 8)
  const parts = []
  if (digits.length >= 2) parts.push(digits.slice(0, 2))
  else if (digits.length > 0) parts.push(digits)
  if (digits.length >= 4) parts.push(digits.slice(2, 4))
  else if (digits.length > 2) parts.push(digits.slice(2))
  if (digits.length > 4) parts.push(digits.slice(4))
  return parts.join("/")
}

function PaycheckSection({ theme, paychecks, savePaychecks, householdMembers = [], accounts = [] }) {
  const t = theme
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(null)
  const FREQS = ["weekly","biweekly","semi-monthly","monthly"]
  const card = makeCard(t)
  const fmt = (n) => `$${Math.round(n).toLocaleString()}`

  function calcDeductions(p) {
    const gross = p.payType === "hourly" ? (p.hourlyRate||0) * (p.typicalHours||0) : (p.gross||0)
    const { contrib: contrib401k, match: match401k } = calc401k(p, gross)
    const preTax = contrib401k + (p.hsa||0) + (p.healthInsurance||0) + (p.dental||0) + (p.vision||0) + (p.preTaxOther||[]).reduce((s,r)=>s+(r.amount||0),0)
    const taxes  = (p.federalTax||0)+(p.stateTax||0)+(p.socialSecurity||0)+(p.medicare||0)
    const post   = (p.contribRoth401k||0)+(p.postTaxOther||[]).reduce((s,r)=>s+(r.amount||0),0)
    return { gross, contrib401k, match401k, preTax, taxes, post, contribRoth401k: p.contribRoth401k||0, takeHome: gross - preTax - taxes - post }
  }

  function newPaycheck() {
    const p = {
      id: crypto.randomUUID(), name: "", employer: "", frequency: "biweekly", payType: "salaried",
      gross: 0, hourlyRate: 0, typicalHours: 80,
      contrib401kType: "percent", contrib401kPct: 0, contrib401k: 0,
      match401kPct: 0, k401AccountId: null, hsa: 0, preTaxOther: [],
      federalTax: 0, stateTax: 0, socialSecurity: 0, medicare: 0,
      healthInsurance: 0, dental: 0, vision: 0, contribRoth401k: 0, postTaxOther: [],
      assigned_to: householdMembers.length > 0 ? householdMembers[0].id : null,
    }
    setForm(p); setEditingId(p.id)
  }
  function openEdit(p) { setForm({...p}); setEditingId(p.id) }
  function saveForm() {
    const next = paychecks.find(p => p.id === form.id)
      ? paychecks.map(p => p.id === form.id ? form : p)
      : [...paychecks, form]
    savePaychecks(next); setEditingId(null); setForm(null)
  }
  function deletePaycheck(id) { savePaychecks(paychecks.filter(p => p.id !== id)); setEditingId(null); setForm(null) }

  const inputStyle = { width: "100%", padding: "7px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, boxSizing: "border-box", outline: "none" }
  const numStyle = { ...inputStyle, textAlign: "right" }
  const fRow = (label, key, hint, gross) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
      <div>
        <span style={{ fontSize: 13, color: t.text }}>{label}</span>
        {hint && <span style={{ fontSize: 11, color: t.textFaint, marginLeft: 6 }}>{hint}</span>}
      </div>
      <input type="number" min={0} value={form[key]||""} placeholder="0"
        onChange={e => setForm(f => ({ ...f, [key]: Number(e.target.value)||0 }))}
        style={{ width: 100, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
    </div>
  )

  // Table row helpers for read view
  const thStyle = { fontSize: 11, color: t.textFaint, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", padding: "0 0 8px", textAlign: "left" }
  const thR = { ...thStyle, textAlign: "right" }
  const sectionHdr = (label) => (
    <tr><td colSpan={3} style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", paddingTop: 12, paddingBottom: 4, borderTop: `1px solid ${t.border}` }}>{label}</td></tr>
  )
  const dataRow = (label, perCheck, monthly, color="#EF4444", sign="−") => (
    <tr>
      <td style={{ fontSize: 12, color: t.textMuted, padding: "4px 0 4px 12px" }}>{label}</td>
      <td style={{ fontSize: 12, color, textAlign: "right", whiteSpace: "nowrap" }}>{sign} {fmt(perCheck)}</td>
      <td style={{ fontSize: 12, color: t.textFaint, textAlign: "right", whiteSpace: "nowrap" }}>{fmt(monthly)}</td>
    </tr>
  )

  const allCards = [...paychecks, ...(form && !paychecks.find(p => p.id === form.id) ? [form] : [])]

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: t.textMuted }}>
          Enter your paycheck details once — Grove uses them to track gross income, taxes, and 401k contributions.
        </p>
        <button onClick={newPaycheck} style={{ ...makeBtnPrimary(t), padding: "8px 16px", fontSize: 13, flexShrink: 0 }}>+ Add Paycheck</button>
      </div>

      {allCards.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 20px", color: t.textFaint, border: `1px dashed ${t.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>💼</div>
          <p style={{ fontWeight: 600, marginBottom: 6, color: t.text }}>No paychecks set up yet</p>
          <p style={{ fontSize: 13 }}>Add your paycheck details to see your gross income, taxes, and deductions broken down clearly.</p>
        </div>
      )}

      {allCards.map(p => {
        const isEditing = editingId === p.id && form
        const isNew = !paychecks.find(x => x.id === p.id)
        const m = FREQ_TO_MONTHLY[p.frequency] || 2.167
        const { gross, contrib401k, match401k, preTax, taxes, post, contribRoth401k, takeHome } = calcDeductions(p)

        return (
          <div key={p.id} style={{ ...card, padding: "22px 24px", marginBottom: 16 }}>
            {isEditing ? (
              /* ── FORM ── */
              <div>
                {isNew && <p style={{ fontSize: 13, fontWeight: 700, color: t.accent, marginBottom: 16 }}>New Paycheck</p>}

                {/* Name + Employer */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  {[["Job Name","name"],["Employer","employer"]].map(([l,k]) => (
                    <div key={k}>
                      <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>{l}</label>
                      <input value={form[k]||""} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} style={inputStyle} />
                    </div>
                  ))}
                </div>

                {/* Household member assignment */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 6 }}>Belongs to</label>
                  {householdMembers.length > 0 ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      {householdMembers.map(m => (
                        <button key={m.id} onClick={() => setForm(f => ({ ...f, assigned_to: m.id }))}
                          style={{ padding: "6px 16px", borderRadius: 99, border: `2px solid ${form.assigned_to === m.id ? m.color || t.accent : t.border}`, background: form.assigned_to === m.id ? (m.color || t.accent) + "22" : "transparent", color: form.assigned_to === m.id ? m.color || t.accent : t.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: 12, color: t.textFaint, margin: 0 }}>Set up household members in Settings → Household to assign this paycheck to a person.</p>
                  )}
                </div>

                {/* Pay type + Frequency */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Pay Type</label>
                    <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}>
                      {["salaried","hourly"].map(pt => (
                        <button key={pt} onClick={() => setForm(f=>({...f, payType: pt}))}
                          style={{ flex: 1, padding: "7px 0", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                            background: form.payType === pt ? t.accent : t.inputBg,
                            color: form.payType === pt ? "#fff" : t.textMuted }}>
                          {pt.charAt(0).toUpperCase()+pt.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Pay Frequency</label>
                    <select value={form.frequency} onChange={e => setForm(f=>({...f,frequency:e.target.value}))} style={inputStyle}>
                      {FREQS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
                    </select>
                  </div>
                </div>

                {/* Gross — salaried vs hourly */}
                {form.payType === "hourly" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
                    <div>
                      <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Hourly Rate ($)</label>
                      <input type="number" min={0} value={form.hourlyRate||""} placeholder="0"
                        onChange={e => {
                          const rate = Number(e.target.value)||0
                          const hrs = form.typicalHours||0
                          const g = rate * hrs
                          setForm(f=>({...f, hourlyRate: rate, socialSecurity: Math.round(g*0.062), medicare: Math.round(g*0.0145)}))
                        }} style={numStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Typical Hours / Period</label>
                      <input type="number" min={0} value={form.typicalHours||""} placeholder="80"
                        onChange={e => {
                          const hrs = Number(e.target.value)||0
                          const rate = form.hourlyRate||0
                          const g = rate * hrs
                          setForm(f=>({...f, typicalHours: hrs, socialSecurity: Math.round(g*0.062), medicare: Math.round(g*0.0145)}))
                        }} style={numStyle} />
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 18 }}>
                    <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 4 }}>Gross per Paycheck ($)</label>
                    <input type="number" min={0} value={form.gross||""} placeholder="0"
                      onChange={e => {
                        const g = Number(e.target.value)||0
                        setForm(f=>({...f, gross: g, socialSecurity: Math.round(g*0.062), medicare: Math.round(g*0.0145)}))
                      }} style={numStyle} />
                  </div>
                )}

                {/* Pre-tax deductions */}
                <p style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Pre-tax Deductions (per paycheck)</p>
                {/* 401k Contribution — $ or % toggle */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: t.text }}>401k Contribution</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: `1px solid ${t.border}` }}>
                      {["dollar","percent"].map(tp => (
                        <button key={tp} onClick={() => setForm(f=>({...f, contrib401kType: tp}))}
                          style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                            background: form.contrib401kType === tp ? t.accent : t.inputBg,
                            color: form.contrib401kType === tp ? "#fff" : t.textMuted }}>
                          {tp === "dollar" ? "$" : "%"}
                        </button>
                      ))}
                    </div>
                    {form.contrib401kType === "percent" ? (
                      <input type="number" min={0} max={100} step={0.1} value={form.contrib401kPct||""} placeholder="0"
                        onChange={e => setForm(f=>({...f, contrib401kPct: Number(e.target.value)||0}))}
                        style={{ width: 80, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                    ) : (
                      <input type="number" min={0} value={form.contrib401k||""} placeholder="0"
                        onChange={e => setForm(f=>({...f, contrib401k: Number(e.target.value)||0}))}
                        style={{ width: 80, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                    )}
                  </div>
                </div>

                {/* 401k Employer Match — always % */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: t.text }}>401k Employer Match <span style={{ fontSize: 11, color: t.textFaint }}>% of gross</span></span>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="number" min={0} max={100} step={0.1} value={form.match401kPct||""} placeholder="0"
                      onChange={e => setForm(f=>({...f, match401kPct: Number(e.target.value)||0}))}
                      style={{ width: 80, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                    <span style={{ fontSize: 13, color: t.textMuted }}>%</span>
                  </div>
                </div>

                {/* Link to 401k account */}
                {(() => {
                  const k401Accounts = accounts.filter(a => ['401k','403b','roth 401k','457b','pension'].some(s => (a.subtype||'').toLowerCase().includes(s)))
                  if (!k401Accounts.length) return null
                  return (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontSize: 13, color: t.text }}>Linked 401k Account <span style={{ fontSize: 11, color: t.textFaint }}>for retirement planning</span></span>
                      <select
                        value={form.k401AccountId || ""}
                        onChange={e => setForm(f => ({ ...f, k401AccountId: e.target.value || null }))}
                        style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none", maxWidth: 180 }}
                      >
                        <option value="">— Not linked —</option>
                        {k401Accounts.map(a => (
                          <option key={a.id} value={a.id}>{a.account_name || a.name || a.subtype}</option>
                        ))}
                      </select>
                    </div>
                  )
                })()}

                {fRow("HSA", "hsa")}
                {fRow("Health Insurance", "healthInsurance")}
                {fRow("Dental", "dental")}
                {fRow("Vision", "vision")}
                {(form.preTaxOther||[]).map((r,i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <input value={r.label||""} placeholder="Label" onChange={e => setForm(f=>({...f,preTaxOther:f.preTaxOther.map((x,j)=>j===i?{...x,label:e.target.value}:x)}))}
                      style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none" }} />
                    <input type="number" min={0} value={r.amount||""} placeholder="0" onChange={e => setForm(f=>({...f,preTaxOther:f.preTaxOther.map((x,j)=>j===i?{...x,amount:Number(e.target.value)||0}:x)}))}
                      style={{ width: 90, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                    <button onClick={() => setForm(f=>({...f,preTaxOther:f.preTaxOther.filter((_,j)=>j!==i)}))} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>
                ))}
                <button onClick={() => setForm(f=>({...f,preTaxOther:[...(f.preTaxOther||[]),{label:"",amount:0}]}))}
                  style={{ fontSize: 12, color: t.accent, background: "none", border: "none", cursor: "pointer", marginBottom: 16 }}>+ Add pre-tax deduction</button>

                {/* Taxes */}
                <p style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Taxes (per paycheck)</p>
                {fRow("Federal Tax Withheld", "federalTax")}
                {fRow("State Tax Withheld", "stateTax")}
                {fRow("Social Security", "socialSecurity", `auto: ${Math.round(((form.payType==="hourly"?(form.hourlyRate||0)*(form.typicalHours||0):form.gross||0))*0.062)}`)}
                {fRow("Medicare", "medicare", `auto: ${Math.round(((form.payType==="hourly"?(form.hourlyRate||0)*(form.typicalHours||0):form.gross||0))*0.0145)}`)}

                {/* Post-tax deductions */}
                <p style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, marginTop: 4 }}>Post-tax Deductions (per paycheck)</p>
                {fRow("Roth 401k Contribution", "contribRoth401k")}
                {(form.postTaxOther||[]).map((r,i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <input value={r.label||""} placeholder="Label" onChange={e => setForm(f=>({...f,postTaxOther:f.postTaxOther.map((x,j)=>j===i?{...x,label:e.target.value}:x)}))}
                      style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none" }} />
                    <input type="number" min={0} value={r.amount||""} placeholder="0" onChange={e => setForm(f=>({...f,postTaxOther:f.postTaxOther.map((x,j)=>j===i?{...x,amount:Number(e.target.value)||0}:x)}))}
                      style={{ width: 90, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                    <button onClick={() => setForm(f=>({...f,postTaxOther:f.postTaxOther.filter((_,j)=>j!==i)}))} style={{ background: "none", border: "none", color: t.textFaint, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>
                ))}
                <button onClick={() => setForm(f=>({...f,postTaxOther:[...(f.postTaxOther||[]),{label:"",amount:0}]}))}
                  style={{ fontSize: 12, color: t.accent, background: "none", border: "none", cursor: "pointer", marginBottom: 20 }}>+ Add post-tax deduction</button>

                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={saveForm} style={{ ...makeBtnPrimary(t), padding: "8px 20px" }}>{isNew ? "Save Paycheck" : "Save"}</button>
                  <button onClick={() => { setEditingId(null); setForm(null) }} style={{ ...makeBtnSecondary(t), padding: "8px 20px" }}>Cancel</button>
                  {!isNew && <button onClick={() => deletePaycheck(p.id)} style={{ marginLeft: "auto", fontSize: 12, color: "#EF4444", background: "none", border: "none", cursor: "pointer" }}>Delete</button>}
                </div>
              </div>
            ) : (
              /* ── READ VIEW ── */
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>{p.name || p.employer || "Paycheck"}</p>
                      {householdMembers.length > 0 && (() => {
                        const member = householdMembers.find(m => m.id === p.assigned_to)
                        return member ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: member.color || t.accent, background: (member.color || t.accent) + "22", borderRadius: 99, padding: "2px 8px" }}>{member.name}</span>
                        ) : null
                      })()}
                    </div>
                    <p style={{ fontSize: 12, color: t.textMuted }}>
                      {p.employer && `${p.employer} · `}
                      {p.payType === "hourly" ? `$${p.hourlyRate||0}/hr · ` : ""}
                      {p.frequency.charAt(0).toUpperCase()+p.frequency.slice(1)}
                    </p>
                  </div>
                  <button onClick={() => openEdit(p)} style={{ ...makeBtnSecondary(t), padding: "6px 14px", fontSize: 12 }}>Edit</button>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Item</th>
                      <th style={thR}>Per paycheck</th>
                      <th style={thR}>Monthly est.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ fontSize: 13, color: t.text, fontWeight: 600, padding: "6px 0", borderTop: `1px solid ${t.border}` }}>
                        {p.payType === "hourly" ? `Gross (${p.typicalHours||0} hrs)` : "Gross"}
                      </td>
                      <td style={{ fontSize: 13, fontWeight: 700, color: t.text, textAlign: "right", borderTop: `1px solid ${t.border}` }}>{fmt(gross)}</td>
                      <td style={{ fontSize: 12, color: t.textMuted, textAlign: "right", borderTop: `1px solid ${t.border}` }}>{fmt(gross*m)}</td>
                    </tr>

                    {preTax > 0 && <>
                      {sectionHdr("Pre-tax Deductions")}
                      {contrib401k > 0 && dataRow(
                        p.contrib401kType === "percent" ? `401k (${p.contrib401kPct}%)` : "401k Contribution",
                        contrib401k, contrib401k*m
                      )}
                      {match401k > 0 && dataRow(`401k Match (${p.match401kPct}%)`, match401k, match401k*m, t.accent2, "+")}
                      {(p.hsa||0)            > 0 && dataRow("HSA", p.hsa, p.hsa*m)}
                      {(p.healthInsurance||0) > 0 && dataRow("Health Insurance", p.healthInsurance, p.healthInsurance*m)}
                      {(p.dental||0)          > 0 && dataRow("Dental", p.dental, p.dental*m)}
                      {(p.vision||0)          > 0 && dataRow("Vision", p.vision, p.vision*m)}
                      {(p.preTaxOther||[]).filter(r=>r.amount>0).map((r,i) => dataRow(r.label||"Other", r.amount, r.amount*m))}
                    </>}

                    {taxes > 0 && <>
                      {sectionHdr("Taxes")}
                      {(p.federalTax||0)     > 0 && dataRow("Federal", p.federalTax, p.federalTax*m)}
                      {(p.stateTax||0)       > 0 && dataRow("State", p.stateTax, p.stateTax*m)}
                      {(p.socialSecurity||0) > 0 && dataRow("Social Security (6.2%)", p.socialSecurity, p.socialSecurity*m)}
                      {(p.medicare||0)       > 0 && dataRow("Medicare (1.45%)", p.medicare, p.medicare*m)}
                    </>}

                    {post > 0 && <>
                      {sectionHdr("Post-tax Deductions")}
                      {contribRoth401k > 0 && dataRow("Roth 401k", contribRoth401k, contribRoth401k*m)}
                      {(p.postTaxOther||[]).filter(r=>r.amount>0).map((r,i) => dataRow(r.label||"Other", r.amount, r.amount*m))}
                    </>}

                    <tr>
                      <td style={{ fontSize: 14, fontWeight: 700, color: t.text, paddingTop: 12, borderTop: `2px solid ${t.border}` }}>
                        Take-home
                        {p.payType === "hourly" && <span style={{ fontSize: 11, fontWeight: 400, color: t.textFaint, marginLeft: 6 }}>· estimated</span>}
                      </td>
                      <td style={{ fontSize: 14, fontWeight: 800, color: t.accent, textAlign: "right", paddingTop: 12, borderTop: `2px solid ${t.border}` }}>{fmt(takeHome)}</td>
                      <td style={{ fontSize: 13, color: t.textMuted, textAlign: "right", paddingTop: 12, borderTop: `2px solid ${t.border}` }}>{fmt(takeHome*m)}</td>
                    </tr>

                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ReportIssueTab({ session, theme: t }) {
  const AREAS = ["Budget & Expenses", "Accounts & Banking", "Investments", "Retirement", "Cash Flow", "Settings", "Other"]
  const SEVERITIES = [
    { value: "annoyance", label: "Minor annoyance", emoji: "😤" },
    { value: "broken",    label: "Something's not working", emoji: "⚠️" },
    { value: "critical",  label: "App is unusable", emoji: "🔴" },
  ]

  const [area, setArea] = useState("")
  const [severity, setSeverity] = useState("")
  const [description, setDescription] = useState("")
  const [status, setStatus] = useState(null) // null | "sending" | "sent" | "error"

  const descOk = description.trim().length >= 50

  async function submit() {
    if (!area || !severity || !descOk) return
    setStatus("sending")
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const res = await fetch(`${API_BASE}/api/report-issue`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${s?.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          area, severity, description,
          userEmail: session.user.email,
          page: window.location.href,
          userAgent: navigator.userAgent,
        }),
      })
      const data = await res.json()
      setStatus(data.ok ? "sent" : "error")
    } catch {
      setStatus("error")
    }
  }

  if (status === "sent") return (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>✅</div>
      <p style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 8 }}>Report submitted!</p>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>We'll look into it. Thanks for helping improve Grove.</p>
      <button onClick={() => { setArea(""); setSeverity(""); setDescription(""); setStatus(null) }}
        style={{ padding: "9px 20px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        Submit another
      </button>
    </div>
  )

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 4 }}>Report an Issue</h2>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Help us fix what's broken — takes 30 seconds.</p>

      {/* Area */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Where is the problem?</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {AREAS.map(a => (
            <button key={a} onClick={() => setArea(a)} style={{
              padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: `1px solid ${area === a ? t.accent : t.border}`,
              background: area === a ? (t.isDark ? "rgba(47,133,90,0.15)" : "rgba(47,133,90,0.08)") : "transparent",
              color: area === a ? t.accent : t.text,
            }}>{a}</button>
          ))}
        </div>
      </div>

      {/* Severity */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>How bad is it?</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SEVERITIES.map(s => (
            <div key={s.value} onClick={() => setSeverity(s.value)} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, cursor: "pointer",
              border: `1px solid ${severity === s.value ? t.accent : t.border}`,
              background: severity === s.value ? (t.isDark ? "rgba(47,133,90,0.1)" : "rgba(47,133,90,0.06)") : "transparent",
            }}>
              <span style={{ fontSize: 18 }}>{s.emoji}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: t.text }}>{s.label}</span>
              {severity === s.value && <span style={{ marginLeft: "auto", fontSize: 12, color: t.accent, fontWeight: 700 }}>✓</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>What happened?</p>
          <span style={{ fontSize: 11, color: descOk ? t.accent : t.textFaint }}>{description.trim().length}/50 min</span>
        </div>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe what you were doing and what went wrong…"
          rows={4}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${description.length > 0 && !descOk ? "#EF4444" : t.border}`, background: t.inputBg, color: t.text, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
        />
        {description.length > 0 && !descOk && (
          <p style={{ fontSize: 11, color: "#EF4444", margin: "4px 0 0" }}>Please add a bit more detail ({50 - description.trim().length} characters left)</p>
        )}
      </div>

      {status === "error" && <p style={{ fontSize: 13, color: "#EF4444", marginBottom: 12 }}>Failed to send. Please try again.</p>}

      <button
        onClick={submit}
        disabled={!area || !severity || !descOk || status === "sending"}
        style={{
          padding: "10px 24px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, cursor: (!area || !severity || !descOk) ? "default" : "pointer",
          background: (!area || !severity || !descOk) ? t.border : t.accent,
          color: (!area || !severity || !descOk) ? t.textFaint : "#fff",
          transition: "background 0.2s",
        }}
      >
        {status === "sending" ? "Submitting…" : "Submit Report"}
      </button>
    </div>
  )
}

function RequestFeatureTab({ session, theme: t }) {
  const CATEGORIES = ["Budget & Spending", "Accounts & Banking", "Investments", "Retirement", "Cash Flow", "Simulators", "Notifications", "Other"]
  const [category, setCategory] = useState("")
  const [description, setDescription] = useState("")
  const [status, setStatus] = useState(null)

  const descOk = description.trim().length >= 30

  async function submit() {
    if (!category || !descOk) return
    setStatus("sending")
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const res = await fetch(`${API_BASE}/api/request-feature`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${s?.access_token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category, description, userEmail: session.user.email }),
      })
      const data = await res.json()
      setStatus(data.ok ? "sent" : "error")
    } catch {
      setStatus("error")
    }
  }

  if (status === "sent") return (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>💡</div>
      <p style={{ fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 8 }}>Idea submitted!</p>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Thanks — we read every suggestion and use them to shape what we build next.</p>
      <button onClick={() => { setCategory(""); setDescription(""); setStatus(null) }}
        style={{ padding: "9px 20px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        Submit another
      </button>
    </div>
  )

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 4 }}>Request a Feature</h2>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 24 }}>Got an idea? We'd love to hear it — this goes straight to the founder.</p>

      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Which area does it relate to?</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)} style={{
              padding: "7px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: `1px solid ${category === c ? t.accent : t.border}`,
              background: category === c ? (t.isDark ? "rgba(47,133,90,0.15)" : "rgba(47,133,90,0.08)") : "transparent",
              color: category === c ? t.accent : t.text,
            }}>{c}</button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 }}>Describe your idea</p>
          <span style={{ fontSize: 11, color: descOk ? t.accent : t.textFaint }}>{description.trim().length}/30 min</span>
        </div>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What would you like to see? The more detail the better…"
          rows={4}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${description.length > 0 && !descOk ? "#EF4444" : t.border}`, background: t.inputBg, color: t.text, fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
        />
        {description.length > 0 && !descOk && (
          <p style={{ fontSize: 11, color: "#EF4444", margin: "4px 0 0" }}>{30 - description.trim().length} more characters needed</p>
        )}
      </div>

      {status === "error" && <p style={{ fontSize: 13, color: "#EF4444", marginBottom: 12 }}>Failed to send. Please try again.</p>}

      <button
        onClick={submit}
        disabled={!category || !descOk || status === "sending"}
        style={{
          padding: "10px 24px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700,
          cursor: (!category || !descOk) ? "default" : "pointer",
          background: (!category || !descOk) ? t.border : t.accent,
          color: (!category || !descOk) ? t.textFaint : "#fff",
          transition: "background 0.2s",
        }}
      >
        {status === "sending" ? "Submitting…" : "Submit Idea"}
      </button>
    </div>
  )
}

function SecuritySettingsTab({ session, theme: t }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteStatus, setDeleteStatus] = useState(null) // null | "deleting" | "done" | "error"
  const [confirmText, setConfirmText] = useState("")

  async function deleteAllData() {
    setDeleteStatus("deleting")
    const uid = session.user.id
    try {
      // Delete all user data across tables (order matters for FK constraints)
      const tables = [
        "transactions",
        "accounts",
        "plaid_tokens",
        "net_worth_snapshots",
        "retirement_account_settings",
        "retirement_inputs",
        "household_members",
        "user_paychecks",
        "user_notification_settings",
        "profiles",
      ]
      for (const table of tables) {
        await supabase.from(table).delete().eq("user_id", uid)
      }
      // Delete auth account last
      await supabase.auth.admin?.deleteUser?.(uid)
      setDeleteStatus("done")
      setTimeout(() => supabase.auth.signOut(), 1500)
    } catch (e) {
      console.error("Delete error:", e)
      setDeleteStatus("error")
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 6 }}>Security</h2>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 18 }}>
        More security controls coming soon
      </p>

      <div style={{ padding: 14, borderRadius: 12, background: t.hover, border: `1px solid ${t.border}`, marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: t.text, fontWeight: 700, marginBottom: 6 }}>Password</div>
        <div style={{ fontSize: 12, color: t.textMuted }}>
          For now, change password from the Supabase auth flow you already have.
          Next we'll move it here.
        </div>
      </div>

      <div style={{ padding: 20, background: "rgba(239,68,68,0.06)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: "#EF4444", marginBottom: 6 }}>Danger Zone</h4>
        <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>These actions are permanent and cannot be undone.</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => supabase.auth.signOut()} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#EF4444", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Sign Out All Devices
          </button>
          <button onClick={() => { setShowDeleteModal(true); setConfirmText(""); setDeleteStatus(null) }} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#EF4444", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Delete My Data
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <>
          <div onClick={() => setShowDeleteModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1001, background: t.card, borderRadius: 16, padding: 28, width: 360, maxWidth: "calc(100vw - 32px)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", border: `1px solid ${t.border}` }}>
            {deleteStatus === "done" ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🗑️</div>
                <p style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 6 }}>All data deleted</p>
                <p style={{ fontSize: 13, color: t.textMuted }}>Signing you out…</p>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 8 }}>Delete all your data?</h3>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
                  This will permanently delete all your transactions, accounts, budgets, investments, and retirement data. <strong style={{ color: t.text }}>There is no way to recover it.</strong>
                </p>
                <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>
                  Type <strong style={{ color: t.text }}>DELETE</strong> to confirm:
                </p>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${confirmText === "DELETE" ? "#EF4444" : t.border}`, background: t.inputBg, color: t.text, fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 16 }}
                />
                {deleteStatus === "error" && (
                  <p style={{ fontSize: 12, color: "#EF4444", marginBottom: 12 }}>Something went wrong. Please try again or contact support.</p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button
                    onClick={deleteAllData}
                    disabled={confirmText !== "DELETE" || deleteStatus === "deleting"}
                    style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: confirmText === "DELETE" ? "#EF4444" : t.border, color: confirmText === "DELETE" ? "#fff" : t.textFaint, fontSize: 13, fontWeight: 700, cursor: confirmText === "DELETE" ? "pointer" : "default", transition: "background 0.2s" }}
                  >
                    {deleteStatus === "deleting" ? "Deleting…" : "Delete Everything"}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function NotificationsSettingsTab({ session, theme: t, settings, onSettingsChange }) {
  const [testStatus, setTestStatus] = useState(null) // null | "sending" | "sent" | "error" | "no_key"

  const NOTIFS = [
    { key: "notif_budget", label: "Budget alerts", desc: "Email when you exceed a budget category" },
    { key: "notif_unusual", label: "Unusual spending", desc: "Transactions that look out of the ordinary" },
    { key: "notif_monthly", label: "Monthly summary", desc: "Your financial recap on the 1st of each month" },
    { key: "notif_retirement", label: "Retirement nudges", desc: "Tips to improve your retirement score" },
    { key: "notif_rates", label: "Rate changes", desc: "When exchange rates shift significantly" },
  ]

  async function sendTestEmail() {
    setTestStatus("sending")
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const token = s?.access_token
      const res = await fetch(`${API_BASE}/api/notify/test`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      })
      const data = await res.json()
      if (data.ok) setTestStatus("sent")
      else if (data.reason === "no_api_key") setTestStatus("no_key")
      else setTestStatus("error")
    } catch {
      setTestStatus("error")
    }
    setTimeout(() => setTestStatus(null), 4000)
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 4 }}>Notifications</h2>
      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 6 }}>
        Email alerts sent to <strong style={{ color: t.text }}>{session.user.email}</strong>
      </p>

      <div style={{ marginBottom: 24 }}>
        {NOTIFS.map(n => (
          <div key={n.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", borderBottom: `1px solid ${t.border}` }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 2 }}>{n.label}</p>
              <p style={{ fontSize: 12, color: t.textMuted }}>{n.desc}</p>
            </div>
            <div onClick={() => onSettingsChange(n.key, !settings[n.key])} style={{ width: 44, height: 24, borderRadius: 12, background: settings[n.key] ? t.accent : t.border, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 2, left: settings[n.key] ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={sendTestEmail}
          disabled={testStatus === "sending"}
          style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${t.accent}`, background: "transparent", color: t.accent, fontSize: 13, fontWeight: 600, cursor: testStatus === "sending" ? "default" : "pointer", opacity: testStatus === "sending" ? 0.6 : 1 }}
        >
          {testStatus === "sending" ? "Sending…" : "Send test email"}
        </button>
        {testStatus === "sent" && <span style={{ fontSize: 13, color: "#059669" }}>Test email sent! Check your inbox.</span>}
        {testStatus === "no_key" && <span style={{ fontSize: 13, color: "#F59E0B" }}>Email not configured yet (RESEND_API_KEY missing on server).</span>}
        {testStatus === "error" && <span style={{ fontSize: 13, color: "#EF4444" }}>Failed to send. Check server logs.</span>}
      </div>
    </div>
  )
}

function SettingsPage({ session, theme, userAvatar, setUserAvatar, catCustom, saveCatCustom, effectiveCategories, householdMembers, onHouseholdChange, settings = {}, onSettingsChange = () => {} }) {
  const t = theme
  const card = makeCard(t)

  const [tab, setTab] = useState("profile")
  const [saveMsg, setSaveMsg] = useState("")
  const [profile, setProfile] = useState({
    nickname: session.user.user_metadata?.nickname || "",
    birthday: session.user.user_metadata?.birthday || "",
    // We'll store avatar_url later when you implement upload storage
  })

const [avatarUploading, setAvatarUploading] = useState(false)

async function uploadAvatar(file) {
  try {
    setAvatarUploading(true)

    const ext = file.name.split(".").pop()
    const filePath = `${session.user.id}/${Date.now()}.${ext}`

    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, { upsert: true })

    if (uploadError) throw uploadError

    const { data } = supabase.storage.from("avatars").getPublicUrl(filePath)
    const publicUrl = data.publicUrl

    // Save to profiles table
    const { error: dbError } = await supabase
      .from("profiles")
      .upsert({ id: session.user.id, avatar_url: publicUrl }, { onConflict: "id" })

    if (dbError) throw dbError

    setUserAvatar(publicUrl)
    setSaveMsg("✅ Photo updated!")
    setTimeout(() => setSaveMsg(""), 2500)
  } catch (e) {
    setSaveMsg("❌ " + (e?.message || "Upload failed"))
    setTimeout(() => setSaveMsg(""), 2500)
  } finally {
    setAvatarUploading(false)
  }
}

  async function saveProfile() {
    const { error } = await supabase.auth.updateUser({
      data: {
        nickname: profile.nickname,
        birthday: profile.birthday,
      },
    })
    setSaveMsg(error ? "❌ " + error.message : "✅ Saved!")
    setTimeout(() => setSaveMsg(""), 2500)
  }

  const inp = {
    width: "100%",
    padding: "10px 12px",
    background: t.inputBg,
    border: `1px solid ${t.border}`,
    borderRadius: 8,
    color: t.text,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  }

  // ── Household state ──
  const primaryName = session.user.user_metadata?.full_name?.split(" ")[0] || session.user.email?.split("@")[0] || ""
  const [localMembers, setLocalMembers] = useState([
    { role: "primary", name: primaryName, color: "#6366f1" },
    { role: "partner", name: "", color: "#f43f5e" },
  ])
  const [householdSaveMsg, setHouseholdSaveMsg] = useState("")

  useEffect(() => {
    if (householdMembers && householdMembers.length > 0) {
      setLocalMembers(prev => prev.map(m => {
        const found = householdMembers.find(d => d.role === m.role)
        return found ? { ...m, name: found.name, color: found.color, id: found.id } : m
      }))
    }
  }, [householdMembers])

  function updateLocalMember(role, field, value) {
    setLocalMembers(prev => prev.map(m => m.role === role ? { ...m, [field]: value } : m))
  }

  async function saveHousehold() {
    const userId = session.user.id
    const { data: existing } = await supabase.from("household_members").select("*").eq("user_id", userId)
    const existingMap = Object.fromEntries((existing || []).map(m => [m.role, m]))
    for (const m of localMembers) {
      if (!m.name.trim()) continue
      const ex = existingMap[m.role]
      if (ex) await supabase.from("household_members").update({ name: m.name.trim(), color: m.color }).eq("id", ex.id)
      else await supabase.from("household_members").insert({ user_id: userId, role: m.role, name: m.name.trim(), color: m.color })
    }
    onHouseholdChange?.()
    setHouseholdSaveMsg("✅ Saved!")
    setTimeout(() => setHouseholdSaveMsg(""), 2500)
  }

  const tabs = [
    { id: "profile",       label: "Profile",       icon: "👤" },
    { id: "household",     label: "Household",     icon: "🏠" },
    { id: "security",      label: "Security",      icon: "🔒" },
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "categories",    label: "Categories",    icon: "🏷️" },
  ]

  // Category settings state
  const [newLabel, setNewLabel] = useState("")
  const [newEmoji, setNewEmoji] = useState("")
  const [newGroup, setNewGroup] = useState("wants")
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const EMOJI_OPTIONS = [
    "🏠","🏡","🔧","⚡","💧","🔑","🏗️",
    "🚗","🚘","🚕","✈️","🚂","⛽","🅿️","🛣️",
    "🛒","🍽️","🥗","🥩","🍕","☕","🍺","🛁",
    "🏥","💊","🏃","💪","🧘","🐾","🌿",
    "📱","💻","📺","🎮","🎬","🎵","🎨","🎟️","📚",
    "🛍️","👕","💅","💇","💄","🛋️","🏋️",
    "💰","💳","🏦","📊","📈","💹","🏖️","🎯","🛟",
    "🎁","🎀","💝","❤️","🌍","🤝","⭐","🔔","🗓️",
    "🎓","👶","🧒","👨‍👩‍👧","🌱","🧾","🔐","🏷️","•",
  ]

  const GROUP_OPTIONS = [
    { id: "income",     label: "Income"     },
    { id: "needs",      label: "Needs"      },
    { id: "wants",      label: "Wants"      },
    { id: "transfer",   label: "Transfer"   },
    { id: "investment", label: "Investment" },
  ]

    function toggleHideCategory(label) {
    const hidden = catCustom.hidden || []
    const next = hidden.includes(label) ? hidden.filter(l => l !== label) : [...hidden, label]
    saveCatCustom({ ...catCustom, hidden: next })
  }

  function reassignCategory(label, newGroupId) {
    saveCatCustom({ ...catCustom, overrides: { ...catCustom.overrides, [label]: { group: newGroupId } } })
  }

  function addCategory() {
    if (!newLabel.trim()) return
    const dup = effectiveCategories.find(c => c.label.toLowerCase() === newLabel.trim().toLowerCase())
    if (dup) return
    saveCatCustom({
      ...catCustom,
      additions: [...(catCustom.additions || []), { label: newLabel.trim(), emoji: newEmoji.trim() || "•", group: newGroup, custom: true }],
    })
    setNewLabel(""); setNewEmoji(""); setNewGroup("wants")
  }

  function deleteAddition(label) {
    saveCatCustom({ ...catCustom, additions: (catCustom.additions || []).filter(c => c.label !== label) })
  }

  function resetOverride(label) {
    const next = { ...catCustom.overrides }
    delete next[label]
    saveCatCustom({ ...catCustom, overrides: next })
  }
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: t.text }}>Settings</h1>
        <p style={{ color: t.textMuted, fontSize: 14 }}>Manage your account preferences</p>
      </div>

      {saveMsg && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 14,
            background: saveMsg.startsWith("✅") ? "rgba(110,231,183,0.10)" : "rgba(244,114,182,0.10)",
            color: saveMsg.startsWith("✅") ? "#2F855A" : "#EF4444",
            fontSize: 13,
            border: `1px solid ${t.border}`,
          }}
        >
          {saveMsg}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 18 }}>
        {/* Left tabs */}
        <div style={{ ...card, padding: 10 }}>
          {tabs.map((x) => (
            <button
              key={x.id}
              onClick={() => setTab(x.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                background: tab === x.id ? t.hover : "transparent",
                border: "none",
                cursor: "pointer",
                color: tab === x.id ? t.text : t.textMuted,
                fontSize: 13,
                fontWeight: tab === x.id ? 700 : 500,
                borderRadius: 10,
                textAlign: "left",
              }}
            >
              <span>{x.icon}</span>
              {x.label}
            </button>
          ))}
        </div>

        {/* Right content */}
        <div style={{ ...card, padding: "18px 20px" }}>
          {tab === "profile" && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 6 }}>Profile</h2>
              <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 18 }}>
                Set how your name appears in Grove Money
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
               <div
  style={{
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: userAvatar
      ? `url(${userAvatar}) center/cover no-repeat`
      : `linear-gradient(135deg, ${t.accent2}, ${t.accent})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 800,
    color: "#fff",
    border: `1px solid ${t.border}`,
  }}
>
  {!userAvatar && (profile.nickname || session.user.email).slice(0, 2).toUpperCase()}
</div>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
                    {profile.nickname || session.user.user_metadata?.full_name || "Your profile"}
                  </div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>{session.user.email}</div>

                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
  <label
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: t.accent,
      cursor: avatarUploading ? "not-allowed" : "pointer",
    }}
  >
    {avatarUploading ? "Uploading..." : "Upload photo"}
    <input
      type="file"
      accept="image/*"
      disabled={avatarUploading}
      style={{ display: "none" }}
      onChange={(e) => {
        const f = e.target.files?.[0]
        if (f) uploadAvatar(f)
      }}
    />
  </label>

  {userAvatar && (
    <button
      onClick={async () => {
        await supabase.from("profiles").update({ avatar_url: null }).eq("id", session.user.id)
        setUserAvatar("")
      }}
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: t.textMuted,
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: 0,
      }}
    >
      Remove
    </button>
  )}
</div>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
    <label style={{ fontSize: 12, color: t.textMuted }}>
      Nickname
    </label>

    <span style={{ fontSize: 11, color: t.textFaint }}>
      (will be displayed to external users)
    </span>
  </div>

  <input
    value={profile.nickname}
    onChange={(e) => setProfile((p) => ({ ...p, nickname: e.target.value }))}
    style={inp}
    placeholder="Flavien"
  />
</div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: t.textMuted, display: "block", marginBottom: 6 }}>Birthday</label>
               <input
  value={profile.birthday}
  onChange={(e) =>
    setProfile((p) => ({
      ...p,
      birthday: formatBirthdayInput(e.target.value),
    }))
  }
  inputMode="numeric"
  placeholder="MM/DD/YYYY"
  maxLength={10}
  style={inp}
/>
              </div>

              <button
                onClick={saveProfile}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${t.accent}, ${t.accent2})`,
                  color: "#0d1117",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Save Profile
              </button>
            </div>
          )}


          {tab === "household" && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 4 }}>Household</h2>
              <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 20 }}>Name the people in your household. You can then assign accounts to each person and see individual vs combined views.</p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                {localMembers.map((m, idx) => (
                  <div key={m.role} style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: "18px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", background: m.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                        {m.name ? m.name[0].toUpperCase() : (idx === 0 ? "A" : "B")}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{idx === 0 ? "You" : "Partner"}</div>
                        <div style={{ fontSize: 11, color: t.textFaint }}>{idx === 0 ? "Primary account holder" : "Second household member"}</div>
                      </div>
                    </div>

                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 5 }}>Name</label>
                    <input
                      value={m.name}
                      onChange={e => updateLocalMember(m.role, "name", e.target.value)}
                      placeholder={idx === 0 ? "Your first name" : "Partner's first name"}
                      style={{ ...inp, marginBottom: 12 }}
                    />

                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 8 }}>Color</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {["#6366f1","#f43f5e","#f59e0b","#10b981","#3b82f6","#8b5cf6","#ec4899","#14b8a6"].map(c => (
                        <button key={c} onClick={() => updateLocalMember(m.role, "color", c)}
                          style={{ width: 24, height: 24, borderRadius: "50%", background: c, border: `3px solid ${m.color === c ? t.text : "transparent"}`, cursor: "pointer", padding: 0, flexShrink: 0 }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: t.hover, borderRadius: 10, padding: "11px 14px", marginBottom: 20, fontSize: 12, color: t.textMuted }}>
                🔗 Separate partner login with invite — coming soon. Manage both household members from this account for now.
              </div>

              <button onClick={saveHousehold}
                style={{ padding: "10px 24px", borderRadius: 9, border: "none", background: t.accent, color: "#0d1117", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                {householdSaveMsg || "Save Household"}
              </button>
            </div>
          )}

          {tab === "security" && (
            <SecuritySettingsTab session={session} theme={t} />
          )}

          {tab === "notifications" && (
            <NotificationsSettingsTab session={session} theme={t} settings={settings} onSettingsChange={onSettingsChange} />
          )}


          {tab === "categories" && (
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: t.text, marginBottom: 4 }}>Categories</h2>
              <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 20 }}>
                Change which group a category belongs to, or add your own.
              </p>

              {/* Add new category */}
              <div style={{ ...card, padding: "18px 20px", marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 14 }}>Add a category</p>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  {/* Emoji picker */}
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowEmojiPicker(v => !v)}
                      style={{
                        width: 56, height: 40, borderRadius: 8, textAlign: "center",
                        border: `1px solid ${showEmojiPicker ? t.accent : t.border}`, background: t.inputBg,
                        fontSize: 20, cursor: "pointer", lineHeight: 1,
                      }}
                    >{newEmoji || "•"}</button>
                    {showEmojiPicker && (
                      <>
                        <div onClick={() => setShowEmojiPicker(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                        <div style={{
                          position: "absolute", top: 44, left: 0, zIndex: 1000,
                          background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: 10, width: 252,
                          display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2,
                        }}>
                          {EMOJI_OPTIONS.map(e => (
                            <button key={e} onClick={() => { setNewEmoji(e); setShowEmojiPicker(false) }}
                              style={{
                                fontSize: 20, padding: "5px 0", borderRadius: 6, border: "none",
                                background: newEmoji === e ? (t.isDark ? "rgba(110,231,183,0.15)" : "rgba(47,133,90,0.1)") : "transparent",
                                cursor: "pointer", lineHeight: 1,
                              }}
                            >{e}</button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addCategory()}
                    placeholder="Category name"
                    style={{
                      flex: 1, minWidth: 140, padding: "8px 12px", borderRadius: 8,
                      border: `1px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: "none",
                    }}
                  />
                  <select
                    value={newGroup}
                    onChange={e => setNewGroup(e.target.value)}
                    style={{
                      padding: "8px 12px", borderRadius: 8,
                      border: `1px solid ${t.border}`, background: t.inputBg,
                      color: t.text, fontSize: 13, outline: "none", cursor: "pointer",
                    }}
                  >
                    {GROUP_OPTIONS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                  </select>
                  <button
                    onClick={addCategory}
                    disabled={!newLabel.trim()}
                    style={{
                      padding: "8px 18px", borderRadius: 8, border: "none",
                      background: newLabel.trim() ? t.accent : t.border,
                      color: newLabel.trim() ? (t.isDark ? "#000" : "#fff") : t.textFaint,
                      fontSize: 13, fontWeight: 700, cursor: newLabel.trim() ? "pointer" : "not-allowed",
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Categories list grouped */}
              {GROUP_OPTIONS.map(grp => {
                const cats = effectiveCategories.filter(c => c.group === grp.id)
                if (cats.length === 0) return null
                const grpColor = GROUP_STYLE[grp.id]?.color || t.textMuted
                return (
                  <div key={grp.id} style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: grpColor, marginBottom: 8 }}>{grp.label}</p>
                    <div style={{ ...card, overflow: "hidden" }}>
                      {cats.map((cat, i) => {
                        const isCustom = cat.custom
                        const isOverridden = !isCustom && catCustom.overrides[cat.label]
                        const isHidden = (catCustom.hidden || []).includes(cat.label)
                        return (
                          <div key={cat.label} style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "11px 16px",
                            borderBottom: i < cats.length - 1 ? `1px solid ${t.border}` : "none",
                            opacity: isHidden ? 0.45 : 1,
                          }}>
                            <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>{cat.emoji}</span>
                            <span style={{ flex: 1, fontSize: 14, color: t.text, fontWeight: 500 }}>{cat.label}</span>

                            {/* Hide toggle */}
                            <button
                              onClick={() => toggleHideCategory(cat.label)}
                              title={isHidden ? "Show category" : "Hide category"}
                              style={{ background: "transparent", border: "none", fontSize: 16, cursor: "pointer", padding: "4px 6px", color: isHidden ? t.textFaint : t.textMuted, lineHeight: 1 }}
                            >{isHidden ? "🚫" : "👁"}</button>

                            {/* Group selector */}
                            <select
                              value={cat.group}
                              onChange={e => {
                                if (isCustom) {
                                  saveCatCustom({
                                    ...catCustom,
                                    additions: (catCustom.additions || []).map(a => a.label === cat.label ? { ...a, group: e.target.value } : a),
                                  })
                                } else {
                                  reassignCategory(cat.label, e.target.value)
                                }
                              }}
                              style={{
                                padding: "5px 10px", borderRadius: 8,
                                border: `1px solid ${t.border}`, background: t.inputBg,
                                color: t.text, fontSize: 12, outline: "none", cursor: "pointer",
                              }}
                            >
                              {GROUP_OPTIONS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                            </select>

                            {/* Reset override (base categories) or Delete (custom) */}
                            {isOverridden && (
                              <button
                                onClick={() => resetOverride(cat.label)}
                                title="Reset to default"
                                style={{ background: "transparent", border: "none", color: t.textFaint, fontSize: 11, cursor: "pointer", padding: "4px 6px" }}
                              >
                                Reset
                              </button>
                            )}
                            {isCustom && (
                              <button
                                onClick={() => deleteAddition(cat.label)}
                                title="Delete"
                                style={{ background: "transparent", border: "none", color: "#EF4444", fontSize: 16, cursor: "pointer", padding: "4px 6px", lineHeight: 1 }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}

            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: t.textFaint }}>
        More settings sections will appear here as new products are added.
      </div>
    </div>
  )
}
const TARGETS = { needs: 50, wants: 30, savings: 20 }
function pctColor(kind, pct, theme) {
  if (kind === "needs" && pct > TARGETS.needs) return "#EF4444"
  if (kind === "wants" && pct > TARGETS.wants) return "#F59E0B"
  if (kind === "savings" && pct < TARGETS.savings) return "#EF4444"
  return theme.textMuted
}

const TAB_INTROS = {
  overview: {
    icon: "🏠",
    title: "Your Financial Command Center",
    desc: "Everything in one place — net worth, recent spending, investment performance, and what needs your attention today.",
    points: [
      "Net worth updates automatically as your accounts sync",
      "The spending breakdown shows where your money went this month",
      "Click any card to dive deeper into that section",
    ],
  },
  accounts: {
    icon: "🏦",
    title: "All Your Accounts, One Place",
    desc: "Connect your bank accounts, credit cards, investments, loans, and real estate. Grove keeps everything in sync so you always know where you stand.",
    points: [
      "Connect via Plaid — your login credentials are never stored by Grove",
      "Add properties to track your home equity over time",
      "Refresh balances anytime with one click",
    ],
  },
  transactions: {
    icon: "📋",
    title: "Every Dollar, Accounted For",
    desc: "See every transaction across all your accounts. Grove auto-categorizes your spending so you can spot patterns and find where to save.",
    points: [
      "Grove auto-categorizes most merchants — no manual entry needed",
      "You can fix any category; your edits improve future suggestions",
      "Filter by account, category, or date to find anything fast",
    ],
  },
  budget: {
    icon: "🎯",
    title: "Spend With Intention",
    desc: "Set monthly limits for each spending category. Grove tracks your actual spending in real time so you always know where you are.",
    points: [
      "Budgets are tied to your real transaction categories",
      "The progress bar turns amber then red as you approach your limit",
      "Start with the categories where you overspend most",
    ],
  },
  cashflow: {
    icon: "💸",
    title: "Money In vs. Money Out",
    desc: "Cash flow is the difference between what you earn and what you spend each month. Positive cash flow = you're building wealth.",
    points: [
      "Income is pulled from your paycheck settings",
      "Expenses come from your categorized transactions",
      "Track the trend month by month to see your progress",
    ],
  },
  investments: {
    icon: "📈",
    title: "Watch Your Wealth Grow",
    desc: "All your investment accounts, individual holdings, and portfolio performance in one view. Track your path to financial independence.",
    points: [
      "Connect your brokerage through Plaid for live data",
      "Add manual holdings for accounts Plaid doesn't support",
      "The portfolio chart shows your growth over time",
    ],
  },
  retirement: {
    icon: "🏖️",
    title: "Are You On Track to Retire?",
    desc: "Grove projects whether you'll hit your retirement number based on your current savings rate, investments, and goals — and shows what to change if you won't.",
    points: [
      "Set your target retirement age and monthly income need",
      "The projection updates automatically as your accounts change",
      "Add life events like a home purchase to see their long-term impact",
    ],
  },
  simulators: {
    icon: "🧮",
    title: "Run the Numbers Before You Decide",
    desc: "Model financial decisions before you make them — from paying off debt to comparing loan scenarios. No real data is changed.",
    points: [
      "Debt payoff: see exactly how extra payments accelerate your free date",
      "Compare aggressive vs. minimum payment strategies side by side",
      "It's a safe sandbox — experiment freely",
    ],
  },
  paycheck: {
    icon: "💼",
    title: "Your Full Compensation Picture",
    desc: "Plaid only sees what lands in your bank account. Enter your gross pay and deductions so Grove can show your true savings rate, 401(k) growth, and real tax burden.",
    points: [
      "Grab your most recent pay stub — it has all the numbers you'll need",
      "Grove calculates FICA (Social Security + Medicare) automatically",
      "Your paycheck data is private and never shared",
    ],
  },
}

function TabIntroModal({ tabId, onClose, onDismiss }) {
  const info = TAB_INTROS[tabId]
  if (!info) return null
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 8000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui" }}>
      <div style={{ width: "min(460px, 90vw)", background: "#fff", borderRadius: 20, padding: "40px 36px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 52, textAlign: "center", marginBottom: 12 }}>{info.icon}</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", textAlign: "center", margin: "0 0 10px", lineHeight: 1.3 }}>{info.title}</h2>
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.75, margin: "0 0 22px", textAlign: "center" }}>{info.desc}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          {info.points.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ color: "#2F855A", fontWeight: 800, fontSize: 14, marginTop: 1, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>{p}</span>
            </div>
          ))}
        </div>
        <button onClick={onDismiss} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.25)" }}>
          Got it, let's go →
        </button>
        <button onClick={onClose} style={{ display: "block", width: "100%", marginTop: 10, background: "none", border: "none", color: "#94A3B8", fontSize: 13, cursor: "pointer", textAlign: "center" }}>
          Close without saving progress
        </button>
      </div>
    </div>
  )
}

function OnboardingOverlay({ session, onComplete, onXPAward, savePaychecks }) {
  const [step, setStep] = useState(0)
  const [linkToken, setLinkToken] = useState(null)
  const [linkTokenError, setLinkTokenError] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const name = session.user.user_metadata?.full_name?.split(" ")[0] || "there"
  const t = lightTheme
  const [pcForm, setPcForm] = useState({ name: "Main Job", frequency: "biweekly", gross: "", contrib401k: "", federalTax: "", stateTax: "" })
  const [financialGoal, setFinancialGoal] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  async function fetchLinkToken() {
    setLinkTokenError(false)
    setLinkToken(null)
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await apiPost("/api/create-link-token", { userId: session.user.id }, session)
        const d = await r.json()
        if (d.link_token) { setLinkToken(d.link_token); return }
      } catch {}
      if (attempt < 2) await new Promise(res => setTimeout(res, 4000))
    }
    setLinkTokenError(true)
  }

  // Start fetching as soon as step 2 is reached
  useEffect(() => {
    if (step === 2) fetchLinkToken()
  }, [step, retryCount]) // eslint-disable-line

  async function onPlaidSuccess(publicToken, metadata) {
    setConnecting(true)
    try {
      const res = await apiPost("/api/connect", {
        userId: session.user.id,
        public_token: publicToken,
        institution_id: metadata.institution?.institution_id || null,
      }, session)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      const accounts = data.accounts || []
      const now = new Date().toISOString()
      await Promise.all(accounts.map(a => supabase.from("accounts").upsert({
        user_id: session.user.id,
        account_name: a.name,
        account_type: a.type,
        subtype: a.subtype,
        balance: a.balances.current || 0,
        plaid_account_id: a.account_id,
        institution_name: metadata.institution?.name || "Bank",
        institution_logo_url: data.institution_logo_url || null,
        updated_at: now,
      }, { onConflict: "user_id,plaid_account_id" })))
    } catch (e) { console.error(e) }
    onXPAward?.("connect_first_account")
    setConnecting(false)
    setStep(3)
  }

  const steps = [
    // Step 0 — Welcome
    <div key="welcome" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>🎉</div>
      <div style={{ display: "inline-block", background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)", borderRadius: 99, padding: "4px 16px", marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#065F46", letterSpacing: "0.05em" }}>YOU MADE THE RIGHT CALL</span>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0F172A", margin: "0 0 16px", lineHeight: 1.2 }}>
        {name}, your financial<br/>glow-up starts today.
      </h1>
      <p style={{ fontSize: 15, color: "#475569", lineHeight: 1.8, margin: "0 0 28px" }}>
        Most people never take this step. You just did — and that already puts you ahead. Grove Money will help you see everything clearly, spend smarter, and build real wealth.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 32 }}>
        {[
          { icon: "🏦", label: "All accounts", sub: "in one place" },
          { icon: "📊", label: "Know where", sub: "your money goes" },
          { icon: "🌱", label: "Grow wealth", sub: "with a real plan" },
        ].map(f => (
          <div key={f.label} style={{ padding: "14px 10px", borderRadius: 12, background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{f.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>{f.label}</div>
            <div style={{ fontSize: 11, color: "#4ADE80", marginTop: 2 }}>{f.sub}</div>
          </div>
        ))}
      </div>
      <button onClick={() => setStep(1)} style={{ width: "100%", padding: "15px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.35)" }}>
        Let's build your financial future →
      </button>
    </div>,

    // Step 1 — Our story
    <div key="story" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🌍</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "0 0 20px" }}>Built from real life.</h2>
      <div style={{ textAlign: "left", background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0", borderRadius: 14, padding: "22px 24px", marginBottom: 28 }}>
        <p style={{ fontSize: 14, color: "#1E3A2F", lineHeight: 1.9, margin: 0 }}>
          Most people in the US are figuring out finances alone. No guide, no mentor — just trial and error with real consequences.
        </p>
        <p style={{ fontSize: 14, color: "#1E3A2F", lineHeight: 1.9, margin: "14px 0 0" }}>
          As a French immigrant, I felt that gap more than most. So after 12 years of learning, I built the tool I wished existed from day one.
        </p>
        <p style={{ fontSize: 14, color: "#166534", fontWeight: 700, lineHeight: 1.9, margin: "14px 0 0" }}>
          Grove Money is for anyone who has ever felt like the financial system wasn't built for them. It was built for you.
        </p>
      </div>
      <button onClick={() => setStep(2)} style={{ width: "100%", padding: "15px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.35)" }}>
        I'm in — let's go →
      </button>
    </div>,

    // Step 2 — Connect accounts
    <div key="connect" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "0 0 12px" }}>Connect your accounts</h2>
      <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, margin: "0 0 8px" }}>
        Link your checking, savings, credit cards, investments and more. Grove Money never stores your credentials.
      </p>
      <p style={{ fontSize: 12, color: "#94A3B8", margin: "0 0 28px" }}>🔒 Bank-level 256-bit encryption via Plaid</p>
      {linkToken ? (
        <PlaidLinkButton linkToken={linkToken} onSuccess={onPlaidSuccess} connecting={connecting} theme={t} label="🔗 Connect My Accounts" />
      ) : linkTokenError ? (
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#EF4444", fontSize: 13, marginBottom: 8 }}>Could not connect to server.</p>
          <button onClick={() => setRetryCount(c => c + 1)} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#2F855A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "20px 0" }}>
          <div style={{ width: 28, height: 28, border: "3px solid #BBF7D0", borderTopColor: "#2F855A", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Preparing secure connection… (may take ~15s)</p>
        </div>
      )}
      <div style={{ marginTop: 20 }}>
        <button onClick={() => setStep(3)} style={{ background: "none", border: "none", color: "#94A3B8", fontSize: 13, cursor: "pointer" }}>
          Skip for now — I'll add accounts manually
        </button>
      </div>
    </div>,

    // Step 3 — Financial goals
    <div key="goals" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "0 0 10px" }}>What's your #1 financial goal?</h2>
      <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, margin: "0 0 24px" }}>
        Grove will personalize your dashboard around what matters most to you.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {[
          { icon: "💳", label: "Pay off debt", sub: "Credit cards, loans, student debt" },
          { icon: "🛟", label: "Build an emergency fund", sub: "3–6 months of expenses saved" },
          { icon: "🏠", label: "Save for a home", sub: "Down payment or home equity" },
          { icon: "📈", label: "Grow my investments", sub: "Build long-term wealth" },
          { icon: "🏖️", label: "Plan for retirement", sub: "Know my number and stay on track" },
          { icon: "✨", label: "All of the above", sub: "I want the full picture" },
        ].map(g => (
          <button key={g.label} onClick={() => { setFinancialGoal(g.label); localStorage.setItem(`grove_goal_${session.user.id}`, g.label); setStep(4) }}
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: financialGoal === g.label ? "linear-gradient(135deg, #F0FDF4, #DCFCE7)" : "#fff", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}
            onMouseEnter={e => { if (financialGoal !== g.label) e.currentTarget.style.borderColor = "#86EFAC" }}
            onMouseLeave={e => { if (financialGoal !== g.label) e.currentTarget.style.borderColor = "#E2E8F0" }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>{g.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{g.label}</div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 1 }}>{g.sub}</div>
            </div>
          </button>
        ))}
      </div>
      <button onClick={() => setStep(4)} style={{ background: "none", border: "none", color: "#94A3B8", fontSize: 13, cursor: "pointer" }}>
        Skip for now
      </button>
    </div>,

    // Step 4 — Paycheck setup (optional)
    <div key="paycheck">
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>💼</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "0 0 8px" }}>How does your paycheck look?</h2>
        <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, margin: 0 }}>
          Plaid only sees your take-home deposit. Enter your gross pay so Grove can show your true savings rate, tax burden, and 401(k) growth.
        </p>
      </div>
      {/* Pay stub tip */}
      <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
        <p style={{ fontSize: 12, color: "#92400E", margin: 0, lineHeight: 1.6 }}>
          <strong>Grab your most recent pay stub</strong> — it has all the numbers below. Look for "Gross Pay", "Federal Income Tax", "State Income Tax", and "401(k)" or "Retirement".
        </p>
      </div>
      {(() => {
        const freq = { weekly: 4.333, biweekly: 2.167, "semi-monthly": 2, monthly: 1 }
        const m = freq[pcForm.frequency] || 2.167
        const gross = Number(pcForm.gross) || 0
        const k401 = Number(pcForm.contrib401k) || 0
        const fed = Number(pcForm.federalTax) || 0
        const state = Number(pcForm.stateTax) || 0
        const fica = Math.round(gross * 0.0765)
        const net = gross - k401 - fed - state - fica
        const inputStyle = { width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 14, color: "#0F172A", background: "#F8FAFC", boxSizing: "border-box" }
        const labelStyle = { fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 4, display: "block" }
        const tipStyle = { fontSize: 11, color: "#94A3B8", marginTop: 3 }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={labelStyle}>Employer / Job name</label>
                <input style={inputStyle} value={pcForm.name} onChange={e => setPcForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Job" />
              </div>
              <div>
                <label style={labelStyle}>Pay frequency</label>
                <select style={inputStyle} value={pcForm.frequency} onChange={e => setPcForm(f => ({ ...f, frequency: e.target.value }))}>
                  <option value="weekly">Weekly (52×/yr)</option>
                  <option value="biweekly">Bi-weekly (26×/yr)</option>
                  <option value="semi-monthly">Semi-monthly (24×/yr)</option>
                  <option value="monthly">Monthly (12×/yr)</option>
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Gross pay per paycheck ($) <span style={{ color: "#94A3B8", fontWeight: 400 }}>— before any deductions</span></label>
              <input style={{ ...inputStyle, fontSize: 16, fontWeight: 700 }} type="number" value={pcForm.gross} onChange={e => setPcForm(f => ({ ...f, gross: e.target.value }))} placeholder="e.g. 3,500" />
              <p style={tipStyle}>On your stub: labeled "Gross Pay" or "Gross Earnings"</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={labelStyle}>401(k) contribution ($)</label>
                <input style={inputStyle} type="number" value={pcForm.contrib401k} onChange={e => setPcForm(f => ({ ...f, contrib401k: e.target.value }))} placeholder={gross ? Math.round(gross * 0.06) : "e.g. 200"} />
                <p style={tipStyle}>Labeled "401k" or "Retirement" — leave blank if none</p>
              </div>
              <div>
                <label style={labelStyle}>Federal income tax ($)</label>
                <input style={inputStyle} type="number" value={pcForm.federalTax} onChange={e => setPcForm(f => ({ ...f, federalTax: e.target.value }))} placeholder={gross ? Math.round(gross * 0.12) : "e.g. 400"} />
                <p style={tipStyle}>Labeled "Federal" or "FWT" on your stub</p>
              </div>
            </div>
            <div>
              <label style={labelStyle}>State income tax ($) <span style={{ color: "#94A3B8", fontWeight: 400 }}>— leave blank if your state has no income tax</span></label>
              <input style={inputStyle} type="number" value={pcForm.stateTax} onChange={e => setPcForm(f => ({ ...f, stateTax: e.target.value }))} placeholder={gross ? Math.round(gross * 0.05) : "e.g. 150"} />
              <p style={tipStyle}>Labeled "State" or "SWT" — FL, TX, WA and 6 others have $0</p>
            </div>
            {gross > 0 && (
              <div style={{ background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0", borderRadius: 10, padding: "12px 14px" }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "#065F46", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Monthly estimate</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                  {[
                    { label: "Gross income", val: `$${Math.round(gross * m).toLocaleString()}` },
                    { label: "401k + taxes + FICA", val: `-$${Math.round((k401 + fed + state + fica) * m).toLocaleString()}` },
                    { label: "Take-home / mo", val: `$${Math.round(net * m).toLocaleString()}`, bold: true },
                  ].map(r => (
                    <div key={r.label} style={{ display: "contents" }}>
                      <span style={{ fontSize: 12, color: "#4B5563", fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                      <span style={{ fontSize: 12, color: r.bold ? "#166534" : "#1E3A2F", fontWeight: r.bold ? 700 : 400, textAlign: "right" }}>{r.val}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 10, color: "#6EE7B7", margin: "8px 0 0" }}>FICA (Social Security + Medicare) calculated automatically at 7.65%</p>
              </div>
            )}
            <button
              onClick={() => {
                if (gross > 0 && savePaychecks) {
                  savePaychecks([{ id: crypto.randomUUID(), name: pcForm.name || "Main Job", employer: "", frequency: pcForm.frequency, gross: Number(pcForm.gross), contrib401k: Number(pcForm.contrib401k) || 0, match401k: 0, hsa: 0, preTaxOther: [], federalTax: Number(pcForm.federalTax) || 0, stateTax: Number(pcForm.stateTax) || 0, socialSecurity: Math.round(gross * 0.062), medicare: Math.round(gross * 0.0145), healthInsurance: 0, dental: 0, vision: 0, postTaxOther: [] }])
                }
                setStep(5)
              }}
              style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: gross > 0 ? "linear-gradient(135deg, #2F855A, #22C55E)" : "#E2E8F0", color: gross > 0 ? "#fff" : "#94A3B8", fontSize: 15, fontWeight: 700, cursor: gross > 0 ? "pointer" : "default" }}
            >
              {gross > 0 ? "Save & continue →" : "Enter gross pay above to continue"}
            </button>
            <button onClick={() => setStep(5)} style={{ background: "none", border: "none", color: "#94A3B8", fontSize: 13, cursor: "pointer", textAlign: "center" }}>
              Skip — I'll set this up later in the Paycheck tab
            </button>
          </div>
        )
      })()}
    </div>,

    // Step 5 — Done
    <div key="done" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🌱</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: "0 0 12px" }}>You're all set. Let's grow.</h2>
      {financialGoal && (
        <div style={{ background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0", borderRadius: 10, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#065F46", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Your goal</p>
          <p style={{ fontSize: 14, color: "#166534", fontWeight: 600, margin: 0 }}>{financialGoal}</p>
        </div>
      )}
      <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, margin: "0 0 20px" }}>
        Grove Money is already working. Check your overview, then explore each tab — a quick tour will pop up the first time you visit each section.
      </p>
      <div style={{ background: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", border: "1px solid #BBF7D0", borderRadius: 12, padding: "16px", marginBottom: 28, display: "flex", justifyContent: "space-between" }}>
        {["🌱", "🪴", "🌿", "🌳", "🌲"].map((icon, i) => (
          <div key={i} style={{ textAlign: "center", opacity: i === 0 ? 1 : 0.35 }}>
            <div style={{ fontSize: 22 }}>{icon}</div>
            <div style={{ fontSize: 9, color: "#166534", fontWeight: 700, marginTop: 4 }}>{["Seedling", "Sprout", "Sapling", "Grove", "Forest"][i]}</div>
          </div>
        ))}
      </div>
      <button onClick={onComplete} style={{ width: "100%", padding: "15px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #2F855A, #22C55E)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.35)" }}>
        Enter Grove Money →
      </button>
    </div>,
  ]

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui" }}>
      <div style={{ width: "min(500px, 92vw)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 20, padding: "40px 36px", boxShadow: "0 24px 80px rgba(0,0,0,0.2)" }}>
        {/* Progress dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 32 }}>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ width: i === step ? 24 : 8, height: 8, borderRadius: 99, background: i === step ? "#2F855A" : i < step ? "#86EFAC" : "#E2E8F0", transition: "all 0.3s" }} />
          ))}
        </div>
        {steps[step]}
      </div>
    </div>
  )
}

function LevelsPage({ session, theme, userXp }) {
  const t = theme
  const card = { background: t.cardBg, borderRadius: 16, boxShadow: t.shadow, border: `1px solid ${t.border}` }
  const lvl = getLevelInfo(userXp)
  const [xpHistory, setXpHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from("xp_events")
      .select("action, xp, created_at")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => { setXpHistory(data || []); setLoading(false) })
  }, [session.user.id])

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 6 }}>Levels & Achievements</h1>
        <p style={{ color: t.textMuted, fontSize: 14 }}>Earn XP by building healthy financial habits</p>
      </div>

      {/* Current level hero */}
      <div style={{ ...card, padding: "28px 32px", marginBottom: 20, background: t.isDark ? "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.06))" : "linear-gradient(135deg, rgba(47,133,90,0.08), rgba(16,185,129,0.04))", borderColor: t.accent + "40" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ fontSize: 64, lineHeight: 1 }}>{lvl.current.emoji}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Level {lvl.current.level}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: t.text, marginBottom: 8 }}>{lvl.current.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1, height: 8, borderRadius: 9999, background: t.border, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${lvl.progress}%`, borderRadius: 9999, background: `linear-gradient(90deg, ${t.accent}, #34d399)`, transition: "width 0.8s ease" }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.text, whiteSpace: "nowrap" }}>{userXp} XP</span>
            </div>
            {lvl.next ? (
              <div style={{ fontSize: 12, color: t.textMuted }}>{lvl.next.xp - userXp} XP needed to reach {lvl.next.emoji} {lvl.next.name}</div>
            ) : (
              <div style={{ fontSize: 12, color: t.accent, fontWeight: 600 }}>Maximum level reached — you're an Ecosystem!</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* All levels */}
        <div style={{ ...card, padding: "22px 24px" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: "0 0 16px" }}>All Levels</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {LEVELS.map(l => {
              const isCurrent = l.level === lvl.current.level
              const isUnlocked = userXp >= l.xp
              return (
                <div key={l.level} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px", borderRadius: 10,
                  background: isCurrent ? (t.isDark ? "rgba(16,185,129,0.15)" : "rgba(47,133,90,0.1)") : isUnlocked ? t.hover : "transparent",
                  border: `1px solid ${isCurrent ? t.accent + "40" : "transparent"}`,
                  opacity: isUnlocked ? 1 : 0.4,
                }}>
                  <span style={{ fontSize: 22, width: 28, textAlign: "center" }}>{l.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{l.name}</div>
                    <div style={{ fontSize: 11, color: t.textFaint }}>{l.xp.toLocaleString()} XP</div>
                  </div>
                  {isCurrent && <span style={{ fontSize: 11, fontWeight: 700, color: t.accent, background: `${t.accent}22`, padding: "2px 8px", borderRadius: 99 }}>Current</span>}
                  {!isCurrent && isUnlocked && <span style={{ fontSize: 13, color: t.accent }}>✓</span>}
                </div>
              )
            })}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* How to earn XP */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: "0 0 16px" }}>How to Earn XP</h3>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {Object.entries(XP_ACTIONS).map(([key, action]) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{action.emoji}</span>
                  <span style={{ fontSize: 12, color: t.textMuted, flex: 1 }}>{action.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: t.accent }}>+{action.xp} XP</span>
                </div>
              ))}
            </div>
          </div>

          {/* XP History */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: "0 0 16px" }}>XP History</h3>
            {loading ? (
              <div style={{ textAlign: "center", color: t.textFaint, fontSize: 12 }}>Loading...</div>
            ) : xpHistory.length === 0 ? (
              <p style={{ fontSize: 12, color: t.textFaint, margin: 0, fontStyle: "italic" }}>No XP earned yet. Start by connecting an account!</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
                {xpHistory.map((ev, i) => {
                  const action = XP_ACTIONS[ev.action]
                  const date = new Date(ev.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 14 }}>{action?.emoji || "⭐"}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: t.text, fontWeight: 600 }}>{action?.label || ev.action}</div>
                        <div style={{ fontSize: 11, color: t.textFaint }}>{date}</div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.accent }}>+{ev.xp}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Dashboard({ session, userAvatar, setUserAvatar }) {
  const [activeNav, setActiveNav] = useState(() => localStorage.getItem("grove_active_nav") || "overview")
  const setActiveNavPersist = (nav) => { setActiveNav(nav); localStorage.setItem("grove_active_nav", nav) }
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [tabIntroId, setTabIntroId] = useState(null)

  // Show tab intro on first visit to each tab
  useEffect(() => {
    if (!TAB_INTROS[activeNav]) return
    const key = `grove_tab_seen_${session.user.id}_${activeNav}`
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setTabIntroId(activeNav), 350)
      return () => clearTimeout(t)
    }
  }, [activeNav, session.user.id])

  function dismissTabIntro() {
    localStorage.setItem(`grove_tab_seen_${session.user.id}_${activeNav}`, "1")
    setTabIntroId(null)
  }

  // ── XP / Levels ──
  const [userXp, setUserXp] = useState(0)
  const [xpToasts, setXpToasts] = useState([])

  useEffect(() => {
    supabase.from("user_xp").select("total_xp").eq("user_id", session.user.id).single()
      .then(({ data }) => { if (data) setUserXp(data.total_xp || 0) })
  }, [session.user.id])

  const dismissToast = useCallback((id) => {
    setXpToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const triggerXP = useCallback(async (actionKey) => {
    const result = await awardXP(session.user.id, actionKey)
    if (!result?.awarded) return
    const action = XP_ACTIONS[actionKey]
    setUserXp(xp => {
      const newXp = xp + result.xp
      const lvl = getLevelInfo(newXp)
      setXpToasts(prev => [...prev, {
        id: crypto.randomUUID(),
        xp: result.xp,
        label: result.label,
        emoji: action?.emoji || lvl.current.emoji,
      }])
      return newXp
    })
  }, [session.user.id])
  const [txFilter, setTxFilter] = useState(null)
  const [cfPeriodType, setCfPeriodType] = useState("month")
  const [cfMonthOffset, setCfMonthOffset] = useState(0)

 // ✅ Net Worth range + series state
const [nwRange, setNwRange] = useState("1Y")
const [netWorthSeries, setNetWorthSeries] = useState([])
const [nwSeriesLoading, setNwSeriesLoading] = useState(true)

// ✅ Load series ONCE (no duplicate declarations)
const loadNetWorthSeries = useCallback(async () => {
  setNwSeriesLoading(true)
  try {
    const { data, error } = await supabase
      .from("net_worth_snapshots")
      .select("snapshot_date,net_worth")
      .eq("user_id", session.user.id)
      .order("snapshot_date", { ascending: true })

    if (error) throw error

    const rows = (data || [])
      .map((r) => ({
        date: r.snapshot_date,
        value: Number(r.net_worth || 0),
      }))
      .filter((r) => r.date)

    setNetWorthSeries(rows)
  } catch (e) {
    fireToast("Failed to load net worth history.")
    setNetWorthSeries([])
  } finally {
    setNwSeriesLoading(false)
  }
}, [session.user.id])

// ✅ Load on mount
useEffect(() => {
  loadNetWorthSeries()
}, [loadNetWorthSeries])

// ----- Net Worth Range Filtering -----
const seriesStart = rangeStartDate(nwRange)

const filteredSeries = seriesStart
  ? netWorthSeries.filter((p) => {
      const d = parseMaybeISODate(p.date)
      return d && d.getTime() >= seriesStart.getTime()
    })
  : netWorthSeries

// Control chart density
const maxPts =
  nwRange === "1W" ? 14 :
  nwRange === "1M" ? 30 :
  nwRange === "3M" ? 60 :
  nwRange === "6M" ? 90 :
  nwRange === "YTD" ? 120 :
  nwRange === "1Y" ? 140 :
  nwRange === "5Y" ? 200 :
  260 // MAX

const displayedNetWorthHistory = downsampleByMaxPoints(filteredSeries, maxPts)

const displayedNetWorthSeries = displayedNetWorthHistory?.length ? displayedNetWorthHistory : []

const latestNetWorthValue =
  displayedNetWorthSeries?.[displayedNetWorthSeries.length - 1]?.value ?? 0

 

  const [settingsOpen, setSettingsOpen] = useState(false)
const [profileMenuOpen, setProfileMenuOpen] = useState(false)
const profileMenuRef = useRef(null)
useEffect(() => {
  async function loadAvatar() {
    const { data } = await supabase
      .from("profiles")
      .select("avatar_url")
      .eq("id", session.user.id)
      .single()

    if (data?.avatar_url) setUserAvatar(data.avatar_url)
  }

  loadAvatar()
}, [session.user.id])
useEffect(() => {
  function handleClickOutside(e) {
    if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
      setProfileMenuOpen(false)
    }
  }
  function handleEsc(e) {
    if (e.key === "Escape") setProfileMenuOpen(false)
  }
  document.addEventListener("mousedown", handleClickOutside)
  document.addEventListener("keydown", handleEsc)
  return () => {
    document.removeEventListener("mousedown", handleClickOutside)
    document.removeEventListener("keydown", handleEsc)
  }
}, [])
  
  // ✅ Accounts
  const [accounts, setAccounts] = useState([])
  const loadAccounts = useCallback(async () => {
    const { data } = await supabase.from('accounts').select('*').eq('user_id', session.user.id)
    if (data) setAccounts(data)
  }, [session.user.id])

  // ✅ Real estate
  const [realEstateProps, setRealEstateProps] = useState([])
  const loadRealEstate = useCallback(async () => {
    const { data } = await supabase.from("real_estate").select("*").eq("user_id", session.user.id)
    if (data) setRealEstateProps(data)
  }, [session.user.id])
  useEffect(() => { loadRealEstate() }, [loadRealEstate])

  // ✅ Household members
  const [householdMembers, setHouseholdMembers] = useState([])
  const loadHousehold = useCallback(async () => {
    const { data } = await supabase.from('household_members').select('*').eq('user_id', session.user.id).order('created_at')
    if (data) setHouseholdMembers(data)
  }, [session.user.id])
  useEffect(() => { loadHousehold() }, [loadHousehold])

  useEffect(() => { loadAccounts() }, [loadAccounts])

  // ── Member filter (defined early so all balance memos can use filteredAccounts) ──
  const [selectedMember, setSelectedMember] = useState(null)
  const memberPlaidIds = useMemo(() => {
    if (!selectedMember) return null
    const ids = new Set()
    accounts.forEach(a => { if (a.assigned_to === selectedMember && a.plaid_account_id) ids.add(a.plaid_account_id) })
    return ids
  }, [selectedMember, accounts])
  const filteredAccounts = useMemo(() => {
    if (!selectedMember) return accounts
    return accounts.filter(a => a.assigned_to === selectedMember)
  }, [selectedMember, accounts])

  // Write a net worth snapshot any time accounts or real estate load/refresh.
  // Debounced 800ms so both accounts and realEstateProps settle before writing.
  useEffect(() => {
    if (!accounts.length) return
    const timer = setTimeout(() => {
      const reEquity = realEstateProps.reduce((sum, prop) => {
        const mortgageAcc = accounts.find(a => a.id === prop.mortgage_account_id)
        return sum + Math.max(0, (prop.estimated_value || 0) - Math.abs(mortgageAcc?.balance || 0))
      }, 0)
      const totalAssets = accounts.filter(a => !["credit","loan"].includes(a.account_type)).reduce((s, a) => s + (a.balance || 0), 0) + reEquity
      const totalLiabilities = accounts.filter(a => ["credit","loan"].includes(a.account_type)).reduce((s, a) => s + (a.balance || 0), 0)
      const netWorth = totalAssets - totalLiabilities
      const today = new Date().toISOString().split("T")[0]
      supabase.from("net_worth_snapshots").upsert(
        { user_id: session.user.id, snapshot_date: today, total_assets: totalAssets, total_liabilities: totalLiabilities, net_worth: netWorth },
        { onConflict: "user_id,snapshot_date" }
      ).then(() => loadNetWorthSeries())
    }, 800)
    return () => clearTimeout(timer)
  }, [accounts, realEstateProps, session.user.id, loadNetWorthSeries])

  const cashBalance = useMemo(
    () => filteredAccounts.filter(a => a.account_type === 'depository').reduce((s, a) => s + (a.balance || 0), 0),
    [filteredAccounts]
  )
  const debtBalance = useMemo(
    () => filteredAccounts.filter(a => a.account_type === 'credit' || a.account_type === 'loan').reduce((s, a) => s + (a.balance || 0), 0),
    [filteredAccounts]
  )
  const investmentAccounts = useMemo(
    () => filteredAccounts.filter(a => a.account_type === 'investment' || a.account_type === 'brokerage' || a.subtype === '401k' || a.subtype === 'ira' || a.subtype === 'roth' || a.subtype === 'roth_ira'),
    [filteredAccounts]
  )
  const investmentBalance = useMemo(
    () => investmentAccounts.reduce((s, a) => s + (a.balance || 0), 0),
    [investmentAccounts]
  )

  const realEstateEquity = useMemo(
    () => realEstateProps.reduce((sum, prop) => {
      const mortgageAcc = accounts.find(a => a.id === prop.mortgage_account_id)
      const mortgage = Math.abs(mortgageAcc?.balance || 0)
      return sum + Math.max(0, (prop.estimated_value || 0) - mortgage)
    }, 0),
    [realEstateProps, accounts]
  )

  // Investment portfolio history (sum of investment account balances by date)
  const [portfolioHistory, setPortfolioHistory] = useState([])
  const loadPortfolioHistory = useCallback(async () => {
    const invIds = investmentAccounts.map(a => a.id)
    if (!invIds.length) { setPortfolioHistory([]); return }

    // Always snapshot today's balance for every investment account so the chart is current
    const today = new Date().toISOString().split("T")[0]
    await Promise.all(investmentAccounts.map(a =>
      supabase.from("account_balance_history").upsert(
        { user_id: session.user.id, account_id: a.id, balance_date: today, balance: a.balance || 0, note: "Auto-snapshot" },
        { onConflict: "user_id,account_id,balance_date" }
      )
    ))

    const { data } = await supabase
      .from("account_balance_history")
      .select("balance_date, balance, account_id")
      .eq("user_id", session.user.id)
      .in("account_id", invIds)
      .order("balance_date", { ascending: true })
    if (!data) return
    // Sum all investment accounts per date
    const byDate = {}
    data.forEach(r => { byDate[r.balance_date] = (byDate[r.balance_date] || 0) + (r.balance || 0) })
    setPortfolioHistory(Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({ date, value: Math.round(value) })))
  }, [session.user.id, investmentAccounts])

  useEffect(() => { loadPortfolioHistory() }, [loadPortfolioHistory])

  const [portRange, setPortRange] = useState("1Y")
  const [invFilter, setInvFilter] = useState("all")
  const [invTab, setInvTab] = useState("overview")
  const [pieMode, setPieMode] = useState("pct") // "pct" | "dollar"
  const [detailsFilter, setDetailsFilter] = useState("all") // "all" | "missing_cost"
  const [invUserAge, setInvUserAge] = useState(null)
  useEffect(() => {
    supabase.from("retirement_goals").select("current_age").eq("user_id", session.user.id).single()
      .then(({ data }) => { if (data?.current_age) setInvUserAge(data.current_age) })
  }, [session.user.id]) // eslint-disable-line
  const [expandedFee, setExpandedFee] = useState(null)
  const [expandedAssets, setExpandedAssets] = useState(new Set())
  const [plaidHoldings, setPlaidHoldings] = useState(null)   // null = not fetched yet
  const [holdingsLoading, setHoldingsLoading] = useState(false)
  const [holdingsNeedsRelink, setHoldingsNeedsRelink] = useState(false)
  const [manualHoldings, setManualHoldings] = useState([])
  const [quotePrices, setQuotePrices] = useState({}) // { ticker: { price, change, changePercent, name } | null }
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [costBasisOverrides, setCostBasisOverrides] = useState({}) // { "plaidAccountId::ticker": avgCost }
  const [editingCostBasis, setEditingCostBasis] = useState(null) // { plaidAccountId, ticker, value }

  const loadCostBasisOverrides = useCallback(async () => {
    const { data } = await supabase.from("user_cost_basis").select("plaid_account_id, ticker, avg_cost").eq("user_id", session.user.id)
    if (data?.length) setCostBasisOverrides(Object.fromEntries(data.map(r => [`${r.plaid_account_id}::${r.ticker}`, r.avg_cost])))
  }, [session.user.id])

  useEffect(() => { loadCostBasisOverrides() }, [loadCostBasisOverrides])

  async function saveCostBasis(plaidAccountId, ticker, avgCost) {
    const val = parseFloat(avgCost)
    if (!ticker || !plaidAccountId || isNaN(val) || val <= 0) return
    const key = `${plaidAccountId}::${ticker}`
    setCostBasisOverrides(prev => ({ ...prev, [key]: val }))
    await supabase.from("user_cost_basis").upsert(
      { user_id: session.user.id, plaid_account_id: plaidAccountId, ticker, avg_cost: val, updated_at: new Date().toISOString() },
      { onConflict: "user_id,plaid_account_id,ticker" }
    )
  }

  const loadManualHoldings = useCallback(async () => {
    // Query all manual_transactions for this user — no complex join
    const { data, error } = await supabase.from("manual_transactions")
      .select("account_id, ticker, name, shares, amount, transaction_type")
      .eq("user_id", session.user.id)
    if (error || !data || !data.length) { setManualHoldings([]); return }
    // Group by accountId + ticker/name
    const map = {}
    for (const r of data) {
      const key = `${r.account_id}::${r.ticker || r.name}`
      if (!map[key]) map[key] = { accountId: r.account_id, ticker: r.ticker || "", name: r.name || r.ticker || "—", shares: 0, costBasis: 0 }
      const isBuy = ["buy", "contribution", "dividend"].includes(r.transaction_type)
      const isSell = ["sell", "fee"].includes(r.transaction_type)
      if (isBuy) { map[key].shares += (r.shares || 0); map[key].costBasis += (r.amount || 0) }
      if (isSell) { map[key].shares -= (r.shares || 0); map[key].costBasis -= (r.amount || 0) }
    }
    const result = Object.values(map).filter(h => h.costBasis > 0 || h.shares > 0)
    setManualHoldings(result)
    // Fetch live quotes for all unique tickers
    const tickers = [...new Set(result.map(h => h.ticker).filter(Boolean))]
    if (tickers.length) fetchQuotes(tickers, result)
  }, [session.user.id]) // eslint-disable-line

  async function fetchQuotes(tickers, holdings) {
    setQuotesLoading(true)
    try {
      const res = await apiPost("/api/quotes", { tickers }, session)
      const prices = await res.json()
      setQuotePrices(prev => ({ ...prev, ...prices }))
      // Update each account's balance to current market value
      const accountTotals = {}
      for (const h of holdings) {
        const q = prices[h.ticker]
        if (!q || !q.price) continue
        const marketVal = (h.shares || 0) * q.price
        accountTotals[h.accountId] = (accountTotals[h.accountId] || 0) + marketVal
      }
      for (const [accountId, marketValue] of Object.entries(accountTotals)) {
        const today = new Date().toISOString().split("T")[0]
        await supabase.from("accounts").update({ balance: marketValue, updated_at: new Date().toISOString() })
          .eq("id", accountId).eq("user_id", session.user.id)
        await supabase.from("account_balance_history").upsert(
          { user_id: session.user.id, account_id: accountId, balance_date: today, balance: marketValue, note: "Auto-updated from live market price" },
          { onConflict: "user_id,account_id,balance_date" }
        )
      }
      if (Object.keys(accountTotals).length) { loadAccounts(); loadPortfolioHistory() }
    } catch (err) {
      fireToast("Failed to fetch investment quotes.")
    }
    setQuotesLoading(false)
  }

  function toggleAsset(name) {
    setExpandedAssets(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n })
  }

  const fetchHoldings = useCallback(async () => {
    setHoldingsLoading(true)
    try {
      const res = await apiPost("/api/holdings", { userId: session.user.id }, session)
      const data = await res.json()
      if (data.needsRelink) { setHoldingsNeedsRelink(true); setPlaidHoldings(null) }
      else {
        setPlaidHoldings(data)
        setHoldingsNeedsRelink(false)
      }
    } catch {
      setHoldingsNeedsRelink(true)
    }
    setHoldingsLoading(false)
  }, [session.user.id])

  useEffect(() => {
    if (activeNav === "investments" && plaidHoldings === null && !holdingsLoading && !holdingsNeedsRelink) fetchHoldings()
  }, [activeNav, plaidHoldings, holdingsLoading, holdingsNeedsRelink, fetchHoldings])

  // Load manual holdings on mount and whenever nav changes to investments
  useEffect(() => {
    loadManualHoldings()
  }, [loadManualHoldings])

  // Fetch live quotes for Plaid tickers whenever plaidHoldings loads (avoids stale closure in fetchHoldings)
  useEffect(() => {
    if (!plaidHoldings?.securities?.length) return
    const tickers = plaidHoldings.securities.map(s => s.ticker_symbol).filter(Boolean)
    if (!tickers.length) return
    apiPost("/api/quotes", { tickers }, session)
      .then(r => r.json())
      .then(prices => setQuotePrices(prev => ({ ...prev, ...prices })))
      .catch(() => fireToast("Failed to fetch investment quotes."))
  }, [plaidHoldings]) // eslint-disable-line

  // Build allocationData + holdingsData from real Plaid holdings
  const SECURITY_TYPE_MAP = {
    equity: "Stocks",
    etf: "ETFs",
    "mutual fund": "Mutual Funds",
    "fixed income": "Bonds",
    bond: "Bonds",
    derivative: "Options",
    option: "Options",
    cryptocurrency: "Crypto",
    "real estate": "Real Estate",
    cash: "Cash",
    "money market": "Cash",
  }
  const ASSET_COLORS = {
    Stocks: "#2F855A",
    ETFs: "#3B82F6",
    "Mutual Funds": "#8B5CF6",
    Bonds: "#7DD3FC",
    Options: "#F97316",
    Crypto: "#EF4444",
    "Real Estate": "#7CB342",
    Cash: "#F59E0B",
    Other: "#94A3B8",
  }

  const QUOTE_TYPE_TO_ASSET = { EQUITY: "Stocks", ETF: "ETFs", MUTUALFUND: "Mutual Funds", BOND: "Bonds", CRYPTOCURRENCY: "Crypto" }

  const { computedAllocationData, computedHoldingsData } = useMemo(() => {
    const hasPlaid = plaidHoldings?.holdings?.length > 0
    const hasManual = manualHoldings.length > 0
    if (!hasPlaid && !hasManual) return { computedAllocationData: null, computedHoldingsData: null }

    const assetGroups = {}

    // — Plaid holdings —
    if (hasPlaid) {
      const { holdings, securities } = plaidHoldings
      const secMap = Object.fromEntries(securities.map(s => [s.security_id, s]))
      const selectedAccount = invFilter !== "all"
        ? (plaidHoldings.accounts || []).find(a => {
            const match = investmentAccounts.find(ia => ia.id === invFilter)
            return match && a.account_id === match.plaid_account_id
          })
        : null
      const filteredHoldings = invFilter === "all"
        ? holdings
        : holdings.filter(h => selectedAccount && h.account_id === selectedAccount.account_id)

      for (const h of filteredHoldings) {
        const sec = secMap[h.security_id] || {}
        const rawType = (sec.type || "").toLowerCase()
        const assetClass = SECURITY_TYPE_MAP[rawType] || "Stocks"
        if (!assetGroups[assetClass]) assetGroups[assetClass] = []
        const ticker = sec.ticker_symbol || sec.name?.slice(0, 6) || "—"
        const livePrice = quotePrices[ticker]?.price
        const currentPrice = livePrice || sec.close_price || 0
        const qty = h.quantity || 0
        // Use Plaid cost_basis if available; fall back to user-entered override (keyed per account)
        const plaidAvg = h.cost_basis && qty ? h.cost_basis / qty : 0
        const overrideAvg = costBasisOverrides[`${h.account_id}::${ticker}`] || 0
        const avgPriceP = plaidAvg || overrideAvg
        const costBasisTotal = avgPriceP * qty
        assetGroups[assetClass].push({
          ticker, name: sec.name || "Unknown",
          shares: qty,
          avgPrice: avgPriceP,
          currentPrice,
          unrealized: currentPrice && costBasisTotal ? (currentPrice * qty) - costBasisTotal : 0,
          realized: 0,
          value: livePrice ? livePrice * qty : (h.institution_value || (sec.close_price * qty) || 0),
          changePercent: quotePrices[ticker]?.changePercent ?? null,
          totalReturn: avgPriceP > 0 && currentPrice > 0 ? ((currentPrice - avgPriceP) / avgPriceP) * 100 : null,
          plaidAccountId: h.account_id,
          isManual: false,
        })
      }
      // Merge duplicate tickers when showing all accounts (weighted avg cost)
      if (invFilter === "all") {
        for (const key of Object.keys(assetGroups)) {
          const merged = {}
          for (const item of assetGroups[key]) {
            if (merged[item.ticker]) {
              const prev = merged[item.ticker]
              const combinedShares = prev.shares + item.shares
              const combinedCost = prev.avgPrice * prev.shares + item.avgPrice * item.shares
              prev.avgPrice = combinedShares > 0 ? combinedCost / combinedShares : 0
              prev.shares = combinedShares
              prev.value += item.value
              prev.unrealized += item.unrealized
              prev.plaidAccountId = null // merged — no single account
            } else {
              merged[item.ticker] = { ...item }
            }
          }
          assetGroups[key] = Object.values(merged)
        }
      }
    }

    // — Manual holdings —
    const visibleManual = invFilter === "all" ? manualHoldings : manualHoldings.filter(h => h.accountId === invFilter)
    for (const h of visibleManual) {
      const q = quotePrices[h.ticker]
      const assetClass = QUOTE_TYPE_TO_ASSET[q?.quoteType] || "Mutual Funds"
      if (!assetGroups[assetClass]) assetGroups[assetClass] = []
      const currentPrice = q?.price || 0
      const shares = h.shares || 0
      const value = currentPrice ? currentPrice * shares : h.costBasis
      const avgPriceM = shares > 0 ? h.costBasis / shares : 0
      assetGroups[assetClass].push({
        ticker: h.ticker || h.name,
        name: q?.name || h.name,
        shares,
        avgPrice: avgPriceM,
        currentPrice,
        unrealized: currentPrice ? value - h.costBasis : 0,
        realized: 0,
        value,
        changePercent: q?.changePercent ?? null,
        totalReturn: avgPriceM > 0 && currentPrice > 0 ? ((currentPrice - avgPriceM) / avgPriceM) * 100 : null,
        isManual: true,
      })
    }

    for (const key of Object.keys(assetGroups)) assetGroups[key].sort((a, b) => b.value - a.value)
    const alloc = Object.entries(assetGroups).map(([name, items]) => ({
      name, color: ASSET_COLORS[name] || ASSET_COLORS.Other,
      value: items.reduce((s, i) => s + i.value, 0),
    })).sort((a, b) => b.value - a.value)
    return { computedAllocationData: alloc, computedHoldingsData: assetGroups }
  }, [plaidHoldings, invFilter, investmentAccounts, quotePrices, manualHoldings, costBasisOverrides])

  const investmentScore = useMemo(() => {
    const allHoldings = Object.values(computedHoldingsData || {}).flat()
    const alloc = computedAllocationData || []
    const totalVal = alloc.reduce((s, a) => s + a.value, 0)
    if (!totalVal || !allHoldings.length) return null

    const metrics = []

    // 1. Fee efficiency (15 pts) — weighted expense ratio
    const feeRows = []
    const secMap = Object.fromEntries((plaidHoldings?.securities || []).map(s => [s.security_id, s]))
    for (const h of plaidHoldings?.holdings || []) {
      const sec = secMap[h.security_id] || {}
      const ticker = sec.ticker_symbol?.toUpperCase()
      if (!ticker || !FUND_FEE_DB[ticker]) continue
      const val = h.institution_value || (sec.close_price * h.quantity) || 0
      if (val > 0) feeRows.push({ val, er: FUND_FEE_DB[ticker].er })
    }
    const feeTotal = feeRows.reduce((s, r) => s + r.val, 0)
    const weightedER = feeTotal > 0 ? feeRows.reduce((s, r) => s + r.er * (r.val / feeTotal), 0) : null
    const feeScore = weightedER == null ? 13 : weightedER < 0.10 ? 25 : weightedER < 0.25 ? 17 : weightedER < 0.50 ? 8 : 0
    metrics.push({ label: "Fee Efficiency", score: feeScore, max: 25, emoji: "💸",
      detail: weightedER != null ? `Weighted expense ratio: ${weightedER.toFixed(2)}%` : "No tracked funds in database",
      tip: "Lower expense ratios mean more of your returns stay in your pocket. Target < 0.10% (index funds).",
      note: "Based on funds in our expense ratio database.",
      rows: [["< 0.10% weighted ER","25 pts"],["0.10–0.25%","17 pts"],["0.25–0.50%","8 pts"],["> 0.50%","0 pts"],["No tracked funds","13 pts (neutral)"]],
    })

    // 2. Cost basis coverage (10 pts)
    const plaidHoldingsList = plaidHoldings?.holdings || []
    const withCost = plaidHoldingsList.filter(h => {
      const sec = secMap[h.security_id] || {}
      const ticker = sec.ticker_symbol || ""
      return h.cost_basis > 0 || costBasisOverrides[`${h.account_id}::${ticker}`]
    })
    const coveragePct = plaidHoldingsList.length > 0 ? withCost.length / plaidHoldingsList.length : 1
    const coverageScore = coveragePct >= 1 ? 10 : coveragePct >= 0.75 ? 7 : coveragePct >= 0.5 ? 4 : 1
    metrics.push({ label: "Cost Basis Coverage", score: coverageScore, max: 10, emoji: "📋",
      detail: `${Math.round(coveragePct * 100)}% of holdings have known avg cost`,
      tip: "Without cost basis you can't measure real returns. Click '+ Add' in the Details tab to fill in missing values.",
      note: "Counts Plaid-provided cost_basis and manually entered avg cost.",
      rows: [["100% of holdings","10 pts"],["75–99%","7 pts"],["50–74%","4 pts"],["< 50%","1 pt"]],
    })

    // 3. Asset class spread (10 pts) — Herfindahl index
    const hhi = alloc.reduce((s, a) => s + Math.pow(a.value / totalVal, 2), 0)
    const spreadScore = hhi < 0.25 ? 10 : hhi < 0.40 ? 7 : hhi < 0.60 ? 3 : 0
    metrics.push({ label: "Diversification", score: spreadScore, max: 10, emoji: "🌐",
      detail: `HHI concentration index: ${hhi.toFixed(2)} (lower = more diversified)`,
      tip: "Spreading across ETFs, stocks, bonds, and other asset classes reduces risk from any single sector.",
      note: "Uses Herfindahl-Hirschman Index on asset class values. < 0.25 = well diversified.",
      rows: [["HHI < 0.25 (well spread)","10 pts"],["0.25–0.40","7 pts"],["0.40–0.60","3 pts"],["> 0.60 (concentrated)","0 pts"]],
    })

    // 4. Number of positions (15 pts)
    const posCount = allHoldings.length
    const posScore = posCount >= 5 && posCount <= 25 ? 15 : posCount >= 3 && posCount <= 35 ? 10 : 4
    metrics.push({ label: "Position Count", score: posScore, max: 15, emoji: "📊",
      detail: `${posCount} position${posCount !== 1 ? "s" : ""} across all accounts`,
      tip: "5–25 positions is the sweet spot: diversified without being over-complicated.",
      note: "Too few positions = concentration risk. Too many = dilution and harder to manage.",
      rows: [["5–25 positions","15 pts"],["3–4 or 26–35","10 pts"],["1–2 or > 35","4 pts"]],
    })

    // 5. International exposure (15 pts)
    const intlKeywords = ["international","intl","global","world","emerging","ex-us","ex us","foreign","developed markets","eafe"]
    const intlTickers = ["IXUS","VEA","VXUS","EFA","EEM","VWO","IEFA","SCHF","SPDW","FZILX","FTIAX","VGTSX"]
    const intlValue = allHoldings.reduce((s, h) => {
      const nameMatch = intlKeywords.some(k => (h.name || "").toLowerCase().includes(k))
      const tickerMatch = intlTickers.includes((h.ticker || "").toUpperCase())
      return s + (nameMatch || tickerMatch ? h.value : 0)
    }, 0)
    const intlPct = totalVal > 0 ? (intlValue / totalVal) * 100 : 0
    const intlScore = intlPct >= 10 && intlPct <= 45 ? 15 : intlPct >= 5 ? 10 : intlPct > 0 ? 5 : 0
    metrics.push({ label: "International Exposure", score: intlScore, max: 15, emoji: "🌍",
      detail: `${intlPct.toFixed(1)}% of portfolio in international funds`,
      tip: "10–45% international exposure is recommended. Global diversification reduces home-country risk.",
      note: "Detected from fund names (international, global, emerging) and common intl ETF tickers.",
      rows: [["10–45% international","15 pts"],["5–10%","10 pts"],["1–5%","5 pts"],["0% (all domestic)","0 pts"]],
    })

    // 6. Concentration risk (25 pts) — largest single position, ETFs penalized less
    const etfTickers = new Set(Object.entries(FUND_FEE_DB).filter(([, v]) => v.cat === "US Large Cap" || v.cat === "Intl Total Market" || v.cat === "US Total Market" || v.cat === "Intl Developed" || v.cat === "US Mid Cap" || v.cat === "US Small Cap").map(([k]) => k))
    const largestHolding = allHoldings.reduce((a, b) => b.value > a.value ? b : a, allHoldings[0])
    const largest = largestHolding?.value || 0
    const largePct = totalVal > 0 ? (largest / totalVal) * 100 : 0
    const largestIsETF = etfTickers.has((largestHolding?.ticker || "").toUpperCase()) || (quotePrices[(largestHolding?.ticker || "")]?.quoteType === "ETF")
    // ETFs get softer thresholds since they're internally diversified
    let concScore
    if (largestIsETF) {
      concScore = largePct < 50 ? 25 : largePct < 70 ? 18 : largePct < 85 ? 10 : 4
    } else {
      concScore = largePct < 15 ? 25 : largePct < 25 ? 17 : largePct < 40 ? 8 : 0
    }
    metrics.push({ label: "Concentration Risk", score: concScore, max: 25, emoji: "⚠️",
      detail: `Largest position: ${largestHolding?.ticker || "—"} at ${largePct.toFixed(1)}%${largestIsETF ? " (ETF — softer threshold)" : ""}`,
      tip: largestIsETF
        ? "Large ETF positions are fine since they're internally diversified. Concentration only matters more for individual stocks."
        : "No single stock should dominate your portfolio. Under 15% keeps single-stock risk manageable.",
      note: "ETFs (like VOO) use softer thresholds since they hold hundreds of stocks internally.",
      rows: [
        ["ETF < 50% of portfolio","25 pts"],["ETF 50–70%","18 pts"],["ETF 70–85%","10 pts"],["ETF > 85%","4 pts"],
        ["Stock < 15%","25 pts"],["Stock 15–25%","17 pts"],["Stock 25–40%","8 pts"],["Stock > 40%","0 pts"],
      ],
    })

    const total = metrics.reduce((s, m) => s + m.score, 0)
    const grade = total >= 80 ? { label: "Excellent", color: "#22C55E" }
      : total >= 65 ? { label: "Good", color: "#3B82F6" }
      : total >= 50 ? { label: "Fair", color: "#F59E0B" }
      : { label: "Needs Work", color: "#EF4444" }
    return { total, metrics, grade }
  }, [computedHoldingsData, computedAllocationData, plaidHoldings, costBasisOverrides]) // eslint-disable-line

  // Estimated dividend income — next 12 months, by payment month
  const estimatedDividends = useMemo(() => {
    const allHoldings = Object.values(computedHoldingsData || {}).flat()
    if (!allHoldings.length || !Object.keys(quotePrices).length) return []

    const today = new Date()
    // Build 12 month slots: { key: "2025-04", label: "Apr", total, byTicker: {} }
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
      return {
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleString("default", { month: "short" }),
        year: d.getFullYear(),
        month: d.getMonth(), // 0-indexed
        total: 0,
        byTicker: {},
      }
    })

    for (const h of allHoldings) {
      if (!h.ticker || h.shares <= 0) continue
      const q = quotePrices[h.ticker]
      if (!q || !q.dividendRate || q.dividendRate <= 0) continue

      const annualPerShare = q.dividendRate
      const annualTotal = annualPerShare * h.shares
      const quarterlyAmt = annualTotal / 4

      // Determine which calendar months payments land in (quarterly pattern)
      // Use exDividendDate to find the base payment month; payments are ~1 month after ex-div
      let basePayMonth = 0 // Jan default → payments in Jan, Apr, Jul, Oct
      if (q.exDividendDate) {
        const exDiv = new Date(q.exDividendDate)
        // payment month ≈ ex-div month + 1
        basePayMonth = (exDiv.getMonth() + 1) % 12
      }
      // The 4 payment months (0-indexed) in the year: basePayMonth, +3, +6, +9
      const payMonths = [0, 3, 6, 9].map(offset => (basePayMonth + offset) % 12)

      for (const slot of months) {
        if (payMonths.includes(slot.month)) {
          slot.total += quarterlyAmt
          slot.byTicker[h.ticker] = (slot.byTicker[h.ticker] || 0) + quarterlyAmt
        }
      }
    }

    return months
  }, [computedHoldingsData, quotePrices])

  const filteredInvestmentAccounts = useMemo(
    () => (invFilter === "all" ? investmentAccounts : investmentAccounts.filter(a => a.id === invFilter))
      .slice().sort((a, b) => (b.balance || 0) - (a.balance || 0)),
    [investmentAccounts, invFilter]
  )
  const filteredInvestmentBalance = useMemo(
    () => filteredInvestmentAccounts.reduce((s, a) => s + (a.balance || 0), 0),
    [filteredInvestmentAccounts]
  )

  // ✅ Transactions (DB + Plaid sync)
  const [txLoading, setTxLoading] = useState(false)
  const [txInitialized, setTxInitialized] = useState(false)
  const [syncWarning, setSyncWarning] = useState("")
  const [transactions, setTransactions] = useState([])

  const [settings, setSettings] = useState(() => {
    const defaults = {
      theme: 'light', currency: 'USD', language: 'en',
      notif_budget: true, notif_unusual: true, notif_monthly: true,
      notif_retirement: true, notif_rates: false,
      privacy_analytics: true, privacy_personalization: true,
    }
    try {
      const saved = JSON.parse(localStorage.getItem(`grove_settings_${session.user.id}`) || '{}')
      return { ...defaults, ...saved }
    } catch { return defaults }
  })
  const [paychecks, setPaychecks] = useState([])
  useEffect(() => {
    async function loadPaychecks() {
      const { data } = await supabase.from("user_paychecks").select("paychecks").eq("user_id", session.user.id).single()
      if (data) {
        setPaychecks(data.paychecks || [])
      } else {
        // Migrate from localStorage if Supabase row doesn't exist yet
        const local = (() => { try { return JSON.parse(localStorage.getItem(`paychecks_${session.user.id}`) || "[]") } catch { return [] } })()
        if (local.length > 0) {
          await supabase.from("user_paychecks").upsert({ user_id: session.user.id, paychecks: local, updated_at: new Date().toISOString() })
          localStorage.removeItem(`paychecks_${session.user.id}`)
          setPaychecks(local)
        }
      }
    }
    loadPaychecks()
  }, [session.user.id])
  async function savePaychecks(next) {
    setPaychecks(next)
    await supabase.from("user_paychecks").upsert({ user_id: session.user.id, paychecks: next, updated_at: new Date().toISOString() })
  }

  const theme = settings.theme === 'dark' ? darkTheme : lightTheme
  const t = theme
  const firstName = session.user.user_metadata?.full_name?.split(' ')[0] || session.user.email.split('@')[0]
const pct = (part, total) => {
  const t = Number(total || 0)
  if (!t) return "0%"
  return `${Math.round((Number(part || 0) / t) * 100)}%`
}
  // ✅ Category customization (overrides + custom additions, persisted in localStorage)
  const [catCustom, setCatCustom] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cat_custom_${session.user.id}`) || '{"overrides":{},"additions":[]}') }
    catch { return { overrides: {}, additions: [] } }
  })
  const saveCatCustom = useCallback((next) => {
    setCatCustom(next)
    localStorage.setItem(`cat_custom_${session.user.id}`, JSON.stringify(next))
  }, [session.user.id])
  const effectiveCategories = useMemo(() => {
    const hiddenSet = new Set(catCustom.hidden || [])
    const base = CATEGORIES.map(c => ({ ...c, group: catCustom.overrides[c.label]?.group ?? c.group, hidden: hiddenSet.has(c.label) }))
    return [...base, ...(catCustom.additions || []).map(c => ({ ...c, hidden: hiddenSet.has(c.label) }))]
  }, [catCustom])
  const hiddenCats = useMemo(() => new Set(catCustom.hidden || []), [catCustom])

  const onSettingsChange = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value }
      try { localStorage.setItem(`grove_settings_${session.user.id}`, JSON.stringify(next)) } catch {}
      return next
    })
  }, [session.user.id])

  // ✅ Currency formatting helper (simple symbol swap; no conversion logic here)
  const currencyMeta = CURRENCIES.find(c => c.code === settings.currency) || CURRENCIES[0]
  const fmtMoney = (n) => {
    const val = Number(n || 0)
    const abs = Math.abs(val).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    return `${val < 0 ? "-" : ""}${currencyMeta.symbol}${abs}`
  }

  // filteredRawTx: member-filtered raw transactions (selectedMember + memberPlaidIds defined earlier)
  const filteredRawTx = useMemo(() => {
    if (!memberPlaidIds) return transactions
    return transactions.filter(tx => !tx.plaid_account_id || memberPlaidIds.has(tx.plaid_account_id))
  }, [transactions, memberPlaidIds])

  // ── Financial Health Score ─────────────────────────────
  const healthScore = useMemo(() => {
    const MONTHS_BACK = 6
    const cutoff6 = new Date(); cutoff6.setMonth(cutoff6.getMonth() - MONTHS_BACK)
    const cutoffStr6 = cutoff6.toISOString().split("T")[0]
    const recentTx = filteredRawTx.filter(tx => tx.date >= cutoffStr6)
    const needsCats = new Set(CATEGORIES.filter(c => c.group === "needs").map(c => c.label))
    const monthlyIncome   = recentTx.filter(t => t.amount < 0 && !t.is_transfer).reduce((s, t) => s + (-t.amount), 0) / MONTHS_BACK
    const monthlyExpenses = recentTx.filter(t => t.amount > 0 && !t.is_transfer && (t.user_group === "needs" || needsCats.has(t.user_category))).reduce((s, t) => s + t.amount, 0) / MONTHS_BACK
    const liquidSavings   = filteredAccounts.filter(a => a.account_type === "depository").reduce((s, a) => s + (a.balance || 0), 0)
    const totalDebt       = debtBalance
    const hasData         = monthlyIncome > 0

    const efMonths = monthlyExpenses > 0 ? liquidSavings / monthlyExpenses : 0
    const efScore  = efMonths >= 6 ? 25 : efMonths >= 3 ? 18 : efMonths >= 1 ? 10 : efMonths > 0 ? 4 : 0

    const savingsRate = hasData && monthlyIncome > 0 ? (monthlyIncome - monthlyExpenses) / monthlyIncome : null
    const srScore = savingsRate === null ? 0 : savingsRate >= 0.20 ? 25 : savingsRate >= 0.10 ? 18 : savingsRate >= 0.05 ? 10 : savingsRate > 0 ? 4 : 0

    const dti = hasData && monthlyIncome > 0 ? (totalDebt / (monthlyIncome * 12)) : null
    const dtiScore = dti === null ? 10 : dti === 0 ? 20 : dti < 0.20 ? 20 : dti < 0.36 ? 14 : dti < 0.50 ? 6 : 0

    const investPct = hasData && monthlyIncome > 0 ? investmentBalance / (monthlyIncome * 12) : 0
    const invScore = investmentBalance === 0 ? 0 : investPct >= 0.15 ? 15 : investPct >= 0.05 ? 10 : 6

    const trendScore = netWorthSeries.length < 2 ? 8
      : netWorthSeries[netWorthSeries.length - 1]?.net_worth > netWorthSeries[0]?.net_worth ? 15
      : netWorthSeries[netWorthSeries.length - 1]?.net_worth === netWorthSeries[0]?.net_worth ? 8 : 0

    const total = efScore + srScore + dtiScore + invScore + trendScore
    const metrics = [
      { label: "Emergency Fund", score: efScore,   max: 25, detail: efMonths >= 6 ? `${efMonths.toFixed(1)} months covered ✅` : efMonths >= 3 ? `${efMonths.toFixed(1)} months — aim for 6` : `${efMonths.toFixed(1)} months — aim for 3–6`, tip: "Keep 3–6 months of expenses in liquid savings as a safety net." },
      { label: "Savings Rate",   score: srScore,   max: 25, detail: savingsRate === null ? "Not enough data" : `${Math.round((savingsRate||0)*100)}% of income saved`, tip: "Aim to save at least 20% of your income. Even 5–10% is a great start." },
      { label: "Debt Load",      score: dtiScore,  max: 20, detail: dti === null ? "Not enough data" : dti === 0 ? "No debt — excellent!" : `DTI ${Math.round((dti||0)*100)}% of annual income`, tip: "Keep total debt under 36% of your annual income. Under 20% is excellent." },
      { label: "Investing",      score: invScore,  max: 15, detail: investmentBalance === 0 ? "No investments yet" : `${fmtMoney(investmentBalance)} invested`, tip: "Invest consistently — even small amounts benefit from compound growth over time." },
      { label: "Net Worth Trend",score: trendScore,max: 15, detail: netWorthSeries.length < 2 ? "Building history…" : trendScore === 15 ? "Growing ↑" : trendScore === 8 ? "Flat" : "Declining ↓", tip: "A growing net worth means your assets are outpacing your liabilities." },
    ]
    const grade = total >= 90 ? { label: "Excellent", color: "#2F855A" }
      : total >= 75 ? { label: "Very Good", color: "#38A169" }
      : total >= 60 ? { label: "Good",      color: "#F59E0B" }
      : total >= 40 ? { label: "Fair",      color: "#ED8936" }
      :               { label: "Needs Work",color: "#EF4444" }
    return { total, metrics, grade }
  }, [filteredRawTx, filteredAccounts, debtBalance, investmentBalance, netWorthSeries, fmtMoney])

  const loadTransactionsFromDb = useCallback(async () => {
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq("user_id", session.user.id)
      .order("date", { ascending: false })
      .limit(2000)

    if (error) { fireToast("Failed to load transactions. Please refresh."); return }
    if (!data) return

    // Auto-classify uncategorized transactions: community votes first, then keyword patterns
    const uncategorized = data.filter(tx => !tx.user_category)
    if (uncategorized.length > 0) {
      const communityMatched = new Set()

      // Step 1: community votes (≥5 votes, ≥60% share)
      const merchantNames = [...new Set(
        uncategorized.map(tx => (tx.merchant_name || tx.name || "").toLowerCase().trim()).filter(Boolean)
      )]
      if (merchantNames.length > 0) {
        const { data: community } = await supabase
          .from("merchant_categories")
          .select("merchant_name, category, grp, votes")
          .in("merchant_name", merchantNames)

        if (community && community.length > 0) {
          const communityMap = {}
          const byMerchant = {}
          community.forEach(row => {
            if (!byMerchant[row.merchant_name]) byMerchant[row.merchant_name] = []
            byMerchant[row.merchant_name].push(row)
          })
          Object.entries(byMerchant).forEach(([name, rows]) => {
            const total = rows.reduce((s, r) => s + r.votes, 0)
            const best = rows.sort((a, b) => b.votes - a.votes)[0]
            if (best.votes >= 5 && best.votes / total >= 0.6) {
              communityMap[name] = { category: best.category, group: best.grp }
            }
          })
          const toUpdate = uncategorized.filter(tx => {
            const key = (tx.merchant_name || tx.name || "").toLowerCase().trim()
            return !!communityMap[key]
          })
          if (toUpdate.length > 0) {
            await Promise.all(toUpdate.map(tx => {
              const key = (tx.merchant_name || tx.name || "").toLowerCase().trim()
              const { category, group } = communityMap[key]
              communityMatched.add(tx.id)
              return supabase.from("transactions")
                .update({ user_category: category, user_group: group })
                .eq("id", tx.id).eq("user_id", session.user.id)
            }))
          }
        }
      }

      // Step 2: keyword patterns for anything still uncategorized
      const patternMatches = uncategorized
        .filter(tx => !communityMatched.has(tx.id))
        .map(tx => ({ tx, s: getPatternSuggestion(tx.merchant_name || tx.name) }))
        .filter(({ s }) => !!s)
      if (patternMatches.length > 0) {
        await Promise.all(patternMatches.map(({ tx, s }) =>
          supabase.from("transactions")
            .update({ user_category: s.category, user_group: s.group })
            .eq("id", tx.id).eq("user_id", session.user.id)
        ))
      }

      // Re-fetch if anything was auto-categorized
      if (communityMatched.size > 0 || patternMatches.length > 0) {
        const { data: patched } = await supabase
          .from("transactions").select("*")
          .eq("user_id", session.user.id).order("date", { ascending: false }).limit(2000)
        if (patched) { setTransactions(patched); setTxInitialized(true); return }
      }
    }

    setTransactions(data)
    setTxInitialized(true)
  }, [session.user.id])

  const syncTransactionsFromPlaid = useCallback(async () => {
    setTxLoading(true)
    try {
      const res = await apiPost("/api/transactions", { userId: session.user.id, days: 180 }, session)
      const payload = await res.json()
      if (!res.ok) throw new Error(payload.error || "Failed to fetch transactions")

      const txCount = (payload.transactions || []).length
      if (payload.hasProductNotReady) {
        setSyncWarning("⚠️ Accounts not ready yet — Plaid is still preparing your data. Try again in 1–2 minutes.")
      } else if (txCount === 0) {
        setSyncWarning("ℹ️ Sync complete but no transactions returned. If you just connected accounts, wait a minute and try again.")
      } else {
        setSyncWarning("")
      }

      // Server saves transactions to DB (bypasses RLS via service role key)

      // Auto-snapshot: record today's real net worth from current account balances + real estate
      const { data: accs } = await supabase
        .from("accounts")
        .select("id, account_type, balance")
        .eq("user_id", session.user.id)

      if (accs?.length) {
        const reEquity = realEstateProps.reduce((sum, prop) => {
          const mortgageAcc = accs.find(a => a.id === prop.mortgage_account_id)
          return sum + Math.max(0, (prop.estimated_value || 0) - Math.abs(mortgageAcc?.balance || 0))
        }, 0)
        const totalAssets = accs.filter(a => !["credit","loan"].includes(a.account_type)).reduce((s, a) => s + (a.balance || 0), 0) + reEquity
        const totalLiabilities = accs.filter(a => ["credit","loan"].includes(a.account_type)).reduce((s, a) => s + (a.balance || 0), 0)
        const netWorth = totalAssets - totalLiabilities
        const today = new Date().toISOString().split("T")[0]
        await supabase.from("net_worth_snapshots").upsert(
          { user_id: session.user.id, snapshot_date: today, total_assets: totalAssets, total_liabilities: totalLiabilities, net_worth: netWorth },
          { onConflict: "user_id,snapshot_date" }
        )
      }

      await Promise.all([loadTransactionsFromDb(), loadAccounts(), loadNetWorthSeries()])
    } catch (e) {
      console.error(e)
      setSyncWarning(`❌ Sync error: ${e.message}`)
      await Promise.all([loadTransactionsFromDb(), loadAccounts()])
    }
    setTxLoading(false)
  },

  [session.user.id, loadTransactionsFromDb, loadAccounts, loadNetWorthSeries])

  // Load from DB immediately on mount so health score shows real data before Plaid sync completes
  useEffect(() => {
    Promise.all([loadTransactionsFromDb(), loadAccounts()])
  }, [loadTransactionsFromDb, loadAccounts])

  // ✅ Auto-sync on mount and every 6 hours while app is open
  useEffect(() => {
    syncTransactionsFromPlaid()
    const interval = setInterval(syncTransactionsFromPlaid, 6 * 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [syncTransactionsFromPlaid])
  const uiTransactions = transactions?.length ? transactions.map(formatTxForUI) : []

  // filteredUiTx: member-filtered version of uiTransactions (filteredRawTx + selectedMember defined earlier)
  const filteredUiTx = useMemo(() => {
    if (!memberPlaidIds) return uiTransactions
    return uiTransactions.filter(tx => !tx.plaid_account_id || memberPlaidIds.has(tx.plaid_account_id))
  }, [uiTransactions, memberPlaidIds])

  const recent5 = filteredUiTx.slice(0, 5)

  const card = makeCard(t)

  function completeOnboarding() {
    localStorage.setItem(`grove_onboarded_${session.user.id}`, "1")
    setShowOnboarding(false)
    loadAccounts()
  }

  return (
  <ThemeContext.Provider value={{ theme: t, settings }}>
    {showOnboarding && <OnboardingOverlay session={session} onComplete={completeOnboarding} onXPAward={triggerXP} savePaychecks={savePaychecks} />}
    {!showOnboarding && tabIntroId && <TabIntroModal tabId={tabIntroId} onDismiss={dismissTabIntro} onClose={() => setTabIntroId(null)} />}
    <XPToastContainer toasts={xpToasts} onDismiss={dismissToast} />
    <ToastContainer />
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: t.bg,
        fontFamily: "system-ui",
        color: t.text,
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          width: 220,
          background: t.navBg,
          borderRight: `1px solid ${t.border}`,
          display: "flex",
          flexDirection: "column",
          padding: "28px 0",
          flexShrink: 0,
        }}
      >
        {/* Logo */}
        <div style={{ padding: "0 16px 28px" }}>
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Grove Money"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>

        <nav style={{ flex: 1, padding: "0 10px" }}>
          {navItems.filter(item => !item.adminOnly || session.user.email === process.env.REACT_APP_ADMIN_EMAIL).map((item) => {
            const active = activeNav === item.id
            return (
              <button
                key={item.id}
                onClick={() => setActiveNavPersist(item.id)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", marginBottom: 2,
                  background: active ? (t.isDark ? "rgba(110,231,183,0.10)" : "rgba(47,133,90,0.08)") : "transparent",
                  border: "none", borderRadius: 10,
                  cursor: "pointer",
                  color: active ? t.accent : t.textMuted,
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  transition: "all 0.15s", textAlign: "left",
                }}
              >
                <span style={{ fontSize: 16, width: 22, textAlign: "center" }}>{item.icon}</span>
                {item.label}
              </button>
            )
          })}
        </nav>

        {/* Tutorial button */}
        {TAB_INTROS[activeNav] && (
          <div style={{ padding: "0 14px 10px" }}>
            <button onClick={() => setTabIntroId(activeNav)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 14 }}>❓</span> Tab Tour
            </button>
          </div>
        )}

        {/* Level badge */}
        {(() => {
          const lvl = getLevelInfo(userXp)
          return (
            <div style={{ padding: "0 14px 14px" }}>
              <div onClick={() => setActiveNavPersist("levels")} style={{ background: t.isDark ? "rgba(110,231,183,0.06)" : "rgba(47,133,90,0.05)", border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
                    {lvl.current.emoji} {lvl.current.name}
                  </span>
                  <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>{userXp} XP</span>
                </div>
                <div style={{ height: 5, borderRadius: 9999, background: t.border, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${lvl.progress}%`, borderRadius: 9999, background: `linear-gradient(90deg, ${t.accent}, #34d399)`, transition: "width 0.6s ease" }} />
                </div>
                {lvl.next ? (
                  <div style={{ fontSize: 10, color: t.textFaint, marginTop: 5 }}>
                    {lvl.next.xp - userXp} XP → {lvl.next.emoji} {lvl.next.name}
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: t.accent, marginTop: 5, fontWeight: 700 }}>Max level reached! 🌏</div>
                )}
              </div>
            </div>
          )
        })()}

        {/* Bottom profile area */}
        <div style={{ padding: "18px 20px", borderTop: `1px solid ${t.border}` }}>
          <div ref={profileMenuRef} style={{ position: "relative" }}>
            {/* Clickable profile row */}
            <button
              onClick={() => setProfileMenuOpen((v) => !v)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 12px",
                borderRadius: 10,
                background: profileMenuOpen ? t.hover : "transparent",
                border: `1px solid ${profileMenuOpen ? t.border : "transparent"}`,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: userAvatar
                      ? `url(${userAvatar}) center/cover no-repeat`
                      : "linear-gradient(135deg, #7CB342, #2F855A)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#fff",
                    flexShrink: 0,
                    border: `1px solid ${t.border}`,
                  }}
                >
                  {!userAvatar && firstName.slice(0, 2).toUpperCase()}
                </div>

                {/* Only name (no email) */}
                <div style={{ textAlign: "left" }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: t.text,
                      lineHeight: 1.1,
                    }}
                  >
                    {firstName}
                  </div>
                </div>
              </div>

              {/* Chevron */}
              <div
                style={{
                  color: t.textMuted,
                  fontSize: 14,
                  transform: profileMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s",
                }}
              >
                ▾
              </div>
            </button>

            {/* Dropdown menu */}
            {profileMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "52px",
                  background: t.surface,
                  border: `1px solid ${t.border}`,
                  borderRadius: 12,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
                  overflow: "hidden",
                  zIndex: 100,
                }}
              >
                {/* Theme toggle */}
                <div style={{ padding: "12px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        opacity: settings.theme === "light" ? 1 : 0.4,
                        transition: "opacity 0.2s",
                      }}
                    >
                      ☀️
                    </span>

                    <div
                      onClick={() =>
                        onSettingsChange(
                          "theme",
                          settings.theme === "dark" ? "light" : "dark"
                        )
                      }
                      style={{
                        width: 54,
                        height: 28,
                        borderRadius: 999,
                        background: settings.theme === "dark" ? t.accent : t.border,
                        cursor: "pointer",
                        position: "relative",
                        transition: "background 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 3,
                          left: settings.theme === "dark" ? 28 : 3,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: "#fff",
                          transition: "left 0.2s",
                        }}
                      />
                    </div>

                    <span
                      style={{
                        fontSize: 16,
                        opacity: settings.theme === "dark" ? 1 : 0.4,
                        transition: "opacity 0.2s",
                      }}
                    >
                      🌙
                    </span>
                  </div>
                </div>

                <div style={{ height: 1, background: t.border }} />

                <button
                  onClick={async () => {
                    setProfileMenuOpen(false)
                    await syncTransactionsFromPlaid()
                  }}
                  disabled={txLoading}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 12px",
                    background: "transparent",
                    border: "none",
                    cursor: txLoading ? "not-allowed" : "pointer",
                    color: txLoading ? t.textMuted : t.text,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span>🔄</span> {txLoading ? "Syncing..." : "Sync transactions"}
                </button>

                <div style={{ height: 1, background: t.border }} />

                <button
                  onClick={() => supabase.auth.signOut()}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 12px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "#EF4444",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span>⎋</span> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

        {/* Settings Panel */}
        <SettingsPanel
  session={session}
  isOpen={settingsOpen}
  onClose={() => setSettingsOpen(false)}
  theme={t}
  settings={settings}
  onSettingsChange={onSettingsChange}
  userAvatar={userAvatar}
/>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: "auto", padding: (activeNav === "retirement" || activeNav === "simulators") ? "32px 28px" : "32px 36px" }}>
          {activeNav === "overview" && (
  <div>
    {/* Greeting */}
    {(() => {
      const hour = new Date().getHours()
      const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening"
      const greetEmoji = hour < 12 ? "☀️" : hour < 17 ? "🌤️" : "🌙"
      return (
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text, margin: 0 }}>
            {greeting}, {firstName}! {greetEmoji}
          </h1>
          <p style={{ color: t.textMuted, fontSize: 14, marginTop: 4 }}>Here's your financial overview.</p>
        </div>
      )
    })()}

    <MemberToggle householdMembers={householdMembers} selected={selectedMember} onSelect={setSelectedMember} theme={t} />

    {/* Quick Start card — shown until all core items are done */}
    {(() => {
      const isOnboarded = !!localStorage.getItem(`grove_onboarded_${session.user.id}`)
      if (!isOnboarded) return null
      const hasAccount = accounts.length > 0
      const hasPaycheck = paychecks.length > 0
      const hasCategorized = transactions.some(tx => tx.user_category)
      const hasProperty = realEstateProps.length > 0
      const allDone = hasAccount && hasPaycheck && hasCategorized
      if (allDone) return null
      const goal = localStorage.getItem(`grove_goal_${session.user.id}`)
      const items = [
        { done: hasAccount, icon: "🏦", label: "Connect your first account", tab: "accounts" },
        { done: hasPaycheck, icon: "💼", label: "Set up your paycheck details", tab: "paycheck" },
        { done: hasCategorized, icon: "📋", label: "Categorize a transaction", tab: "transactions" },
        { done: hasProperty, icon: "🏠", label: "Add a property (optional)", tab: "accounts", optional: true },
      ]
      const doneCount = [hasAccount, hasPaycheck, hasCategorized].filter(Boolean).length
      return (
        <div style={{ ...card, padding: "20px 24px", marginBottom: 20, borderLeft: `4px solid ${t.accent}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Quick Start</h3>
              {goal && <p style={{ fontSize: 12, color: t.textMuted, margin: "3px 0 0" }}>Your goal: <strong>{goal}</strong></p>}
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.accent }}>{doneCount}/3 done</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((item, i) => (
              <div key={i} onClick={() => !item.done && setActiveNavPersist(item.tab)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 12px", borderRadius: 10, background: item.done ? (t.isDark ? "rgba(110,231,183,0.07)" : "rgba(47,133,90,0.06)") : t.hover, cursor: item.done ? "default" : "pointer", opacity: item.optional && !item.done ? 0.7 : 1 }}>
                <span style={{ fontSize: 16, width: 20, textAlign: "center" }}>{item.done ? "✅" : item.icon}</span>
                <span style={{ fontSize: 13, color: item.done ? t.textMuted : t.text, fontWeight: item.done ? 400 : 600, flex: 1, textDecoration: item.done ? "line-through" : "none" }}>{item.label}</span>
                {!item.done && <span style={{ fontSize: 11, color: t.accent, fontWeight: 700 }}>Go →</span>}
                {item.optional && !item.done && <span style={{ fontSize: 10, color: t.textFaint }}>optional</span>}
              </div>
            ))}
          </div>
        </div>
      )
    })()}

    {/* ✅ Net Worth + Health Score (2-column) */}
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, marginBottom: 20 }}>
    <div style={{ ...card, padding: "22px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Total Net Worth</h3>
          <p style={{ fontSize: 12, color: t.textMuted, margin: "6px 0 0" }}>
            {nwSeriesLoading ? "Loading history..." : "Across all connected accounts"}
          </p>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: t.accent }}>
            {fmtMoney(latestNetWorthValue)}
          </div>

          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 10, flexWrap: "wrap" }}>
            {["1W", "1M", "3M", "YTD", "1Y", "5Y", "MAX"].map(r => (
              <button
                key={r}
                onClick={() => setNwRange(r)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: `1px solid ${nwRange === r ? t.accent : t.border}`,
                  background: nwRange === r
                    ? (t.isDark ? "rgba(110,231,183,0.10)" : "rgba(47,133,90,0.10)")
                    : "transparent",
                  color: nwRange === r ? t.accent : t.textMuted,
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ✅ CHART IS RIGHT UNDER HEADER */}
      <div style={{ height: 260 }}>
        {displayedNetWorthSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={displayedNetWorthSeries}>
              <defs>
                <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={t.accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={t.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: t.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={d => { const [y,m,dd] = d.split("-"); return `${m}/${dd}/${y}` }} />
              <YAxis hide domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip theme={t} formatValue={fmtMoney} />} />
              <Area type="monotone" dataKey="value" stroke={t.accent} strokeWidth={2} fill="url(#nwGrad)" name="Net Worth" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <div style={{ fontSize: 28 }}>📈</div>
            <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: 0 }}>No history yet</p>
            <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Connect accounts and sync to start tracking</p>
          </div>
        )}
      </div>
    </div>

    {/* Financial Health Score card */}
    <HealthScoreCard healthScore={healthScore} theme={t} card={card} loading={!txInitialized} />
    </div>
    {/* 4 balance cards */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
      {[
        { label: "Cash", value: cashBalance, color: t.accent, sub: "Checking & savings", nav: "accounts", emoji: "💵",
          tip: (() => { if (cashBalance <= 0) return "Connect a bank account"; const cut = new Date(); cut.setMonth(cut.getMonth() - 6); const cs = cut.toISOString().split("T")[0]; const needsCats = new Set(CATEGORIES.filter(c => c.group === "needs").map(c => c.label)); const mo6 = transactions.filter(t => t.date >= cs && t.amount > 0 && !t.is_transfer && (t.user_group === "needs" || needsCats.has(t.user_category))).reduce((s, t) => s + t.amount, 0) / 6; return mo6 > 0 ? `~${Math.floor(cashBalance / mo6)} month${Math.floor(cashBalance / mo6) !== 1 ? "s" : ""} of expenses covered` : "Liquid funds on hand" })() },
        { label: "Debt", value: debtBalance, color: "#EF4444", sub: "Credit cards & loans", nav: "accounts", emoji: "💳",
          tip: debtBalance > 0 ? (debtBalance < cashBalance ? "You have enough cash to cover it 👍" : "Consider a paydown plan") : "No debt — great!" },
        { label: "Investments", value: investmentBalance, color: t.accent2, sub: "Brokerage & retirement", nav: "investments", emoji: "📈",
          tip: investmentBalance > 0 ? "Growing your future wealth 🌱" : "Start investing today" },
        { label: "Real Estate", value: realEstateEquity, color: "#7CB342", sub: "Home equity", nav: "accounts", emoji: "🏠",
          tip: realEstateEquity > 0 ? `Equity across ${realEstateProps.length} propert${realEstateProps.length !== 1 ? "ies" : "y"}` : "Add a property in Accounts" },
      ].map(({ label, value, color, sub, nav, emoji, tip }) => (
        <div
          key={label}
          onClick={() => setActiveNavPersist(nav)}
          style={{
            ...card, padding: "20px 22px", cursor: "pointer",
            transition: "transform 0.15s, box-shadow 0.15s",
            borderTop: `3px solid ${color}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.15)` }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
            <span style={{ fontSize: 20 }}>{emoji}</span>
          </div>
          <p style={{ margin: "0 0 4px", fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{fmtMoney(value)}</p>
          <p style={{ margin: "0 0 12px", fontSize: 11, color: t.textFaint }}>{sub}</p>
          <p style={{ margin: 0, fontSize: 12, color: t.textMuted, fontStyle: "italic" }}>{tip}</p>
        </div>
      ))}
    </div>

    {/* Insight strip */}
    {(() => {
      const totalAssets = cashBalance + investmentBalance
      const insights = [
        totalAssets > 0 && debtBalance / totalAssets < 0.3
          ? { emoji: "✅", text: "Your debt-to-assets ratio looks healthy", color: t.accent }
          : debtBalance > 0
          ? { emoji: "⚠️", text: "Your debt is high relative to your assets", color: "#F59E0B" }
          : null,
        cashBalance > 0 && investmentBalance > 0
          ? { emoji: "🌱", text: "You're building both savings and investments — great balance!", color: t.accent }
          : cashBalance > 0
          ? { emoji: "💡", text: "Consider investing some of your cash for long-term growth", color: "#F59E0B" }
          : { emoji: "🏦", text: "Connect your bank account to unlock your full financial picture", color: t.textMuted },
        latestNetWorthValue > 0
          ? { emoji: "📊", text: `Your total net worth stands at ${fmtMoney(latestNetWorthValue)}. Keep building!`, color: t.textMuted }
          : null,
      ].filter(Boolean).slice(0, 3)

      if (insights.length === 0) return null
      return (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          {insights.map((ins, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 200, display: "flex", alignItems: "flex-start", gap: 10,
              padding: "14px 16px", borderRadius: 12,
              background: t.isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
              border: `1px solid ${t.border}`,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{ins.emoji}</span>
              <p style={{ margin: 0, fontSize: 13, color: ins.color, lineHeight: 1.45 }}>{ins.text}</p>
            </div>
          ))}
        </div>
      )
    })()}

    {/* Recent Transactions */}
    <div style={{ ...card, padding: "22px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text }}>Recent Transactions</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={syncTransactionsFromPlaid} disabled={txLoading} style={{ fontSize: 12, color: txLoading ? t.textFaint : t.accent, background: "none", border: "none", cursor: txLoading ? "not-allowed" : "pointer", fontWeight: 600 }}>
            {txLoading ? "Syncing…" : "🔄 Sync"}
          </button>
          <button onClick={() => setActiveNavPersist("transactions")} style={{ fontSize: 12, color: t.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
            See all →
          </button>
        </div>
      </div>

      {recent5.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏦</div>
          <p style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 6 }}>No transactions yet</p>
          <p style={{ fontSize: 13, color: t.textMuted }}>Your transactions will appear here once synced</p>
        </div>
      ) : (
        recent5.map((tx, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 0", borderBottom: i < recent5.length - 1 ? `1px solid ${t.border}` : "none",
            transition: "opacity 0.15s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: t.isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
              }}>
                {tx.icon}
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: 0 }}>{tx.name}</p>
                <p style={{ fontSize: 11, color: t.textFaint, margin: "2px 0 0" }}>{tx.category} · {tx.date}</p>
              </div>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: tx.amount > 0 ? t.accent : t.text }}>
              {tx.amount > 0 ? "+" : "–"}{fmtMoney(Math.abs(tx.amount))}
            </span>
          </div>
        ))
      )}
    </div>
  </div>
)}
{activeNav === "accounts" && <AccountsPage session={session} theme={t} onSync={syncTransactionsFromPlaid} onShowOnboarding={() => setShowOnboarding(true)} onXPAward={triggerXP} householdMembers={householdMembers} onAccountsChange={() => { loadAccounts(); loadManualHoldings(); loadRealEstate() }} />}
{activeNav === "transactions" && (
  <div>
    <MemberToggle householdMembers={householdMembers} selected={selectedMember} onSelect={setSelectedMember} theme={t} />
    <TransactionsPage
      session={session}
      theme={t}
      transactions={filteredRawTx}
      accounts={accounts}
      onSync={syncTransactionsFromPlaid}
      txLoading={txLoading}
      fmtMoney={fmtMoney}
      onTransactionsLoaded={setTransactions}
      syncWarning={syncWarning}
      hiddenCats={hiddenCats}
    />
  </div>
)}
{activeNav === "budget" && (
  <div>
    <MemberToggle householdMembers={householdMembers} selected={selectedMember} onSelect={setSelectedMember} theme={t} />
    <BudgetPage
      session={session}
      theme={t}
      transactions={filteredRawTx}
      fmtMoney={fmtMoney}
      categories={effectiveCategories}
      hiddenCats={hiddenCats}
      onTransactionsLoaded={setTransactions}
      paychecks={paychecks}
      onXPAward={triggerXP}
      notifBudget={settings.notif_budget}
    />
  </div>
)}
          {activeNav === "cashflow" && (() => {
            // ── Compute real cash flow from transactions ──
            const hasTxData = transactions.length > 0
            const cfNow = new Date()

            // Period keys + label based on type + offset
            let cfPeriodKeys, cfPeriodLabel
            if (cfPeriodType === "quarter") {
              const curQ = Math.floor(cfNow.getMonth() / 3)
              const qStart = new Date(cfNow.getFullYear(), (curQ + cfMonthOffset) * 3, 1)
              const q = Math.floor(qStart.getMonth() / 3) + 1
              cfPeriodKeys = [0,1,2].map(i => { const md = new Date(qStart.getFullYear(), qStart.getMonth()+i, 1); return `${md.getFullYear()}-${String(md.getMonth()+1).padStart(2,'0')}` })
              cfPeriodLabel = `Q${q} ${qStart.getFullYear()}`
            } else if (cfPeriodType === "year") {
              const year = cfNow.getFullYear() + cfMonthOffset
              cfPeriodKeys = Array.from({length:12}, (_,i) => `${year}-${String(i+1).padStart(2,'0')}`)
              cfPeriodLabel = String(year)
            } else {
              const d = new Date(cfNow.getFullYear(), cfNow.getMonth() + cfMonthOffset, 1)
              cfPeriodKeys = [`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`]
              cfPeriodLabel = d.toLocaleString('default', { month: 'long', year: 'numeric' })
            }

            const txThisMonth = filteredUiTx.filter(tx => {
              const d = parseMaybeISODate(tx.date)
              if (!d) return false
              return cfPeriodKeys.includes(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
            })

            const rawIncome = txThisMonth.filter(tx => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0)
            const rawExpenses = txThisMonth.filter(tx => tx.amount < 0).reduce((s, tx) => s + Math.abs(tx.amount), 0)
            const displayIncome = rawIncome > 0 ? rawIncome : (hasTxData ? 0 : incomeTotal)
            const displayExpenses = rawExpenses > 0 ? rawExpenses : (hasTxData ? 0 : monthlyExpensesTotal)

            const cfIncomeRows = [
              { label: "Paychecks", filterKey: "Paycheck" },
              { label: "Dividends", filterKey: "Dividends" },
              { label: "Interest",  filterKey: "Interest"  },
              { label: "Other",     filterKey: "income_other" },
            ].map(r => ({
              ...r,
              value: r.filterKey === "income_other"
                ? txThisMonth.filter(tx => tx.amount > 0 && !["Paycheck","Dividends","Interest","Other Income"].includes(tx.category)).reduce((s, tx) => s + tx.amount, 0)
                : txThisMonth.filter(tx => tx.amount > 0 && tx.category === r.filterKey).reduce((s, tx) => s + tx.amount, 0)
            }))
            const displayIncomeRows = cfIncomeRows.some(r => r.value > 0)
              ? cfIncomeRows.filter(r => r.value > 0)
              : incomeBreakdown

            const cfNeeds = txThisMonth.filter(tx => tx.amount < 0 && getTxGroup(tx) === 'needs').reduce((s, tx) => s + Math.abs(tx.amount), 0)
            const cfWants = rawExpenses - cfNeeds
            const displayNeeds = rawExpenses > 0 ? cfNeeds : (hasTxData ? 0 : expenseSplit[0]?.value || 0)
            const displayWants = rawExpenses > 0 ? cfWants : (hasTxData ? 0 : expenseSplit[1]?.value || 0)

            const savingsAmt = displayIncome - displayExpenses
            const savingsRate = displayIncome > 0 ? Math.round((savingsAmt / displayIncome) * 100) : 0

            // Last 6 months chart
            const monthKeys = []
            for (let i = 5; i >= 0; i--) {
              const d = new Date(cfNow.getFullYear(), cfNow.getMonth() - i, 1)
              monthKeys.push({ key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: d.toLocaleString('default', { month: 'short' }) })
            }
            const realChartData = monthKeys.map(({ key, label }) => {
              const mTx = filteredUiTx.filter(tx => {
                const d = parseMaybeISODate(tx.date)
                if (!d) return false
                return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` === key
              })
              const inc = mTx.filter(tx => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0)
              const exp = mTx.filter(tx => tx.amount < 0).reduce((s, tx) => s + Math.abs(tx.amount), 0)
              return { month: label, income: Math.round(inc), expenses: Math.round(exp) }
            })
            const hasChartData = realChartData.some(d => d.income > 0 || d.expenses > 0)

            return (
              <div>
                <MemberToggle householdMembers={householdMembers} selected={selectedMember} onSelect={setSelectedMember} theme={t} />
                {/* Header */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 }}>
                  <div>
                    <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6, color: t.text }}>Cash Flow</h1>
                    <p style={{ color: t.textMuted, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{hasTxData ? "Live data" : "Connect accounts to see your cash flow"}</span>
                      <button onClick={syncTransactionsFromPlaid} disabled={txLoading}
                        style={{ padding: "2px 10px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: txLoading ? t.textFaint : t.accent, fontSize: 12, fontWeight: 600, cursor: txLoading ? "not-allowed" : "pointer" }}>
                        {txLoading ? "Syncing..." : "🔄 Sync"}
                      </button>
                      {savingsRate !== 0 && <span style={{ color: savingsRate >= 20 ? t.accent : savingsRate >= 10 ? "#F59E0B" : t.textMuted, fontWeight: 600 }}>
                        {savingsRate >= 20 ? `Saving ${savingsRate}% 🎉` : `Saving ${savingsRate}%`}
                      </span>}
                    </p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {/* Period type toggle */}
                    <div style={{ display: "flex", background: t.hover, borderRadius: 10, padding: 3, gap: 2 }}>
                      {["month", "quarter", "year"].map(pt => (
                        <button key={pt} onClick={() => { setCfPeriodType(pt); setCfMonthOffset(0) }}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                            background: cfPeriodType === pt ? t.surface : "transparent",
                            color: cfPeriodType === pt ? t.text : t.textMuted,
                            fontSize: 12, fontWeight: cfPeriodType === pt ? 700 : 500,
                            boxShadow: cfPeriodType === pt ? `0 1px 3px rgba(0,0,0,0.15)` : "none",
                            textTransform: "capitalize" }}>
                          {pt}
                        </button>
                      ))}
                    </div>
                    {/* Prev / label / Next */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button onClick={() => setCfMonthOffset(o => o - 1)}
                        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
                      <span style={{ minWidth: 130, textAlign: "center", fontSize: 13, fontWeight: 700, color: t.text }}>{cfPeriodLabel}</span>
                      <button onClick={() => setCfMonthOffset(o => o + 1)} disabled={cfMonthOffset >= 0}
                        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: "transparent", color: cfMonthOffset >= 0 ? t.textFaint : t.text, fontSize: 16, cursor: cfMonthOffset >= 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                  {/* Income */}
                  <div
                    onClick={() => setTxFilter(txFilter === "income" ? null : "income")}
                    style={{
                      ...card, padding: "20px 22px", cursor: "pointer",
                      borderTop: `3px solid ${t.accent}`,
                      outline: txFilter === "income" ? `2px solid ${t.accent}` : "none",
                      outlineOffset: "-1px",
                    }}
                  >
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>💵 Income</p>
                    <p style={{ fontSize: 28, fontWeight: 800, color: t.accent, marginBottom: 14 }}>{fmtMoney(displayIncome)}</p>
                    {displayIncomeRows.map(row => {
                      const isActive = txFilter === row.filterKey
                      return (
                        <div
                          key={row.label}
                          onClick={e => { e.stopPropagation(); setTxFilter(isActive ? null : row.filterKey) }}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: `1px solid ${t.border}`, cursor: "pointer" }}
                        >
                          <span style={{ fontSize: 12, color: isActive ? t.accent : t.textFaint, fontWeight: isActive ? 700 : 400 }}>{row.label}</span>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{fmtMoney(row.value)}</span>
                            <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 6 }}>{pct(row.value, displayIncome)}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Expenses */}
                  <div
                    onClick={() => setTxFilter(txFilter === "expenses" ? null : "expenses")}
                    style={{
                      ...card, padding: "20px 22px", cursor: "pointer",
                      borderTop: `3px solid #EF4444`,
                      outline: txFilter === "expenses" ? `2px solid #EF4444` : "none",
                      outlineOffset: "-1px",
                    }}
                  >
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>💸 Expenses</p>
                    <p style={{ fontSize: 28, fontWeight: 800, color: "#EF4444", marginBottom: 14 }}>{fmtMoney(displayExpenses)}</p>
                    {[
                      { label: "Needs", value: displayNeeds, group: "needs" },
                      { label: "Wants", value: displayWants, group: "wants" },
                    ].map(row => {
                      const isActive = txFilter === row.group
                      return (
                        <div
                          key={row.label}
                          onClick={e => { e.stopPropagation(); setTxFilter(isActive ? null : row.group) }}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: `1px solid ${t.border}`, cursor: "pointer" }}
                        >
                          <span style={{ fontSize: 12, color: isActive ? "#EF4444" : t.textFaint, fontWeight: isActive ? 700 : 400 }}>{row.label}</span>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>{fmtMoney(row.value)}</span>
                            <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 6 }}>{pct(row.value, displayExpenses)}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Net Savings */}
                  <div style={{ ...card, padding: "20px 22px", borderTop: `3px solid ${t.accent2}` }}>
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>📈 Net Savings</p>
                    <p style={{ fontSize: 28, fontWeight: 800, color: savingsAmt >= 0 ? t.accent2 : "#EF4444", marginBottom: 14 }}>
                      {savingsAmt >= 0 ? "+" : ""}{fmtMoney(savingsAmt)}
                    </p>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: t.textFaint }}>Savings rate</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: savingsRate >= 20 ? t.accent : savingsRate >= 10 ? "#F59E0B" : "#EF4444" }}>
                          {savingsRate}%
                        </span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: t.hover, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: 3,
                          width: `${Math.min(100, Math.max(0, savingsRate))}%`,
                          background: savingsRate >= 20 ? t.accent : savingsRate >= 10 ? "#F59E0B" : "#EF4444",
                          transition: "width 0.4s ease",
                        }} />
                      </div>
                    </div>
                    <p style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>
                      {savingsRate >= 20 ? "Excellent — above the 20% goal 🎉" : savingsRate >= 10 ? "Getting there — aim for 20% 💪" : "Aim to save at least 20% of income"}
                    </p>
                  </div>
                </div>

                {/* Monthly chart */}
                <div style={{ ...card, padding: "22px 24px", marginBottom: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 2 }}>6-Month Overview</h3>
                      <p style={{ fontSize: 12, color: t.textFaint }}>
                        {hasChartData ? "Income vs. expenses from your transactions" : "Connect accounts to see your history"}
                      </p>
                    </div>
                    {hasChartData && (
                      <div style={{ display: "flex", gap: 14 }}>
                        {[{ label: "Income", color: t.accent }, { label: "Expenses", color: "#EF4444" }].map(l => (
                          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                            <span style={{ fontSize: 12, color: t.textMuted }}>{l.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasChartData ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={realChartData} barGap={4}>
                        <XAxis dataKey="month" tick={{ fill: t.textFaint, fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: t.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${currencyMeta.symbol}${v / 1000}k`} />
                        <Tooltip content={<ChartTooltip theme={t} formatValue={fmtMoney} />} />
                        <Bar dataKey="income" name="Income" fill={t.accent} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ height: 240, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <div style={{ fontSize: 28 }}>🏦</div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: 0 }}>No transaction data yet</p>
                      <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Connect a bank account and sync to see your cash flow history</p>
                    </div>
                  )}
                </div>

                {/* Spending breakdown by category */}
                {(() => {
                  // Build subgroup emoji map from CATEGORIES (first emoji per subgroup)
                  const sgEmoji = {}
                  CATEGORIES.forEach(c => { if (c.subgroup && !sgEmoji[c.subgroup]) sgEmoji[c.subgroup] = c.emoji })

                  // Aggregate expense spend by subgroup for the period using raw transactions (respects user_category overrides)
                  const sgMap = {}
                  filteredRawTx.forEach(tx => {
                    const d = parseMaybeISODate(tx.date)
                    if (!d) return
                    const m = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`
                    if (!cfPeriodKeys.includes(m)) return
                    const ui = formatTxForUI(tx)
                    if (ui.amount >= 0) return // skip income
                    const raw = tx.user_category || ui.category || "Other"
                    const catObj = CATEGORIES.find(c => c.label.toLowerCase() === raw.toLowerCase())
                    if (!catObj || catObj.group === "income") return
                    const key = catObj.subgroup || catObj.group
                    if (!sgMap[key]) sgMap[key] = { total: 0, group: catObj.group }
                    sgMap[key].total += Math.abs(ui.amount)
                  })
                  const allSgRows = Object.entries(sgMap)
                    .map(([name, v]) => ({ name, total: v.total, group: v.group, emoji: sgEmoji[name] || "💳" }))
                  const totalSpend = allSgRows.reduce((s, r) => s + r.total, 0)

                  const groupColor = { needs: GROUP_STYLE.needs.color, wants: GROUP_STYLE.wants.color, savings: GROUP_STYLE.savings.color }
                  const GROUP_ORDER = ["needs", "wants", "savings"]
                  const groupLabel = { needs: "Needs", wants: "Wants", savings: "Savings" }

                  // Group subrows by main group, sorted by spend within each group
                  const byGroup = GROUP_ORDER
                    .map(g => ({
                      id: g,
                      label: groupLabel[g],
                      color: groupColor[g],
                      rows: allSgRows.filter(r => r.group === g).sort((a, b) => b.total - a.total),
                      total: allSgRows.filter(r => r.group === g).reduce((s, r) => s + r.total, 0),
                    }))
                    .filter(g => g.rows.length > 0)

                  return (
                    <div style={{ ...card, padding: "22px 24px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <div>
                          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>Where did your money go?</h3>
                          <p style={{ fontSize: 12, color: t.textFaint, margin: "3px 0 0" }}>
                            {transactions.length ? cfPeriodLabel : "Connect accounts to see your spending breakdown"}
                          </p>
                        </div>
                        {totalSpend > 0 && (
                          <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{fmtMoney(totalSpend)}</span>
                        )}
                      </div>

                      {byGroup.length === 0 ? (
                        <p style={{ color: t.textFaint, fontSize: 13, textAlign: "center", padding: "20px 0" }}>No spending data for this period</p>
                      ) : (
                        <div>
                          {byGroup.map((group, gi) => (
                            <div key={group.id} style={{ marginBottom: gi < byGroup.length - 1 ? 20 : 0 }}>
                              {/* Group header */}
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0 8px", borderBottom: `2px solid ${group.color}30` }}>
                                <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: group.color }}>{group.label}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: group.color }}>{fmtMoney(group.total)}</span>
                              </div>
                              {/* Subgroup rows */}
                              {group.rows.map((row, i) => {
                                const pctVal = totalSpend > 0 ? (row.total / totalSpend) * 100 : 0
                                return (
                                  <div key={row.name} style={{ padding: "9px 0 9px 4px", borderBottom: i < group.rows.length - 1 ? `1px solid ${t.border}` : "none" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 15, flexShrink: 0 }}>{row.emoji}</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{row.name}</span>
                                      </div>
                                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{fmtMoney(row.total)}</span>
                                        <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8 }}>{Math.round(pctVal)}%</span>
                                      </div>
                                    </div>
                                    <div style={{ height: 4, borderRadius: 4, background: t.hover, overflow: "hidden" }}>
                                      <div style={{ height: "100%", width: `${pctVal}%`, background: group.color, borderRadius: 4, transition: "width 0.4s ease" }} />
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            )
          })()}

          {activeNav === "investments" && (
            <div>
              {/* Header */}
              <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4, color: t.text }}>Investments</h1>
                <p style={{ color: t.textMuted, fontSize: 14, margin: 0 }}>Your portfolio across all investment accounts</p>
              </div>

              {/* Tab nav */}
              <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                {[["overview", "Overview"], ["details", "Details"], ["dividends", "Dividends"], ["fees", "Fund Analyzer"]].map(([id, label]) => (
                  <button key={id} onClick={() => setInvTab(id)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${invTab === id ? t.accent2 : t.border}`, background: invTab === id ? (t.isDark ? "rgba(96,165,250,0.1)" : "rgba(59,130,246,0.08)") : "transparent", color: invTab === id ? t.accent2 : t.textMuted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Hero total */}
              <div style={{ ...card, padding: "24px 28px", marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Portfolio Value</div>
                <div style={{ fontSize: 38, fontWeight: 800, color: t.accent2, letterSpacing: "-0.5px" }}>{fmtMoney(investmentBalance)}</div>
              </div>


              {/* ── OVERVIEW TAB ── */}
              {invTab === "overview" && <div>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20, marginBottom: 20 }}>

                {/* Row 1 col 1 — Portfolio Performance */}
                {(() => {
                  const rangeStart = rangeStartDate(portRange)
                  const filtered = rangeStart
                    ? portfolioHistory.filter(p => new Date(p.date) >= rangeStart)
                    : portfolioHistory
                  const gain = filtered.length >= 2 ? filtered[filtered.length - 1].value - filtered[0].value : null
                  const gainPct = gain != null && filtered[0].value > 0 ? (gain / filtered[0].value) * 100 : null
                  return (
                  <div style={{ ...card, padding: "22px 24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: 0 }}>Portfolio Performance</h3>
                      {gain != null && (
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: gain >= 0 ? "#22C55E" : "#EF4444" }}>
                            {gain >= 0 ? "+" : ""}{fmtMoney(gain)}
                          </div>
                          <div style={{ fontSize: 11, color: t.textFaint }}>{gainPct >= 0 ? "+" : ""}{gainPct?.toFixed(1)}% · {portRange === "MAX" ? "all time" : portRange}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
                      {["1W","1M","3M","YTD","1Y","5Y","MAX"].map(r => (
                        <button key={r} onClick={() => setPortRange(r)} style={{ padding: "4px 9px", borderRadius: 999, border: `1px solid ${portRange === r ? t.accent2 : t.border}`, background: portRange === r ? (t.isDark ? "rgba(96,165,250,0.12)" : "rgba(59,130,246,0.1)") : "transparent", color: portRange === r ? t.accent2 : t.textMuted, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                          {r}
                        </button>
                      ))}
                    </div>
                    {filtered.length === 1 ? (
                      <div style={{ height: 210, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <div style={{ fontSize: 32, fontWeight: 900, color: t.accent2 }}>{fmtMoney(filtered[0].value)}</div>
                        <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>First snapshot taken today</p>
                        <p style={{ fontSize: 12, color: t.textFaint, margin: 0 }}>Check back tomorrow — history builds daily</p>
                      </div>
                    ) : filtered.length > 1 ? (
                      <ResponsiveContainer width="100%" height={210}>
                        <AreaChart data={filtered}>
                          <defs>
                            <linearGradient id="invGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={t.accent2} stopOpacity={0.25} />
                              <stop offset="95%" stopColor={t.accent2} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fill: t.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={d => { const p = d?.split("-"); return p?.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : d }} />
                          <YAxis hide domain={["auto", "auto"]} />
                          <Tooltip content={<ChartTooltip theme={t} formatValue={fmtMoney} />} />
                          <Area type="monotone" dataKey="value" stroke={t.accent2} strokeWidth={2} fill="url(#invGrad)" name="Portfolio" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 210, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <div style={{ fontSize: 28 }}>📊</div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: 0 }}>{portfolioHistory.length <= 1 ? "No history yet" : "No data in this range"}</p>
                        <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>{portfolioHistory.length <= 1 ? "Balances are recorded each time you sync or refresh quotes" : "Try a wider range"}</p>
                      </div>
                    )}
                  </div>
                  )
                })()}

                {/* Row 1 col 2 — Investment Score */}
                {investmentScore
                  ? <InvestmentScoreCard score={investmentScore} theme={t} card={card} />
                  : <div style={{ ...card }} />}

                {/* Row 2 col 1 — Dividend Income */}
                {(() => {
                  const hasDividends = estimatedDividends.some(m => m.total > 0)
                  const annualEst = estimatedDividends.reduce((s, m) => s + m.total, 0)
                  return (
                    <div style={{ ...card, padding: "22px 24px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                        <div>
                          <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: "0 0 3px" }}>Estimated Dividend Income</h3>
                          <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Trailing 12-month rates · next 12 months</p>
                        </div>
                        {hasDividends && (
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Annual Est.</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#22C55E" }}>{fmtMoney(annualEst)}</div>
                          </div>
                        )}
                      </div>
                      {!hasDividends ? (
                        <div style={{ textAlign: "center", padding: "20px 0", color: t.textMuted, fontSize: 13 }}>
                          <div style={{ fontSize: 24, marginBottom: 8 }}>🌱</div>
                          No dividend-paying holdings detected yet.
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={estimatedDividends} barSize={24}>
                            <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null
                                const slot = estimatedDividends.find(m => m.label === label)
                                const tickers = slot ? Object.entries(slot.byTicker).sort((a, b) => b[1] - a[1]) : []
                                return (
                                  <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 14px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>{label} {slot?.year}</div>
                                    {tickers.map(([ticker, amt]) => (
                                      <div key={ticker} style={{ display: "flex", justifyContent: "space-between", gap: 20, fontSize: 11, color: t.textMuted, marginBottom: 2 }}>
                                        <span>{ticker}</span><span style={{ color: "#22C55E", fontWeight: 700 }}>{fmtMoney(amt)}</span>
                                      </div>
                                    ))}
                                    <div style={{ borderTop: `1px solid ${t.border}`, marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700 }}>
                                      <span style={{ color: t.text }}>Total</span>
                                      <span style={{ color: "#22C55E" }}>{fmtMoney(payload[0].value)}</span>
                                    </div>
                                  </div>
                                )
                              }}
                            />
                            <Bar dataKey="total" fill="#22C55E" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  )
                })()}

                {/* Row 2 col 2 — Allocation */}
                <div style={{ ...card, padding: "22px 24px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: 0 }}>Allocation</h3>
                    <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: `1px solid ${t.border}` }}>
                      {[["pct", "%"], ["dollar", "$"]].map(([mode, label]) => (
                        <button key={mode} onClick={() => setPieMode(mode)} style={{ padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none", background: pieMode === mode ? t.accent2 : "transparent", color: pieMode === mode ? "#fff" : t.textMuted }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const pieData = computedAllocationData || []
                    const total = pieData.reduce((s, a) => s + a.value, 0)
                    const RADIAN = Math.PI / 180
                    return (
                      <>
                        <ResponsiveContainer width="100%" height={230}>
                          <PieChart margin={{ top: 10, bottom: 10, left: 20, right: 20 }}>
                            <Pie
                              data={pieData}
                              cx="50%" cy="50%"
                              innerRadius={52} outerRadius={76}
                              dataKey="value"
                              strokeWidth={0}
                              labelLine={false}
                              isAnimationActive={false}
                              label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                                const pct = Math.round((value / total) * 100)
                                if (pct < 5) return null
                                const entry = pieData[index]
                                if (!entry) return null
                                const r = outerRadius + 26
                                const x = cx + r * Math.cos(-midAngle * RADIAN)
                                const y = cy + r * Math.sin(-midAngle * RADIAN)
                                const displayVal = pieMode === "dollar" ? `$${Math.round(value / 1000)}k` : `${pct}%`
                                return (
                                  <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
                                    fill={entry.color} fontSize={11} fontWeight={700}>
                                    {displayVal}
                                  </text>
                                )
                              }}
                            >
                              {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
                          {pieData.map(a => (
                            <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ width: 7, height: 7, borderRadius: "50%", background: a.color }} />
                              <span style={{ fontSize: 12, color: t.textMuted }}>{a.name}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )
                  })()}
                </div>

              </div>
              </div>}

              {/* ── DETAILS TAB ── */}
              {invTab === "details" && <div>
              {/* Holdings */}
              {holdingsLoading && (
                <div style={{ ...card, padding: 32, textAlign: "center", color: t.textMuted, fontSize: 14 }}>Loading holdings…</div>
              )}
              {!holdingsLoading && holdingsNeedsRelink && (
                <div style={{ ...card, padding: "28px 32px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 8 }}>Account needs to be reconnected</h3>
                  <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 20, maxWidth: 380, margin: "0 auto 20px" }}>
                    One of your linked accounts needs to be re-authenticated. Go to Accounts and reconnect it to restore holdings data.
                  </p>
                  <button onClick={() => { setActiveNavPersist("accounts") }} style={{ padding: "10px 22px", borderRadius: 8, border: "none", background: t.accent, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    Go to Accounts →
                  </button>
                </div>
              )}
              {!holdingsLoading && !holdingsNeedsRelink && plaidHoldings && !plaidHoldings.holdings?.length && (
                <div style={{ ...card, padding: "28px 32px", textAlign: "center", color: t.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 6 }}>No holdings data available</p>
                  <p style={{ fontSize: 13 }}>Your connected accounts don't have investment holdings through Plaid. You can still track balances manually under Accounts.</p>
                </div>
              )}
              {!holdingsLoading && !holdingsNeedsRelink && ((!plaidHoldings || plaidHoldings.holdings?.length > 0) || manualHoldings.length > 0) && (() => {
                const selectedAcc = invFilter !== "all" ? investmentAccounts.find(a => a.id === invFilter) : null

                const visibleAllocation = computedAllocationData || []
                const visibleHoldingsData = computedHoldingsData || {}

                const totalPortfolio = visibleAllocation.reduce((s, x) => s + x.value, 0)
                const assetReturns   = {}
                for (const [assetName, items] of Object.entries(visibleHoldingsData)) {
                  const totalCost = items.reduce((s, h) => s + (h.avgPrice > 0 && h.shares > 0 ? h.avgPrice * h.shares : 0), 0)
                  const totalUnrealized = items.reduce((s, h) => s + (h.unrealized || 0), 0)
                  if (totalCost > 0) assetReturns[assetName] = `${totalUnrealized >= 0 ? "+" : ""}${((totalUnrealized / totalCost) * 100).toFixed(1)}%`
                }
                const isMissingCost = (assetName, h) => !h.isManual && assetName !== "Cash" && h.avgPrice === 0
                const missingCostCount = Object.entries(visibleHoldingsData).reduce((s, [k, items]) => s + items.filter(h => isMissingCost(k, h)).length, 0)
                // When filtering to missing cost, only show asset groups that have at least one such holding
                const filteredHoldingsData = detailsFilter === "missing_cost"
                  ? Object.fromEntries(
                      Object.entries(visibleHoldingsData)
                        .map(([k, items]) => [k, items.filter(h => isMissingCost(k, h))])
                        .filter(([, items]) => items.length > 0)
                    )
                  : visibleHoldingsData
                const filteredAllocation = detailsFilter === "missing_cost"
                  ? visibleAllocation.filter(a => filteredHoldingsData[a.name])
                  : visibleAllocation

                const colParent = "36px 2fr 110px 70px 110px 90px 80px"
                const colChild  = "36px 90px 1.6fr 80px 100px 110px 110px 80px 80px"
                const hStyle = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: t.textFaint, textAlign: "right" }
                return (
                  <div style={{ ...card, overflow: "hidden" }}>
                    <div style={{ padding: "22px 24px 0", display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: 0 }}>Holdings</h3>
                      {selectedAcc && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: `${t.accent2}18`, color: t.accent2 }}>
                          {selectedAcc.account_name || selectedAcc.name}
                        </span>
                      )}
                      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                        {[["all", "All"], ["missing_cost", `⚠️ Missing Cost${missingCostCount > 0 ? ` · ${missingCostCount}` : ""}`]].map(([id, label]) => {
                          const active = detailsFilter === id
                          return (
                            <button key={id} onClick={() => setDetailsFilter(id)} style={{
                              padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                              border: `1px solid ${active ? (id === "missing_cost" ? "#F59E0B" : t.accent) : t.border}`,
                              background: active ? (id === "missing_cost" ? "rgba(245,158,11,0.1)" : t.hover) : "transparent",
                              color: active ? (id === "missing_cost" ? "#F59E0B" : t.accent) : t.textMuted,
                            }}>
                              {label}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Parent header */}
                    <div style={{ display: "grid", gridTemplateColumns: colParent, padding: "0 20px 10px", borderBottom: `1px solid ${t.border}` }}>
                      <div />
                      <div style={{ ...hStyle, textAlign: "left" }}>Asset</div>
                      <div style={hStyle}>Value</div>
                      <div style={hStyle}>Alloc</div>
                      <div style={hStyle}>Unrealized</div>
                      <div style={hStyle}>Return</div>
                      <div style={hStyle}>Today</div>
                    </div>

                    {filteredAllocation.map((a, i) => {
                      const pct        = totalPortfolio > 0 ? ((a.value / totalPortfolio) * 100).toFixed(1) : "0.0"
                      const holdings   = filteredHoldingsData[a.name] || []
                      const unrealized = holdings.reduce((s, h) => s + h.unrealized, 0)
                      const isOpen     = detailsFilter === "missing_cost" || expandedAssets.has(a.name)
                      // Weighted avg today% by position value
                      const todayHoldings = holdings.filter(h => h.changePercent != null && h.value > 0)
                      const totalVal = todayHoldings.reduce((s, h) => s + h.value, 0)
                      const assetToday = totalVal > 0 ? todayHoldings.reduce((s, h) => s + h.changePercent * (h.value / totalVal), 0) : null
                      // Return % for color
                      const returnStr = assetReturns[a.name] || null
                      const returnVal = returnStr ? parseFloat(returnStr) : null

                      return (
                        <div key={a.name}>
                          {/* Asset class row */}
                          <div
                            onClick={() => toggleAsset(a.name)}
                            style={{
                              display: "grid", gridTemplateColumns: colParent,
                              padding: "13px 20px", alignItems: "center",
                              borderBottom: `1px solid ${t.border}`,
                              cursor: "pointer", background: isOpen ? t.hover : "transparent",
                              transition: "background 0.15s",
                            }}
                          >
                            <span style={{ fontSize: 11, color: t.textMuted, userSelect: "none" }}>{isOpen ? "▾" : "▸"}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 9, height: 9, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{a.name}</span>
                              <span style={{ fontSize: 11, color: t.textMuted }}>{holdings.length} positions</span>
                            </div>
                            <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600, color: t.text }}>{fmtMoney(a.value)}</div>
                            <div style={{ textAlign: "right", fontSize: 13, color: t.textMuted }}>{pct}%</div>
                            <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600, color: unrealized >= 0 ? t.accent : "#EF4444" }}>
                              {unrealized >= 0 ? "+" : ""}{fmtMoney(unrealized)}
                            </div>
                            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: returnVal == null ? t.textFaint : returnVal >= 0 ? t.accent : "#EF4444" }}>{returnStr || "—"}</div>
                            <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: assetToday == null ? t.textFaint : assetToday >= 0 ? t.accent : "#EF4444" }}>
                              {assetToday != null ? `${assetToday >= 0 ? "+" : ""}${assetToday.toFixed(2)}%` : "—"}
                            </div>
                          </div>

                          {/* Expanded holdings */}
                          {isOpen && (
                            <div style={{ background: t.isDark ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.02)", borderBottom: `1px solid ${t.border}` }}>
                              {/* Child header */}
                              <div style={{ display: "grid", gridTemplateColumns: colChild, padding: "10px 20px 8px", borderBottom: `1px solid ${t.border}` }}>
                                <div />
                                <div style={{ ...hStyle, textAlign: "left" }}>Ticker</div>
                                <div style={{ ...hStyle, textAlign: "left" }}>Name</div>
                                <div style={hStyle}>Shares</div>
                                <div style={{ ...hStyle, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                  Avg Cost
                                  <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
                                    onMouseEnter={e => e.currentTarget.querySelector(".cb-tip").style.display = "block"}
                                    onMouseLeave={e => e.currentTarget.querySelector(".cb-tip").style.display = "none"}
                                  >
                                    <span style={{ cursor: "help", color: t.accent, fontSize: 11, fontWeight: 700, lineHeight: 1 }}>ⓘ</span>
                                    <div className="cb-tip" style={{
                                      display: "none", position: "absolute", bottom: "calc(100% + 6px)", right: 0,
                                      width: 260, background: t.isDark ? "#1e2330" : "#1a1a2e", color: "#e2e8f0",
                                      fontSize: 12, lineHeight: 1.5, padding: "10px 12px", borderRadius: 8,
                                      boxShadow: "0 4px 20px rgba(0,0,0,0.3)", zIndex: 100, fontWeight: 400,
                                      textTransform: "none", letterSpacing: "normal",
                                    }}>
                                      Some brokers (e.g. SoFi) don't share cost basis with Plaid. Click any <strong style={{ color: "#7dd3fc" }}>+ Add</strong> cell to enter your avg cost manually — it saves per account.
                                    </div>
                                  </span>
                                </div>
                                <div style={hStyle}>Value</div>
                                <div style={hStyle}>Unrealized</div>
                                <div style={hStyle}>Today</div>
                                <div style={hStyle}>Return</div>
                              </div>

                              {holdings.map((h, j) => {
                                const value = h.currentPrice ? h.shares * h.currentPrice : h.value
                                const isEditingThis = editingCostBasis?.ticker === h.ticker && editingCostBasis?.plaidAccountId === h.plaidAccountId
                                const isCash = a.name === "Cash"
                                const needsInput = !h.isManual && !isCash && h.avgPrice === 0
                                return (
                                  <div
                                    key={h.ticker + j}
                                    style={{
                                      display: "grid", gridTemplateColumns: colChild,
                                      padding: "11px 20px", alignItems: "center",
                                      borderBottom: j < holdings.length - 1 ? `1px solid ${t.border}` : "none",
                                    }}
                                  >
                                    <div />
                                    <div>
                                      <span style={{ fontSize: 13, fontWeight: 800, color: a.color, letterSpacing: "0.03em" }}>{h.ticker}</span>
                                      {h.isManual && <span style={{ fontSize: 9, marginLeft: 4, color: t.textFaint, fontWeight: 600 }}>MANUAL</span>}
                                    </div>
                                    <div style={{ fontSize: 12, color: t.textMuted, paddingRight: 8 }}>{h.name}</div>
                                    <div style={{ textAlign: "right", fontSize: 13, color: t.text }}>{h.shares > 0 ? h.shares.toFixed(4) : "—"}</div>
                                    <div
                                      style={{ textAlign: "right", fontSize: 13, color: isEditingThis ? t.text : needsInput ? t.textFaint : t.text, cursor: h.isManual ? "default" : "pointer" }}
                                      onClick={() => { if (!h.isManual && !isCash && h.plaidAccountId) setEditingCostBasis({ plaidAccountId: h.plaidAccountId, ticker: h.ticker, value: h.avgPrice > 0 ? h.avgPrice.toFixed(2) : "" }) }}
                                    >
                                      {isEditingThis ? (
                                        <input
                                          autoFocus
                                          type="number"
                                          step="0.01"
                                          value={editingCostBasis.value}
                                          onChange={e => setEditingCostBasis(prev => ({ ...prev, value: e.target.value }))}
                                          onBlur={() => { saveCostBasis(editingCostBasis.plaidAccountId, h.ticker, editingCostBasis.value); setEditingCostBasis(null) }}
                                          onKeyDown={e => { if (e.key === "Enter") { saveCostBasis(editingCostBasis.plaidAccountId, h.ticker, editingCostBasis.value); setEditingCostBasis(null) } if (e.key === "Escape") setEditingCostBasis(null) }}
                                          style={{ width: 72, textAlign: "right", fontSize: 13, background: t.hover, border: `1px solid ${t.accent}`, borderRadius: 4, padding: "2px 4px", color: t.text, outline: "none" }}
                                        />
                                      ) : needsInput ? (
                                        <span style={{ fontSize: 11, color: t.accent, fontWeight: 600 }}>+ Add</span>
                                      ) : isCash ? "—" : fmtMoney(h.avgPrice)}
                                    </div>
                                    <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: t.text }}>{fmtMoney(value)}</div>
                                    <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: h.unrealized >= 0 ? t.accent : "#EF4444" }}>
                                      {h.unrealized !== 0 ? (h.unrealized >= 0 ? "+" : "") + fmtMoney(h.unrealized) : "—"}
                                    </div>
                                    <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: h.changePercent == null ? t.textFaint : h.changePercent >= 0 ? t.accent : "#EF4444" }}>
                                      {h.changePercent != null ? `${h.changePercent >= 0 ? "+" : ""}${h.changePercent.toFixed(2)}%` : "—"}
                                    </div>
                                    <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: h.totalReturn == null ? t.textFaint : h.totalReturn >= 0 ? t.accent : "#EF4444" }}>
                                      {h.totalReturn != null ? `${h.totalReturn >= 0 ? "+" : ""}${h.totalReturn.toFixed(1)}%` : "—"}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              </div>}

              {/* ── FEES TAB ── */}
              {invTab === "fees" && (() => {
                const holdings = plaidHoldings?.holdings || []
                const securities = plaidHoldings?.securities || []
                if (!holdings.length && !computedHoldingsData) return null

                // Build per-holding fee rows from real Plaid data or mock data
                const rows = []
                if (holdings.length) {
                  const secMap = Object.fromEntries(securities.map(s => [s.security_id, s]))
                  for (const h of holdings) {
                    const sec = secMap[h.security_id] || {}
                    const ticker = sec.ticker_symbol?.toUpperCase()
                    if (!ticker) continue
                    const db = FUND_FEE_DB[ticker]
                    if (!db) continue
                    const value = h.institution_value || (sec.close_price * h.quantity) || 0
                    if (value <= 0) continue
                    const annualFee = value * db.er / 100
                    const threshold = FEE_THRESHOLDS[db.cat] || 0.20
                    rows.push({ ticker, name: sec.name || ticker, value, er: db.er, annualFee, cat: db.cat, flagged: db.er > threshold, alts: db.alts })
                  }
                } else {
                  const mockHoldings = Object.values(computedHoldingsData || {}).flat()
                  for (const h of mockHoldings) {
                    const ticker = h.ticker?.toUpperCase()
                    if (!ticker) continue
                    const db = FUND_FEE_DB[ticker]
                    if (!db) continue
                    const value = h.value || 0
                    if (value <= 0) continue
                    const annualFee = value * db.er / 100
                    const threshold = FEE_THRESHOLDS[db.cat] || 0.20
                    rows.push({ ticker, name: h.name || ticker, value, er: db.er, annualFee, cat: db.cat, flagged: db.er > threshold, alts: db.alts })
                  }
                }
                if (!rows.length) return null

                // Merge duplicate tickers (same fund held in multiple accounts)
                const merged = Object.values(rows.reduce((acc, r) => {
                  if (acc[r.ticker]) {
                    acc[r.ticker].value += r.value
                    acc[r.ticker].annualFee += r.annualFee
                  } else {
                    acc[r.ticker] = { ...r }
                  }
                  return acc
                }, {}))

                const totalValue = merged.reduce((s, r) => s + r.value, 0)
                const totalAnnualFee = merged.reduce((s, r) => s + r.annualFee, 0)
                const weightedER = totalValue > 0 ? (totalAnnualFee / totalValue) * 100 : 0
                const flaggedRows = merged.filter(r => r.flagged).sort((a, b) => b.annualFee - a.annualFee)
                const okRows = merged.filter(r => !r.flagged).sort((a, b) => b.value - a.value)

                // Potential savings if all flagged switch to best alt
                const potentialSavings = flaggedRows.reduce((s, r) => {
                  if (!r.alts.length) return s
                  const bestAlt = r.alts.reduce((b, a) => a.er < b.er ? a : b)
                  return s + r.value * (r.er - bestAlt.er) / 100
                }, 0)

                // 30-year projections
                const years = 30, growthRate = 0.07
                const project30 = (annualFee) => {
                  let drag = 0
                  for (let y = 1; y <= years; y++) drag += annualFee * Math.pow(1 + growthRate, years - y)
                  return Math.round(drag)
                }
                const currentDrag = project30(totalAnnualFee)
                const optimizedDrag = project30(totalAnnualFee - potentialSavings)
                const projSave = currentDrag - optimizedDrag

                // ER-based color: green < 0.10% · yellow 0.10–0.25% · red > 0.25%
                const feeColor = weightedER < 0.10 ? "#22C55E" : weightedER < 0.25 ? "#F59E0B" : "#EF4444"
                // ER-based color for the weighted average badge
                const statusColor = weightedER < 0.10 ? "#22C55E" : weightedER < 0.30 ? "#F59E0B" : "#EF4444"
                const statusLabel = weightedER < 0.10 ? "Excellent" : weightedER < 0.30 ? "Moderate" : "High"

                return (
                  <div style={{ ...card, marginTop: 20, overflow: "hidden" }}>
                    {/* Header */}
                    <div style={{ padding: "22px 24px 18px", borderBottom: `1px solid ${t.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <div>
                          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0, marginBottom: 3 }}>Fund Fee Analyzer</h3>
                          <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>Expense ratios on your ETFs & funds — and what cheaper alternatives could save you</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: feeColor }}>${Math.round(totalAnnualFee).toLocaleString()}<span style={{ fontSize: 12, fontWeight: 500, color: t.textMuted }}>/yr</span></div>
                          <div style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: `${statusColor}20`, color: statusColor, fontWeight: 700, display: "inline-block", marginTop: 3 }}>{statusLabel} · {weightedER.toFixed(2)}% weighted avg</div>
                        </div>
                      </div>
                      {/* 30-year projection */}
                      {potentialSavings > 0 && (
                        <div style={{ background: t.isDark ? "rgba(34,197,94,0.08)" : "rgba(34,197,94,0.06)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#22C55E", marginBottom: 2 }}>💡 Optimization opportunity</div>
                            <div style={{ fontSize: 12, color: t.textMuted }}>Switching flagged funds to lower-cost equivalents could save <strong style={{ color: t.text }}>${Math.round(potentialSavings).toLocaleString()}/yr</strong> — worth <strong style={{ color: "#22C55E" }}>${projSave.toLocaleString()}</strong> over 30 years at 7% growth</div>
                          </div>
                          <div style={{ flexShrink: 0, textAlign: "right" }}>
                            <div style={{ fontSize: 11, color: t.textFaint }}>30-yr drag</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: "#EF4444" }}>${currentDrag.toLocaleString()}</div>
                            <div style={{ fontSize: 11, color: "#22C55E", fontWeight: 700 }}>→ ${optimizedDrag.toLocaleString()}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Holdings list */}
                    <div style={{ padding: "0 24px 20px" }}>
                      {/* Header row */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 90px", gap: 8, padding: "12px 0 8px", borderBottom: `1px solid ${t.border}` }}>
                        {["Fund", "Expense Ratio", "Annual Cost", "Value"].map((h, i) => (
                          <span key={h} style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "left" : "right" }}>{h}</span>
                        ))}
                      </div>

                      {[...flaggedRows, ...okRows].map(r => {
                        const isExpanded = expandedFee === r.ticker
                        const threshold = FEE_THRESHOLDS[r.cat] || 0.20
                        const erColor = r.flagged ? "#EF4444" : "#22C55E"
                        return (
                          <div key={r.ticker}>
                            <div
                              onClick={() => r.alts.length ? setExpandedFee(isExpanded ? null : r.ticker) : null}
                              style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 90px", gap: 8, padding: "11px 0", borderBottom: `1px solid ${t.border}`, cursor: r.alts.length ? "pointer" : "default", alignItems: "center" }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                {r.flagged && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 4, background: "#EF444420", color: "#EF4444", flexShrink: 0 }}>HIGH</span>}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{r.ticker}</div>
                                  <div style={{ fontSize: 11, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.cat}</div>
                                </div>
                                {r.alts.length > 0 && <span style={{ fontSize: 10, color: t.accent, flexShrink: 0 }}>{isExpanded ? "▴" : "▾"} {r.alts.length} alt{r.alts.length > 1 ? "s" : ""}</span>}
                              </div>
                              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: erColor }}>{r.er.toFixed(2)}%</div>
                              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 600, color: r.flagged ? "#EF4444" : "#22C55E" }}>${Math.round(r.annualFee).toLocaleString()}</div>
                              <div style={{ textAlign: "right", fontSize: 12, color: t.textMuted }}>${Math.round(r.value).toLocaleString()}</div>
                            </div>
                            {isExpanded && r.alts.length > 0 && (
                              <div style={{ background: t.isDark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)", borderRadius: 8, padding: "12px 14px", margin: "4px 0 8px", borderLeft: `3px solid ${t.accent}` }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Lower-cost alternatives — same exposure</div>
                                {r.alts.map(alt => {
                                  const annualSaving = r.value * (r.er - alt.er) / 100
                                  const save30 = project30(annualSaving)
                                  return (
                                    <div key={alt.t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${t.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}` }}>
                                      <div>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{alt.t}</span>
                                        <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8 }}>{alt.n}</span>
                                      </div>
                                      <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#22C55E" }}>{alt.er.toFixed(2)}% · save ${Math.round(annualSaving).toLocaleString()}/yr</div>
                                        <div style={{ fontSize: 11, color: t.textFaint }}>≈ ${save30.toLocaleString()} over 30 yrs</div>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}

                      {/* Summary footer */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 90px", gap: 8, paddingTop: 12, alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>Total tracked funds</span>
                        <span style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: t.textMuted }}>{weightedER.toFixed(2)}% avg</span>
                        <span style={{ textAlign: "right", fontSize: 13, fontWeight: 800, color: feeColor }}>${Math.round(totalAnnualFee).toLocaleString()}</span>
                        <span style={{ textAlign: "right", fontSize: 12, color: t.textMuted }}>${Math.round(totalValue).toLocaleString()}</span>
                      </div>
                      <p style={{ fontSize: 11, color: t.textFaint, marginTop: 12, fontStyle: "italic" }}>
                        Expense ratios sourced from fund providers. Holdings not in our database are excluded. Click any flagged fund to see alternatives.
                      </p>
                    </div>
                  </div>
                )
              })()}

              {/* ── DIVIDENDS TAB ── */}
              {invTab === "dividends" && (() => {
                const annualEst = estimatedDividends.reduce((s, m) => s + m.total, 0)
                const hasDivs = annualEst > 0

                const allHoldings = Object.values(computedHoldingsData || {}).flat()
                const tickerDivMap = {}
                for (const h of allHoldings) {
                  if (!h.ticker || h.shares <= 0) continue
                  const q = quotePrices[h.ticker]
                  if (!q || !q.dividendRate || q.dividendRate <= 0) continue
                  if (!tickerDivMap[h.ticker]) tickerDivMap[h.ticker] = { ticker: h.ticker, shares: 0, dividendRate: q.dividendRate }
                  tickerDivMap[h.ticker].shares += h.shares
                }
                const tickerDivData = Object.values(tickerDivMap).map(d => {
                  const annualIncome = d.shares * d.dividendRate
                  const costKeys = Object.keys(costBasisOverrides).filter(k => k.endsWith(`::${d.ticker}`))
                  const avgCost = costKeys.length > 0 ? costKeys.reduce((s, k) => s + (costBasisOverrides[k] || 0), 0) / costKeys.length : 0
                  const yoc = avgCost > 0 ? (d.dividendRate / avgCost) * 100 : null
                  return { ...d, annualIncome, yoc }
                }).sort((a, b) => b.annualIncome - a.annualIncome)

                const typeMap = {}
                for (const d of tickerDivData) {
                  const q = quotePrices[d.ticker]
                  const type = q?.quoteType === "ETF" ? "ETFs" : q?.quoteType === "MUTUALFUND" ? "Mutual Funds" : "Stocks"
                  typeMap[type] = (typeMap[type] || 0) + d.annualIncome
                }
                const typeColors = { "ETFs": "#3B82F6", "Stocks": "#8B5CF6", "Mutual Funds": "#F59E0B" }
                const typeData = Object.entries(typeMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
                const typeTotal = typeData.reduce((s, d) => s + d.value, 0)

                return (
                  <div>
                    {/* 4 stat cards */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                      {[["Daily", annualEst / 365], ["Monthly", annualEst / 12], ["Quarterly", annualEst / 4], ["Yearly", annualEst]].map(([label, value]) => (
                        <div key={label} style={{ ...card, padding: "18px 20px" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</div>
                          <div style={{ fontSize: hasDivs ? 22 : 16, fontWeight: 800, color: hasDivs ? "#22C55E" : t.textFaint }}>{hasDivs ? fmtMoney(value) : "—"}</div>
                        </div>
                      ))}
                    </div>

                    {!hasDivs ? (
                      <div style={{ ...card, padding: "48px 32px", textAlign: "center" }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🌱</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 6 }}>No dividend income detected</div>
                        <div style={{ fontSize: 13, color: t.textMuted }}>Holdings like VOO, SCHD, or dividend stocks will appear here once Yahoo Finance returns dividend data.</div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
                        {/* Left — Income by Ticker */}
                        <div style={{ ...card, padding: "22px 24px" }}>
                          <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: "0 0 14px" }}>Income by Ticker</h3>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 70px 52px", gap: 8, paddingBottom: 8, borderBottom: `1px solid ${t.border}`, marginBottom: 4 }}>
                            {["Ticker", "Annual", "% Total", "YoC"].map((h, i) => (
                              <span key={h} style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "left" : "right" }}>{h}</span>
                            ))}
                          </div>
                          {tickerDivData.map(d => (
                            <div key={d.ticker} style={{ display: "grid", gridTemplateColumns: "1fr 60px 70px 52px", gap: 8, padding: "9px 0", borderBottom: `1px solid ${t.border}` }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{d.ticker}</div>
                                <div style={{ fontSize: 11, color: t.textMuted }}>{d.shares.toFixed(4)} sh · ${d.dividendRate.toFixed(2)}/yr</div>
                              </div>
                              <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "#22C55E", alignSelf: "center" }}>{fmtMoney(d.annualIncome)}</div>
                              <div style={{ textAlign: "right", fontSize: 13, color: t.textMuted, alignSelf: "center" }}>{((d.annualIncome / annualEst) * 100).toFixed(1)}%</div>
                              <div style={{ textAlign: "right", alignSelf: "center" }}>
                                {d.yoc != null
                                  ? <span style={{ fontSize: 12, fontWeight: 700, color: d.yoc >= 4 ? "#22C55E" : d.yoc >= 2 ? "#F59E0B" : t.textMuted }}>{d.yoc.toFixed(2)}%</span>
                                  : <span style={{ fontSize: 11, color: t.textFaint }}>—</span>}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Right — Dividend by Asset Type */}
                        <div style={{ ...card, padding: "22px 24px" }}>
                          <h3 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: "0 0 16px" }}>Dividend by Asset Type</h3>
                          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {typeData.map(d => {
                              const pct = typeTotal > 0 ? (d.value / typeTotal) * 100 : 0
                              const color = typeColors[d.name] || "#94A3B8"
                              return (
                                <div key={d.name}>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{d.name}</span>
                                    <div style={{ display: "flex", gap: 10 }}>
                                      <span style={{ fontSize: 13, fontWeight: 700, color }}>{fmtMoney(d.value)}/yr</span>
                                      <span style={{ fontSize: 12, color: t.textMuted }}>{pct.toFixed(1)}%</span>
                                    </div>
                                  </div>
                                  <div style={{ height: 8, borderRadius: 4, background: t.hover, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: color, transition: "width 0.5s ease" }} />
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          )}

          {activeNav === "retirement" && <RetirementPlanner session={session} theme={t} paychecks={paychecks} transactions={transactions} onXPAward={triggerXP} householdMembers={householdMembers} />}
          {activeNav === "simulators" && <SimulatorsPage session={session} theme={t} transactions={transactions} />}
          {activeNav === "paycheck" && (
            <div>
              <div style={{ marginBottom: 28 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text, marginBottom: 6 }}>Paycheck</h1>
                <p style={{ color: t.textMuted, fontSize: 14 }}>Track your gross income, taxes, and deductions to see your full compensation picture</p>
              </div>
              <PaycheckSection theme={t} paychecks={paychecks} savePaychecks={savePaychecks} householdMembers={householdMembers} accounts={accounts} />
            </div>
          )}
          {activeNav === "levels" && <LevelsPage session={session} theme={t} userXp={userXp} />}
          {activeNav === "settings" && (
  <SettingsPage
    session={session}
    theme={t}
    userAvatar={userAvatar}
    setUserAvatar={setUserAvatar}
    catCustom={catCustom}
    saveCatCustom={saveCatCustom}
    effectiveCategories={effectiveCategories}
    householdMembers={householdMembers}
    onHouseholdChange={loadHousehold}
    settings={settings}
    onSettingsChange={onSettingsChange}
    />
          )}
          {activeNav === "report" && (
            <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 0" }}>
              <ReportIssueTab session={session} theme={t} />
            </div>
          )}
          {activeNav === "feature" && (
            <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 0" }}>
              <RequestFeatureTab session={session} theme={t} />
            </div>
          )}
          {activeNav === "waitlist" && (
            <div style={{ maxWidth: 700, margin: "0 auto", padding: "32px 0" }}>
              <WaitlistAdminPage session={session} theme={t} />
            </div>
          )}
        </main>
      </div>
    </ThemeContext.Provider>
  )
}

// ── APP ────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userAvatar, setUserAvatar] = useState("")
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSession(session)
        setNeedsPasswordReset(true)
      } else {
        setSession(session)
      }
    })

    // ── Auto-detect uncaught JS errors ──
    function onError(event) { autoReportError(event.message, event.error?.stack) }
    function onUnhandledRejection(event) { autoReportError(String(event.reason), event.reason?.stack) }
    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#64748b', fontFamily: 'system-ui' }}>Loading...</p>
      </div>
    )
  }

  if (needsPasswordReset) return <SetNewPasswordPage onDone={() => setNeedsPasswordReset(false)} />

  if (!session) return <LandingPage />

  return (
    <ErrorBoundary>
      <Dashboard
        session={session}
        userAvatar={userAvatar}
        setUserAvatar={setUserAvatar}
      />
    </ErrorBoundary>
  )
}